import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Navbar, Nav, Container, Button, Modal, Form, Row, Col, Card, Dropdown, Table, Alert, Badge, Nav as TabNav
} from 'react-bootstrap';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import axiosReal from 'axios';
import './i18n';
import { useTranslation } from 'react-i18next';
import Dexie from 'dexie';
import { requestForToken } from './firebaseFrontend'; // 👈 YAHAN RAKHEIN IS LINE KO!
//import PlanSelector from './components/billing/PlanSelector';
//import SubscriptionStatusBanner from './components/billing/SubscriptionStatusBanner';
import PaymentGate from './components/billing/PaymentGate';
import AdminApprovalPanel from './components/billing/AdminApprovalPanel';

axios.defaults.withCredentials = true;
axiosReal.defaults.withCredentials = true;

const offlineDB = new Dexie('ConstructionSiteOfflineDB');
offlineDB.version(1).stores({
  cachedAssets: '_id, name, serialNumber, status', // Server se aaye hue assets ka local cache
  cachedClashes: '_id, modelId, title, status',   // BIM Clashes ka local cache
  outboxQueue: '++localId, url, method, data, timestamp' // Offline submit hone wale forms ka outbox ledger
});

const allLanguagesList = [
  { code: 'en', name: 'English' },
  { code: 'ur', name: 'Urdu (اردو)' },
  { code: 'ur-roman', name: 'Roman Urdu' }
];

const AppContent = () => {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language || 'en');
  const footerRef = useRef(null);
  const navigate = useNavigate();

  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  const [userRole, setUserRole] = useState(localStorage.getItem('role') || '');
  const [userName, setUserName] = useState(localStorage.getItem('userName') || 'User');
  const [searchTerm, setSearchTerm] = useState('');

  const [projects, setProjects] = useState([]);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [newProjectData, setNewProjectData] = useState({ name: '', description: '', location: '' });
  const [showMoreLangModal, setShowMoreLangModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  const [signupData, setSignupData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'Admin',
    phone: '',    // Live field link initialized
    company: ''   // Corporate field link initialized
  });
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const [selectedPlan, setSelectedPlan] = useState(''); // 'trial', '4-sites', '1-year'
  const [receiptFile, setReceiptFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleForgotPasswordClick = () => {
    alert("Password reset link template sequence initiated. Please check email ledger.");
  };

  const handleSwitchToSignup = () => {
    setShowLoginModal(false);
    setShowSignupModal(true);
  };

  const fetchProjects = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await axiosReal.get('https://api.mfrbuild.com/api/projects', {
        headers: { 'x-auth-token': token }
      });

      if (res.data) {
        // Backend 'title' ko Frontend 'name' mein map kiya taake cards crash na hon
        const mappedProjects = res.data.map(proj => ({
          _id: proj._id,
          name: proj.title,
          location: proj.location || 'No Location',
          description: proj.description || `Operational logs tracking real-time construction pipelines.`
        }));

        setProjects(mappedProjects);
      }
    } catch (err) {       // 🌟 KEEPING THIS: Yeh database tracking error debugging ke liye zaroori hai
      console.error("Database tracking fault:", err.message);
      // Fallback dummy data agar server down ho
      setProjects([
        { _id: 'p1', name: 'MFR Commercial Plaza', description: 'Main structural steel frame construction.', location: 'Lahore' },
        { _id: 'p2', name: 'Alpha Infrastructure Ring', description: 'Road grading and asphalt layers production.', location: 'Kuala Lumpur' }
      ]);
    }
  }, []);

  // ==========================================
  // 1. MODALS LOGIC (ROLE BASED) - FIXED ✅
  // ==========================================
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedRole = localStorage.getItem('role') || userRole;

    if (!token || !isAuthenticated) {
      setShowLoginModal(false);
      setShowSubscriptionModal(false);
      return;
    }

    if (isAuthenticated && token) {
      if (storedRole === 'Admin' && localStorage.getItem('isPlanActive') !== 'true') {
        setShowSubscriptionModal(true);
        setShowLoginModal(false);
      } else {
        setShowSubscriptionModal(false);
        setShowLoginModal(false);
      }
    }
  }, [isAuthenticated, userRole]);

  // ==========================================
  // 2. PROJECTS DATA FETCHING (ROLE BASED)
  // ==========================================
  useEffect(() => {
    if (isAuthenticated) {
      const token = localStorage.getItem('token');
      const storedRole = localStorage.getItem('role');

      // Backend API ko role ke mutabiq request bhejen
      // Taake backend khud hi filter kar ke data bheeje
      if (storedRole === 'Admin') {
        // Admin saare projects dekhega
        fetchProjects('all');
      } else {
        // Client, Supervisor, Labour sirf assigned projects dekhenge
        fetchProjects('assigned');
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (i18n.language && i18n.language !== language) {
      setLanguage(i18n.language);
    }
  }, [i18n.language, language]);

  const handleCreateProjectSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    const formData = new FormData(e.currentTarget);
    const name = formData.get('projectName') || newProjectData.name;
    const location = formData.get('projectLocation') || newProjectData.location;
    const description = formData.get('projectDescription') || newProjectData.description || '';

    if (!name || !location) {
      // Dynamic validation alert
      alert(t('errorFillRequiredFields') || "Please fill in both Project Name and Location fields!");
      return;
    }

    try {
      const res = await axiosReal.post('https://api.mfrbuild.com/api/projects',
        { name, location, description },
        { headers: { 'x-auth-token': token } }
      );

      if (res.data) {
        const createdSector = {
          _id: res.data._id,
          name: res.data.title,
          location: res.data.location || 'No Location',
          description: res.data.description || description || 'Field core operations loop mapped for construction task orchestration.'
        };

        setProjects([createdSector, ...projects]);
        setShowNewProjectModal(false);
        setNewProjectData({ name: '', description: '', location: '' });
        e.target.reset();
      }
    } catch (err) {
      console.error("Database initialization fault:", err.response?.data || err.message);

      const mockProject = {
        _id: 'mock_' + Math.random().toString(36).substr(2, 9),
        name: name,
        location: location,
        description: description || 'Workspace sandbox environment.'
      };

      setProjects([mockProject, ...projects]);
      setShowNewProjectModal(false);
      setNewProjectData({ name: '', description: '', location: '' });
    }
  };

  const scrollToFooter = () => {
    if (footerRef.current) {
      footerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axiosReal.post('https://api.mfrbuild.com/api/auth/login', loginData);

      if (res.data) {
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('role', res.data.role);
        localStorage.setItem('userName', res.data.name || 'User');

        // Agar admin ne pehle se plan liya hua hai to backend se response mein 'isPlanActive' mangwayen
        if (res.data.isPlanActive) {
          localStorage.setItem('isPlanActive', 'true');
        }

        setUserRole(res.data.role);
        setIsAuthenticated(true);
        setShowLoginModal(false);

        navigate('/'); // Dashboard par bhej dein
      }
    } catch (err) {
      console.error("Login Error:", err.message);
      alert(err.response?.data?.message || "Invalid Credentials");
    }
  };

  // ==========================================
  // 4. SUBSCRIPTION/RECEIPT SUBMIT (UPDATED)
  // ==========================================
  // Jab Admin receipt submit kare aur response OK ho:
  const handleReceiptSuccess = () => {
    localStorage.setItem('isPlanActive', 'true'); // Flag active karein
    localStorage.removeItem('isNewRegistration'); // Flag clear karein
    setShowSubscriptionModal(false);
    setShowLoginModal(false);
    window.location.reload(); // Ek dafa reload taake state fresh ho jaye live par
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();

    const name = signupData.name || "";
    const email = signupData.email || "";
    const password = signupData.password || "";
    const phone = signupData.phone || "";
    const company = signupData.company || "";
    let role = signupData.role || "Admin";

    if (!name || !email || !password || !phone) {
      alert(t('errorFillRequiredFields') || "Please fill all required registration fields properly.");
      return;
    }

    // 🟢 URL se invite params nikaalein (agar invite link se aaya ho)
    const urlParams = new URLSearchParams(window.location.search);
    const inviteProjectId = urlParams.get('project');
    const inviteEmail = urlParams.get('email');
    const inviteToken = urlParams.get('token');
    const inviteRole = urlParams.get('role');

    // 🟢 Agar invite se role mila hai, usay use karein (form ka manual role ignore)
    if (inviteRole) {
      role = inviteRole;
    }

    try {
      const payload = {
        name,
        email,
        password,
        role,
        phone,
        company
      };

      if (inviteProjectId && inviteToken) {
        payload.projectId = inviteProjectId;
        payload.token = inviteToken;
      }

      const response = await axiosReal.post('https://api.mfrbuild.com/api/auth/signup', payload);

      if (response.data && response.data.success) {
        if (typeof setShowSignupModal !== 'undefined') {
          setShowSignupModal(false);
        }

        if (inviteProjectId && inviteToken) {
          alert(response.data.msg || "Registration successful! You've been added to the project. Please login.");
          if (typeof setShowLoginModal !== 'undefined') {
            setShowLoginModal(true);
          }
        } else if (role.includes('Admin')) {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('role', response.data.role);
          localStorage.setItem('userName', response.data.name || 'User');
          if (response.data.isPlanActive) {
            localStorage.setItem('isPlanActive', 'true');
          }
          setIsAuthenticated(true);
          setUserRole(response.data.role);
          if (typeof setShowSubscriptionModal !== 'undefined' && !response.data.isPlanActive) {
            setShowSubscriptionModal(true);
          }
        }

        else {
          if (typeof setShowLoginModal !== 'undefined') {
            setShowLoginModal(true);
          }
        }
      }
    } catch (err) {
      console.error("Signup routing loop issue:", err.message);

      if (err.response) {
        console.log("Server Error Data:", err.response.data);
      }

      const feedback = err.response?.data?.msg || err.response?.data?.message || "Signup Matrix failure. Check parameters.";
      alert(feedback);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    setIsAuthenticated(false);
    setUserRole('');
    setUserName('User');
    setProjects([]);
    navigate('/');
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>

      <style>{`

         .mfr-navbar-welcome-banner {
          font-weight: 600 !important;
           color: #475569 !important;
          font-size: 0.95rem !important;
          background-color: #f1f5f9 !important;
  padding: 6px 16px !important;
  border-radius: 50px !important;
  border: 1px solid #e2e8f0 !important;
  letter-spacing: 0.2px !important;
}

.mfr-navbar-contact-btn {
  border: 1px solid #cbd5e1 !important;
  color: #334155 !important;
  font-weight: 600 !important;
  font-size: 0.9rem !important;
  border-radius: 10px !important;
  padding: 8px 18px !important;
  transition: all 0.2s ease-in-out !important;
  background-color: transparent !important;
}

.mfr-navbar-contact-btn:hover {
  background-color: #f8fafc !important;
  border-color: #0d6efd !important;
  color: #0d6efd !important;
}

/* Language Dropdown Custom Global Standard */
.mfr-navbar-lang-toggle {
  font-weight: 600 !important;
  font-size: 0.9rem !important;
  border-radius: 10px !important;
  padding: 8px 16px !important;
  border: 1px solid #e2e8f0 !important;
}
        
/* 🟢 MFR Ultimate High-Level Corporate WhatsApp Button */
.mfr-advisory-whatsapp {
  position: fixed !important;
  bottom: 30px !important;
  right: 30px !important;
  background-color: #25D366 !important;
  border-radius: 50% !important;
  width: 60px !important;
  height: 60px !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  z-index: 999999 !important;
  text-decoration: none !important;
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important;
  box-shadow: 0 8px 24px rgba(37, 211, 102, 0.35), 
              0 0 0 0 rgba(37, 211, 102, 0.5) !important;
  animation: mfr-premium-whatsapp-wave 2s infinite !important;
}

/* Perfect Inside Official Logo Alignment & Border Safe Zone */
.mfr-advisory-whatsapp svg {
  width: 32px !important;
  height: 32px !important;
  fill: #ffffff !important;
  display: block !important;
  z-index: 2 !important;
}

/* Premium Hover State - Pop Out Luxury Look */
.mfr-advisory-whatsapp:hover {
  transform: scale(1.1) translateY(-3px) !important;
  background-color: #20ba5a !important;
  box-shadow: 0 12px 28px rgba(32, 186, 90, 0.45) !important;
}

/* 🌊 High-Level Subtle Ripple Wave Animation (Matches Advisory Apps Premium Flow) */
@keyframes mfr-premium-whatsapp-wave {
  0% {
    box-shadow: 0 8px 24px rgba(37, 211, 102, 0.35), 
                0 0 0 0 rgba(37, 211, 102, 0.6) !important;
  }
  70% {
    box-shadow: 0 8px 24px rgba(37, 211, 102, 0.35), 
                0 0 0 15px rgba(37, 211, 102, 0) !important;
  }
  100% {
    box-shadow: 0 8px 24px rgba(37, 211, 102, 0.35), 
                0 0 0 0 rgba(37, 211, 102, 0) !important;
  }
}

        /* 🌊 MFR Hero Section Upgraded Styles */
.mfr-hero-wrapper {
  background-color: #fcfdfe !important;
  overflow: hidden !important;
}
.mfr-hero-title {
  font-weight: 800 !important;
  font-size: 3.4rem !important;
  letter-spacing: -1px !important;
  color: #1e293b !important;
  line-height: 1.15 !important;
}
.mfr-hero-gradient-text {
  background: linear-gradient(45deg, #0d6efd, #0a4ba8) !important;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
}
.mfr-hero-subtext {
  line-height: 1.6 !important;
  font-weight: 400 !important;
  max-width: 580px !important;
}
/* 🖼️ Premium Glass Frame with Smooth Floating Animation */
.mfr-hero-image-frame {
  position: relative !important;
  padding: 10px !important;
  background: rgba(255, 255, 255, 0.7) !important;
  backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(226, 232, 240, 0.9) !important;
  border-radius: 26px !important;
  box-shadow: 0 25px 60px -15px rgba(0, 0, 0, 0.09) !important;
  animation: mfrHeroFloat 4s ease-in-out infinite !important; /* 🔥 Infinite Floating Animation */
}
.mfr-hero-img {
  object-fit: cover !important;
  max-height: 500px !important;
  width: 100% !important;
  border-radius: 18px !important;
  display: block !important;
}

/* 🔄 Animation Keyframes */
@keyframes mfrHeroFloat {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
}

/* 🛠️ MFR Features Grid Upgraded Styles */
.mfr-features-section {
  background-color: #ffffff !important;
}
.mfr-features-heading {
  font-weight: 800 !important;
  color: #1e293b !important;
  letter-spacing: -0.5px !important;
  font-size: 2.2rem !important;
}
.mfr-feature-card-title {
  font-weight: 700 !important;
  color: #1e293b !important;
  font-size: 1.2rem !important;
  margin-bottom: 10px !important;
}
.mfr-feature-card-text {
  color: #64748b !important;
  line-height: 1.5 !important;
  font-size: 0.9rem !important;
}
  .mfr-premium-card {
          transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1) !important;
          border: 1px solid #eaeaea !important;
        }
        .mfr-premium-card:hover {
          transform: translateY(-6px) !important;
          box-shadow: 0 12px 25px rgba(0, 0, 0, 0.08) !important;
          border-color: #bc5736 !important;
        }
        /* 📡 Naye sections ke liye extra premium animations aur styling */
        .mfr-animate-card {
          transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1) !important;
        }
        .mfr-animate-card:hover {
          transform: translateY(-10px) !important;
          box-shadow: 0 20px 38px rgba(0,0,0,0.15) !important;
        }
        .mfr-animate-card img {
          transition: transform 0.6s ease !important;
        }
        .mfr-animate-card:hover img {
          transform: scale(1.06) !important;
        }
        .mfr-cta-bg {
          background: linear-gradient(rgba(0, 52, 129, 0.85), rgba(42, 8, 8, 0.9)), 
                      url('https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=1200&auto=format&fit=crop') !important;
          background-size: cover !important;
          background-position: center !important;
          background-attachment: fixed !important;
        }
/* 🏢 MFR Upgraded Corporate Footer Styles - Ultra-Visible Edition */
.mfr-corporate-footer {
  background-color: #0b0f19 !important; /* Dark Navy Slate */
  color: #cbd5e1 !important; /* Bright Slate for high readability */
  font-size: 0.9rem !important;
}
.mfr-footer-brand {
  font-weight: 800 !important;
  font-size: 1.5rem !important;
  letter-spacing: -0.5px !important;
}
.mfr-footer-heading {
  font-weight: 700 !important;
  color: #ffffff !important; /* Solid White Headings */
  font-size: 0.95rem !important;
  letter-spacing: 0.3px !important;
}
.mfr-footer-link {
  color: #94a3b8 !important;
  text-decoration: none !important;
  transition: color 0.2s ease-in-out !important;
  font-size: 0.85rem !important;
  display: inline-block !important;
}
.mfr-footer-link:hover {
  color: #0d6efd !important;
}
.mfr-footer-store-btn {
  height: 40px !important;
  width: auto !important;
  transition: transform 0.2s ease !important;
}
.mfr-footer-store-btn:hover {
  transform: scale(1.04) !important;
}
.mfr-footer-bottom-bar {
  border-top: 1px solid rgba(255, 255, 255, 0.15) !important;
  font-size: 0.85rem !important;
  color: #e2e8f0 !important; /* High Visibility text for copyright notice */
}
.mfr-footer-social-icon {
  color: #94a3b8 !important;
  font-size: 1.1rem !important;
  transition: color 0.2s ease !important;
}
.mfr-footer-social-icon:hover {
  color: #0d6efd !important;
}
/* Special Fix for previously hidden elements */
.mfr-text-highlight-visible {
  color: #f1f5f9 !important; /* Dynamic off-white for superb readability */
  font-weight: 500 !important;
}
/* 📊 MFR Ultimate High-Level Enterprise Dashboard Styles */
.mfr-dashboard-bg {
  background-color: #f8fafc !important; /* Soft premium off-white corporate app color */
  min-height: 100vh;
}

.mfr-stat-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 16px !important;
  padding: 20px !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease !important;
}
.mfr-stat-card:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05) !important;
}

.mfr-project-premium-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 16px !important;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
  overflow: hidden;
}
.mfr-project-premium-card:hover {
  transform: translateY(-4px) !important;
  border-color: #0d6efd !important;
  box-shadow: 0 12px 30px rgba(13, 110, 253, 0.08) !important;
}

.mfr-activity-feed-panel {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 16px !important;
  padding: 20px !important;
  position: sticky !important;
  top: 90px !important;
}

.mfr-activity-item {
  border-left: 2px solid #e2e8f0 !important;
  padding-left: 15px !important;
  position: relative !important;
}
.mfr-activity-item::before {
  content: '' !important;
  position: absolute !important;
  left: -6px !important;
  top: 4px !important;
  width: 10px !important;
  height: 10px !important;
  border-radius: 50% !important;
  background-color: #0d6efd !important;
}

.mfr-search-input-wrapper {
  background: #ffffff !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 12px !important;
  padding: 4px 12px !important;
}
.mfr-search-input-wrapper input {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.mfr-custom-progress-bar {
  height: 6px !important;
  border-radius: 10px !important;
  background-color: #e2e8f0 !important;
}
.mfr-custom-progress-fill {
  height: 100% !important;
  border-radius: 10px !important;
  background: linear-gradient(90deg, #0d6efd, #0dcaf0) !important;
}

      `}</style>
      {/* HEADER NAVBAR */}
      <Navbar bg="white" expand="lg" className="shadow-sm sticky-top px-2 px-md-3">
        <Container fluid>
          {/* 🏆 BRAND LOGO */}
          <Navbar.Brand
            as={Link}
            to="/"
            onClick={(e) => {
              if (window.location.pathname === '/') {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
            className="d-flex align-items-center font-weight-bold mfr-navbar-brand-bold m-0"
            style={{ gap: '8px', fontFamily: "'Montserrat', sans-serif", textDecoration: 'none', cursor: 'pointer' }}
          >
            <svg width="36" height="36" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ minWidth: '32px' }}>
              <path d="M20 35L50 15L80 35V75L50 95L20 75V35Z" stroke="#0d6efd" strokeWidth="6" strokeLinejoin="round" />
              <path d="M50 15V95" stroke="#0d6efd" strokeWidth="4" />
              <path d="M20 35L50 55L80 35" stroke="#0d6efd" strokeWidth="4" />
              <path d="M35 60L50 50L65 60" stroke="#ffc107" strokeWidth="5" strokeLinecap="round" />
              <path d="M35 75L50 65L65 75" stroke="#ffc107" strokeWidth="5" strokeLinecap="round" />
            </svg>

            <span style={{ fontSize: 'calc(1.1rem + 0.3vw)', letterSpacing: '0.5px', lineHeight: '1' }}>
              <strong className="text-primary" style={{ fontWeight: '800' }}>MFR</strong>
              <span style={{ color: '#5a626a', fontWeight: '400', marginLeft: '4px' }}>Build</span>
            </span>
          </Navbar.Brand>
          {/* Navbar Toggle Button */}
          <Navbar.Toggle aria-controls="basic-navbar-nav" className="border-0 p-1" />
          <Navbar.Collapse id="basic-navbar-nav" className="justify-content-end">
            <Nav className="align-items-stretch align-items-lg-center gap-2 gap-lg-3 pt-3 pt-lg-0 text-nowrap">
              {/* 📢 CENTRAL WELCOME MESSAGE BANNER */}
              {!isAuthenticated && (
                <div className="d-none d-lg-block my-2 my-lg-0 me-lg-2">
                  <span className="mfr-navbar-welcome-banner text-nowrap">
                    ✨ {t('welcomeBanner')}
                  </span>
                </div>
              )}
              {/* 👤 AUTHENTICATED WELCOME MESSAGE */}
              {isAuthenticated && (
                <span className="my-1 my-lg-0 me-lg-2 text-dark fw-bold" style={{ fontSize: '0.9rem' }}>
                  {t('welcome')}, {userName} ({userRole})
                </span>
              )}
              {/* 📞 CONTACT BUTTON */}
              {!isAuthenticated && (
                <Nav.Link
                  onClick={scrollToFooter}
                  className="mfr-navbar-contact-btn py-1 px-3 text-center"
                  style={{ cursor: 'pointer' }}
                >
                  {t('contact')}
                </Nav.Link>
              )}
              {/* 🌐 GLOBAL TRANSLATION DROPDOWN */}
              <Dropdown align="end" className="my-1 my-lg-0">
                <Dropdown.Toggle variant="light" id="dropdown-language" className="mfr-navbar-lang-toggle d-flex align-items-center justify-content-between gap-2 px-3">
                  <div>
                    <i className="bi bi-translate text-primary me-2"></i>
                    {language === 'ur' ? 'Urdu (اردو)' : language === 'ur-roman' ? 'Roman Urdu' : 'English'}
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu className="shadow border-0 rounded-3 mt-2 dropdown-menu-end">
                  <Dropdown.Item onClick={() => { setLanguage('en'); i18n.changeLanguage('en'); }}>English</Dropdown.Item>
                  <Dropdown.Item onClick={() => { setLanguage('ur'); i18n.changeLanguage('ur'); }}>Urdu (اردو)</Dropdown.Item>
                  <Dropdown.Item onClick={() => { setLanguage('ur-roman'); i18n.changeLanguage('ur-roman'); }}>Roman Urdu</Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => setShowMoreLangModal(true)} className="text-primary fw-bold">
                    {t('more_languages')}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              {/* 🔐 AUTH BUTTONS */}
              <div className="d-flex flex-column flex-lg-row gap-2 mt-2 mt-lg-0">
                {!isAuthenticated ? (
                  <>
                    <Button variant="outline-primary" className="rounded-pill px-4 order-2 order-lg-1" onClick={() => setShowLoginModal(true)}>
                      {t('login')}
                    </Button>
                    <Button variant="primary" className="rounded-pill px-4 order-1 order-lg-2" onClick={() => setShowSignupModal(true)}>
                      {t('get_started')}
                    </Button>
                  </>
                ) : (
                  <Button variant="danger" className="rounded-pill px-4" onClick={handleLogout}>
                    {t('logout')}
                  </Button>
                )}
              </div>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <div style={{ flex: 1 }}>
        <Routes>
          <Route path="/owner-approvals" element={<AdminApprovalPanel />} />
          <Route path="/" element={
            !isAuthenticated ? (
              <>
                {/* 1️⃣ SECTION 1: HERO CONTAINER - FULLY RESPONSIVE & MOBILE OPTIMIZED */}
                <div className="py-4 py-md-5 position-relative mfr-hero-wrapper">
                  <Container fluid="lg" className="py-2 py-md-4">
                    <Row className="align-items-center justify-content-between g-4">
                      {/* Left Column: Wider Content Text Layout with Responsive Text Alignment */}
                      <Col lg={7} className="text-center text-lg-start pe-lg-5">
                        <h1 className="mfr-hero-title mb-3 mb-md-4" style={{ fontSize: 'calc(1.8rem + 1.5vw)', fontWeight: '800' }}>
                          {t('heroTitlePart1')} <span className="mfr-hero-gradient-text">{t('heroTitleGradient')}</span>
                        </h1>
                        <p className="text-muted mb-4 mfr-hero-subtext" style={{ fontSize: 'calc(0.95rem + 0.2vw)', lineHeight: '1.6' }}>
                          {t('heroSubtextPart1')} <strong style={{ color: '#0d6efd' }}>MFR Build</strong>.
                        </p>
                        <Button
                          variant="primary"
                          size="lg"
                          className="rounded-4 px-4 px-md-5 py-2 py-md-3 mt-2 shadow-sm mfr-animate-card w-100 w-md-auto" // 👈 FIXED: 'w-sm-auto' ko 'w-md-auto' kiya taake desktop/tablet par width clean auto ho jaye
                          style={{ fontWeight: '600', letterSpacing: '0.3px', maxWidth: '350px' }} // 👈 OPTIONAL: 'maxWidth' lagayi hai taake agar mobile screen bohot badi ho toh button ajeeb sa stretch na ho
                          onClick={() => setShowSignupModal(true)}
                        >
                          {t('getStartedBtn')}
                        </Button>
                      </Col>
                      {/* Right Column: Clean Floating Glass Image Section - Centered nicely on Mobile */}
                      <Col lg={5} className="mt-4 mt-lg-0 d-flex justify-content-center justify-content-lg-end">
                        <div className="mfr-hero-image-frame w-100" style={{ maxWidth: '480px' }}>
                          <img
                            src="https://images.unsplash.com/photo-1508450859948-4e04fabaa4ea?w=600&auto=format&fit=crop&q=60"
                            alt="Construction Layout"
                            className="mfr-hero-img img-fluid rounded-4"
                          />
                        </div>
                      </Col>
                    </Row>
                  </Container>
                </div>
                {/* 2️⃣ SECTION 2: PLATFORM EXCELLENCE (3 CARDS GRID) */}
                <div className="py-4 py-md-5 bg-light border-bottom">
                  <Container>
                    <div className="text-center mb-4 mb-md-5">
                      <span className="badge bg-primary-subtle text-primary px-3 py-2 rounded-pill fw-bold mb-2">
                        {t('platformExcellenceBadge')}
                      </span>
                      <h2 className="fw-bold text-dark mb-0" style={{ fontSize: 'calc(1.4rem + 0.8vw)' }}>
                        {t('platformExcellenceHeading')}
                      </h2>
                    </div>
                    <Row className="g-4">
                      {/* Card 1 */}
                      <Col lg={4} md={6} className="col-12">
                        <Card className="border-0 shadow-sm rounded-4 overflow-hidden h-100 mfr-animate-card bg-white">
                          <div style={{ overflow: 'hidden', height: '200px' }}>
                            <img src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=600&auto=format&fit=crop" alt="Structural Infrastructure" className="w-100 h-100" style={{ objectFit: 'cover' }} />
                          </div>
                          <Card.Body className="text-start p-4">
                            <div className="d-flex align-items-center mb-2 text-primary">
                              <i className="bi bi-building-gear fs-4 me-2"></i>
                              <h5 className="fw-bold mb-0 text-dark">{t('card1Title')}</h5>
                            </div>
                            <Card.Text className="text-muted small">
                              {t('card1Desc')}
                            </Card.Text>
                          </Card.Body>
                        </Card>
                      </Col>
                      {/* Card 2 */}
                      <Col lg={4} md={6} className="col-12">
                        <Card className="border-0 shadow-sm rounded-4 overflow-hidden h-100 mfr-animate-card bg-white">
                          <div style={{ overflow: 'hidden', height: '200px' }}>
                            <img src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=600&auto=format&fit=crop" alt="Site Engineering Coordination" className="w-100 h-100" style={{ objectFit: 'cover' }} />
                          </div>
                          <Card.Body className="text-start p-4">
                            <div className="d-flex align-items-center mb-2 text-success">
                              <i className="bi bi-people-fill fs-4 me-2"></i>
                              <h5 className="fw-bold mb-0 text-dark">{t('card2Title')}</h5>
                            </div>
                            <Card.Text className="text-muted small">
                              {t('card2Desc')}
                            </Card.Text>
                          </Card.Body>
                        </Card>
                      </Col>
                      {/* Card 3 */}
                      <Col lg={4} md={6} className="col-12">
                        <Card className="border-0 shadow-sm rounded-4 overflow-hidden h-100 mfr-animate-card bg-white">
                          <div style={{ overflow: 'hidden', height: '200px' }}>
                            <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=600&auto=format&fit=crop" alt="Resource Analytics" className="w-100 h-100" style={{ objectFit: 'cover' }} />
                          </div>
                          <Card.Body className="text-start p-4">
                            <div className="d-flex align-items-center mb-2 text-warning">
                              <i className="bi bi-currency-exchange fs-4 me-2"></i>
                              <h5 className="fw-bold mb-0 text-dark">{t('card3Title')}</h5>
                            </div>
                            <Card.Text className="text-muted small">
                              {t('card3Desc')}
                            </Card.Text>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Container>
                </div>
                {/* 3️⃣ SECTION 3: FEATURES GRID (UPGRADED LOOP WITH TRANSLATION COMPATIBILITY) */}
                <div className="py-4 py-md-5 mfr-features-section border-bottom">
                  <Container>
                    <div className="text-center mb-4 mb-md-5">
                      <h2 className="mfr-features-heading mb-0" style={{ fontSize: 'calc(1.3rem + 0.8vw)', fontWeight: '700' }}>
                        {t('featuresSectionHeading')}
                      </h2>
                    </div>
                    <Row className="g-3 g-md-4">
                      {[
                        {
                          titleKey: "feature1Title",
                          descKey: "feature1Desc"
                        },
                        {
                          titleKey: "feature2Title",
                          descKey: "feature2Desc"
                        },
                        {
                          titleKey: "feature3Title",
                          descKey: "feature3Desc"
                        },
                        {
                          titleKey: "feature4Title",
                          descKey: "feature4Desc"
                        },
                        {
                          titleKey: "feature5Title",
                          descKey: "feature5Desc"
                        },
                        {
                          titleKey: "feature6Title",
                          descKey: "feature6Desc"
                        }
                      ].map((feature, idx) => (
                        <Col lg={4} md={6} className="col-12" key={idx}>
                          <Card className="shadow-sm rounded-4 overflow-hidden h-100 mfr-premium-card p-2 p-md-3 border-0" style={{ background: '#f8fafc' }}>
                            <Card.Body className="text-start d-flex flex-column justify-content-between">
                              <div>
                                <Card.Title className="mfr-feature-card-title fw-bold text-dark mb-2" style={{ fontSize: '1.1rem' }}>
                                  {t(feature.titleKey)}
                                </Card.Title>
                                <Card.Text className="mfr-feature-card-text text-muted small" style={{ lineHeight: '1.5' }}>
                                  {t(feature.feature6Desc || feature.descKey)}
                                </Card.Text>
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </Container>
                </div>

                {/* 4️⃣ SECTION 4: BOTTOM CALL TO ACTION BACKDROP + SECURE SIGNUP LINK */}
                <div className="mfr-cta-bg py-5 text-white text-center d-flex align-items-center" style={{ minHeight: '380px' }}>
                  <Container className="py-4">
                    <Row className="justify-content-center">
                      <Col lg={8} md={10}>
                        <h2 className="display-5 fw-extrabold text-white mb-3" style={{ fontWeight: '800', letterSpacing: '-0.5px' }}>
                          {t('ctaHeading')}
                        </h2>
                        <p className="text-white-50 fs-5 mb-4 px-lg-5" style={{ fontWeight: '400' }}>
                          {t('ctaSubtext')}
                        </p>
                        <Button
                          variant="light"
                          size="lg"
                          className="text-primary fw-bold rounded-4 px-5 py-3 shadow-lg btn-animate"
                          style={{ transition: 'all 0.2s ease-in-out' }}
                          onClick={() => setShowSignupModal(true)}
                        >
                          {t('getStartedBtn')}
                        </Button>
                      </Col>
                    </Row>
                  </Container>
                </div>
              </>
            ) : (userRole === 'Admin' && localStorage.getItem('isPlanActive') !== 'true') ? (
              <PaymentGate onApproved={() => {
                localStorage.setItem('isPlanActive', 'true');
                window.location.reload();
              }} />
            ) : (
              /* ⚙️ HUB DASHBOARD SYSTEM (UPGRADED ENTERPRISE ARCHITECTURE) */
              <div className="mfr-dashboard-bg w-100 visual-root-container" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
                <Container fluid className="px-lg-5 py-4">
                  {/* TOP NAVBAR REFINED ALIGNMENT */}
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 pb-4 mb-4 border-bottom">
                    <div>
                      <h1 className="fw-bold text-dark tracking-tight mb-1" style={{ letterSpacing: '-1px', fontSize: '2.2rem' }}>
                        {t('dashboardTitle')}
                      </h1>
                      <p className="text-muted mb-0" style={{ fontSize: '1rem' }}>
                        {t('dashboardSubtext')}
                      </p>
                    </div>
                    {/* Action Controller Block - Safe Width Metrics Locked */}
                    <div className="d-flex flex-wrap align-items-center gap-3 ms-auto">
                      {userRole === 'Admin' && (
                        <Button
                          variant="primary"
                          className="rounded-3 px-4 py-2 fw-semibold shadow-sm d-flex align-items-center gap-2"
                          style={{ width: 'auto', whiteSpace: 'nowrap' }}
                          onClick={() => setShowNewProjectModal(true)}
                        >
                          <i className="bi bi-plus-circle-fill"></i> {t('createNewProjectBtn')}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* STATS ROW WITH VISIBLE ICONS */}
                  <Row className="g-4 mb-5">
                    <Col xl={3} sm={6}>
                      <div className="p-4 bg-white rounded-4 shadow-sm border d-flex align-items-center gap-3">
                        <div className="p-3 bg-primary bg-opacity-10 rounded-3 text-primary fs-3 d-flex align-items-center justify-content-center" style={{ width: '55px', height: '55px' }}>
                          <i className="bi bi-building"></i>
                        </div>
                        <div>
                          <h6 className="text-muted mb-1 small text-uppercase fw-bold" style={{ letterSpacing: '0.5px' }}>{t('statActiveSectors')}</h6>
                          <h3 className="fw-bold mb-0 text-dark">{(projects || []).length} {t('statZonesLabel')}</h3>
                        </div>
                      </div>
                    </Col>
                    <Col xl={3} sm={6}>
                      <div className="p-4 bg-white rounded-4 shadow-sm border d-flex align-items-center gap-3">
                        <div className="p-3 bg-success bg-opacity-10 rounded-3 text-success fs-3 d-flex align-items-center justify-content-center" style={{ width: '55px', height: '55px' }}>
                          <i className="bi bi-people-fill"></i>
                        </div>
                        <div>
                          <h6 className="text-muted mb-1 small text-uppercase fw-bold" style={{ letterSpacing: '0.5px' }}>{t('statWorkforce')}</h6>
                          <h3 className="fw-bold mb-0 text-dark">{t('statActiveLogs')}</h3>
                        </div>
                      </div>
                    </Col>
                    <Col xl={3} sm={6}>
                      <div className="p-4 bg-white rounded-4 shadow-sm border d-flex align-items-center gap-3">
                        <div className="p-3 bg-warning bg-opacity-10 rounded-3 text-warning fs-3 d-flex align-items-center justify-content-center" style={{ width: '55px', height: '55px' }}>
                          <i className="bi bi-currency-wallet"></i>
                        </div>
                        <div>
                          <h6 className="text-muted mb-1 small text-uppercase fw-bold" style={{ letterSpacing: '0.5px' }}>{t('statFinancialLedger')}</h6>
                          <h3 className="fw-bold mb-0 text-dark">{t('statSyncOk')}</h3>
                        </div>
                      </div>
                    </Col>
                    <Col xl={3} sm={6}>
                      <div className="p-4 bg-white rounded-4 shadow-sm border d-flex align-items-center gap-3">
                        <div className="p-3 bg-info bg-opacity-10 rounded-3 text-info fs-3 d-flex align-items-center justify-content-center" style={{ width: '55px', height: '55px' }}>
                          <i className="bi bi-cloud-check-fill"></i>
                        </div>
                        <div>
                          <h6 className="text-muted mb-1 small text-uppercase fw-bold" style={{ letterSpacing: '0.5px' }}>{t('statFcmPush')}</h6>
                          <h3 className="fw-bold mb-0 text-dark">{t('statOnline')}</h3>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {/* MAIN CONTENT AREA */}
                  <Row className="g-4">
                    <Col lg={9}>
                      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3 mb-4">
                        <h4 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2">
                          {t('activeProjectSectors')} <Badge bg="primary" className="fs-6 rounded-pill">{(projects || []).length}</Badge>
                        </h4>

                        <div className="mfr-search-input-wrapper d-flex align-items-center gap-2 bg-white px-3 py-2 rounded-3 border shadow-sm" style={{ minWidth: '280px' }}>
                          <i className="bi bi-search text-muted"></i>
                          <input
                            type="text"
                            placeholder={t('searchPlaceholder')}
                            className="form-control border-0 p-0 m-0 shadow-none bg-transparent"
                            value={searchTerm || ''}
                            onChange={(e) => typeof setSearchTerm === 'function' && setSearchTerm(e.target.value)}
                            style={{ fontSize: '0.9rem' }}
                          />
                        </div>
                      </div>

                      {/* PROJECTS GRID BUILDER */}
                      <Row className="g-4">
                        {(projects || [])
                          .filter((p) => {
                            if (!searchTerm) return true;
                            return (p?.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                              (p?.location || "").toLowerCase().includes(searchTerm.toLowerCase());
                          })
                          .map((proj) => (
                            <Col md={6} key={proj._id}>
                              <Card className="h-100 shadow-sm border-0 rounded-4 overflow-hidden" style={{ transition: 'all 0.3s ease' }}>
                                <Card.Body className="p-4 d-flex flex-column h-100">
                                  <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div>
                                      <h5 className="fw-bold text-dark mb-1">{proj.name}</h5>
                                      <div className="text-primary small fw-semibold d-flex align-items-center gap-1">
                                        <i className="bi bi-geo-alt-fill"></i> {proj.location || t('defaultLocation')}
                                      </div>
                                    </div>
                                    <Badge bg="light" text="dark" className="border px-2 py-1 rounded-3 small">
                                      {t('idLabel')}: {proj._id ? proj._id.substring(0, 5) : 'MFR'}
                                    </Badge>
                                  </div>

                                  <Card.Text className="text-muted small mb-4 flex-grow-1" style={{ lineHeight: '1.6' }}>
                                    {proj.description || `${t('fallbackDescPart1')} ${proj.name || t('fallbackDescPart2')} ${t('fallbackDescPart3')}`}
                                  </Card.Text>

                                  <Row className="g-2 mb-4 bg-light p-3 rounded-3 mx-0 text-center">
                                    <Col xs={4} className="border-end">
                                      <div className="fw-bold text-dark small">{t('gridActive')}</div>
                                      <div className="text-muted xs-font" style={{ fontSize: '0.75rem' }}>{t('gridTasksLogs')}</div>
                                    </Col>
                                    <Col xs={4} className="border-end">
                                      <div className="fw-bold text-dark small">{t('gridVerified')}</div>
                                      <div className="text-muted xs-font" style={{ fontSize: '0.75rem' }}>{t('gridBudget')}</div>
                                    </Col>
                                    <Col xs={4}>
                                      <div className="fw-bold text-success small">100%</div>
                                      <div className="text-muted xs-font" style={{ fontSize: '0.75rem' }}>{t('gridFcmSync')}</div>
                                    </Col>
                                  </Row>

                                  <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-1 small text-muted">
                                      <span>{t('sectorVolumeLabel')}</span>
                                      <span className="fw-bold text-primary">65%</span>
                                    </div>
                                    <div className="progress rounded-pill" style={{ height: '7px' }}>
                                      <div className="progress-bar progress-bar-striped progress-bar-animated rounded-pill" role="progressbar" style={{ width: '65%' }}></div>
                                    </div>
                                  </div>

                                  <Button
                                    variant="outline-primary"
                                    className="w-100 mt-auto rounded-3 py-2 fw-semibold d-flex align-items-center justify-content-center gap-2"
                                    onClick={() => navigate(`/project/${proj._id}`)}
                                  >
                                    {t('openControlBoardBtn')} <i className="bi bi-arrow-right"></i>
                                  </Button>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                      </Row>
                    </Col>

                    {/* RIGHT COLUMN SIDEBAR */}
                    <Col lg={3}>
                      <div className="p-4 bg-white rounded-4 shadow-sm border">
                        <h5 className="fw-bold text-dark mb-4 d-flex align-items-center gap-2">
                          <i className="bi bi-lightning-charge-fill text-warning"></i> {t('recentSiteLogsHeading')}
                        </h5>
                        <div className="d-flex flex-column gap-4">
                          <div className="position-relative ps-3" style={{ borderLeft: '2px solid #0d6efd' }}>
                            <div className="fw-bold text-dark small">{t('log1Title')}</div>
                            <div className="text-muted small" style={{ fontSize: '0.8rem' }}>{t('log1Desc')}</div>
                            <span className="text-primary fw-medium" style={{ fontSize: '0.7rem' }}>{t('timeJustNow')}</span>
                          </div>
                          <div className="position-relative ps-3" style={{ borderLeft: '2px solid #198754' }}>
                            <div className="fw-bold text-dark small">{t('log2Title')}</div>
                            <div className="text-muted small" style={{ fontSize: '0.8rem' }}>{t('log2Desc')}</div>
                            <span className="text-muted fw-medium" style={{ fontSize: '0.7rem' }}>{t('timeMinsAgo')}</span>
                          </div>
                          <div className="position-relative ps-3" style={{ borderLeft: '2px solid #ffc107' }}>
                            <div className="fw-bold text-dark small">{t('log3Title')}</div>
                            <div className="text-muted small" style={{ fontSize: '0.8rem' }}>{t('log3Desc')}</div>
                            <span className="text-muted fw-medium" style={{ fontSize: '0.7rem' }}>{t('timeHourAgo')}</span>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Container>
              </div>
            )}
          />

          <Route path="/project/:projectId" element={<ProjectConsolePanel userRole={userRole} projects={projects} />} />
        </Routes>
      </div>

      {/* WHATSAPP FLOAT */}
      {!isAuthenticated && (
        <a
          href="https://wa.me/+923044395007"
          target="_blank"
          rel="noopener noreferrer"
          className="mfr-advisory-whatsapp"
          aria-label="Contact us on WhatsApp"
        >
          <svg viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg">
            <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
          </svg>
        </a>
      )}

      {/* ✅ CORPORATE FOOTER */}
      {!isAuthenticated && (
        <footer ref={footerRef} className="mfr-corporate-footer pt-5 pb-3 mt-auto">
          <Container>
            <Row className="gy-4 text-start pb-4">
              {/* Column 1: Brand Logo & Bio */}
              <Col lg={4} md={12}>
                <div className="d-flex align-items-center font-weight-bold mb-3" style={{ gap: '10px', fontFamily: "'Montserrat', sans-serif" }}>
                  <svg width="40" height="40" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ minWidth: '40px' }}>
                    <path d="M20 35L50 15L80 35V75L50 95L20 75V35Z" stroke="#0d6efd" strokeWidth="6" strokeLinejoin="round" />
                    <path d="M50 15V95" stroke="#0d6efd" strokeWidth="4" />
                    <path d="M20 35L50 55L80 35" stroke="#0d6efd" strokeWidth="4" />
                    <path d="M35 60L50 50L65 60" stroke="#ffc107" strokeWidth="5" strokeLinecap="round" />
                    <path d="M35 75L50 65L65 75" stroke="#ffc107" strokeWidth="5" strokeLinecap="round" />
                  </svg>
                  <span style={{ fontSize: '24px', letterSpacing: '0.5px', lineHeight: '1' }}>
                    <strong className="text-primary" style={{ fontWeight: '800' }}>MFR</strong>
                    <span style={{ color: '#e2e8f0', fontWeight: '400', marginLeft: '6px' }}>{t('footerBrandBuild')}</span>
                  </span>
                </div>
                <p className="small pe-lg-4" style={{ lineHeight: '1.6', color: '#94a3b8' }}>
                  {t('footerBioText')}
                </p>
                <p className="mb-1 small mfr-text-highlight-visible">
                  <i className="bi bi-envelope me-2 text-primary"></i>{t('footerEmail', { defaultValue: 'ubaidr162@gmail.com' })}
                </p>
              </Col>

              {/* Column 2: Platforms */}
              <Col lg={2} md={4} sm={6} className="col-6">
                <h5 className="mfr-footer-heading mb-3">{t('Platforms')}</h5>
                <ul className="list-unstyled d-flex flex-column gap-2">
                  <li><a href="#iphone" className="mfr-footer-link">{t('platformIphone')}</a></li>
                  <li><a href="#ipad" className="mfr-footer-link">{t('platformIpad')}</a></li>
                  <li><a href="#android" className="mfr-footer-link">{t('platformAndroid')}</a></li>
                  <li><a href="#web" className="mfr-footer-link">{t('platformWeb')}</a></li>
                </ul>
              </Col>

              {/* Column 3: Corporate & Enterprise Desk */}
              <Col lg={2} md={4} sm={6} className="col-6">
                <h5 className="mfr-footer-heading mb-3">{t('Corporate Desk')}</h5>
                <ul className="list-unstyled d-flex flex-column gap-2">
                  <li><a href="#precon" className="mfr-footer-link">{t('corporatePrecon')}</a></li>
                  <li><a href="#pm" className="mfr-footer-link">{t('corporatePm')}</a></li>
                  <li><a href="#finance" className="mfr-footer-link">{t('corporateFinance')}</a></li>
                  <li><a href="#communication" className="mfr-footer-link">{t('corporateComm')}</a></li>
                </ul>
              </Col>

              {/* Column 4: App Store Badges & Address */}
              <Col lg={4} md={4} sm={12} className="text-start">
                <h5 className="mfr-footer-heading mb-3">{t('MobileApp')}</h5>
                <div className="d-flex flex-wrap gap-2 mb-4">
                  <a href="#appstore" target="_blank" rel="noreferrer">
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                      alt="Download on App Store"
                      className="mfr-footer-store-btn"
                    />
                  </a>
                  <a href="#playstore" target="_blank" rel="noreferrer">
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                      alt="Get it on Google Play"
                      className="mfr-footer-store-btn"
                    />
                  </a>
                </div>
                <div className="small mfr-text-highlight-visible" style={{ fontSize: '0.8rem' }}>
                  <i className="bi bi-geo-alt me-1 text-primary"></i> {t('Address')}
                </div>
              </Col>
            </Row>

            {/* Thin Horizontal Separation Bar Row */}
            <Row className="mfr-footer-bottom-bar pt-4 mt-2 align-items-center">
              <Col md={8} className="text-center text-md-start mb-3 mb-md-0">
                <div className="d-flex flex-wrap justify-content-center justify-content-md-start gap-3">
                  <span className="mfr-text-highlight-visible">{t('Copyright')}</span>
                  <a href="#legal" className="mfr-footer-link">{t('LegalDocs')}</a>
                  <a href="#privacy" className="mfr-footer-link">{t('PrivacyNotice')}</a>
                  <a href="#cookies" className="mfr-footer-link">{t('CookieSettings')}</a>
                </div>
              </Col>

              <Col md={4} className="text-center text-md-end">
                <div className="d-flex justify-content-center justify-content-md-end gap-3">
                  <a href="#instagram" className="mfr-footer-social-icon"><i className="bi bi-instagram"></i></a>
                  <a href="#facebook" className="mfr-footer-social-icon"><i className="bi bi-facebook"></i></a>
                  <a href="#linkedin" className="mfr-footer-social-icon"><i className="bi bi-linkedin"></i></a>
                  <a href="#twitter" className="mfr-footer-social-icon"><i className="bi bi-twitter-x"></i></a>
                  <a href="#youtube" className="mfr-footer-social-icon"><i className="bi bi-youtube"></i></a>
                </div>
              </Col>
            </Row>
          </Container>
        </footer>
      )}

      {/* MODALS */}

      {/* 🔐 REFINED PLATFORM LOGIN MODAL */}
      <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)} centered className="rounded-4">
        <Modal.Header closeButton className="border-0 px-4 pt-4 pb-0">
          <Modal.Title className="w-100">
            {/* 🚀 Dynamic Branding Header Layout */}
            <div className="d-flex align-items-center font-weight-bold mb-1" style={{ gap: '10px', fontFamily: "'Montserrat', sans-serif" }}>
              {/* Live Dynamic SVG Icon */}
              <svg width="40" height="40" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ minWidth: '40px' }}>
                <path d="M20 35L50 15L80 35V75L50 95L20 75V35Z" stroke="#0d6efd" strokeWidth="6" strokeLinejoin="round" />
                <path d="M50 15V95" stroke="#0d6efd" strokeWidth="4" />
                <path d="M20 35L50 55L80 35" stroke="#0d6efd" strokeWidth="4" />
                <path d="M35 60L50 50L65 60" stroke="#ffc107" strokeWidth="5" strokeLinecap="round" />
                <path d="M35 75L50 65L65 75" stroke="#ffc107" strokeWidth="5" strokeLinecap="round" />
              </svg>

              {/* Branding Text with Professional Contrast */}
              <span style={{ fontSize: '24px', letterSpacing: '0.5px', lineHeight: '1' }}>
                <strong className="text-primary" style={{ fontWeight: '800' }}>MFR</strong>
                <span style={{ color: '#334155', fontWeight: '500', marginLeft: '6px' }}>Build</span>
              </span>
            </div>
            <p className="text-muted small mb-0 fw-normal ms-md-5 ps-md-2" style={{ fontSize: '0.85rem' }}>
              {t('loginEcosystemSubtext')}
            </p>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4 pb-4 pt-3">
          <Form onSubmit={handleLoginSubmit}>

            {/* 1. Email Address Input */}
            <Form.Group className="mb-3">
              <Form.Label className="text-secondary small fw-semibold mb-1">{t('emailLabel')}</Form.Label>
              <Form.Control
                type="email"
                value={loginData.email || ''}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                placeholder="name@mfrbuild.com"
                className="py-2 rounded-3 border-secondary-subtle"
                required
              />
            </Form.Group>

            {/* 2. Secure Password Input (With Inline Eye Toggle Button) */}
            <Form.Group className="mb-3">
              <Form.Label className="text-secondary small fw-semibold mb-1">{t('passwordLabel')}</Form.Label>
              <div className="position-relative">
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  value={loginData.password || ''}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  placeholder="•••••••••••••"
                  className="py-2 rounded-3 border-secondary-subtle pe-5"
                  required
                />
                {/* Eye Icon Button inside Input Element Box */}
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="position-absolute end-0 top-50 translate-middle-y border-0 bg-transparent text-secondary px-3"
                  style={{ zIndex: 5, cursor: 'pointer' }}
                >
                  {showPassword ? (
                    <span style={{ fontSize: '1.1rem' }}>🙈</span>
                  ) : (
                    <span style={{ fontSize: '1.1rem' }}>👁️</span>
                  )}
                </button>
              </div>
            </Form.Group>

            {/* 3. Remember Session Checkbox & Forgot Password Alignment */}
            <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between mb-4 gap-2 small fw-semibold">
              <Form.Check
                type="checkbox"
                id="remember-session"
                label={t('rememberSessionLabel')}
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="text-secondary"
                style={{ cursor: 'pointer' }}
              />
              <a
                href="#forgot"
                onClick={(e) => { e.preventDefault(); handleForgotPasswordClick(); }}
                className="text-primary text-decoration-none"
              >
                {t('forgotPasswordLink')}
              </a>
            </div>

            {/* 4. Main Login Action Button */}
            <Button
              variant="primary"
              type="submit"
              className="w-100 py-2 rounded-3 fw-bold shadow-sm mb-3 d-flex align-items-center justify-content-center"
              style={{ backgroundColor: '#0d6efd', border: 'none', fontSize: '1rem' }}
            >
              <span className="me-2">🔐</span> {t('secureLoginBtn')}
            </Button>

            {/* 5. Footer Redirect Navigation Option */}
            <div className="text-center small text-secondary pt-2 border-top border-light-subtle">
              {t('noEnterprisePlanText')}{' '}
              <a
                href="#signup"
                onClick={(e) => { e.preventDefault(); handleSwitchToSignup(); }}
                className="text-primary fw-bold text-decoration-none ms-1"
              >
                {t('createAccountLink')}
              </a>
            </div>

          </Form>
        </Modal.Body>
      </Modal>

      {/* 🔥 REFINED PLATFORM SIGNUP MODAL */}
      <Modal show={showSignupModal} onHide={() => setShowSignupModal(false)} centered className="rounded-4">
        <Modal.Header closeButton className="border-0 px-4 pt-4 pb-2">
          <Modal.Title className="fw-bold text-dark" style={{ fontSize: '1.6rem' }}>
            {t('createAccountTitle')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 pb-4 pt-2">
          <Form onSubmit={handleSignupSubmit}>

            {/* 1. Full Name */}
            <Form.Group className="mb-3">
              <Form.Label className="text-secondary small fw-semibold mb-2">{t('fullNameLabel')}</Form.Label>
              <Form.Control
                type="text"
                value={signupData.name || ''}
                onChange={(e) => setSignupData({ ...signupData, name: e.target.value })}
                placeholder="e.g., Malik Faisal"
                className="py-2 rounded-3 border-secondary-subtle"
                required
              />
            </Form.Group>

            {/* 2. Email Address */}
            <Form.Group className="mb-3">
              <Form.Label className="text-secondary small fw-semibold mb-2">{t('emailLabel')}</Form.Label>
              <Form.Control
                type="email"
                value={signupData.email || ''}
                onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                placeholder="name@mfrbuild.com"
                className="py-2 rounded-3 border-secondary-subtle"
                required
              />
            </Form.Group>

            {/* 3. Secure Password (With Inline Eye Toggle Button) - UPDATED ✅ */}
            <Form.Group className="mb-3">
              <Form.Label className="text-secondary small fw-semibold mb-2">{t('passwordLabel')}</Form.Label>
              <div className="position-relative">
                <Form.Control
                  type={showSignupPassword ? "text" : "password"}
                  value={signupData.password || ''}
                  onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                  placeholder="••••••"
                  className="py-2 rounded-3 border-secondary-subtle pe-5"
                  required
                />
                {/* Eye Icon Button inside Input Element Box */}
                <button
                  type="button"
                  onClick={() => setShowSignupPassword(!showSignupPassword)}
                  className="position-absolute end-0 top-50 translate-middle-y border-0 bg-transparent text-secondary px-3"
                  style={{ zIndex: 5, cursor: 'pointer' }}
                >
                  {showSignupPassword ? (
                    <span style={{ fontSize: '1.1rem' }}>🙈</span>
                  ) : (
                    <span style={{ fontSize: '1.1rem' }}>👁️</span>
                  )}
                </button>
              </div>
            </Form.Group>

            {/* 4. Contact Number (WhatsApp Integrated) */}
            <Form.Group className="mb-3">
              <Form.Label className="text-secondary small fw-semibold mb-2">{t('contactNumberLabel')}</Form.Label>
              <Form.Control
                type="text"
                value={signupData.phone || ''}
                onChange={(e) => setSignupData({ ...signupData, phone: e.target.value })}
                placeholder="e.g., 923001234567"
                className="py-2 rounded-3 border-secondary-subtle"
                required
              />
              <Form.Text className="text-muted small">{t('whatsappHelpText')}</Form.Text>
            </Form.Group>

            {/* 5. Company Name */}
            <Form.Group className="mb-3">
              <Form.Label className="text-secondary small fw-semibold mb-2">{t('companyNameLabel')}</Form.Label>
              <Form.Control
                type="text"
                value={signupData.company || ''}
                onChange={(e) => setSignupData({ ...signupData, company: e.target.value })}
                placeholder="e.g., MFR Builders & Developers"
                className="py-2 rounded-3 border-secondary-subtle"
              />
            </Form.Group>

            {/* 6. Role Selection */}
            <Form.Group className="mb-4">
              <Form.Label className="text-secondary small fw-semibold mb-2">{t('accountRoleTypeLabel')}</Form.Label>
              <Form.Select
                value={signupData.role || 'Admin'}
                onChange={(e) => setSignupData({ ...signupData, role: e.target.value })}
                className="py-2 rounded-3 border-secondary-subtle fw-semibold text-dark"
              >
                <option value="Admin">{t('roleAdmin')}</option>
                <option value="Client">{t('roleClient')}</option>
                <option value="Supervisor">{t('roleSupervisor')}</option>
                <option value="SiteManager">{t('roleSiteManager')}</option>
                <option value="Labour">{t('roleLabour')}</option>
              </Form.Select>
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              className="w-100 py-2.5 rounded-3 fw-bold shadow-sm"
              style={{ backgroundColor: '#0d6efd', border: 'none' }}
            >
              🚀 {t('registerPlanBtn')}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* SUBSCRIPTION MODAL — signup ke baad Admin ke liye khulta hai */}

      <Modal
        show={showSubscriptionModal}
        onHide={() => { }}
        backdrop="static"
        keyboard={false}
        size="lg"
        centered
      >
        <Modal.Header>
          <Modal.Title className="fw-bold">
            💼 {t('subscriptionModalTitle', 'MFR Build Licensing & Billing Center')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <PaymentGate onApproved={() => {
            localStorage.setItem('isPlanActive', 'true');
            setShowSubscriptionModal(false);
            window.location.reload();
          }} />
        </Modal.Body>
      </Modal>


      {/* 🔥 REFINED NEW PROJECT MODAL */}
      <Modal show={showNewProjectModal} onHide={() => setShowNewProjectModal(false)} centered className="rounded-4">
        <Modal.Header closeButton className="border-0 px-4 pt-4 pb-2">
          <Modal.Title className="fw-bold text-dark" style={{ fontSize: '1.6rem' }}>
            {t('initializeProjectSectorTitle')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4 pb-4 pt-2">
          <Form onSubmit={handleCreateProjectSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="text-secondary small fw-semibold mb-2">{t('projectNameLabel')}</Form.Label>
              <Form.Control
                type="text"
                name="projectName"
                placeholder="e.g., Ali Town Sector C"
                className="py-2.5 rounded-3 border-secondary-subtle"
                required
              />
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label className="text-secondary small fw-semibold mb-2">{t('siteLocationLabel')}</Form.Label>
              <Form.Control
                type="text"
                name="projectLocation"
                placeholder="e.g., Lahore, Pakistan"
                className="py-2.5 rounded-3 border-secondary-subtle"
                required
              />
            </Form.Group>
            <Button
              variant="success"
              type="submit"
              className="w-100 py-2.5 rounded-3 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm"
              style={{ backgroundColor: '#0f8b53', border: 'none' }}
            >
              🚀 {t('launchProjectLoopBtn')}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* 🌍 SYSTEM LANGUAGE MODAL SWITCHER */}
      <Modal show={showMoreLangModal} onHide={() => setShowMoreLangModal(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold small">{t('system_languages')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="list-group list-group-flush">
            {allLanguagesList.map((lang) => (
              <button
                key={lang.code}
                className={`list-group-item list-group-item-action text-start py-2 small ${language === lang.code ? 'bg-light fw-bold text-primary' : ''}`}
                onClick={() => {
                  setLanguage(lang.code);
                  i18n.changeLanguage(lang.code);
                  setShowMoreLangModal(false);
                }}
              >
                {lang.name}
              </button>
            ))}
          </div>
        </Modal.Body>
      </Modal>

    </div>
  );
};

// --- 🖥️ HIGH-FIDELITY DATABASE-BINDED CRUD CONSOLE ---
const ProjectConsolePanel = ({ userRole, projects }) => {

  const { t } = useTranslation();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const targetProjectMeta = (projects || []).find(p => p._id === projectId) || {
    name: "Project Console",
    location: "Active Site Zone"
  };

  // BILLING component:
  const [showBillingModal, setShowBillingModal] = useState(false);

  // --- DAILY OPERATIONS DATA LISTS ---
  const [projectTasks, setProjectTasks] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [dailyLogs, setDailyLogs] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [payrollRecords, setPayrollRecords] = useState([]);

  // --- CORE SYSTEM & TABS STATES ---
  const [activeTab, setActiveTab] = useState('tasks');
  const [btnLoading, setBtnLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [formFeedbackMessage, setFormFeedbackMessage] = useState('');

  // --- DAILY OPERATIONS FORM & MODAL STATES ---
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showAddAttendanceModal, setShowAddAttendanceModal] = useState(false);
  const [showAddLogModal, setShowAddLogModal] = useState(false);
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showCameraView, setShowCameraView] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [logModalTab, setLogModalTab] = useState('general');
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState('');
  const [budgetLimit, setBudgetLimit] = useState(5000);
  const [dailyRemarks, setDailyRemarks] = useState("");
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const [photoViewerLog, setPhotoViewerLog] = useState(null);

  // --- FORM VALUES TRACKING ---
  const [taskData, setTaskData] = useState({
    title: '', description: '', assignedTo: '', roleAtAssignment: 'Supervisor', startDate: '', dueDate: '', isMilestone: false
  });
  const [attendanceData, setAttendanceData] = useState({ workerName: '', role: 'Labour', status: 'Present' });
  const [logData, setLogData] = useState({ weather: 'Sunny', materialUsed: '', notes: '', paymentStatus: "Paid" });
  const [expenseData, setExpenseData] = useState({ item: '', amount: '', category: 'Materials' });

  const currentUser = JSON.parse(localStorage.getItem('user')) || { name: 'Admin', role: 'Admin' };

  // --- INVENTORY STATES ---
  const [inventory, setInventory] = useState([]);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [inventoryData, setInventoryData] = useState({
    itemName: '', category: 'Materials', quantityChange: '', unit: 'Bags', minThreshold: '10', type: 'StockIn'
  });

  // --- DOCUMENT DRIVE STATES ---
  const [documents, setDocuments] = useState([]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [documentData, setDocumentData] = useState({
    title: '', category: 'Architectural', fileType: 'PDF', fileUrl: ''
  });
  const [selectedDocForRevision, setSelectedDocForRevision] = useState(null);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionUrl, setRevisionUrl] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);

  // --- QUALITY CONTROL: PUNCH LIST ---
  const [punchItems, setPunchItems] = useState([]);
  const [showAddPunchModal, setShowAddPunchModal] = useState(false);
  const [punchBeforeImage, setPunchBeforeImage] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [punchAfterImage, setPunchAfterImage] = useState(null);
  const [newPunch, setNewPunch] = useState({
    issueDescription: '', location: '', assignedContractor: '', priority: 'Medium', dueDate: ''
  });

  // --- QUALITY CONTROL: RFI STATES ---
  const [rfiItems, setRfiItems] = useState([]);
  const [showRfiModal, setShowRfiModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showRfiAnswerModal, setShowRfiAnswerModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [selectedRfi, setSelectedRfi] = useState(null);
  const [rfiAnswerText, setRfiAnswerText] = useState('');
  const [newRfi, setNewRfi] = useState({ assignedTo: '', textQuestion: '', urgency: 'Medium' });
  // eslint-disable-next-line no-unused-vars
  const [rfiImageFile, setRfiImageFile] = useState(null);

  // --- VOICE / AUDIO ENGINE ---
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);

  // --- BIM (BUILDING INFORMATION MODELING) STATES ---
  // eslint-disable-next-line no-unused-vars
  const [bimModels, setBimModels] = useState([]);
  const [activeBimModel, setActiveBimModel] = useState(null);
  const [bimClashes, setBimClashes] = useState([]);
  const [showModelUploadModal, setShowModelUploadModal] = useState(false);
  const [showClashTicketModal, setShowClashTicketModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [bimModelForm, setBimModelForm] = useState({ title: '', fileUrl: '', fileSize: '' });
  const [clashTicketForm, setClashTicketForm] = useState({
    title: '', description: '', clashType: 'Hard Clash', priority: 'Medium', coordinateX: '', coordinateY: '', coordinateZ: '', assignedTo: ''
  });

  // --- PROCUREMENT SYSTEM STATES ---
  const [requisitions, setRequisitions] = useState([]);
  const [comparisonMatrix, setComparisonMatrix] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [activeProcureTab, setActiveProcureTab] = useState('requisitions');
  const [showReqModal, setShowReqModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [selectedReqId, setSelectedReqId] = useState(null);
  const [selectedPo, setSelectedPo] = useState(null);
  const [reqForm, setReqForm] = useState({ materialName: '', quantity: 0, unit: 'Bags', estimatedBudget: 0 });
  const [quoteForm, setQuoteForm] = useState({ vendorName: '', vendorPhone: '', ratePerUnit: 0, deliveryLeadTimeDays: 3 });
  const [auditForm, setAuditForm] = useState({ receivedQuantity: 0, orderedQuantity: 0, invoiceAmount: 0 });

  // --- SAFETY (HSE) MODULE STATES ---
  const [safetyActiveTab, setSafetyActiveTab] = useState('dashboard');
  const [safetyData, setSafetyData] = useState(null);
  const [safetyLoading, setSafetyLoading] = useState(true);
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [showPermitModal, setShowPermitModal] = useState(false);
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [showFineModal, setShowFineModal] = useState(false);
  const [inspectionForm, setInspectionForm] = useState({
    hasHelmetsAndBoots: false, isScaffoldingSecure: false, isFireExtinguisherPresent: false, isSignageVisible: false, topic: 'Daily Morning Safety Briefing', attendanceCount: 0
  });
  // eslint-disable-next-line no-unused-vars
  const [tbtImage, setTbtImage] = useState(null);
  const [permitForm, setPermitForm] = useState({ permitType: 'Height Work', requestedBy: '', validTill: '', safetyChecklistPassed: false });
  const [incidentForm, setIncidentForm] = useState({ title: '', incidentType: 'Near-Miss (Baal Baal Bachna)', location: '', description: '', actionTaken: '' });
  // eslint-disable-next-line no-unused-vars
  const [incidentImage, setIncidentImage] = useState(null);
  const [fineForm, setFineForm] = useState({ subcontractorName: '', violationReason: '', fineAmount: '' });

  // --- EQUIPMENT & ASSET TRACKING MODULE STATES ---
  const [assets, setAssets] = useState([]);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [showLogShiftModal, setShowLogShiftModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [assetForm, setAssetForm] = useState({
    name: '', modelNumber: '', serialNumber: '', category: 'Heavy Machinery', ownership: 'Owned', purchasePrice: '', rentalRatePerHour: '', nextServiceDueHours: 250
  });
  const [shiftLogForm, setShiftLogForm] = useState({
    assetId: '', operatorId: '', hoursOperated: '', idleHours: '', fuelAddedLiters: '', fuelCost: '', fuelReceiptImage: '', preStartChecklistPassed: true, notes: ''
  });
  const [maintenanceForm, setMaintenanceForm] = useState({
    assetId: '', type: 'Preventative', description: '', costIncurred: '', partsReplaced: ''
  });

  // --- AI COPILOT FLOATING CHAT ---
  const [showFloatingAIChat, setShowFloatingAIChat] = useState(false);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  // Yeh lines component ke start mein honi chahiye (around line 1730-1800)
  const [allUsers, setAllUsers] = useState([]);
  const [contractorsList, setContractorsList] = useState([]);
  const [managementList, setManagementList] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  // 👥 MAIN SUB-SECTION STATES (Direct inside ProjectConsolePanel)
  const [selectedUserId, setSelectedUserId] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteRows, setInviteRows] = useState([
    { phone: '', email: '', role: 'Labour', dailyWage: '' }
  ]);
  const [invitedList, setInvitedList] = useState([]);

  // --- ROLE LIMITS CONSTANT ---
  const ROLE_LIMITS = {
    Client: 4,
    SiteManager: 1,
    Supervisor: 1,
    Labour: Infinity
  };


  // --- PUSH NOTIFICATION ENGINE ---
  useEffect(() => {
    const setupPushNotifications = async () => {
      try {
        const token = await requestForToken();
        if (token) {
          const authToken = localStorage.getItem('token');
          if (authToken) {
            await axiosReal.put('https://api.mfrbuild.com/api/auth/update-fcm-token',
              { fcmToken: token },
              { headers: { 'x-auth-token': authToken } }
            );
            console.log("FCM Token successfully synchronized with backend cluster!");
          }
        }
      } catch (err) {
        console.error("FCM Token sync failed: ", err);
      }
    };
    setupPushNotifications();
  }, []);

  // 🔄 Fetch users for assignment dropdown (Final Clean Copy)
  useEffect(() => {
    const fetchUsers = async () => {
      // 🚨 Strict Sanitization: Spaces saaf karein aur exact 24-character hexadecimal check karein
      const cleanId = typeof projectId === 'string' ? projectId.trim() : '';
      const isIdValid = /^[0-9a-fA-F]{24}$/.test(cleanId) && !cleanId.includes('.');

      // Kuch bhi ghalat ho toh API request bilkul nahi bhejni
      if (!cleanId || !isIdValid) {
        setAllUsers([]);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setAllUsers([]);
          return;
        }

        const res = await axios.get(`https://api.mfrbuild.com/api/projects/${cleanId}/team`, {
          headers: { 'x-auth-token': token }
        });

        if (res.data && Array.isArray(res.data)) {
          setAllUsers(res.data.filter(u => u.role !== 'Admin'));
        } else {
          setAllUsers([]);
        }
      } catch (err) {
        setAllUsers([]);
      }
    };

    if (userRole === 'Admin') {
      fetchUsers();
    } else {
      setAllUsers([]);
    }
  }, [userRole, projectId]);

  // --- 🔄 ALL ENDPOINTS SYNC INGESTION ---
  const fetchConsoleCoreData = useCallback(async () => {
    const token = localStorage.getItem('token');
    const config = { headers: { 'x-auth-token': token } };

    // --- DAILY LOGS FETCH ---
    try {
      const res = await axiosReal.get(`https://api.mfrbuild.com/api/daily-logs/project/${projectId}`, config);
      setDailyLogs(res.data?.data || res.data || []);
    } catch (err) {
      console.error("Error fetching daily logs:", err);
      setDailyLogs([]);
    }

    // --- EXPENSES FETCH ---
    try {
      const res = await axiosReal.get(`https://api.mfrbuild.com/api/expenses/project/${projectId}`, config);
      setExpenses(res.data || []);
    } catch (err) {
      console.error("Error fetching expenses:", err);
      setExpenses([]);
    }

    // --- PUNCHLIST FETCH ---
    try {
      const res = await axiosReal.get(`https://api.mfrbuild.com/api/punchlists/project/${projectId}`, config);
      setPunchItems(res.data || []);
    } catch (err) {
      console.error("Error fetching punchlists:", err);
      setPunchItems([]);
    }

    // --- INVENTORY FETCH ---
    try {
      const res = await axiosReal.get(`https://api.mfrbuild.com/api/inventory/${projectId}`, config);
      setInventory(res.data.data || res.data || []);
    } catch (err) {
      console.error("Error fetching inventory:", err);
      setInventory([]);
    } finally {
      setLoadingInventory(false);
    }

  }, [projectId]);

  const triggerSystemNotification = useCallback((title, message) => {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(title, {
        body: message,
        icon: "/assets/3596091.png",
        silent: false
      });
    }
  }, []);

  const fetchProjectPayroll = useCallback(async () => {
    if (!projectId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axiosReal.get(`https://api.mfrbuild.com/api/attendance/payroll/${projectId}`, {
        headers: {
          'x-auth-token': token,
          'Authorization': `Bearer ${token}` // 🔥 FIX: Dual alignment added
        }
      });

      if (typeof setPayrollRecords === 'function') {
        setPayrollRecords(res.data || []);
      }
    } catch (err) {
      console.error("Frontend Fetch Payroll Error:", err.message);
    }
  }, [projectId]);

  const fetchProjectAttendance = useCallback(async () => {
    if (!projectId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axiosReal.get(`https://api.mfrbuild.com/api/attendance/project/${projectId}`, {
        headers: {
          'x-auth-token': token,
          'Authorization': `Bearer ${token}` // 🔥 FIX: Dual alignment added
        }
      });
      setAttendanceRecords(res.data || []);
    } catch (err) {
      console.error("Frontend Fetch Attendance Error:", err.message);
    }
  }, [projectId]);

  // 📦 1. Fetch Assets Stream Definition First
  const fetchAssetsDataStream = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // 🟢 Dual headers alignment for secure production authentication
    const config = {
      headers: {
        'x-auth-token': token,
        'Authorization': `Bearer ${token}`
      }
    };

    if (navigator.onLine) {
      try {
        // 🎯 FIX: Changed to absolute api endpoint for strict data stream sync
        const res = await axiosReal.get('https://api.mfrbuild.com/api/v1/assets', config);
        const productionData = res.data?.data || res.data || [];
        setAssets(productionData);

        if (typeof offlineDB !== 'undefined' && offlineDB.cachedAssets) {
          await offlineDB.cachedAssets.clear();
          await offlineDB.cachedAssets.bulkPut(productionData);
        }
      } catch (err) {
        console.error("Online pull failed, switching to local cache:", err.message);
        if (typeof offlineDB !== 'undefined' && offlineDB.cachedAssets) {
          const localCachedAssets = await offlineDB.cachedAssets.toArray();
          setAssets(localCachedAssets);
        }
      }
    } else {
      if (typeof offlineDB !== 'undefined' && offlineDB.cachedAssets) {
        const localCachedAssets = await offlineDB.cachedAssets.toArray();
        setAssets(localCachedAssets);
      }
    }
  }, []);

  // 🚀 2. Process Offline Outbox Queue Definition Second
  const processOfflineOutboxQueue = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token || !projectId) return;

    try {
      if (typeof offlineDB === 'undefined' || !offlineDB.outboxQueue) return;

      const pendingRequests = await offlineDB.outboxQueue.toArray();
      if (pendingRequests.length === 0) return;

      console.log(`📡 Total ${pendingRequests.length} offline operations found. Starting sequential sync...`);

      for (const req of pendingRequests) {
        let finalUrl = req.url;
        if (!finalUrl.startsWith('http')) {
          finalUrl = `https://api.mfrbuild.com${finalUrl.startsWith('/') ? '' : '/'}${finalUrl}`;
        }

        const recordPrimaryKey = req.id !== undefined ? req.id : (req._id !== undefined ? req._id : null);

        try {
          const response = await fetch(finalUrl, {
            method: req.method || 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-auth-token': token,
              'Authorization': `Bearer ${token}`
            },
            body: req.data ? JSON.stringify(req.data) : undefined
          });

          if (response.ok) {
            if (recordPrimaryKey !== null) {
              await offlineDB.outboxQueue.delete(recordPrimaryKey);
            } else {
              await offlineDB.outboxQueue.where('timestamp').equals(req.timestamp).delete();
            }
            console.log(`✓ Successfully synchronized and DELETED from outbox: ${req.method} -> ${finalUrl}`);
          } else {
            console.warn(`🛑 Server rejected offline operation: ${response.status}. Keeping in queue.`);
          }
        } catch (singleReqErr) {
          console.error(`❌ Failed to sync individual request to ${finalUrl}:`, singleReqErr.message);
          break;
        }
      }

      const remaining = await offlineDB.outboxQueue.count();
      if (remaining === 0) {
        if (typeof triggerSystemNotification === 'function') {
          triggerSystemNotification("🚀 Sync Successful", "All pending operations are synchronized!");
        } else {
          alert("🚀 Offline data successfully synchronized with live server!");
        }

        // AB YEH SAFELY CHALEIN GE KYUNKI YEH DONO CODE MEIN UPAR DEFINE HO CHUKE HAIN
        if (typeof fetchProjectAttendance === 'function') fetchProjectAttendance();
        if (typeof fetchProjectPayroll === 'function') fetchProjectPayroll();
        if (typeof fetchProjectTasksOnly === 'function') fetchProjectTasksOnly();
        if (typeof fetchConsoleCoreData === 'function') fetchConsoleCoreData();
      }

    } catch (queueErr) {
      console.error("Critical Outbox Processing Error:", queueErr);
    }
  }, [projectId, triggerSystemNotification, fetchProjectAttendance, fetchProjectPayroll]);


  // 📡 3. Network status listener effect (Placed AFTER definitions to avoid ReferenceError)
  useEffect(() => {
    const goOnline = () => {
      setIsOnline(true);
      processOfflineOutboxQueue();
    };

    const goOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    if (navigator.onLine) {
      processOfflineOutboxQueue();
      if (typeof fetchAssetsDataStream === 'function') {
        fetchAssetsDataStream();
      }
    }

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, [processOfflineOutboxQueue, fetchAssetsDataStream]);

  // 🔔 4. Notification permissions request on boot
  useEffect(() => {
    if ("Notification" in window && Notification.permission !== "granted" && Notification.permission !== "denied") {
      Notification.requestPermission();
    }
  }, []);

  const fetchProjectTasksOnly = useCallback(async () => {
    if (!projectId) return;
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };

      const res = await axiosReal.get(`https://api.mfrbuild.com/api/tasks/project/${projectId}`, config);

      // 🔥 SENIOR DEVELOPER CRASH PROTECTION:
      const fetchedData = res.data?.data || res.data;

      if (Array.isArray(fetchedData)) {
        setProjectTasks(fetchedData);
      } else {
        console.warn("Tasks data backend se array nahi aaya, object aaya:", fetchedData);
        setProjectTasks([]); // Safe fallback
      }
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setProjectTasks([]); // Safe fallback on error
    }
  }, [projectId]);



  // 🌐 FETCH DOCUMENTS FUNCTION
  const fetchUbaidDocumentsOnly = useCallback(async () => {
    if (!projectId) return;

    setLoadingDocuments(true);
    const token = localStorage.getItem('token');
    const config = {
      headers: {
        'x-auth-token': token,
        'Content-Type': 'application/json'
      }
    };

    try {
      const res = await axiosReal.get(`https://api.mfrbuild.com/api/documents/${projectId}`, config);

      if (res.data && res.data.success) {
        setDocuments(res.data.data || []);
      } else if (Array.isArray(res.data)) {
        setDocuments(res.data);
      } else {
        setDocuments([]);
      }
    } catch (err) {
      console.error("❌ Catch block error in document system:", err.response?.data || err.message);
      setDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  }, [projectId]);

  // ✅ DATABASE SE REAL ROLES FETCH KARNE KA FUNCTION (Final Clean Copy)
  const fetchContractorsAndStaff = useCallback(async () => {
    const token = localStorage.getItem('token');

    // 🚨 Strict Sanitization: Spaces saaf karein aur exact 24-character hexadecimal check karein
    const cleanId = typeof projectId === 'string' ? projectId.trim() : '';

    // Agar ID khali hai, length 24 nahi hai, ya dots hain, ya invalid hex hai toh block karein
    const isIdValid = /^[0-9a-fA-F]{24}$/.test(cleanId) && !cleanId.includes('.');

    if (!token || !isIdValid) {
      // Khamoshi se list khali karein aur return ho jayein, koi API request nahi jayegi
      setContractorsList([]);
      setManagementList([]);
      return;
    }

    try {
      const config = { headers: { 'x-auth-token': token } };
      const res = await axiosReal.get(`https://api.mfrbuild.com/api/projects/${cleanId}/team`, config);

      if (res.data && Array.isArray(res.data)) {
        const contractors = res.data.filter(user => {
          const role = user.role ? user.role.toLowerCase() : '';
          return role === 'contractor' || role === 'thekedar' || role === 'supervisor' || role === 'labour';
        });
        setContractorsList(contractors);

        const management = res.data.filter(user => {
          const role = user.role ? user.role.toLowerCase() : '';
          return role === 'admin' || role === 'sitemanager' || role === 'client';
        });
        setManagementList(management);
      } else {
        setContractorsList([]);
        setManagementList([]);
      }

    } catch (err) {
      setContractorsList([]);
      setManagementList([]);
    }
  }, [projectId]);

  // 🌐 FETCH ALL REQUISITIONS FOR ACTIVE PROJECT
  const fetchRequisitions = useCallback(async () => {
    if (!projectId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axiosReal.get(`https://api.mfrbuild.com/api/procurement/requisition/${projectId}`, {
        headers: { 'x-auth-token': token }
      });

      if (res.data && res.data.success && Array.isArray(res.data.data)) {
        setRequisitions(res.data.data);
      } else if (Array.isArray(res.data)) {
        setRequisitions(res.data);
      } else {
        setRequisitions([]);
      }
    } catch (err) {
      console.error("Error fetching requisitions:", err);
      setRequisitions([]);
    }
  }, [projectId]);

  // 📊 FETCH COMPARISON MATRIX FOR A SELECTED INDENT
  const fetchMatrix = async (reqId) => {
    setSelectedReqId(reqId);
    try {
      const token = localStorage.getItem('token');
      const res = await axiosReal.get(`https://api.mfrbuild.com/api/procurement/matrix/${reqId}`, {
        headers: { 'x-auth-token': token }
      });
      if (res.data && res.data.success) setComparisonMatrix(res.data.data);
    } catch (err) {
      console.error("Error fetching matrix:", err);
    }
  };

  // 🔥 FETCH ALL INDEPENDENT PURCHASE ORDERS FOR ACTIVE PROJECT
  const fetchPurchaseOrders = useCallback(async () => {
    if (!projectId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axiosReal.get(`https://api.mfrbuild.com/api/procurement/po/${projectId}`, {
        headers: { 'x-auth-token': token }
      });

      if (res.data && res.data.success && Array.isArray(res.data.data)) {
        setPurchaseOrders(res.data.data);
      } else if (Array.isArray(res.data)) {
        setPurchaseOrders(res.data);
      } else {
        setPurchaseOrders([]);
      }
    } catch (err) {
      console.error("Error loading purchase order logs:", err);
      setPurchaseOrders([]);
    }
  }, [projectId]);

  // 🛡️ FETCH SAFETY DASHBOARD DATA
  const fetchSafetyDashboard = useCallback(async () => {
    if (!projectId) return;

    try {
      setSafetyLoading(true);
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };

      const res = await axiosReal.get(`https://api.mfrbuild.com/api/safety/project/${projectId}`, config);

      if (res.data && res.data.success) {
        setSafetyData(res.data.data);
      }
    } catch (err) {
      console.error("HSE Data Fetch Error:", err);
    } finally {
      setSafetyLoading(false);
    }
  }, [projectId]);

  // 🇵🇰 FIXED PROJECT METADATA & BUDGET LIMIT FETCH
  useEffect(() => {
    const fetchProjectBudget = async () => {
      if (!projectId) return;
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { 'x-auth-token': token } };

        const res = await axiosReal.get(`https://api.mfrbuild.com/api/expenses/project-meta/${projectId}`, config);

        if (res.data && res.data.success && res.data.budgetLimit !== undefined) {
          setBudgetLimit(Number(res.data.budgetLimit));
          localStorage.setItem(`project_budget_${projectId}`, res.data.budgetLimit);
        }
      } catch (err) {
        console.error("API Fetch Error:", err);
      }
    };

    fetchProjectBudget();
  }, [projectId]);

  // ⚙️ FETCH ASSETS FROM DATABASE SYNC
  const fetchProjectAssets = useCallback(async () => {
    const token = localStorage.getItem('token');
    const config = { headers: { 'x-auth-token': token } };
    try {
      const res = await axiosReal.get(`https://api.mfrbuild.com/api/v1/assets`, config);
      setAssets(res.data?.data || res.data || []);
    } catch (err) {
      console.error("Asset telemetry link error, loading mock playground:", err.message);
      setAssets([
        { _id: 'ast1', name: 'CAT Excavator 320D', modelNumber: '2024-X', serialNumber: 'EXC-99823', category: 'Heavy Machinery', status: 'Active', totalHoursOperated: 180, nextServiceDueHours: 250, ownership: 'Owned', gpsLocation: { latitude: 31.5204, longitude: 74.3587 }, geofence: { isViolated: false } },
        { _id: 'ast2', name: 'Concrete Mixer Truck', modelNumber: 'MIX-45', serialNumber: 'MIX-1102', category: 'Mixer', status: 'Breakdown', totalHoursOperated: 410, nextServiceDueHours: 400, ownership: 'Rented', gpsLocation: { latitude: 31.5110, longitude: 74.3420 }, geofence: { isViolated: true } }
      ]);
    }
  }, []);

  // 🏢 FETCH BIM MODEL & CLASHES FROM LEDGER
  const fetchBimCoreData = useCallback(async () => {
    if (!projectId) return;
    const token = localStorage.getItem('token');
    const config = { headers: { 'x-auth-token': token } };

    try {
      const modelRes = await axiosReal.get(`https://api.mfrbuild.com/api/v1/bim/projects/${projectId}/active`, config);
      const activeModel = modelRes.data?.data || null;
      setActiveBimModel(activeModel);

      if (activeModel) {
        const clashRes = await axiosReal.get(`https://api.mfrbuild.com/api/v1/bim/models/${activeModel._id}/clashes`, config);
        setBimClashes(clashRes.data?.data || []);
      }
    } catch (err) {
      console.error("BIM core mesh offline, triggering mock space sync:", err.message);
      setActiveBimModel({ _id: 'bim_mod_99', title: 'Main Tower Architectural Matrix', fileUrl: 'https://example.com/models/tower.ifc', fileSize: '42.5 MB', version: 1 });
      setBimClashes([
        { _id: 'c1', modelId: 'bim_mod_99', title: 'HVAC Duct vs Structural Column', description: 'Air conditioning trunk line is passing inside the load-bearing concrete column at Sector B.', clashType: 'Hard Clash', priority: 'Critical', status: 'Open', coordinates: { x: 12.4, y: 45.2, z: 3.1 }, assignedTo: { name: 'Engr. Haris', role: 'Structural Lead' } },
        { _id: 'c2', modelId: 'bim_mod_99', title: 'Plumbing Route Overlap', description: 'High pressure water line clearance is less than 5 inches from electrical tray panel grid.', clashType: 'Soft Clash/Clearance', priority: 'High', status: 'In Review', coordinates: { x: -5.2, y: 18.1, z: 14.8 }, assignedTo: { name: 'Engr. Ali', role: 'MEP Specialist' } }
      ]);
    }
  }, [projectId]);

  // 🔴 REQUEST WEB NOTIFICATION PERMISSIONS ON BOOTSTRAP
  useEffect(() => {
    if ("Notification" in window) {
      if (Notification.permission !== "granted" && Notification.permission !== "denied") {
        Notification.requestPermission().then((permission) => {
          if (permission === "granted" && typeof requestForToken === 'function') {
            requestForToken().catch(err => console.error("Initial boot token request failed:", err));
          }
        });
      }
    }
  }, []);

  // 🏁 MASTER EFFECT SYNC
  useEffect(() => {
    if (projectId) {
      fetchConsoleCoreData();
      fetchUbaidDocumentsOnly();
      fetchProjectTasksOnly();
      fetchProjectAttendance();
      fetchProjectPayroll();
      fetchContractorsAndStaff();
      fetchRequisitions();
      fetchPurchaseOrders();
      fetchSafetyDashboard();
      fetchProjectAssets();
      fetchBimCoreData();

      const syncFirebaseDeviceToken = async () => {
        try {
          if (typeof requestForToken === 'function') {
            const currentToken = await requestForToken();
            const authToken = localStorage.getItem('token');

            if (currentToken && authToken) {
              await axiosReal.put(
                'https://api.mfrbuild.com/api/auth/update-fcm-token',
                { fcmToken: currentToken },
                { headers: { 'x-auth-token': authToken } }
              );
            }
          }
        } catch (fcmErr) {
          console.error("🚨 Background FCM Token Ledger Synchronization Failure:", fcmErr.message);
        }
      };

      syncFirebaseDeviceToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [projectId]);

  // --- 🗑️ DATABASE DELETE EXECUTION ---
  const handleDeleteTask = async (id) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    const token = localStorage.getItem('token');

    try {
      await axiosReal.delete(`https://api.mfrbuild.com/api/tasks/${id}`, {
        headers: { 'x-auth-token': token }
      });

      setProjectTasks((prevTasks) => {
        if (!prevTasks || !Array.isArray(prevTasks)) return [];
        return prevTasks.filter(t => t._id !== id);
      });
    } catch (err) {
      console.error("Error deleting task:", err.message);
    }
  };

  const handleDeleteAttendance = async (id) => {
    if (!window.confirm("Remove this attendance record?")) return;
    try {
      await axiosReal.delete(`https://api.mfrbuild.com/api/attendance/${id}`);
      setAttendanceRecords(attendanceRecords.filter(a => a._id !== id));
    } catch (err) {
      console.error("Error deleting attendance:", err.message);
    }
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm("Kya aap waqai is DPR footprint ko delete karna chahte hain?")) return;

    const token = localStorage.getItem('token');
    if (!token) return alert("Session expired. Please login again.");

    const config = { headers: { 'x-auth-token': token } };

    try {
      await axiosReal.delete(`https://api.mfrbuild.com/api/daily-logs/${logId}`, config);
      setDailyLogs(prev => prev.filter(l => l._id !== logId));
    } catch (err) {
      console.error("Delete Error context:", err.message);
      alert(err.response?.data?.msg || "Failed to delete log entry. 401 Unauthorized.");
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm("Permanently remove this ledger cost allocation?")) return;
    try {
      await axiosReal.delete(`https://api.mfrbuild.com/api/expenses/${id}`);
      setExpenses(expenses.filter(e => e._id !== id));
    } catch (err) {
      console.error("Error deleting expense:", err.message);
    }
  };

  // --- 📝 EDIT TRIGGER LOOPS ---
  const handleEditTaskClick = (task) => {
    setEditingItemId(task._id);
    setTaskData({
      title: task.title,
      description: task.description || '',
      assignedTo: task.assignedTo?.name || task.assignedTo || '',
      roleAtAssignment: task.roleAtAssignment || 'Labour',
      dueDate: task.dueDate ? task.dueDate.substring(0, 10) : ''
    });
    setShowAddTaskModal(true);
  };

  const handleEditAttendanceClick = (rec) => {
    setEditingItemId(rec._id);
    setAttendanceData({ workerName: rec.workerName, role: rec.role, status: rec.status });
    setShowAddAttendanceModal(true);
  };

  // eslint-disable-next-line no-unused-vars
  const handleEditExpenseClick = (exp) => {
    setEditingItemId(exp._id);
    setExpenseData({ item: exp.item, amount: exp.amount, category: exp.category });
    setShowAddExpenseModal(true);
  };

  // Fetch invited list on load/modal trigger
  const fetchInvitedMembers = async () => {
    try {
      const token = localStorage.getItem('token');

      // ✅ FIX 1: Exact backend route use karein
      const res = await axiosReal.get(`https://api.mfrbuild.com/api/auth/invite-list/${projectId}`, {
        headers: { 'x-auth-token': token }
      });

      // ✅ FIX 2: res.data.invitations check karein jo backend bhej raha hai
      if (res.data && res.data.success) {
        setInvitedList(res.data.invitations || []);
      } else {
        setInvitedList([]);
      }
    } catch (err) {
      console.error("Error pulling database team matrix entries:", err);
      setInvitedList([]); // Safe fallback on error
    }
  };

  const handleBulkInviteSubmit = (e) => {
    e.preventDefault();

    // Check missing entries
    const validRows = inviteRows.filter(row => row.phone.trim() !== "");
    if (validRows.length === 0) return alert("Kam se kam ek WhatsApp number zaroor likhein!");

    // 📱 AUTOMATIC WHATSAPP MSG LOGIC (For 2 or 2+ entries)
    if (validRows.length >= 2) {
      validRows.forEach(row => {
        const textMessage = `Assalam-o-Alaikum, you are invited to join the project workspace as ${row.role}. Daily Wage: Rs.${row.dailyWage || 'N/A'}. Please click secure link to join.`;
        // Automatic WhatsApp API Trigger (Deep link windows/mobile auto dispatch)
        const whatsappUrl = `https://api.whatsapp.com/send?phone=${row.phone}&text=${encodeURIComponent(textMessage)}`;
        window.open(whatsappUrl, '_blank');
      });
    } else {
      alert("Invitation processed successfully!");
    }

    setShowInviteModal(false);
  };

  // --- 💾 REAL FULL-STACK SAVE/UPDATE LOOPS ---
  const handleCreateTaskSubmit = async (e) => {
    e.preventDefault();

    // 1. Date Validation Check
    if (taskData.startDate && taskData.dueDate) {
      if (new Date(taskData.dueDate) < new Date(taskData.startDate)) {
        alert("⚠️ Error: Target End Date (Due Date) kabhi bhi Start Date se pehle nahi ho sakti!");
        return;
      }
    }

    // 2. Dropdown Assignment Check
    if (!taskData.assignedTo || taskData.assignedTo === "" || taskData.assignedTo === "ttt" || taskData.assignedTo === "new") {
      alert("Please select a valid Staff/Labour member from the dropdown list!");
      setShowAddTaskModal(false);
      return;
    }

    // 🔑 THE EXACT BACKEND HEADER FIX
    const token = localStorage.getItem('token');
    const config = {
      headers: {
        'x-auth-token': token  // Node.js/Express backend aksar isi header ko bina Bearer ke accept karta hai
      }
    };

    // Payload structure taiyar karein
    const targetPayload = editingItemId
      ? { ...taskData }
      : {
        title: taskData.title,
        description: taskData.description || 'Field allocation task asset logs',
        assignedTo: taskData.assignedTo,
        roleAtAssignment: taskData.roleAtAssignment,
        project: projectId,
        dueDate: taskData.dueDate || new Date().toISOString()
      };

    const localTemporaryId = editingItemId || 'temp_task_' + Date.now().toString(36);
    const finalLocalData = { ...targetPayload, _id: localTemporaryId };

    // ==========================================
    // 🌐 ONLINE FLOW
    // ==========================================
    if (navigator.onLine) {
      if (editingItemId) {
        try {
          const res = await axios.put(`https://api.mfrbuild.com/api/tasks/${editingItemId}`, taskData, config);

          if (res.data) {
            const updatedTask = res.data.data || res.data;
            setProjectTasks(prevTasks => {
              const currentTasks = Array.isArray(prevTasks) ? prevTasks : [];
              return currentTasks.map(t => t._id === editingItemId ? updatedTask : t);
            });

            triggerSystemNotification(
              "📝 TASK MODIFIED",
              `Task parameters for "${taskData.title || 'Allocated Task'}" have been updated by Admin.`
            );

            if (typeof fetchProjectTasksOnly === 'function') fetchProjectTasksOnly();
          }
        } catch (err) {
          console.error("Task Edit Error:", err.message);
          alert("Error updating task: " + (err.response?.data?.message || err.message));
        }
        setEditingItemId(null);
      }
      else {
        try {
          // 🚀 Sending with exact URL and custom x-auth-token header
          const res = await axios.post('https://api.mfrbuild.com/api/tasks', targetPayload, config);

          if (res.data) {
            const newTaskData = res.data.data || res.data;

            setProjectTasks(prevTasks => {
              const currentTasks = Array.isArray(prevTasks) ? prevTasks : [];
              return [newTaskData, ...currentTasks];
            });

            triggerSystemNotification(
              "📋 NEW TASK ASSIGNED",
              `Task: "${targetPayload.title}" has been successfully dispatched to the field workforce logs.`
            );

            if (typeof fetchProjectTasksOnly === 'function') fetchProjectTasksOnly();
          }
        } catch (err) {
          console.error("Task Deployment Error:", err.message);
          alert("Error: Failed to deploy task. " + (err.response?.data?.message || err.message));
        }
      }
    }
    // ==========================================
    // 📡 OFFLINE FLOW
    // ==========================================
    else {
      try {
        const syncUrl = editingItemId ? `/api/tasks/${editingItemId}` : '/api/tasks';
        const syncMethod = editingItemId ? 'PUT' : 'POST';

        await offlineDB.outboxQueue.add({
          url: syncUrl,
          method: syncMethod,
          data: targetPayload,
          timestamp: Date.now()
        });

        setProjectTasks(prevTasks => {
          const currentTasks = Array.isArray(prevTasks) ? prevTasks : [];
          if (editingItemId) {
            return currentTasks.map(t => t._id === editingItemId ? finalLocalData : t);
          } else {
            return [finalLocalData, ...currentTasks];
          }
        });

        alert("⚠️ Device Network Offline: Task operation saved securely inside device local storage vault!");
        if (editingItemId) setEditingItemId(null);

      } catch (dbErr) {
        console.error("Local database task injection error:", dbErr.message);
      }
    }

    setShowAddTaskModal(false);
    setTaskData({ title: '', description: '', assignedTo: '', roleAtAssignment: 'Labour', dueDate: '' });
  };

  const handleCreateAttendanceSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return alert("Session expired. Please login again.");
    if (!attendanceData.workerName || attendanceData.workerName.trim() === "") {
      return alert("Please select a registered worker/staff from the dropdown list!");
    }

    const config = { headers: { 'x-auth-token': token, 'Content-Type': 'application/json' } };
    const attendancePayload = {
      // 🟢 FIXED: Backend ko user ki ObjectId 'workerName' key mein chahiye
      workerName: attendanceData.workerId || attendanceData.worker || attendanceData.workerName,
      role: attendanceData.role,
      status: attendanceData.status,
      // 🟢 FIXED: Backend model mein key ka naam 'project' hai, 'projectId' nahi!
      project: projectId,
      paymentType: attendanceData.paymentType || 'Daily-Wage',
      dailyRate: Number(attendanceData.dailyRate) || 0,
      workVolume: Number(attendanceData.workVolume) || 0,
      advanceTaken: Number(attendanceData.advanceTaken) || 0,
      date: new Date().toISOString()
    };

    const isOnline = navigator.onLine;

    if (editingItemId) {
      const targetUrl = `https://api.mfrbuild.com/api/attendance/${editingItemId}`;

      if (isOnline) {
        try {
          const res = await axiosReal.put(targetUrl, attendancePayload, config);
          setAttendanceRecords(attendanceRecords.map(a => a._id === editingItemId ? (res.data.data || res.data) : a));
          fetchProjectAttendance();
          fetchProjectPayroll();
        } catch (err) {
          console.error("Attendance Update Error:", err.message);
        }
      } else {
        if (typeof offlineDB !== 'undefined' && offlineDB.outboxQueue) {
          await offlineDB.outboxQueue.add({
            url: `/api/attendance/${editingItemId}`,
            method: 'PUT',
            data: attendancePayload,
            timestamp: Date.now()
          });
          alert("📝 Device offline! Attendance update saved locally.");
        }
      }
      setEditingItemId(null);
    } else {
      const targetUrl = 'https://api.mfrbuild.com/api/attendance';

      if (isOnline) {
        try {
          const res = await axiosReal.post(targetUrl, attendancePayload, config);
          const newRecord = res.data.data || res.data;
          setAttendanceRecords([newRecord, ...attendanceRecords]);
          fetchProjectAttendance();
          fetchProjectPayroll();
        } catch (err) {
          console.error("Attendance Creation Error:", err.message);
          alert("Failed to save attendance.");
        }
      } else {
        if (typeof offlineDB !== 'undefined' && offlineDB.outboxQueue) {
          const temporaryId = `temp-${Date.now()}`;

          // 💡 PERFECT MATCH: Dropdown se align karte hue 'allUsers' ka use kiya
          const selectedWorker = typeof allUsers !== 'undefined' && Array.isArray(allUsers)
            ? allUsers.find(u => u?._id === attendancePayload.workerName || u?.id === attendancePayload.workerName)
            : null;

          const localOfflineRecord = {
            _id: temporaryId,
            ...attendancePayload,
            // Agar database list se worker mil jaye to uska naam object format mein UI ko dedo
            workerName: selectedWorker
              ? { _id: selectedWorker._id, name: selectedWorker.name }
              : attendancePayload.workerName,
            isOfflinePending: true
          };

          await offlineDB.outboxQueue.add({
            url: '/api/attendance',
            method: 'POST',
            data: attendancePayload,
            timestamp: Date.now()
          });

          setAttendanceRecords([localOfflineRecord, ...attendanceRecords]);
          alert("💾 Device offline! Attendance saved to device storage.");
        }
      }
    }

    setShowAddAttendanceModal(false);
    setAttendanceData({ workerName: '', role: 'Labour', status: 'Present', paymentType: 'Daily-Wage', dailyRate: 0, workVolume: 0, advanceTaken: 0 });
  };

  const handleCreateLogSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return alert("Session expired. Please login again.");

    // 🟢 LIVE SERVER CONFIG: Standard headers aligned across all pipelines
    const config = {
      headers: {
        'x-auth-token': token,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
    setBtnLoading(true);

    const logPayload = {
      projectId: projectId,
      date: new Date().toISOString(),
      weather: logData.weather || "Sunny",
      materialUsed: logData.materialUsed || "",
      notes: logData.notes || "",
      paymentStatus: logData.paymentStatus || "Paid",
      workSummary: logData.workSummary || "",
      weatherDelayHours: parseInt(logData.weatherDelayHours) || 0,
      roadblocks: {
        reason: logData.roadblocks?.reason || "None",
        description: logData.roadblocks?.description || ""
      },
      safetyChecklist: {
        helmetsWorn: logData.safetyChecklist?.helmetsWorn || false,
        safetyShoesWorn: logData.safetyChecklist?.safetyShoesWorn || false,
        incidentReported: logData.safetyChecklist?.incidentReported || false,
        incidentDetails: logData.safetyChecklist?.incidentDetails || ""
      },
      photos: logData.photos ? [...logData.photos] : []
    };

    const localTemporaryId = editingItemId || 'temp_log_' + Date.now().toString(36);
    const finalLocalData = { ...logPayload, _id: localTemporaryId };

    // ==========================================
    // 🌐 ONLINE FLOW
    // ==========================================
    if (navigator.onLine) {
      if (editingItemId) {
        try {
          // 🎯 FIX: Aligned to production subdomain and correct version route
          const res = await axiosReal.put(`https://api.mfrbuild.com/api/daily-logs/${editingItemId}`, logPayload, config);
          const updatedData = res.data?.data || res.data || {};

          setDailyLogs(prevLogs => prevLogs.map(l => l._id === editingItemId ? updatedData : l));
          setShowAddLogModal(false);

          if (typeof fetchConsoleCoreData === 'function') fetchConsoleCoreData();
        } catch (err) {
          console.error("Daily Log Update Error:", err.message);
          alert(err.response?.data?.msg || "Failed to update daily log entry.");
        }
        setEditingItemId(null);
      } else {
        try {
          // 🎯 FIX: Absolute url target to stop 405 Method Not Allowed
          const res = await axiosReal.post('https://api.mfrbuild.com/api/daily-logs', logPayload, config);
          const newData = res.data?.data || res.data || {};

          setDailyLogs(prevLogs => [newData, ...prevLogs]);
          setShowAddLogModal(false);

          setLogData({
            workSummary: '', notes: '', weather: 'Sunny', weatherDelayHours: 0, paymentStatus: 'Paid',
            roadblocks: { reason: 'None', description: '' },
            safetyChecklist: { helmetsWorn: false, safetyShoesWorn: false, incidentReported: false, incidentDetails: '' },
            materialUsed: '', photos: []
          });

          if (typeof fetchConsoleCoreData === 'function') fetchConsoleCoreData();
        } catch (err) {
          console.error("Daily Log Creation Error:", err.message);
          alert(err.response?.data?.msg || "Failed to save daily report. Please check server log.");
        }
      }
    }
    // ==========================================
    // 📡 OFFLINE FLOW
    // ==========================================
    else {
      try {
        // 🎯 FIX: Absolute paths injected directly into outbox queues for background worker sync
        const syncUrl = editingItemId
          ? `https://api.mfrbuild.com/api/daily-logs/${editingItemId}`
          : 'https://api.mfrbuild.com/api/daily-logs';
        const syncMethod = editingItemId ? 'PUT' : 'POST';

        await offlineDB.outboxQueue.add({
          url: syncUrl,
          method: syncMethod,
          data: logPayload,
          timestamp: Date.now()
        });

        setDailyLogs(prevLogs => {
          const current = Array.isArray(prevLogs) ? prevLogs : [];
          if (editingItemId) {
            return current.map(l => l._id === editingItemId ? finalLocalData : l);
          } else {
            return [finalLocalData, ...current];
          }
        });

        setShowAddLogModal(false);

        setLogData({
          workSummary: '', notes: '', weather: 'Sunny', weatherDelayHours: 0, paymentStatus: 'Paid',
          roadblocks: { reason: 'None', description: '' },
          safetyChecklist: { helmetsWorn: false, safetyShoesWorn: false, incidentReported: false, incidentDetails: '' },
          materialUsed: '', photos: []
        });

        alert("⚠️ Device Network Offline: Daily report log cached securely inside device memory vault!");
        if (editingItemId) setEditingItemId(null);
      } catch (dbErr) {
        console.error("Local database log injection error:", dbErr.message);
      }
    }
    setBtnLoading(false);
  };

  const handleChooseFromGallery = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const galleryPhotoObj = {
        url: reader.result,
        timestamp: new Date(),
        tag: 'Live Stamp',
        isLive: false
      };
      setLogData(prev => ({ ...prev, photos: [...(prev.photos || []), galleryPhotoObj] }));
    };
  };

  const handleStartCamera = async () => {
    setBtnLoading(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      setCameraStream(stream);
      setShowCameraView(true);
      setBtnLoading(false);

      setTimeout(() => {
        const videoEl = document.getElementById('live-webcam-preview');
        if (videoEl) videoEl.srcObject = stream;
      }, 300);
    } catch (error) {
      setBtnLoading(false);
      alert("Camera permissions denied or device camera not found. Trying default input selector.");

      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.capture = 'environment';
      input.onchange = (e) => {
        const file = e.target.files[0];
        if (file) {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            const newPhoto = { url: reader.result, timestamp: new Date(), tag: 'Live Capture' };
            setLogData(prev => ({ ...prev, photos: [...(prev.photos || []), newPhoto] }));
          };
        }
      };
      input.click();
    }
  };

  const handleCaptureSnapshot = () => {
    const videoEl = document.getElementById('live-webcam-preview');
    if (!videoEl) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoEl.videoWidth || 640;
    canvas.height = videoEl.videoHeight || 480;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
    const base64Data = canvas.toDataURL('image/jpeg', 0.7);

    const appendPhoto = (coords = null) => {
      const newPhoto = {
        url: base64Data,
        timestamp: new Date(),
        coordinates: coords,
        tag: 'Live Stamp',
        isLive: true
      };
      setLogData(prev => ({ ...prev, photos: [...(prev.photos || []), newPhoto] }));
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => appendPhoto({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => appendPhoto()
      );
    } else {
      appendPhoto();
    }

    if (cameraStream) cameraStream.getTracks().forEach(track => track.stop());
    setCameraStream(null);
    setShowCameraView(false);
  };

  const startVoiceToTextEngine = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Voice typing is not supported in this browser. Please use Google Chrome.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.lang = 'en-US';
    recognition.interimResults = false;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onerror = (event) => {
      console.error("Speech Recognition Engine Error:", event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setLogData((prevData) => ({
        ...prevData,
        workSummary: prevData.workSummary ? `${prevData.workSummary} ${transcript}` : transcript
      }));
    };

    recognition.start();
  };

  const handleEditLogClick = (log) => {
    setEditingItemId(log._id);
    setLogData({
      workSummary: log.workSummary || '',
      notes: log.notes || '',
      weather: log.weather || 'Sunny',
      weatherDelayHours: log.weatherDelayHours || 0,
      paymentStatus: log.paymentStatus || 'Paid',
      roadblocks: log.roadblocks || { reason: 'None', description: '' },
      safetyChecklist: log.safetyChecklist || { helmetsWorn: false, safetyShoesWorn: false, incidentReported: false, incidentDetails: '' },
      materialUsed: log.materialUsed || '',
      photos: log.photos || []
    });
    setLogModalTab('general');
    setShowAddLogModal(true);
  };

  {/*
  // eslint-disable-next-line no-unused-vars
  const handleCreateExpenseSubmit = async (e) => {
    e.preventDefault();
    
    // 1. SILENT LAYER: Check network state immediately without creating noisy console exceptions
    const isDeviceOnline = !!(navigator && navigator.onLine);

    // 2. SESSION VALIDATION
    const token = localStorage.getItem('token');
    if (!token) {
      alert("Session expired. Please login again.");
      return;
    }

    // 3. SECURE AUTH CONFIGURATION (Supports Legacy & Modern Backend Formats)
    const config = { 
      headers: { 
        'x-auth-token': token,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      } 
    };

    // 4. FRONTEND VALIDATION GUARDRAILS (Prevents processing empty values)
    if (!expenseData.item || !expenseData.item.trim()) {
      alert("Please enter a valid item name.");
      return;
    }
    if (!expenseData.amount || Number(expenseData.amount) <= 0) {
      alert("Please enter an amount greater than 0.");
      return;
    }

    // 5. DATA INJECTION & BACKEND ROUTE ALIGNMENT
    // We map frontend modal fields (paymentMode, vendor) to controller specs (paymentType, vendorName)
    const expensePayload = {
      project: projectId,
      item: expenseData.item.trim(),
      amount: Number(expenseData.amount) || 0,
      category: expenseData.category || 'Materials',
      paymentType: expenseData.paymentMode || 'Cash',         // Maps to controller destructuring
      vendorName: expenseData.vendor || 'General Market',     // Maps to controller destructuring
      receiptImage: expenseData.receiptImage || '',           // Preserve uploaded base64 / paths if any
      loggedBy: expenseData.loggedBy || 'Supervisor'          // Explicit loggedBy tracking
    };

    // 6. LOCAL PERSISTENCE COPIES
    const localTemporaryId = editingItemId || 'temp_exp_' + Date.now().toString(36);
    
    // Dynamic status determination to mirror backend 5,000 PKR Safety Guardrail locally
    let simulatedStatus = 'Approved';
    if (expensePayload.amount > 5000) {
      simulatedStatus = 'Pending Approval';
    }

    const finalLocalData = { 
      ...expensePayload, 
      _id: localTemporaryId, 
      status: simulatedStatus,
      createdAt: new Date().toISOString(),
      date: new Date().toISOString() 
    };

    // =========================================================================
    // 📡 ISOLATED OFFLINE STORAGE ENGINE (Dexie Outbox Queue Delivery)
    // =========================================================================
    const saveToDeviceOfflineVault = async () => {
      try {
        const syncUrl = editingItemId 
          ? `https://api.mfrbuild.com/api/expenses/${editingItemId}` 
          : 'https://api.mfrbuild.com/api/expenses';
        const syncMethod = editingItemId ? 'PUT' : 'POST';

        // Direct async injection to database outbox table
        if (typeof offlineDB !== 'undefined' && offlineDB.outboxQueue) {
          await offlineDB.outboxQueue.add({
            url: syncUrl,
            method: syncMethod,
            data: expensePayload,
            timestamp: Date.now()
          });
        } else {
          console.error("Dexie local engine instance 'offlineDB' is unavailable on window context.");
        }

        // Direct mutation hook to render immediate element on site manager's viewport
        setExpenses(prevExpenses => {
          const current = Array.isArray(prevExpenses) ? prevExpenses : [];
          if (editingItemId) {
            return current.map(exp => exp._id === editingItemId ? finalLocalData : exp);
          } else {
            return [finalLocalData, ...current];
          }
        });

        alert(`⚠️ Local Vault Sync Saved!\nItem: ${expensePayload.item}\nStatus: ${simulatedStatus} (Saved Offline)`);
        
        // Clean conditional selection contexts
        if (editingItemId) setEditingItemId(null);
      } catch (dbErr) {
        console.error("Critical Outbox Queue Injection Failure:", dbErr.message);
      }
    };

    // =========================================================================
    // 🛡️ DECISION ROUTER: ABSOLUTE NETWORK BYPASS FOR FORCED OFFLINE STATE
    // =========================================================================
    if (!isDeviceOnline) {
      await saveToDeviceOfflineVault();
      
      // Complete state-machine cleanup to mimic normal application workflow closures
      setShowAddExpenseModal(false);
      if (typeof setExpenseData === 'function') {
        setExpenseData({ 
          item: '', 
          amount: '', 
          category: 'Materials', 
          paymentMode: 'Cash', 
          vendor: '', 
          receiptImage: '',
          loggedBy: 'Supervisor' 
        });
      }
      return; // Breaks sequence completely so Axios net blocks can never evaluate or error
    }

    // =========================================================================
    // 🌐 ONLINE PIPELINE (RUNS ONLY WHEN HARDWARE INTERFACE DETECTS NET)
    // =========================================================================
    try {
      if (editingItemId) {
        // Handle Put Operations
        const res = await axiosReal.put(`https://api.mfrbuild.com/api/expenses/${editingItemId}`, expensePayload, config);
        const updatedData = res.data?.data || res.data || finalLocalData;
        
        setExpenses(prevExpenses => {
          const current = Array.isArray(prevExpenses) ? prevExpenses : [];
          return current.map(exp => exp._id === editingItemId ? updatedData : exp);
        });
        
        if (typeof fetchConsoleCoreData === 'function') fetchConsoleCoreData();
        setEditingItemId(null);
      } else {
        // Handle Post Operations
        const res = await axiosReal.post('https://api.mfrbuild.com/api/expenses', expensePayload, config);
        const newData = res.data?.data || res.data || finalLocalData;
        
        setExpenses(prevExpenses => {
          const current = Array.isArray(prevExpenses) ? prevExpenses : [];
          return [newData, ...current];
        });

        if (typeof fetchConsoleCoreData === 'function') fetchConsoleCoreData();
      }
    } catch (err) {
      // Emergency network breakdown capture block
      console.warn("Axios target unreached or server dropped connection. Initiating secure device database redirect.", err.message);
      await saveToDeviceOfflineVault();
    }

    // =========================================================================
    // 🧹 FINAL CYCLE CLEANUP (Executes at the very end of both online/offline paths)
    // =========================================================================
    setShowAddExpenseModal(false);
    if (typeof setExpenseData === 'function') {
      setExpenseData({ 
        item: '', 
        amount: '', 
        category: 'Materials', 
        paymentMode: 'Cash', 
        vendor: '', 
        receiptImage: '',
        loggedBy: 'Supervisor' 
      });
    }
  };
*/}
  // 📸 Local Image to Base64 String Converter Handler
  const handleParchiFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setExpenseData({ ...expenseData, receiptImage: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  // 🟢 Green Button Approval Quick Trigger
  const handleApproveClick = async (expenseId) => {
    const token = localStorage.getItem('token');
    if (!token) return alert("Session expired. Please login again.");
    const config = { headers: { 'x-auth-token': token } };

    try {
      const res = await axiosReal.put(`https://api.mfrbuild.com/api/expenses/approve/${expenseId}`, {}, config);
      if (res.data) {
        setExpenses(expenses.map(exp => exp._id === expenseId ? res.data : exp));
      }
    } catch (err) {
      console.error("Green Button Approval Click Error:", err.message);
      alert("Approval fail hui! Poshida wajah: Backend route check karein.");
    }
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    const token = localStorage.getItem('token');
    if (!token) return alert("Session expired. Please login again.");
    const config = { headers: { 'x-auth-token': token } };

    try {
      await axiosReal.put(`https://api.mfrbuild.com/api/tasks/status/${taskId}`, { status: newStatus }, config);

      setProjectTasks((prevTasks) => {
        if (!prevTasks || !Array.isArray(prevTasks)) return [];
        return prevTasks.map(t => t._id === taskId ? { ...t, status: newStatus } : t);
      });
    } catch (err) {
      console.error("Task Status Change Error:", err.message);
    }
  };

  const handleAssignUserSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUserId) return alert("Please select a user first!");
    setAssignLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`https://api.mfrbuild.com/api/projects/assign-user`, {
        projectId: targetProjectMeta._id,
        userId: selectedUserId
      }, { headers: { 'x-auth-token': token } });

      setSelectedUserId('');
      if (window.location.reload) window.location.reload();
    } catch (err) {
      console.error("Assign User Submit Error:", err.message || err);
    } finally {
      setAssignLoading(false);
    }
  };

  // ==========================================
  // 🔊 VOICE NOTE RECORDER HANDLERS (RFI MODULE)
  // ==========================================
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      let chunks = [];

      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/mp3' });
        setAudioBlob(blob);
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (err) {
      console.error("Mic Access Denied:", err);
      alert("Microphone allow karein taake voice note record ho sake!");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
    }
  };

  // 🥊 PUNCH LIST HANDLERS KA SATH ATTACH HA (UPGRADED)
  // File ko string (Base64) mein badalne ka function taake offline save ho sake
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // ==========================================
  // 🥊 PUNCH LIST HANDLERS (UPGRADED)
  // ==========================================
  const handleCreatePunchSubmit = async (e) => {
    e.preventDefault();
    if (!punchBeforeImage) return alert("Defect ki shuruati (Before) tasveer lazmi hai!");

    // 1. Offline Data Tayyar Karein
    let base64Image = "";
    try {
      base64Image = await fileToBase64(punchBeforeImage);
    } catch (err) {
      console.error("Image conversion error:", err);
    }

    const offlineId = `offline_punch_${Date.now()}`;
    const tempPunchItem = {
      _id: offlineId,
      project: projectId,
      issueDescription: newPunch.issueDescription,
      location: newPunch.location,
      assignedContractor: newPunch.assignedContractor,
      priority: newPunch.priority,
      dueDate: newPunch.dueDate || null,
      punchBeforeImage: base64Image, // Temporary local display ke liye base64 string
      status: 'Open',
      isOffline: true, // Tag taake pata chale yeh offline data hai
      pendingSync: 'CREATE'
    };

    // 2. AGAR OFFLINE HAI
    if (!navigator.onLine) {
      // Purana data nikalen ya khali array len
      const existingOffline = JSON.parse(localStorage.getItem(`offline_punches_${projectId}`) || '[]');
      existingOffline.push(tempPunchItem);
      localStorage.setItem(`offline_punches_${projectId}`, JSON.stringify(existingOffline));

      // UI State update karein taake user ko fauran naya item nazar aaye
      setPunchItems([tempPunchItem, ...punchItems]);

      // Form saaf karein aur modal band karein
      setNewPunch({ issueDescription: '', location: '', assignedContractor: '', priority: 'Medium', dueDate: '' });
      setPunchBeforeImage(null);
      setShowAddPunchModal(false);

      alert("Internet nahi hai! Punchlist item offline save kar liya gaya hai, net aane par sync ho jayega.");
      return;
    }

    // 3. AGAR ONLINE HAI (Aapka Asal Code)
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('project', projectId);
      formData.append('issueDescription', newPunch.issueDescription);
      formData.append('location', newPunch.location);
      formData.append('assignedContractor', newPunch.assignedContractor);
      formData.append('priority', newPunch.priority);
      if (newPunch.dueDate) formData.append('dueDate', newPunch.dueDate);
      formData.append('image', punchBeforeImage);

      const config = { headers: { 'x-auth-token': token, 'Content-Type': 'multipart/form-data' } };
      const res = await axiosReal.post('https://api.mfrbuild.com/api/punchlists/add', formData, config);

      setPunchItems([res.data, ...punchItems]);
      setNewPunch({ issueDescription: '', location: '', assignedContractor: '', priority: 'Medium', dueDate: '' });
      setPunchBeforeImage(null);
      setShowAddPunchModal(false);
    } catch (err) {
      console.error("Punch creation error:", err.message || err);
    }
  };

  const handleUpdatePunchStatus = async (itemId, newStatus, afterFile = null) => {
    // 1. AGAR OFFLINE HAI
    if (!navigator.onLine) {
      let base64AfterImage = "";
      if (afterFile) {
        try {
          base64AfterImage = await fileToBase64(afterFile);
        } catch (err) {
          console.error("After image conversion error:", err);
        }
      }

      // UI Update karein fauran
      setPunchItems(punchItems.map(item => {
        if (item._id === itemId) {
          return {
            ...item,
            status: newStatus,
            punchAfterImage: base64AfterImage || item.punchAfterImage,
            isOfflineUpdated: true,
            pendingSync: 'UPDATE',
            // Agar yeh pehle se offline create hua tha, to binary file save karlein sync ke liye
            localAfterFile: base64AfterImage
          };
        }
        return item;
      }));

      // LocalStorage mein update save karein
      const existingOfflineUpdates = JSON.parse(localStorage.getItem(`offline_punch_updates_${projectId}`) || '[]');
      existingOfflineUpdates.push({ itemId, newStatus, afterImageBase64: base64AfterImage });
      localStorage.setItem(`offline_punch_updates_${projectId}`, JSON.stringify(existingOfflineUpdates));

      alert("Status offline update ho gaya hai!");
      return;
    }

    // 2. AGAR ONLINE HAI (Aapka Asal Code)
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('status', newStatus);

      if (newStatus === 'Resolved' && afterFile) {
        formData.append('image', afterFile);
      }

      const config = { headers: { 'x-auth-token': token, 'Content-Type': 'multipart/form-data' } };
      const res = await axiosReal.patch(`https://api.mfrbuild.com/api/punchlists/${itemId}`, formData, config);

      setPunchItems(punchItems.map(item => item._id === itemId ? res.data : item));
    } catch (err) {
      alert(err.response?.data?.message || "Status update failed!");
      console.error("Punch Status Update Error:", err.message);
    }
  };

  // ==========================================
  // 🔊 RFI HANDLERS (NEW MODULE)
  // ==========================================
  const handleCreateRfiSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('project', projectId);
      formData.append('assignedTo', newRfi.assignedTo);
      formData.append('textQuestion', newRfi.textQuestion);
      formData.append('urgency', newRfi.urgency);

      if (audioBlob) formData.append('audio', audioBlob, 'rfi_voice.mp3');
      if (rfiImageFile) formData.append('image', rfiImageFile);

      const config = { headers: { 'x-auth-token': token, 'Content-Type': 'multipart/form-data' } };
      const res = await axiosReal.post('https://api.mfrbuild.com/api/rfis/add', formData, config);

      setRfiItems([res.data, ...rfiItems]);
      setNewRfi({ assignedTo: '', textQuestion: '', urgency: 'Medium' });
      setAudioBlob(null);
      setRfiImageFile(null);
      setShowRfiModal(false);
    } catch (err) {
      console.error("RFI Creation Error:", err.message || err);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleAnswerRfiSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      const res = await axiosReal.patch(`https://api.mfrbuild.com/api/rfis/answer/${selectedRfi._id}`, { answer: rfiAnswerText }, config);

      setRfiItems(rfiItems.map(item => item._id === selectedRfi._id ? res.data : item));
      setShowRfiAnswerModal(false);
      setRfiAnswerText('');
    } catch (err) {
      console.error("RFI Answer Submit Error:", err.message || err);
    }
  };
  // ==========================================
  // 📲 LOCAL MARKET EXTRA HACK: WHATSAPP SUMMARY
  // ==========================================
  const shareDailyReportOnWhatsApp = () => {
    const openIssues = punchItems.filter(i => i.status !== 'Verified').length;
    const pendingRfis = rfiItems.filter(r => r.status === 'Pending').length;

    const textSummary = `*🏗️ SITE DAILY PROGRESS REPORT* \n\n` +
      `*Punch List (Defects Broken Down):* ${openIssues} Active Issues Pending Fix.\n` +
      `*RFIs (Kaam Ruka Hua Sawaal):* ${pendingRfis} Technical Questions Pending.\n\n` +
      `_Sent via Construction Management Engine Pro._`;

    const encodedText = encodeURIComponent(textSummary);
    window.open(`https://api.whatsapp.com/send?text=${encodedText}`, '_blank');
  };

  const handleDeletePunchItem = async (itemId) => {
    if (!window.confirm("Are you sure you want to delete this QA/QC item?")) return;
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      await axiosReal.delete(`https://api.mfrbuild.com/api/punchlists/${itemId}`, config);
      setPunchItems(punchItems.filter(item => item._id !== itemId));
    } catch (err) {
      console.error("Delete punch item error:", err.message);
    }
  };

  // =========================================================================
  // 📦 INVENTORY SUBMISSION HANDLER (WITH LIVE DEPLETION NOTIFICATION ALERT)
  // =========================================================================
  const handleInventorySubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const config = { headers: { 'x-auth-token': token } };

    // 1. Base Data Object (Jo dono online/offline me common hai)
    const baseData = {
      projectId: projectId,
      itemName: inventoryData.itemName || inventoryData.materialName || '',
      category: inventoryData.category || 'Materials',
      quantityChange: Number(inventoryData.quantity) || Number(inventoryData.quantityChange) || 0,
      unit: inventoryData.unit || 'Bags',
      minThreshold: Number(inventoryData.minThreshold) || Number(inventoryData.safetyLimit) || 10,
      type: inventoryData.type || 'StockIn'
    };

    // 2. 🚫 AGAR INTERNET NAHI HAI (OFFLINE MODE)
    if (!navigator.onLine) {
      const offlinePayload = {
        ...baseData,
        _id: `offline_inv_${Date.now()}`, // Sirf offline ke liye temporary ID
        currentLevelStock: baseData.quantityChange,
        minSafetyLimit: baseData.minThreshold,
        status: baseData.quantityChange < baseData.minThreshold ? 'Low Stock' : 'Healthy',
        updatedBy: "Admin Portal",
        createdBy: "System",
        isOffline: true // Yellow row trigger sirf offline k liye
      };

      const existingOffline = JSON.parse(localStorage.getItem(`offline_inventory_${projectId}`) || '[]');
      existingOffline.push(offlinePayload);
      localStorage.setItem(`offline_inventory_${projectId}`, JSON.stringify(existingOffline));

      if (typeof setInventory === 'function') {
        setInventory(prevItems => [offlinePayload, ...prevItems]);
      }

      alert("🚨 Internet disconnected! Item locally save ho gaya hai.");
      setShowInventoryModal(false);
      return;
    }

    // 3. 🌐 AGAR INTERNET HAI (ONLINE MODE - NO FAKE IDS)
    try {
      // Online submit me sirf fresh pure fields bhej rahe hain taake backend reject na kare
      const res = await axiosReal.post('https://api.mfrbuild.com/api/inventory/update', baseData, config);

      if (res.data.success || res.status === 200) {
        // Clean up: Jab online successful submit ho jaye, to purani pending list ko clear kar dein
        localStorage.removeItem(`offline_inventory_${projectId}`);

        setShowInventoryModal(false);
        if (typeof fetchConsoleCoreData === 'function') {
          fetchConsoleCoreData(); // Fresh live data pull karein
        }
      }
    } catch (err) {
      if (!navigator.onLine || err.message === "Network Error") {
        // Fallback to offline if network drops mid-request
        const fallbackPayload = {
          ...baseData,
          _id: `offline_inv_${Date.now()}`,
          currentLevelStock: baseData.quantityChange,
          minSafetyLimit: baseData.minThreshold,
          isOffline: true
        };
        const existingOffline = JSON.parse(localStorage.getItem(`offline_inventory_${projectId}`) || '[]');
        existingOffline.push(fallbackPayload);
        localStorage.setItem(`offline_inventory_${projectId}`, JSON.stringify(existingOffline));

        if (typeof setInventory === 'function') setInventory(prevItems => [fallbackPayload, ...prevItems]);
        setShowInventoryModal(false);
      } else {
        console.error("Inventory entry fail:", err.message || err);
        alert(`Error: ${err.response?.data?.message || err.message}`);
      }
    }
  };

  const handleInventoryDelete = async (itemId) => {
    if (!window.confirm("🚨 Kya aap sach mein delete karna chahte hain?")) return;

    // 1. AGAR OFFLINE HAI
    if (!navigator.onLine) {
      // Agar yeh item abhi abhi offline create hua tha (database tak gaya hi nahi), to usko offline list se direct uda dein
      const existingOfflineInv = JSON.parse(localStorage.getItem(`offline_inventory_${projectId}`) || '[]');
      const filteredOffline = existingOfflineInv.filter(item => item._id !== itemId);
      localStorage.setItem(`offline_inventory_${projectId}`, JSON.stringify(filteredOffline));

      // Agar yeh purana item tha jo DB mein hai, to iski ID delete queue mein daal dein
      if (!itemId.startsWith('offline_inv_')) {
        const deleteQueue = JSON.parse(localStorage.getItem(`offline_inventory_deletes_${projectId}`) || '[]');
        deleteQueue.push(itemId);
        localStorage.setItem(`offline_inventory_deletes_${projectId}`, JSON.stringify(deleteQueue));
      }

      alert("Item offline delete kar diya gaya hai! Net aane par server se bhi delete ho jayega.");

      // UI state saaf karne ke liye local reload ya state filter lagayein agar zarurat ho
      // e.g., setInventoryItems(inventoryItems.filter(item => item._id !== itemId));

      return;
    }

    // 2. AGAR ONLINE HAI (Aapka Asal Code)
    const token = localStorage.getItem('token');
    const config = { headers: { 'x-auth-token': token } };
    try {
      const res = await axiosReal.delete(`https://api.mfrbuild.com/api/inventory/${itemId}`, config);
      if (res.data.success || res.status === 200) {
        fetchConsoleCoreData();
      }
    } catch (err) {
      console.error("Error deleting material asset:", err.message || err);
    }
  };

  // 📥 1. SUBMIT NEW INITIAL BLUEPRINT ENTRY (PORT 5000 CORRECTION)
  const handleDocumentSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("Session expired! Please login again.");
        return;
      }
      const targetProjectId = projectId || documentData.projectId || documentData.project;
      const response = await fetch('https://api.mfrbuild.com/api/documents', {
        method: 'POST',
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          projectId: targetProjectId,
          project: targetProjectId,
          ...documentData
        })
      });
      const result = await response.json();
      if (result.success || response.status === 201 || response.status === 200) {
        setShowDocumentModal(false);
        setDocumentData({ title: '', category: 'Architectural', fileType: 'PDF', fileUrl: '' });
        if (typeof fetchUbaidDocumentsOnly === 'function') {
          fetchUbaidDocumentsOnly();
        } else {
          window.location.reload();
        }
      } else {
        alert("Upload failed: " + (result.message || result.error || "Unknown server error"));
      }
    } catch (error) {
      console.error("Upload handler runtime error:", error.message || error);
      alert("Network Error: Could not connect to the server.");
    }
  };

  // 🔄 2. SUBMIT NEW VERSION FOR AN ASSET (PORT 5000 CORRECTION)
  const handleRevisionSubmit = async (e) => {
    e.preventDefault();
    if (!revisionUrl) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://api.mfrbuild.com/api/documents/revision/${selectedDocForRevision}`, {
        method: 'PATCH',
        headers: {
          'x-auth-token': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fileUrl: revisionUrl })
      });

      const result = await response.json();
      if (result.success) {
        setShowRevisionModal(false);
        setRevisionUrl('');
        setSelectedDocForRevision(null);
        if (typeof fetchUbaidDocumentsOnly === 'function') {
          fetchUbaidDocumentsOnly();
        }
      }
    } catch (error) {
      console.error("Revision submit error:", error.message || error);
    }
  };

  // 🗑️ 3. PERMANENT DELETE BLUEPRINT LOGIC (PORT 5000 CORRECTION)
  const handleDocumentDelete = async (docId) => {
    if (!window.confirm("Kya aap waqai is drawing ko database se delete karna chahte hain?")) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`https://api.mfrbuild.com/api/documents/${docId}`, {
        method: 'DELETE',
        headers: {
          'x-auth-token': token
        }
      });

      const result = await response.json();
      if (result.success) {
        fetchUbaidDocumentsOnly();
      }
    } catch (error) {
      console.error("Delete call crashed:", error.message || error);
    }
  };

  const handleLocalFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingFile(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'mfr_build');

    const CLOUD_NAME = "dgvt3atnc";
    const cloudUrl = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/auto/upload`;
    try {
      const res = await axios.post(cloudUrl, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data.secure_url) {
        setDocumentData(prev => ({
          ...prev,
          title: prev.title || file.name.split('.')[0],
          fileUrl: res.data.secure_url
        }));
      }
    } catch (err) {
      console.error("Cloudinary upload fault:", err.message || err);
    } finally {
      setUploadingFile(false);
    }
  };

  // 📥 3. SUBMIT NEW SITE MATERIAL REQUISITION
  const handleReqSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        projectId,
        estimatedBudget: reqForm.estimatedBudget,
        items: [{ materialName: reqForm.materialName, quantity: reqForm.quantity, unit: reqForm.unit }]
      };
      const res = await axiosReal.post('https://api.mfrbuild.com/api/procurement/requisition', payload, {
        headers: { 'x-auth-token': token }
      });
      if (res.data.success) {
        setShowReqModal(false);
        setReqForm({ materialName: '', quantity: 0, unit: 'Bags', estimatedBudget: 0 });
        fetchRequisitions();
      }
    } catch (err) {
      alert("Error creating requisition: " + err.message);
    }
  };

  // 💰 4. SUBMIT VENDOR QUOTATION (RFP)
  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        requisitionId: selectedReqId,
        vendorName: quoteForm.vendorName,
        vendorPhone: quoteForm.vendorPhone,
        deliveryLeadTimeDays: quoteForm.deliveryLeadTimeDays,
        quotedRates: [{ materialName: 'Requested Asset', ratePerUnit: Number(quoteForm.ratePerUnit) }]
      };
      const res = await axiosReal.post('https://api.mfrbuild.com/api/procurement/rfq', payload, {
        headers: { 'x-auth-token': token }
      });
      if (res.data.success) {
        setShowQuoteModal(false);
        setQuoteForm({ vendorName: '', vendorPhone: '', ratePerUnit: 0, deliveryLeadTimeDays: 3 });
        fetchMatrix(selectedReqId);
      }
    } catch (err) {
      console.error("Vendor quotation engine error:", err.message || err);
    }
  };

  // 📄 5. CONVERT WINNER RFQ TO OFFICIAL PURCHASE ORDER
  const handleGeneratePO = async (rfqId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axiosReal.post('https://api.mfrbuild.com/api/procurement/po', {
        requisitionId: selectedReqId,
        rfqId
      }, { headers: { 'x-auth-token': token } });

      if (res.data.success) {
        setActiveProcureTab('po');
      }
    } catch (err) {
      console.error("PO generation error pipeline:", err.message || err);
    }
  };

  // ⚖️ 6. TRIGGER 3-WAY MATCHING AUDIT
  const handleAuditSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await axiosReal.patch(`https://api.mfrbuild.com/api/procurement/po/verify/${selectedPo._id}`, auditForm, {
        headers: { 'x-auth-token': token }
      });
      if (res.data.success) {
        setShowMatchModal(false);
      }
    } catch (err) {
      console.error("3-way audit matching validation error:", err.message || err);
    }
  };

  // =========================================================
  // 🚀 1. HIGH-RISK PERMIT SUBMIT HANDLER
  // =========================================================
  const handlePermitSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (!token) return alert("Session expired. Please login again.");

      let mappedPermitType = "Hot Work (Welding/Cutting)";
      if (permitForm?.permitType === "Height Work > 2 Meters") {
        mappedPermitType = "Height Work";
      } else if (permitForm?.permitType === "Confined Space Entry") {
        mappedPermitType = "Confined Space";
      } else if (permitForm?.permitType === "Electrical Shutdown Work") {
        mappedPermitType = "Electrical Work";
      }

      const payload = {
        projectId: projectId,
        permitType: mappedPermitType,
        requestedBy: permitForm?.requestedBy || "Site Engineer",
        validTill: permitForm?.validTill || new Date().toISOString(),
        safetyChecklistPassed: permitForm?.safetyChecklistPassed === true
      };

      const config = { headers: { 'x-auth-token': token } };
      const res = await axiosReal.post('https://api.mfrbuild.com/api/safety/permit/request', payload, config);

      if (res.data.success) {
        setShowPermitModal(false);
        if (typeof fetchSafetyDashboard === 'function') fetchSafetyDashboard();
      }
    } catch (err) {
      console.error("Permit Submission Error:", err.message || err);
      alert("Permit Request Error: " + (err.response?.data?.error || err.message));
    }
  };

  // =========================================================
  // 🚨 2. EMERGENCY INCIDENT REPORT HANDLER
  // =========================================================
  const handleIncidentSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (!token) return alert("Session expired. Please login again.");

      // Strict Schema Enum Mapping Fix
      let mappedType = "Minor Injury";
      if (incidentForm?.incidentType === "Near-Miss") mappedType = "Near-Miss (Baal Baal Bachna)";
      if (incidentForm?.incidentType === "First-Aid Case") mappedType = "Minor Injury";
      if (incidentForm?.incidentType === "Major Injury") mappedType = "Major Incident";
      if (incidentForm?.incidentType === "Asset Damage") mappedType = "Property Damage";

      const payload = {
        projectId: projectId,
        title: incidentForm.title,
        incidentType: mappedType,
        location: incidentForm.location,
        description: incidentForm.description,
        actionTaken: incidentForm.actionTaken || "First Aid Administered"
      };

      const config = { headers: { 'x-auth-token': token } };
      const res = await axiosReal.post('https://api.mfrbuild.com/api/safety/incident/report', payload, config);

      if (res.data.success) {
        setShowIncidentModal(false);
        if (typeof fetchSafetyDashboard === 'function') fetchSafetyDashboard();
      }
    } catch (err) {
      console.error("Incident Log Error:", err.message || err);
      alert("Incident Submission Failure: " + (err.response?.data?.error || err.message));
    }
  };

  // =========================================================
  // 💰 3. SUBCONTRACTOR FINE / CHALLAN HANDLER
  // =========================================================
  const handleFineSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (!token) return alert("Session expired. Please login again.");

      const payload = {
        projectId: projectId,
        subcontractorName: fineForm.subcontractorName,
        violationReason: fineForm.violationReason,
        fineAmount: Number(fineForm.fineAmount)
      };

      const config = { headers: { 'x-auth-token': token } };
      const res = await axiosReal.post('https://api.mfrbuild.com/api/safety/fine/issue', payload, config);

      if (res.data.success) {
        setShowFineModal(false);
        if (typeof fetchSafetyDashboard === 'function') fetchSafetyDashboard();
      }
    } catch (err) {
      console.error("Fine Deduction System Error:", err.message || err);
      alert("Fine Error: " + (err.response?.data?.error || err.message));
    }
  };

  const handlePermitStatusUpdate = async (permitId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return alert("Session expired. Please login again.");

      await axiosReal.patch(`https://api.mfrbuild.com/api/safety/permit/review/${projectId}/${permitId}`, {
        status: newStatus
      }, { headers: { 'x-auth-token': token } });

      if (typeof fetchSafetyDashboard === 'function') fetchSafetyDashboard();
    } catch (err) {
      console.error("Permit Status Update Error:", err.message || err);
      alert("Failed to update permit status: " + (err.response?.data?.message || err.message));
    }
  };

  // --- ⚙️ ASSET OPERATIONS CORE HANDLERS ---
  const handleCreateAssetSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      const res = await axiosReal.post('https://api.mfrbuild.com/api/v1/assets', assetForm, config);
      setAssets([res.data?.data || res.data, ...assets]);
      setShowAssetModal(false);
    } catch (err) {
      const mockAsset = { _id: 'mock_ast_' + Math.random().toString(36).substr(2, 5), ...assetForm, status: 'Idle', totalHoursOperated: 0, gpsLocation: { latitude: 31.52, longitude: 74.35 }, geofence: { isViolated: false } };
      setAssets([mockAsset, ...assets]);
      setShowAssetModal(false);
    }
  };

  const handleShiftLogSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      const res = await axiosReal.put(`https://api.mfrbuild.com/api/v1/logs/${shiftLogForm.assetId}/end`, shiftLogForm, config);
      if (res.data?.maintenanceAlert) {
        alert("⚠️ CRITICAL ALERT: This asset has crossed runtime parameter matrix! Service due immediately.");
      }
      setShowLogShiftModal(false);
      fetchProjectAssets();
    } catch (err) {
      setAssets(assets.map(ast => ast._id === shiftLogForm.assetId ? { ...ast, totalHoursOperated: ast.totalHoursOperated + Number(shiftLogForm.hoursOperated), status: 'Idle' } : ast));
      setShowLogShiftModal(false);
    }
  };

  const handleMaintenanceSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      await axiosReal.post(`https://api.mfrbuild.com/api/v1/maintenance/ticket`, maintenanceForm, config);
      setShowMaintenanceModal(false);
      fetchProjectAssets();
    } catch (err) {
      setAssets(assets.map(ast => ast._id === maintenanceForm.assetId ? { ...ast, status: maintenanceForm.type === 'Corrective/Breakdown' ? 'Breakdown' : 'Under Maintenance' } : ast));
      setShowMaintenanceModal(false);
    }
  };

  // --- 🏢 BIM OPERATIONS CORE HANDLERS ---
  const handleBimModelSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (!token) return alert("Session expired. Please login again.");

      if (!bimModelForm.modelFile) {
        return alert("Please select a 3D Mesh file (.ifc, .gltf, .glb) to deploy.");
      }

      const formData = new FormData();
      formData.append("projectId", projectId);
      formData.append("title", bimModelForm.title || "Untitled Revision");
      formData.append("fileSize", bimModelForm.fileSize || "0 MB");
      formData.append("modelFile", bimModelForm.modelFile);

      const config = {
        headers: {
          'x-auth-token': token,
          'Content-Type': 'multipart/form-data'
        }
      };

      const res = await axiosReal.post('https://api.mfrbuild.com/api/projects/bim-model/upload', formData, config);

      if (res.data.success) {
        setShowModelUploadModal(false);
        if (typeof fetchBimCoreData === 'function') {
          fetchBimCoreData();
        }
      }
    } catch (err) {
      console.error("BIM Model Deployment Error:", err.message || err);
      alert("Deployment Failure: " + (err.response?.data?.message || err.message));
    }
  };

  const handleClashTicketSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      const payload = {
        projectId,
        modelId: activeBimModel?._id,
        title: clashTicketForm.title,
        description: clashTicketForm.description,
        clashType: clashTicketForm.clashType,
        priority: clashTicketForm.priority,
        assignedTo: clashTicketForm.assignedTo,
        coordinates: {
          x: Number(clashTicketForm.coordinateX),
          y: Number(clashTicketForm.coordinateY),
          z: Number(clashTicketForm.coordinateZ)
        }
      };

      const res = await axiosReal.post('https://api.mfrbuild.com/api/v1/bim/clashes', payload, config);
      setBimClashes([res.data?.data, ...bimClashes]);
      setShowClashTicketModal(false);
    } catch (err) {
      const mockNewClash = {
        _id: 'mock_clash_' + Math.random().toString(36).substr(2, 4),
        title: clashTicketForm.title,
        description: clashTicketForm.description,
        clashType: clashTicketForm.clashType,
        priority: clashTicketForm.priority,
        status: 'Open',
        coordinates: { x: Number(clashTicketForm.coordinateX), y: Number(clashTicketForm.coordinateY), z: Number(clashTicketForm.coordinateZ) },
        assignedTo: allUsers.find(u => u._id === clashTicketForm.assignedTo) || { name: 'Assigned Lead' }
      };
      setBimClashes([mockNewClash, ...bimClashes]);
      setShowClashTicketModal(false);
    }
  };

  const handleResolveClash = async (clashId, note) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      await axiosReal.put(`https://api.mfrbuild.com/api/v1/bim/clashes/${clashId}/resolve`, { status: 'Resolved', resolutionNotes: note }, config);
      fetchBimCoreData();
    } catch (err) {
      setBimClashes(bimClashes.map(c => c._id === clashId ? { ...c, status: 'Resolved', resolutionNotes: note } : c));
    }
  };

  // eslint-disable-next-line
  const handleAssetFormInsertion = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const config = { headers: { 'x-auth-token': token } };

    const localTemporaryId = 'temp_' + Date.now().toString(36);
    const finalFormData = { ...assetForm, _id: localTemporaryId };

    if (navigator.onLine) {
      try {
        const res = await axiosReal.post('https://api.mfrbuild.com/api/v1/assets', assetForm, config);
        setAssets([res.data?.data, ...assets]);
      } catch (err) {
        alert("Server payload error: " + err.message);
      }
    } else {
      try {
        await offlineDB.outboxQueue.add({
          url: 'https://api.mfrbuild.com/api/v1/assets',
          method: 'POST',
          data: assetForm,
          timestamp: Date.now()
        });

        await offlineDB.cachedAssets.add(finalFormData);
        setAssets([finalFormData, ...assets]);
        setShowAssetModal(false);
        alert("⚠️ Device Network Offline: Form transaction saved securely inside device local storage vault!");
      } catch (dbErr) {
        console.error("Local database injection breach:", dbErr.message);
      }
    }
  };

  // 1. GPS Ping Handler (Fixes 'setSelectedAssetTelemetry' is not defined)
  const setSelectedAssetTelemetry = (asset) => {
    if (window.confirm && typeof t === 'function') {
      window.confirm(`${t('alertGpsPingInitiated')}\nMachine: ${asset.name}\nCoords: ${asset.gpsLocation?.latitude}, ${asset.gpsLocation?.longitude}`);
    }
  };

  // 2. BIM Map Pin Click Handler (Fixes 'handleSelectClashMarker' is not defined)
  const handleSelectClashMarker = (clash) => {
    if (window.confirm && typeof t === 'function') {
      window.confirm(`📍 ${t('spatialInterferenceTitle')}\nIssue: ${clash.title}\nCoordinates: X:${clash.coordinates?.x}, Y:${clash.coordinates?.y}`);
    }
  };

  // 3. Action Ticket Resolution Flow (Fixes 'triggerResolutionFlow' is not defined)
  const triggerResolutionFlow = (clashId) => {
    const note = prompt(typeof t === 'function' ? t('promptResolutionNotes') : "Enter resolution notes:");
    if (note && typeof handleResolveClash === 'function') {
      handleResolveClash(clashId, note);
    }
  };

  // ✨ GLOBAL AI TEXT POLISHER HANDLER
  const handleAITextPolish = async (currentValue, context, updateStateCallback) => {
    if (!currentValue || currentValue.trim() === "") {
      alert(`Pehle ${context} field mein kuch likhein ya bol kar input dein!`);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const res = await axiosReal.post('https://api.mfrbuild.com/api/ai/polish-text',
        { text: currentValue, context: context },
        { headers: { 'x-auth-token': token } }
      );

      if (res.data.success) {
        updateStateCallback(res.data.polishedText);
      }
    } catch (err) {
      console.error("AI Dynamic Polish Error:", err.message || err);
      alert("AI engine is offline or busy.");
    }
  };

  // Floating AI ko handle karne ka function
  const handleFloatingAISubmit = async (e) => {
    e.preventDefault();
    if (!aiQuery.trim()) return;

    setAiLoading(true);
    setAiResponse('');
    try {
      const token = localStorage.getItem('token');
      const res = await axiosReal.post('https://api.mfrbuild.com/api/ai/polish-text',
        { text: aiQuery, context: "General Site Assistant Chatbot Help" },
        { headers: { 'x-auth-token': token } }
      );

      if (res.data.success) {
        setAiResponse(res.data.polishedText);
      }
    } catch (err) {
      console.error("Floating AI Error:", err.message || err);
      setAiResponse("Sorry, AI assistant is busy right now.");
    }
    setAiLoading(false);
  };

  // ====== CALCULATIONS ======
  let currentProjectProgress = 0;
  let totalTasksCount = 0;
  let pendingTasksCount = 0;
  let completedPhasesCount = 0;

  if (projectTasks && Array.isArray(projectTasks) && projectTasks.length > 0) {
    totalTasksCount = projectTasks.length;
    // eslint-disable-next-line
    pendingTasksCount = projectTasks.filter(t => t.status !== 'Completed').length;
    // eslint-disable-next-line
    completedPhasesCount = projectTasks.filter(t => t.status === 'Completed').length;
    const totalProgressPoints = projectTasks.reduce((acc, task) => {
      if (task.status === 'Completed') return acc + 100;
      if (task.status === 'In Progress') return acc + 50;
      return acc + 0;
    }, 0);
    currentProjectProgress = Math.round(totalProgressPoints / totalTasksCount);
  }

  const isLaboure = currentUser?.role === 'Laboure';

  return (
    <> {/* 👈 Yeh empty wrapper lagayein taake adjacent elements wrap ho sakein */}
      <Container fluid="md" className="py-4 px-2 px-md-3" style={{ overflowX: 'hidden' }}>

        {/* 🟢 TELEMETRY BAR: Connected / Offline Status */}
        <div className={`p-2 mb-3 text-center text-white fw-bold shadow-sm transition-all rounded-3 animate__animated ${isOnline ? 'bg-success animate__slideInDown' : 'bg-danger animate__pulse animate__infinite'}`} style={{ fontSize: '13px', letterSpacing: '0.5px' }}>
          {isOnline ? (
            <span><i className="bi bi-wifi me-2"></i> {t('telemetryOnline', 'TELEMETRY ONLINE: Central MongoDB Data Cluster Connected')}</span>
          ) : (
            <span><i className="bi bi-wifi-off me-2"></i> {t('telemetryOffline', 'OFFLINE FALLBACK MODE ACTIVE: Operations Mapped To Device Memory Vault')}</span>
          )}
        </div>

        {/* 📋 HEADER SECTION: Clean Column on Mobile, Row on Desktop */}
        <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-start align-items-md-center gap-3 mb-4 border-bottom pb-3">

          {/* Left Side: Back button & Project Info */}
          <div className="text-start w-100 w-md-auto">
            {/* 🔄 BACK TO DASHBOARD */}
            <Button variant="outline-secondary" size="sm" className="mb-2 rounded-3" onClick={() => navigate('/')}>
              &larr; {t('backToDashboard', 'Back to Dashboard')}
            </Button>
            <h2 className="fw-bold text-dark m-0" style={{ fontSize: 'calc(1.4rem + 0.6vw)' }}>{targetProjectMeta?.name}</h2>
            <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.9rem' }}>
              <i className="bi bi-geo-alt-fill text-primary me-1"></i> {t('locationLabel', 'Location')}: {targetProjectMeta?.location}
            </p>
          </div>

          {/* Right Side: Action Controls (Responsive Wrap) - ONLY FOR ADMIN */}
          <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 w-100 w-md-auto">

            {/* 🔒 STRICT ADMIN ONLY: SUBSCRIPTION BUTTON */}
            {userRole === 'Admin' && (
              <Button
                variant="outline-danger"
                size="sm"
                className="rounded-3 px-3 fw-bold d-flex align-items-center justify-content-center gap-1 w-100 w-sm-auto"
                style={{ height: '42px', borderWidth: '2px', whiteSpace: 'nowrap' }}
                onClick={() => setShowSubscriptionModal(true)}
              >
                💼 {t('subscriptionBtn', 'Billing & Subscription')}
              </Button>
            )}

            {/* 🛡️ ADMIN ONLY: QUICK ASSIGNMENT FIELD */}
            {/* 🛡️ ADMIN ONLY: QUICK ASSIGNMENT FIELD */}
            {userRole === 'Admin' && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (selectedUserId === "INVITE_NEW") {
                    if (typeof setShowInviteModal === 'function') setShowInviteModal(true);
                    if (typeof setSelectedUserId === 'function') setSelectedUserId("");
                  } else if (selectedUserId === "VIEW_LIST") {
                    if (typeof fetchInvitedMembers === 'function') fetchInvitedMembers();
                    if (typeof setShowListModal === 'function') setShowListModal(true);
                    if (typeof setSelectedUserId === 'function') setSelectedUserId("");
                  } else {
                    if (typeof handleAssignUserSubmit === 'function') handleAssignUserSubmit(e);
                  }
                }}
                className="d-flex align-items-center bg-light p-2 rounded-3 border w-100 w-sm-auto"
                style={{ height: '42px' }}
              >
                <select
                  className="form-select form-select-sm border-0 bg-transparent me-2 fw-semibold text-dark flex-grow-1"
                  style={{ minWidth: '160px', maxWidth: '100%', boxShadow: 'none' }}
                  value={selectedUserId || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "INVITE_NEW") {
                      if (typeof setShowInviteModal === 'function') setShowInviteModal(true);
                      if (typeof setSelectedUserId === 'function') setSelectedUserId("");
                    } else if (val === "VIEW_LIST") {
                      if (typeof fetchInvitedMembers === 'function') fetchInvitedMembers();
                      if (typeof setShowListModal === 'function') setShowListModal(true);
                      if (typeof setSelectedUserId === 'function') setSelectedUserId("");
                    } else {
                      if (typeof setSelectedUserId === 'function') setSelectedUserId(val);
                    }
                  }}
                >
                  <option value="">➕ {t('assignStaffLabel', 'Assign Staff/Labour')}</option>
                  <option value="INVITE_NEW" style={{ fontWeight: 'bold', color: '#0d6efd' }}>
                    🚀 {t('inviteNewTeamMember', 'Invite New Team Member (Bulk Setup)')}
                  </option>
                  <option value="VIEW_LIST" style={{ fontWeight: 'bold', color: '#198754' }}>
                    📋 {t('viewInvitedListPortal', 'View Invited Members List Portal')}
                  </option>
                  <option disabled>────────────────────</option>

                  {/* 🟢 BINA STATE KE DIRECT INLINE RENDERING */}
                  {allUsers && allUsers
                    .filter(user => user && user.role !== 'Admin')
                    .map(u => (
                      <option key={u._id} value={u._id}>
                        {u.name} ({t(u.role ? u.role.toLowerCase() : '', u.role || '')})
                      </option>
                    ))
                  }
                </select>

                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  className="rounded-2 px-3 fw-bold"
                  disabled={(typeof assignLoading !== 'undefined' && assignLoading) || !selectedUserId}
                >
                  {(typeof assignLoading !== 'undefined' && assignLoading) ? '...' : t('assignBtn', 'Assign')}
                </Button>
              </form>
            )}
          </div>
        </div>

        {/* 💰 DYNAMIC BUDGET OVERRUN ALERT BOX */}
        {(() => {
          const matTotal = expenses?.filter(e => e.category === 'Materials').reduce((a, c) => a + Number(c.amount), 0) || 0;
          const laborTotal = expenses?.filter(e => e.category === 'Labor Wages').reduce((a, c) => a + Number(c.amount), 0) || 0;
          const otherTotal = expenses?.filter(e => e.category !== 'Materials' && e.category !== 'Labor Wages').reduce((a, c) => a + Number(c.amount), 0) || 0;
          const grandTotal = matTotal + laborTotal + otherTotal || 0;
          const projectBudgetLimit = Number(budgetLimit) || 500000;
          const isOverBudget = grandTotal > projectBudgetLimit;

          return isOverBudget ? (
            <div className="alert alert-danger d-flex align-items-start my-3 p-3 animate__animated animate__shakeX rounded-3" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2 fs-5 mt-0.5"></i>
              <div className="small text-start">
                <strong>⚠️ {t('budgetWarningTitle', 'Budget Overrun Warning!')}</strong> {t('budgetWarningText1', 'Your total expense has exceeded the set limit')}
                <span className="badge bg-dark mx-1">Rs. {projectBudgetLimit.toLocaleString('en-IN')}</span> {t('budgetWarningText2', 'se')}
                <span className="fw-bold text-decoration-underline mx-1">Rs. {(grandTotal - projectBudgetLimit).toLocaleString('en-IN')}</span> {t('budgetWarningText3', 'upar nikal chuka hai!')}
              </div>
            </div>
          ) : null;
        })()}
        {/* CHARTS & METRICS ROW */}
        <Row className="mb-4 g-3 text-start">
          {/* Card 1: Cost Ingestion Breakdown Chart */}
          <Col xs={12} lg={6}>
            <Card className="shadow-sm border-0 rounded-3 h-100 p-2 p-sm-3 bg-white">
              <Card.Body>
                <h5 className="fw-bold text-dark mb-3" style={{ fontSize: '1.1rem' }}>{t('costStreamsTitle', 'Cost Streams Visual Breakdown')}</h5>
                <div className="d-flex align-items-center justify-content-around flex-wrap gap-3">

                  <div style={{ position: 'relative', width: '130px', height: '130px', flexShrink: 0 }}>
                    <svg viewBox="0 0 36 36" className="w-100 h-100">
                      <circle cx="18" cy="18" r="15.915" fill="none" stroke="#eaeaea" strokeWidth="3.8" />
                      {(() => {
                        const matTotal = expenses?.filter(e => e.category === 'Materials').reduce((a, c) => a + Number(c.amount), 0) || 0;
                        const laborTotal = expenses?.filter(e => e.category === 'Labor Wages').reduce((a, c) => a + Number(c.amount), 0) || 0;
                        const otherTotal = expenses?.filter(e => e.category !== 'Materials' && e.category !== 'Labor Wages').reduce((a, c) => a + Number(c.amount), 0) || 0;
                        const grandTotal = matTotal + laborTotal + otherTotal || 0;

                        const matPct = grandTotal > 0 ? (matTotal / grandTotal) * 100 : 0;
                        const laborPct = grandTotal > 0 ? (laborTotal / grandTotal) * 100 : 0;
                        const otherPct = grandTotal > 0 ? (otherTotal / grandTotal) * 100 : 0;

                        // ✅ same budgetLimit jo Alert box mein use ho raha hai
                        const projectBudgetLimit = Number(budgetLimit) || 500000;
                        const isOverBudget = grandTotal > projectBudgetLimit;

                        return (
                          <>
                            {/* Materials */}
                            <circle
                              cx="18" cy="18" r="15.915" fill="none"
                              stroke={isOverBudget ? "#dc3545" : "#0d6efd"}
                              strokeWidth="3.8"
                              strokeDasharray={`${matPct} ${100 - matPct}`}
                              strokeDashoffset="25"
                            />
                            {/* Labor Wages */}
                            <circle
                              cx="18" cy="18" r="15.915" fill="none"
                              stroke="#198754"
                              strokeWidth="3.8"
                              strokeDasharray={`${laborPct} ${100 - laborPct}`}
                              strokeDashoffset={25 - matPct}
                            />
                            {/* ✅ Logistics & Rentals — missing tha, add kiya */}
                            <circle
                              cx="18" cy="18" r="15.915" fill="none"
                              stroke="#6c757d"
                              strokeWidth="3.8"
                              strokeDasharray={`${otherPct} ${100 - otherPct}`}
                              strokeDashoffset={25 - matPct - laborPct}
                            />
                          </>
                        );
                      })()}
                    </svg>
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} className="text-center">
                      <span className="fw-bold style-label text-muted" style={{ fontSize: '0.75rem' }}>{t('expensesCircleLabel', 'Expenses')}</span>
                    </div>
                  </div>
                  <div className="flex-grow-1" style={{ minWidth: '200px' }}>
                    <div className="d-flex align-items-center mb-2">
                      <span className="badge bg-danger p-1.5 me-2" style={{ width: '10px', height: '10px', borderRadius: '50%' }}></span>
                      <span className="small fw-semibold text-muted" style={{ fontSize: '0.85rem' }}>{t('materials', 'Materials')}: {expenses?.filter(e => e.category === 'Materials').reduce((a, c) => a + Number(c.amount), 0).toLocaleString() || 0} PKR</span>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <span className="badge bg-success p-1.5 me-2" style={{ width: '10px', height: '10px', borderRadius: '50%' }}></span>
                      <span className="small fw-semibold text-muted" style={{ fontSize: '0.85rem' }}>{t('laborWages', 'Labor Wages')}: {expenses?.filter(e => e.category === 'Labor Wages').reduce((a, c) => a + Number(c.amount), 0).toLocaleString() || 0} PKR</span>
                    </div>
                    <div className="d-flex align-items-center">
                      <span className="badge bg-secondary p-1.5 me-2" style={{ width: '10px', height: '10px', borderRadius: '50%' }}></span>
                      <span className="small fw-semibold text-muted" style={{ fontSize: '0.85rem' }}>{t('logisticsRentals', 'Logistics & Rentals')}: {expenses?.filter(e => e.category !== 'Materials' && e.category !== 'Labor Wages').reduce((a, c) => a + Number(c.amount), 0).toLocaleString() || 0} PKR</span>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Card 2: Production Metrics Tracker */}
          <Col xs={12} lg={6}>
            <Card className="shadow-sm border-0 rounded-3 h-100 p-2 p-sm-3 bg-white">
              <Card.Body className="d-flex flex-column justify-content-between">
                <div>
                  <h5 className="fw-bold text-dark mb-1" style={{ fontSize: '1.1rem' }}>{t('productionMetricsTitle', 'Production Metrics Tracker')}</h5>
                  <p className="text-muted small mb-3">{t('productionMetricsSub', 'Live dynamic tasks telemetry')}</p>
                </div>

                {(() => {
                  const safeTasks = Array.isArray(projectTasks) ? projectTasks : [];
                  const totalTasks = safeTasks.length;
                  const completedTasks = safeTasks.filter(t => t && (t.status === 'Completed' || t.status === 'Done')).length;
                  const pendingTasks = totalTasks - completedTasks;

                  return (
                    <div>
                      <div className="mt-2 text-start">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <small className="fw-bold text-secondary" style={{ fontSize: '0.8rem' }}>{t('siteOptimizationRate', 'Site Optimization Rate')}</small>
                          <span className="fw-bold text-primary fs-5">{currentProjectProgress}%</span>
                        </div>
                        <div className="progress mb-3" style={{ height: '12px' }}>
                          <div className="progress-bar progress-bar-striped progress-bar-animated bg-primary" role="progressbar" style={{ width: `${currentProjectProgress}%` }} aria-valuenow={currentProjectProgress} aria-valuemin="0" aria-valuemax="100"></div>
                        </div>
                      </div>

                      <Row className="text-center g-2 mt-1">
                        <Col xs={4}>
                          <div className="bg-light p-2 rounded-2">
                            <div className="text-muted small text-uppercase" style={{ fontSize: '0.7rem' }}>{t('total', 'Total')}</div>
                            <div className="fw-bold text-dark fs-6">{totalTasks}</div>
                          </div>
                        </Col>
                        <Col xs={4}>
                          <div className="bg-light p-2 rounded-2">
                            <div className="text-muted small text-uppercase" style={{ fontSize: '0.7rem' }}>{t('pending', 'Pending')}</div>
                            <div className="fw-bold text-warning fs-6">{pendingTasks}</div>
                          </div>
                        </Col>
                        <Col xs={4}>
                          <div className="bg-light p-2 rounded-2">
                            <div className="text-muted small text-uppercase" style={{ fontSize: '0.7rem' }}>{t('done', 'Done')}</div>
                            <div className="fw-bold text-success fs-6">{completedTasks}</div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  );
                })()}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* 📋 PILLS NAVIGATION BANNER: Scrollable on Mobile to protect screen edge limits */}
        <div className="w-100" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <TabNav
            variant="pills"
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-4 bg-light p-2 rounded-3 shadow-sm gap-2 d-flex flex-nowrap text-nowrap"
            style={{ minWidth: 'max-content' }}
          >
            {/* 📝 1. Tasks Pipeline (Sabko nazar aayega, including Labour) */}
            <TabNav.Item>
              <TabNav.Link eventKey="tasks" className="fw-semibold px-3 py-2">
                {t('tasksPipelineTab', 'Tasks Pipeline')}
              </TabNav.Link>
            </TabNav.Item>

            {/* 📝 2. Attendance Roster (Sabko nazar aayega, including Labour) */}
            <TabNav.Item>
              <TabNav.Link eventKey="attendance" className="fw-semibold px-3 py-2">
                {t('attendanceRosterTab', 'Attendance Roster')}
              </TabNav.Link>
            </TabNav.Item>

            {/* 📝 3. Daily Field Logs (Sabko nazar aayega, including Labour) */}
            <TabNav.Item>
              <TabNav.Link eventKey="logs" className="fw-semibold px-3 py-2">
                {t('dailyFieldLogsTab', 'Daily Field Logs')}
              </TabNav.Link>
            </TabNav.Item>

            {/* 🔒 4. Cost & Budget (Labour ko nazar nahi aayega) */}
            {userRole !== 'Labour' && (
              <TabNav.Item>
                <TabNav.Link eventKey="financials" className="fw-semibold px-3 py-2">
                  {t('costBudgetTab', 'Cost & Budget')}
                </TabNav.Link>
              </TabNav.Item>
            )}

            {/* 📝 5. Punch List (Sabko nazar aayega, including Labour) */}
            <TabNav.Item>
              <TabNav.Link eventKey="punchlist" className="fw-semibold px-3 py-2">
                {t('punchListTab', 'Punch List')}
              </TabNav.Link>
            </TabNav.Item>

            {/* 🔒 6. Material Inventory (Labour ko nazar nahi aayega) */}
            {userRole !== 'Labour' && (
              <TabNav.Item>
                <TabNav.Link eventKey="inventory" className="fw-semibold px-3 py-2">
                  {t('materialInventoryTab', 'Material Inventory')}
                </TabNav.Link>
              </TabNav.Item>
            )}

            {/* 📝 7. Site Documents (Sabko nazar aayega, including Labour) */}
            <TabNav.Item>
              <TabNav.Link eventKey="documents" className="fw-semibold px-3 py-2">
                {t('siteDocumentsTab', 'Site Documents')}
              </TabNav.Link>
            </TabNav.Item>

            {/* 🔒 8. Procurement & Vendors (Labour ko nazar nahi aayega) */}
            {userRole !== 'Labour' && (
              <TabNav.Item>
                <TabNav.Link eventKey="procurement" className="fw-semibold px-3 py-2">
                  {t('procurementTab', 'Procurement & Vendors')}
                </TabNav.Link>
              </TabNav.Item>
            )}

            {/* 🔒 9. Safety & HSE (Labour ko nazar nahi aayega) */}
            {userRole !== 'Labour' && (
              <TabNav.Item>
                <TabNav.Link eventKey="safety" className="fw-semibold px-3 py-2 bg-danger-subtle text-danger">
                  🛡️ {t('safetyHseTab', 'Safety & HSE')}
                </TabNav.Link>
              </TabNav.Item>
            )}

            {/* 🔒 10. Machinery & Asset Tracking (Labour ko nazar nahi aayega) */}
            {userRole !== 'Labour' && (
              <TabNav.Item>
                <TabNav.Link eventKey="assets" className="fw-semibold px-3 py-2 bg-primary-subtle text-primary">
                  ⚙️ {t('machineryAssetTab', 'Machinery & Asset Tracking')}
                </TabNav.Link>
              </TabNav.Item>
            )}

            {/* 🔒 11. BIM & 3D Clash Workspace (Labour ko nazar nahi aayega) */}
            {userRole !== 'Labour' && (
              <TabNav.Item>
                <TabNav.Link eventKey="bim" className="fw-semibold px-3 py-2 bg-dark-subtle text-dark">
                  🏢 {t('bimWorkspaceTab', 'BIM & 3D Clash Workspace')}
                </TabNav.Link>
              </TabNav.Item>
            )}
          </TabNav>
        </div>

        {/* DYNAMIC GRIDS CONTAINER LAYER */}
        <Card className="shadow-sm border-0 rounded-3 p-2 p-sm-4 bg-white text-start">

          {/* TAB 1: TASKS */}
          {activeTab === 'tasks' && (() => {
            return (
              <div className="animate__animated animate__fadeIn">
                {/* 📊 INTERACTIVE TASK QUICK SUMMARY COUNTERS */}
                <div className="row g-2 mb-4">
                  <div className="col-6 col-md-3">
                    <div className="p-3 h-100 bg-white border rounded shadow-sm text-center d-flex flex-column justify-content-center">
                      <span className="text-muted fw-bold d-block text-uppercase small" style={{ fontSize: '0.75rem' }}>{t('totalActions', 'Total Tasks')}</span>
                      <h3 className="fw-bold text-primary mb-0 mt-1">{projectTasks.length}</h3>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="p-3 h-100 bg-white border rounded shadow-sm text-center d-flex flex-column justify-content-center">
                      <span className="text-muted fw-bold d-block text-uppercase small" style={{ fontSize: '0.75rem' }}>{t('pendingTasks', 'Pending Tasks')}</span>
                      <h3 className="fw-bold text-warning mb-0 mt-1">
                        {projectTasks.filter(t => t.status !== 'Completed').length}
                      </h3>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="p-3 h-100 bg-white border rounded shadow-sm text-center d-flex flex-column justify-content-center">
                      <span className="text-muted fw-bold d-block text-uppercase small" style={{ fontSize: '0.75rem' }}>{t('completedPhases', 'Completed Phases')}</span>
                      <h3 className="fw-bold text-success mb-0 mt-1">
                        {projectTasks.filter(t => t.status === 'Completed').length}
                      </h3>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="p-3 h-100 bg-white border rounded shadow-sm text-center d-flex flex-column justify-content-center">
                      <span className="text-muted fw-bold d-block text-uppercase small" style={{ fontSize: '0.75rem' }}>{t('criticalOverdue', 'Critical Overdue')}</span>
                      <h3 className="fw-bold text-danger mb-0 mt-1">
                        {projectTasks.filter(t => t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'Completed').length}
                      </h3>
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3">
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <h4 className="fw-bold text-primary mb-0" style={{ fontSize: 'calc(1.1rem + 0.3vw)' }}>
                      {t('tasksPipeline', 'Tasks Pipeline')}
                    </h4>
                    <span className="badge bg-primary-subtle text-primary rounded-pill fw-bold px-2 py-1" style={{ fontSize: '0.75rem' }}>
                      {t('timelineActive', 'Timeline Active')}
                    </span>
                  </div>

                  {/* 🔒 ROLE SECURITY: Labour cannot add tasks. Client, Supervisor, SiteManager can add tasks. */}
                  {userRole !== 'Labour' && (
                    <Button
                      variant="primary"
                      size="sm"
                      className="fw-bold shadow-sm mt-2 mt-sm-0 px-3 py-2"
                      style={{
                        width: 'auto',
                        maxWidth: '220px',
                        minWidth: '120px',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={() => {
                        setEditingItemId(null);
                        setTaskData({
                          title: '', description: '', assignedTo: '',
                          roleAtAssignment: userRole || 'Supervisor', startDate: '', dueDate: '', isMilestone: false,
                          createdBy: userRole // Tracking who initiates the task payload
                        });
                        setShowAddTaskModal(true);
                      }}
                    >
                      <i className="bi bi-plus-circle-fill me-1"></i>
                      {t('addTask', 'Add Task')}
                    </Button>
                  )}
                </div>

                {/* TASKS PIPELINE TABLE */}
                <div className="table-responsive bg-white border rounded shadow-sm" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <Table hover className="align-middle mb-0 text-start" style={{ minWidth: '950px' }}>
                    <thead className="table-primary text-dark fw-bold">
                      <tr>
                        <th style={{ width: '30%' }}>{t('taskPhaseTitle', 'Task Title & Logs')}</th>
                        <th style={{ width: '18%' }}>{t('assignedPersonnel', 'Assigned Personnel')}</th>
                        <th style={{ width: '12%' }}>{t('fieldRole', 'Field Role')}</th>
                        <th style={{ width: '15%' }}>{t('targetTimeline', 'Target Timeline')}</th>
                        <th style={{ width: '15%' }}>{t('statusTracking', 'Status Tracking')}</th>
                        {userRole !== 'Labour' && <th style={{ width: '10%' }} className="text-center">{t('actions', 'Actions')}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {projectTasks.length === 0 ? (
                        <tr>
                          <td colSpan={userRole === 'Labour' ? "5" : "6"} className="text-center text-muted py-4">
                            {t('noDataStreams', 'No active tasks found in this timeline grid.')}
                          </td>
                        </tr>
                      ) : (
                        projectTasks.map((task, index) => {
                          const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'Completed';
                          const isCompleted = task.status === 'Completed';
                          const currentRole = task.roleAtAssignment || task.fieldRole || 'N/A';

                          return (
                            <tr key={task._id || task.id || `task-row-${index}`} className={isCompleted ? "table-light text-muted" : ""}>

                              {/* 1. Task Title & Audit Log Tracking Block */}
                              <td>
                                <div className="d-flex align-items-start gap-2 py-1">
                                  <img
                                    src="/assets/3596091.png"
                                    alt="task-icon"
                                    style={{ width: '22px', height: '22px', objectFit: 'contain', marginTop: '3px' }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                                    }}
                                  />
                                  <div style={{ display: 'none' }} className="text-primary fs-6 mt-1">
                                    <i className="bi bi-clipboard-check-fill"></i>
                                  </div>

                                  <div>
                                    <div className="fw-bold text-dark mb-0">
                                      {task.isMilestone && <i className="bi bi-star-fill text-warning me-1 small"></i>}
                                      {task.title || "Untitled Task"}
                                    </div>
                                    <p className="text-muted text-truncate mb-1" style={{ maxWidth: '280px', fontSize: '0.85rem' }}>
                                      {task.description || "No description logged."}
                                    </p>

                                    {/* 🛡️ SECURITY AUDIT FOOTPRINT: Tracking who managed this data */}
                                    <div className="d-flex flex-wrap gap-1 mt-1" style={{ fontSize: '0.7rem' }}>
                                      {task.createdBy && (
                                        <span className="badge bg-light text-secondary border">
                                          ➕ {t('addedBy', 'Added by')}: <strong className="text-dark">{task.createdBy.name || "Unknown"}</strong>
                                        </span>
                                      )}
                                      {task.updatedBy && (
                                        <span className="badge bg-light text-secondary border">
                                          📝 {t('editedBy', 'Edited by')}: <strong className="text-dark">{task.updatedBy}</strong>
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {/* 2. Assigned To */}
                              <td className="fw-semibold text-secondary">
                                <i className="bi bi-person text-secondary me-1"></i>
                                {task.assignedTo?.name || "Unassigned"}
                              </td>

                              {/* 3. Field Role Badge */}
                              <td>
                                <Badge
                                  bg={currentRole === 'SiteManager' ? 'dark' : currentRole === 'Supervisor' ? 'warning' : 'info'}
                                  className={`fw-bold px-2 py-1 ${currentRole === 'SiteManager' ? 'text-white' : 'text-dark'}`}
                                >
                                  {currentRole}
                                </Badge>
                              </td>

                              {/* 4. Due Date & Timeline Status */}
                              <td>
                                <div className="small mb-1" style={{ fontSize: '0.8rem' }}>
                                  <span className="text-muted">S: </span>
                                  {task.startDate ? new Date(task.startDate).toLocaleDateString('en-GB') : 'N/A'}
                                </div>
                                <div style={{ fontSize: '0.85rem' }}>
                                  <span className="text-muted">E: </span>
                                  <span className={isOverdue ? "text-danger fw-bold" : "text-dark fw-semibold"}>
                                    {task.dueDate ? new Date(task.dueDate).toLocaleDateString('en-GB') : 'No Date'}
                                  </span>
                                  {isOverdue && (
                                    <span className="badge bg-danger ms-1 animate__animated animate__flash animate__infinite" style={{ fontSize: '0.65rem' }}>
                                      ⚠️ {t('overdueLabel', 'OVERDUE')}
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* 5. Status Tracking Dropdown */}
                              <td>
                                <Dropdown onSelect={(status) => {
                                  // 🔒 SECURITY CHECK: Labour cannot modify statuses
                                  if (userRole === 'Labour') return;
                                  handleTaskStatusChange(task._id || task.id, status);
                                }}>
                                  <Dropdown.Toggle
                                    variant={isCompleted ? 'success' : task.status === 'In Progress' ? 'warning' : 'secondary'}
                                    size="sm"
                                    disabled={userRole === 'Labour'} // Labour is completely blocked from changing status
                                    className="fw-bold w-100 text-start d-flex justify-content-between align-items-center"
                                  >
                                    {task.status || 'To Do'}
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    <Dropdown.Item eventKey="To Do">{t('statusToDo', 'To Do')}</Dropdown.Item>
                                    <Dropdown.Item eventKey="In Progress">{t('statusInProgress', 'In Progress')}</Dropdown.Item>
                                    <Dropdown.Item eventKey="Completed">{t('statusCompleted', 'Completed')}</Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </td>

                              {/* 6. Actions Buttons (Strictly Protected for Admin & Mod Roles with Pending Approval Flagging) */}
                              {userRole !== 'Labour' && (
                                <td className="text-center">
                                  <div className="d-flex justify-content-center gap-1">

                                    {/* Edit Action Button */}
                                    <Button
                                      variant="outline-warning"
                                      size="sm"
                                      className="px-2 py-1"
                                      onClick={() => {
                                        // If non-admin changes data, append modified footprint log
                                        const updatedTask = { ...task, updatedBy: userRole };
                                        if (userRole !== 'Admin') {
                                          // Add non-admin notice banner logic inside click hook if needed
                                        }
                                        handleEditTaskClick(updatedTask);
                                      }}
                                    >
                                      <i className="bi bi-pencil-square"></i>
                                    </Button>

                                    {/* Delete Action Button */}
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      className="px-2 py-1"
                                      onClick={() => {
                                        if (userRole === 'Admin') {
                                          handleDeleteTask(task._id || task.id);
                                        } else {
                                          // Non-admins actions trigger alert system rather than absolute pipeline removal
                                          alert(t('adminApprovalRequired', 'Action Requested! Admin approval is required to permanently delete this task record.'));
                                        }
                                      }}
                                    >
                                      <i className="bi bi-trash-fill"></i>
                                    </Button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            );
          })()}

          {/* TAB 2: ATTENDANCE */}
          {activeTab === 'attendance' && (
            <div>
              {/* 🟢 TOP CONTROLS & SUB-TABS MATRIX */}
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-3 p-2 bg-white rounded shadow-sm border">
                <div>
                  <h4 className="fw-bold text-success mb-0" style={{ fontSize: 'calc(1.1rem + 0.3vw)' }}>{t('attendanceRoster', 'Attendance Roster')}</h4>
                  <small className="text-muted small d-block">{t('attendanceSubHeading', 'Real-time site management and ledger logs')}</small>
                </div>
                <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2 w-100 w-md-auto">

                  {/* Sub-Tab Navigation Toggle Control */}
                  <div className="btn-group p-1 bg-light rounded-2 w-100 w-sm-auto" role="group">
                    <button
                      type="button"
                      className="btn btn-sm btn-success fw-bold text-uppercase px-2 px-sm-3 rounded-2 me-2"
                      id="logs-sub-tab"
                      style={{
                        fontSize: '0.75rem',
                        width: 'auto',
                        minWidth: '140px',
                        maxWidth: '220px',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={() => {
                        document.getElementById('attendanceLogsSubView').classList.add('show', 'active');
                        document.getElementById('payrollMatrixSubView').classList.remove('show', 'active');
                        document.getElementById('logs-sub-tab').classList.add('btn-success');
                        document.getElementById('logs-sub-tab').classList.remove('btn-light', 'text-dark');
                        document.getElementById('payroll-sub-tab').classList.remove('btn-success');
                        document.getElementById('payroll-sub-tab').classList.add('btn-light', 'text-dark');
                      }}
                    >
                      {t('attendanceLogsBtn', 'Attendance Logs')}
                    </button>

                    {/* Payroll sheet access hidden from raw Labor accounts for masking privacy metrics */}
                    {userRole !== 'Labour' && (
                      <button
                        type="button"
                        className="btn btn-sm btn-light text-dark fw-bold text-uppercase px-2 px-sm-3 rounded-2"
                        id="payroll-sub-tab"
                        style={{
                          fontSize: '0.75rem',
                          width: 'auto',
                          minWidth: '140px',
                          maxWidth: '220px',
                          whiteSpace: 'nowrap'
                        }}
                        onClick={() => {
                          document.getElementById('payrollMatrixSubView').classList.add('show', 'active');
                          document.getElementById('attendanceLogsSubView').classList.remove('show', 'active');
                          document.getElementById('payroll-sub-tab').classList.add('btn-success');
                          document.getElementById('payroll-sub-tab').classList.remove('btn-light', 'text-dark');
                          document.getElementById('logs-sub-tab').classList.remove('btn-success');
                          document.getElementById('logs-sub-tab').classList.add('btn-light', 'text-dark');
                        }}
                      >
                        {t('financialPayrollSheetBtn', 'Payroll Sheet')}
                      </button>
                    )}
                  </div>

                  {/* 🔒 SECURITY: Only authorized field heads or workers can trigger the raw submission module. Clients can only see. */}
                  {(userRole === 'Admin' || userRole === 'SiteManager' || userRole === 'Supervisor' || userRole === 'Labour') && (
                    <Button
                      variant="success"
                      size="sm"
                      className="fw-bold px-3 py-2 py-sm-1 rounded-2"
                      style={{
                        width: 'auto',
                        maxWidth: '200px',
                        minWidth: '140px',
                        whiteSpace: 'nowrap'
                      }}
                      onClick={() => {
                        setEditingItemId(null);
                        // Setup default payload tracks for security logging
                        setAttendanceData?.({
                          date: new Date().toISOString().split('T')[0],
                          status: 'Present',
                          loggedByRole: userRole
                        });
                        setShowAddAttendanceModal(true);
                      }}
                    >
                      {userRole === 'Labour' ? `+ ${t('markMyAttendance', 'Mark My Attendance')}` : `+ ${t('logAttendance', 'Log Attendance')}`}
                    </Button>
                  )}
                </div>
              </div>

              <div className="tab-content">
                {/* 🟢 SUB-TAB 1: LIVE ATTENDANCE ROSTER LOGS */}
                <div className="tab-pane fade show active" id="attendanceLogsSubView" role="tabpanel">
                  <div className="table-responsive bg-white border rounded shadow-sm" style={{ WebkitOverflowScrolling: 'touch' }}>
                    <Table hover className="align-middle mb-0 text-center small" style={{ minWidth: '1000px' }}>
                      <thead className="table-success text-uppercase" style={{ fontSize: '0.8rem' }}>
                        <tr>
                          <th>{t('thDate', 'Date')}</th>
                          <th className="text-start">{t('thWorkerName', 'Worker Identity')}</th>
                          <th>{t('thRole', 'Role')}</th>
                          <th>{t('thStatus', 'Status')}</th>
                          <th>{t('thWageSetup', 'Wage Structure')}</th>
                          <th>{t('thDailyRate', 'Daily Rate')}</th>
                          <th>{t('thOutputVolume', 'Output Volume')}</th>
                          <th>{t('thKharchiAdvance', 'Kharchi / Advance')}</th>
                          {userRole !== 'Client' && <th>{t('thActions', 'Actions')}</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {attendanceRecords.length === 0 ? (
                          <tr>
                            <td colSpan={userRole === 'Client' ? "8" : "9"} className="text-muted py-4">
                              {t('noAttendanceRecords', 'No site attendance streams synchronized for this period.')}
                            </td>
                          </tr>
                        ) : (
                          attendanceRecords.map((rec, index) => (
                            <tr key={rec._id || `attendance-${index}`}>
                              <td className="fw-semibold text-secondary">
                                {rec.date ? new Date(rec.date).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')}
                              </td>
                              <td className="text-start ps-2">
                                <div className="fw-bold text-dark">
                                  {(() => {
                                    const nameStr = rec.workerName?.name || (typeof rec.workerName === 'string' ? rec.workerName : '');
                                    // Agar name khali hai ya uski length 24 characters (ID) hai, toh dash dikhao
                                    return !nameStr || nameStr.length === 24 ? "—" : nameStr;
                                  })()}
                                </div>
                                {/* Security Log Tracking: Shows who introduced or updated this entry */}
                                {rec.capturedBy && (
                                  <div style={{ fontSize: '0.65rem' }} className="text-muted">
                                    ✍️ {t('loggedBy', 'Logged by')}: <span className="text-success fw-bold">{rec.capturedBy}</span>
                                  </div>
                                )}
                              </td>
                              <td>
                                <Badge bg="secondary" className="px-2 py-1">
                                  {rec.role}
                                </Badge>
                              </td>
                              <td>
                                <Badge bg={rec.status === 'Present' ? 'success' : rec.status === 'Absent' ? 'danger' : 'warning'} className="px-2 py-1">
                                  {rec.status}
                                </Badge>
                              </td>
                              <td className="text-muted fw-bold">{rec.paymentType || 'Daily-Wage'}</td>
                              <td className="fw-bold text-dark">PKR {rec.dailyRate || 0}</td>
                              <td className="fw-semibold text-secondary">
                                {rec.paymentType === 'Piece-Rate' ? `${rec.workVolume} ${t('sqFt', 'Sq.Ft')}` : '-'}
                              </td>
                              <td className="fw-bold text-danger">
                                {rec.advanceTaken > 0 ? `PKR ${rec.advanceTaken}` : '0'}
                              </td>

                              {/* Action Block securely hidden away from non-operational accounts like Clients */}
                              {userRole !== 'Client' && (
                                <td className="text-center">
                                  <div className="d-flex justify-content-center gap-1">
                                    {/* Admin, Supervisor or SiteManagers can edit logs, Labour cannot change tracking list once submitted */}
                                    {(userRole === 'Admin' || userRole === 'SiteManager' || userRole === 'Supervisor') ? (
                                      <>
                                        <Button variant="outline-warning" size="sm" className="px-2 py-1" onClick={() => handleEditAttendanceClick(rec)}>
                                          <i className="bi bi-pencil-square"></i>
                                        </Button>
                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          className="px-2 py-1"
                                          onClick={() => {
                                            if (userRole === 'Admin') {
                                              handleDeleteAttendance(rec._id);
                                            } else {
                                              // Security Notification callback logic fallback replacement
                                            }
                                          }}
                                          disabled={userRole !== 'Admin'} // Restrict structural deletes only to Main Admin
                                        >
                                          <i className="bi bi-trash-fill"></i>
                                        </Button>
                                      </>
                                    ) : (
                                      <span className="text-muted small" style={{ fontSize: '0.7rem' }}>{t('lockedState', 'Immutable')}</span>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </div>

                {/* 🟢 SUB-TAB 2: INTEGRATED PAYROLL MATRIX */}
                {userRole !== 'Labour' && (
                  <div className="tab-pane fade" id="payrollMatrixSubView" role="tabpanel">
                    <div className="table-responsive bg-white border rounded shadow-sm">
                      <Table hover className="align-middle mb-0 text-center small" style={{ minWidth: '850px' }}>
                        <thead className="table-dark text-uppercase" style={{ fontSize: '0.8rem' }}>
                          <tr>
                            <th className="text-start ps-3">{t('thWorkerIdentityName', 'Worker Designation')}</th>
                            <th>{t('thRoleProfile', 'Profile')}</th>
                            <th>{t('thPresentDays', 'Present (Days)')}</th>
                            <th>{t('thHalfDays', 'Half Days')}</th>
                            <th>{t('thTotalGrossEarnings', 'Gross Earnings')}</th>
                            <th>{t('thAdvancesKharchiDeductions', 'Deductions')}</th>
                            <th>{t('thNetPayoutBalance', 'Net Payout')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payrollRecords.length === 0 ? (
                            <tr>
                              <td colSpan="7" className="text-muted py-4">{t('noPayrollMetrics', 'No ledger statements generated.')}</td>
                            </tr>
                          ) : (
                            payrollRecords.map((pay, pIdx) => (
                              <tr key={pay._id || `payroll-row-${pIdx}`}>
                                <td className="fw-bold text-dark text-start ps-3">
                                  {/* Agar payroll ka name string hai aur 24 length ki ID hai to temporary safe dash dikhao */}
                                  {pay.workerName && pay.workerName.length !== 24 ? pay.workerName : "—"}
                                </td>
                                <td><Badge bg="light" className="text-dark border px-2 py-1">{pay.role}</Badge></td>
                                <td className="fw-bold text-success">{pay.presentDays}</td>
                                <td className="fw-bold text-warning">{pay.halfDays}</td>
                                <td className="fw-bold text-dark">PKR {pay.totalWagesEarned}</td>
                                <td className="fw-bold text-danger">- PKR {pay.totalAdvancesTaken}</td>
                                <td>
                                  <Badge bg={pay.netPayable >= 0 ? 'primary' : 'danger'} className="px-3 py-1 fs-6">
                                    PKR {pay.netPayable}
                                  </Badge>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>

              {/* 📝 SITE PROGRESS REPORTING CARD: Restricted safely from Clients and raw Labor accounts */}
              {(userRole === 'Admin' || userRole === 'SiteManager' || userRole === 'Supervisor') && (
                <div className="card shadow-sm border-0 rounded-3 mt-4 bg-white text-start">
                  <div className="card-header bg-light py-3 border-0">
                    <h5 className="fw-bold text-dark mb-0" style={{ fontSize: 'calc(1rem + 0.2vw)' }}>
                      <i className="bi bi-file-earmark-text-fill text-success me-2"></i>
                      {t('submitDailyProgressReport', 'Lock Daily Progress Log')}
                    </h5>
                  </div>
                  <div className="card-body p-3 p-sm-4">
                    <div className="row g-3">
                      {/* Auto Labor Summary */}
                      <div className="col-12 col-md-4">
                        <div className="bg-light p-3 rounded-3 border-start border-success border-4 h-100">
                          <span className="text-muted small fw-bold text-uppercase d-block mb-2" style={{ fontSize: '0.75rem' }}>{t('todaysWorkforceSummary', 'Active Headcount Summary')}</span>
                          <div className="fs-6 fw-bold text-dark mb-1">
                            {t('labelPresent', 'Present')}: <span className="text-success">{attendanceRecords?.filter(a => a.status === 'Present').length || 0}</span>
                          </div>
                          <div className="fs-6 fw-bold text-dark">
                            {t('labelAbsent', 'Absent')}: <span className="text-danger">{attendanceRecords?.filter(a => a.status === 'Absent').length || 0}</span>
                          </div>
                        </div>
                      </div>

                      {/* Supervisor Remarks Input */}
                      <div className="col-12 col-md-8">
                        <div className="mb-3">
                          <label className="form-label fw-semibold text-secondary small">{t('siteRemarksLabel', 'Field Log Observations & Materials Remarks')}</label>
                          <textarea
                            className="form-control"
                            rows="3"
                            placeholder={t('siteRemarksPlaceholder', 'Enter site operational metrics managed today...')}
                            value={dailyRemarks}
                            onChange={(e) => setDailyRemarks(e.target.value)}
                            required
                          ></textarea>
                        </div>
                        <div className="text-end">
                          <button
                            type="button"
                            className="btn btn-success fw-bold px-4 w-100 w-sm-auto"
                            disabled={!dailyRemarks || isSubmittingReport}
                            onClick={async () => {
                              setIsSubmittingReport(true);
                              // Standard dynamic dispatch updates logic goes here safely without crude alerts
                              setDailyRemarks("");
                              setIsSubmittingReport(false);
                            }}
                          >
                            {isSubmittingReport ? t('savingReport', 'Securing Record Stream...') : `🚀 ${t('lockSubmitReport', 'Lock & Dispatch Report')}`}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 3: DAILY FIELD LOGS */}
          {activeTab === 'logs' && (
            <div>
              {/* 🟢 TOP BAR CONTROLS: Column layout on mobile, inline-flex row on desktop */}
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3 mb-3 p-2 bg-white rounded shadow-sm border">
                <h4 className="fw-bold text-warning mb-0" style={{ fontSize: 'calc(1.1rem + 0.3vw)' }}>
                  {t('dailyFieldSiteLogsHeading')}
                </h4>
                {(userRole === 'Admin' || userRole === 'SiteManager' || userRole === 'Supervisor') && (
                  <Button
                    variant="warning"
                    size="sm"
                    /* 🌟 FIX: 'w-100 w-sm-auto' hata kar padding aur dynamic sizing behtar ki */
                    className="text-dark fw-semibold py-2 py-sm-1 px-3"
                    /* 🌟 STRICT INLINE SIZING: Isay stretch hone se har haal mein rokega */
                    style={{
                      width: 'auto',
                      maxWidth: '220px',
                      minWidth: '150px',
                      whiteSpace: 'nowrap'
                    }}
                    onClick={() => {
                      setEditingItemId(null);
                      setLogData({
                        workSummary: '',
                        notes: '',
                        weather: 'Sunny',
                        weatherDelayHours: 0,
                        paymentStatus: 'Paid',
                        roadblocks: { reason: 'None', description: '' },
                        safetyChecklist: { helmetsWorn: false, safetyShoesWorn: false, incidentReported: false, incidentDetails: '' },
                        materialUsed: '',
                        photos: []
                      });
                      setLogModalTab('general');
                      setShowAddLogModal(true);
                    }}
                  >
                    {t('btnCreateDailyLog')}
                  </Button>
                )}
              </div>

              {/* Logs Display Table Wrapper for Mobile Horizontal Scroll */}
              <div className="table-responsive bg-white border rounded shadow-sm">
                <Table hover className="align-middle mb-0" style={{ minWidth: '1200px' }}>
                  <thead className="table-warning text-dark">
                    <tr>
                      <th>{t('thDate')}</th>
                      <th>{t('thWeather')}</th>
                      <th>{t('thWorkSummary')}</th>
                      <th>{t('thMaterialUsage')}</th>
                      <th>{t('thPaymentStatus')}</th>
                      <th>{t('thRoadblocks')}</th>
                      <th>{t('thProofPhotos')}</th>
                      {/* 🔐 Security Tracking Columns */}
                      <th>{t('thCreatedBy')}</th>
                      <th>{t('thUpdatedBy')}</th>
                      {(userRole === 'Admin' || userRole === 'SiteManager' || userRole === 'Supervisor') && (
                        <th className="text-center">{t('thActions')}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {dailyLogs && dailyLogs.map((log, index) => (
                      <tr key={`${log._id || index}-${index}`}>
                        <td className="fw-bold">{log.date ? log.date.substring(0, 10) : t('txtToday')}</td>

                        <td>
                          <div className="d-inline-flex flex-wrap gap-1">
                            <Badge bg="dark">{log.weather}</Badge>
                            {log.weatherDelayHours > 0 && (
                              <Badge bg="danger">
                                {log.weatherDelayHours}
                                {t('txtHourDelay')}
                              </Badge>
                            )}
                          </div>
                        </td>

                        <td>
                          <span className="text-truncate d-inline-block" style={{ maxWidth: '180px' }}>
                            {log.workSummary || t('txtNotAvailable')}
                          </span>
                        </td>

                        <td>{log.materialUsed}</td>

                        <td>
                          {log.paymentStatus ? (
                            <span className={`badge ${log.paymentStatus === 'Paid' ? 'bg-success' : log.paymentStatus === 'Unpaid' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                              {log.paymentStatus === 'Unpaid' ? `🔴 ${t('statusUnpaid')}` : log.paymentStatus === 'Partial' ? `🟡 ${t('statusPartial')}` : `🟢 ${t('statusPaid')}`}
                            </span>
                          ) : (
                            <span className="badge bg-secondary">🔄 {t('statusNoStatus')}</span>
                          )}
                        </td>

                        <td>
                          {log.roadblocks && log.roadblocks.reason !== 'None' ? (
                            <span className="text-danger small fw-semibold">⚠️ {log.roadblocks.reason}</span>
                          ) : (
                            <span className="text-success small">✔️ {t('statusClear')}</span>
                          )}
                        </td>

                        {/* 🔥 Photos Display Dropdown Matrix */}
                        <td>
                          {log.photos && log.photos.filter(p => p.url && !p.url.includes('...')).length > 0 ? (
                            <button
                              type="button"
                              className="btn btn-dark btn-sm fw-medium d-inline-flex align-items-center gap-1 shadow-sm text-nowrap"
                              onClick={(e) => {
                                e.stopPropagation();
                                const validPhotos = log.photos.filter(p => p.url && !p.url.includes('...'));

                                // Generate HTML Cards Grid
                                const photosHtmlCards = validPhotos.map((pic) => {
                                  const imgUrl = pic.url || pic;
                                  const hasValidCoords = pic.coordinates &&
                                    pic.coordinates.latitude &&
                                    parseFloat(pic.coordinates.latitude) !== 0 &&
                                    pic.coordinates.latitude !== 31.4602;

                                  const isLivePhoto = (pic.tag === 'Live Stamp' || pic.isLive === true || pic.isLive === 'true') && hasValidCoords;
                                  const timeString = pic.timestamp ? new Date(pic.timestamp).toLocaleTimeString() : new Date().toLocaleTimeString();

                                  const badgeClass = isLivePhoto ? 'badge-live' : 'badge-gallery';
                                  const badgeText = isLivePhoto ? t('popLiveCaptured') : t('popDeviceStorage');

                                  const metaContent = isLivePhoto
                                    ? `<div class="meta-box live">
                               <strong class="text-success">📍 ${t('popGpsVerified')}</strong><br/>
                               <span class="text-muted" style="font-size: 11px;">
                                 Lat: <strong>${pic.coordinates?.latitude?.toFixed(4)}</strong> | 
                                 Lon: <strong>${pic.coordinates?.longitude?.toFixed(4)}</strong>
                               </span>
                             </div>`
                                    : `<div class="meta-box gallery">
                               <strong class="text-primary">📂 ${t('popAttachedDoc')}</strong><br/>
                               <span class="text-muted" style="font-size: 11px;">${t('popUploadedLocal')}</span>
                             </div>`;

                                  return `
                          <div class="col-md-6 col-lg-4">
                            <div class="card asset-card">
                              <div class="img-container">
                                <img src="${imgUrl}" alt="MFR Site Proof Image" class="clickable-img">
                              </div>
                              <div class="card-body p-3">
                                <div class="d-flex justify-content-between align-items-center mb-3">
                                  <span class="${badgeClass}">${badgeText}</span>
                                  <span class="text-muted small fw-medium">⏰ ${timeString}</span>
                                </div>
                                ${metaContent}
                              </div>
                            </div>
                          </div>
                        `;
                                }).join('');

                                // Open Page & Write Document With Lightbox Layout
                                const win = window.open("", "_blank");
                                win.document.write(`
                        <html>
                          <head>
                            <title>${t('popPageTitle')}</title>
                            <meta name="viewport" content="width=device-width, initial-scale=1.0">
                            <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
                            <style>
                              body { background-color: #f3f4f6; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 20px 10px; }
                              @media(min-width: 576px) { body { padding: 40px 20px; } }
                              .report-header { background: #1a1d20; color: white; padding: 20px; border-radius: 12px; margin-bottom: 30px; box-shadow: 0 4px 6px rgba(0,0,0,0.05); }
                              .asset-card { border: none; border-radius: 12px; box-shadow: 0 4px 12px rgba(0,0,0,0.04); overflow: hidden; background: white; transition: transform 0.2s; }
                              .asset-card:hover { transform: translateY(-4px); box-shadow: 0 6px 16px rgba(0,0,0,0.08); }
                              .img-container { position: relative; width: 100%; height: 260px; background: #eaedf1; overflow: hidden; }
                              .img-container img { width: 100%; height: 100%; object-fit: cover; cursor: pointer; transition: filter 0.2s; }
                              .img-container img:hover { filter: brightness(0.9); }
                              .badge-live { background-color: #198754; color: white; font-weight: 600; padding: 6px 12px; font-size: 11px; text-transform: uppercase; border-radius: 6px; }
                              .badge-gallery { background-color: #0d6efd; color: white; font-weight: 600; padding: 6px 12px; font-size: 11px; text-transform: uppercase; border-radius: 6px; }
                              .meta-box { padding: 12px; border-radius: 8px; font-size: 12px; line-height: 1.5; }
                              .meta-box.live { background-color: #f0fdf4; border: 1px solid #bbf7d0; }
                              .meta-box.gallery { background-color: #eff6ff; border: 1px solid #bfdbfe; }
                              #lightboxOverlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(15, 23, 42, 0.9); display: none; justify-content: center; align-items: center; z-index: 9999; opacity: 0; transition: opacity 0.3s ease; }
                              #lightboxOverlay.active { display: flex; opacity: 1; }
                              #lightboxImage { max-width: 90%; max-height: 85%; border-radius: 8px; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.5); transform: scale(0.95); transition: transform 0.3s ease; }
                              #lightboxOverlay.active #lightboxImage { transform: scale(1); }
                              .close-lightbox { position: absolute; top: 20px; right: 25px; color: #fff; font-size: 35px; font-weight: bold; background: none; border: none; outline: none; transition: color 0.2s; }
                              .close-lightbox:hover { color: #cbd5e1; }
                            </style>
                          </head>
                          <body>
                            <div class="container-fluid container-md">
                              <div class="report-header d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3">
                                <div>
                                  <h4 class="mb-1 fw-bold">🖼️ ${t('popReportInventoryHeading')}</h4>
                                  <p class="mb-0 text-muted small">${t('popReportSubheading')}</p>
                                </div>
                                <div>
                                  <span class="badge bg-warning text-dark px-3 py-2 fw-bold rounded-pill">${t('thDate')}: ${log.date ? new Date(log.date).toLocaleDateString() : t('txtNotAvailable')}</span>
                                </div>
                              </div>
                              <div class="row g-4">
                                ${photosHtmlCards}
                              </div>
                              <div class="text-center mt-5 text-muted small">
                                <hr/>
                                <p>${t('popFooterText')}</p>
                              </div>
                            </div>
                            <div id="lightboxOverlay" onclick="closeLightbox()">
                              <button class="close-lightbox" onclick="closeLightbox()">&times;</button>
                              <img id="lightboxImage" src="" alt="Expanded View" onclick="event.stopPropagation()">
                            </div>
                            <script>
                              const overlay = document.getElementById('lightboxOverlay');
                              const lightboxImg = document.getElementById('lightboxImage');
                              document.querySelectorAll('.clickable-img').forEach(img => {
                                img.addEventListener('click', () => {
                                  lightboxImg.src = img.src;
                                  overlay.classList.add('active');
                                  document.body.style.overflow = 'hidden';
                                });
                              });
                              function closeLightbox() {
                                overlay.classList.remove('active');
                                document.body.style.overflow = '';
                                setTimeout(() => { lightboxImg.src = ''; }, 300);
                              }
                              document.addEventListener('keydown', (e) => {
                                if (e.key === 'Escape') closeLightbox();
                              });
                            </script>
                          </body>
                        </html>
                      `);
                                win.document.close();
                              }}
                            >
                              🖼️ {t('btnViewPhotos')} ({log.photos.filter(p => p.url && !p.url.includes('...')).length})
                            </button>
                          ) : (
                            <span className="text-muted small">{t('txtNoAssetsLoaded')}</span>
                          )}
                        </td>

                        {/* 🔐 Data Accountability Fields */}
                        <td>
                          <span className="badge bg-light text-dark border fw-medium px-2 py-1">
                            👤 {log.createdBy ? t(`role${log.createdBy}`) : t('roleUnknown')}
                          </span>
                        </td>
                        <td>
                          <span className="badge bg-light text-secondary border fw-normal px-2 py-1">
                            ✍️ {log.updatedBy ? t(`role${log.updatedBy}`) : t('txtNoEdits')}
                          </span>
                        </td>

                        {/* 🛡️ Strict Role Access: Action Column */}
                        {(userRole === 'Admin' || userRole === 'SiteManager' || userRole === 'Supervisor') && (
                          <td className="text-center" style={{ minWidth: '110px' }}>
                            <div className="d-flex justify-content-center gap-1">
                              <Button
                                variant="outline-warning"
                                size="sm"
                                className="px-2 py-1"
                                onClick={() => handleEditLogClick(log)}
                              >
                                <i className="bi bi-pencil-square"></i>
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="px-2 py-1"
                                onClick={() => handleDeleteLog(log._id)}
                              >
                                <i className="bi bi-trash-fill"></i>
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          {/* TAB 4: COST & BUDGET */}
          {activeTab === 'financials' && (() => {
            // 🛡️ Guard Clause: Agar logged in user Labour hai to table aur details kuch nahi dikhegi
            if (userRole === 'Labour') {
              return (
                <div className="alert alert-warning border-0 shadow-sm p-3 text-center" role="alert">
                  <i className="bi bi-shield-lock-fill fs-3 d-block mb-2 text-warning"></i>
                  <strong className="d-block mb-1">{t('accessDeniedTitle', 'Access Denied')}</strong>
                  <span className="small text-muted">{t('accessDeniedMessage', 'Labour profile accounts are restricted from viewing financial data records.')}</span>
                </div>
              );
            }

            const totalCost = expenses.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
            const isOverBudget = totalCost > budgetLimit;
            const budgetPercentage = Math.min((totalCost / budgetLimit) * 100, 100);

            return (
              <div>
                {/* ⚠️ BUDGET ALERTS & PROGRESS BAR SYSTEM */}
                {isOverBudget ? (
                  <div className="alert alert-danger d-flex flex-column flex-sm-row align-items-start align-items-sm-center border-2 border-danger shadow-sm mb-4 p-3" role="alert">
                    <i className="bi bi-exclamation-triangle-fill fs-3 me-0 me-sm-3 mb-2 mb-sm-0 text-danger"></i>
                    <div>
                      <strong className="fs-5 text-danger d-block mb-1">{t('budgetViolationAlert', 'Budget Violation Alert!')}</strong>
                      <p className="mb-0 text-dark small" style={{ fontSize: '14px' }}>
                        {t('budgetExceededText', 'Your actual construction expenses')} <span className="fw-bold">Rs. {totalCost.toLocaleString()}</span> {t('budgetExceededTextMiddle', 'have exceeded the allocated project budget limit of')} <span className="fw-bold">Rs. {budgetLimit.toLocaleString()}</span> PKR!
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="alert alert-success d-flex flex-column flex-sm-row align-items-start align-items-sm-center border-0 shadow-sm mb-4 p-3" role="alert">
                    <i className="bi bi-check-circle-fill fs-3 me-0 me-sm-3 mb-2 mb-sm-0 text-success"></i>
                    <div>
                      <strong className="text-success d-block mb-1">{t('financialHealthStable', 'Financial Health Stable')}</strong>
                      <p className="mb-0 text-muted small" style={{ fontSize: '14px' }}>
                        {t('budgetSafeText', 'Expenses are within the safe threshold. Remaining Buffer:')} Rs. {(budgetLimit - totalCost).toLocaleString()} PKR.
                      </p>
                    </div>
                  </div>
                )}

                {/* PROGRESS BAR */}
                <div className="mb-4 bg-light p-3 rounded border shadow-sm">
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-1 mb-2">
                    <span className="fw-bold text-secondary fs-7 text-uppercase" style={{ fontSize: '12px' }}>{t('budgetConsumptionRate')}</span>
                    <span className={`fw-bold small ${isOverBudget ? 'text-danger' : 'text-success'}`} style={{ fontSize: '14px' }}>
                      {totalCost.toLocaleString()} / {budgetLimit.toLocaleString()} PKR ({Math.round((totalCost / budgetLimit) * 100)}%)
                    </span>
                  </div>
                  <div className="progress" style={{ height: '14px' }}>
                    <div
                      className={`progress-bar progress-bar-striped progress-bar-animated ${isOverBudget ? 'bg-danger' : 'bg-success'}`}
                      role="progressbar"
                      style={{ width: `${budgetPercentage}%` }}
                    ></div>
                  </div>
                </div>

                {/* HEADER SECTION */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-3 p-3 bg-white border rounded shadow-sm">
                  <h4 className="fw-bold text-danger mb-0" style={{ fontSize: 'calc(1.1rem + 0.3vw)' }}>{t('costBudgetAnalytics')}</h4>
                  <div className="d-flex align-items-center flex-wrap gap-3 w-100 w-md-auto justify-content-between justify-content-md-end">
                    {userRole === 'Admin' && (
                      <div className="d-flex align-items-center gap-2 flex-grow-1 flex-sm-grow-0">
                        <label className="fw-bold text-secondary text-nowrap mb-0" style={{ fontSize: '13px' }}>
                          {t('setBudgetLabel')}
                        </label>
                        <input
                          type="number"
                          className="form-control form-control-sm fw-bold border-danger text-danger"
                          style={{ width: '120px', minWidth: '100px' }}
                          value={budgetLimit}
                          onChange={(e) => {
                            const val = e.target.value;
                            setBudgetLimit(val === '' ? '' : Number(val));
                          }}
                          onBlur={async () => {
                            if (!projectId) return;
                            try {
                              const token = localStorage.getItem('token');
                              const config = { headers: { 'x-auth-token': token } };
                              const safeBudget = Number(budgetLimit) || 0;

                              const res = await axiosReal.put(
                                `https://api.mfrbuild.com/api/expenses/${projectId}/budget`,
                                { budgetLimit: safeBudget },
                                config
                              );

                              if (res.data && res.data.success) {
                                localStorage.setItem(`project_budget_${projectId}`, safeBudget);
                              }
                            } catch (err) {
                              console.error("Database sync status error:", err);
                            }
                          }}
                          placeholder={t('setBudgetPlaceholder')}
                        />
                      </div>
                    )}
                    {/* Allow Action Buttons for non-Labour users */}
                    <Button
                      variant="danger"
                      size="sm"
                      className="px-3 py-2 py-md-1 fw-semibold flex-grow-1 flex-sm-grow-0 text-nowrap"
                      onClick={() => { setEditingItemId(null); setExpenseData({ item: '', amount: '', category: 'Materials', paymentType: 'Cash', vendorName: '', receiptImage: '' }); setShowAddExpenseModal(true); }}
                    >
                      {t('logExpenseBtn')}
                    </Button>
                  </div>
                </div>

                {/* EXPENSES TABLE WITH SCROLL IMPLEMENTATION */}
                <div className="table-responsive bg-white border rounded shadow-sm">
                  <Table hover className="align-middle mb-0" style={{ minWidth: '1350px' }}>
                    <thead className="table-success">
                      <tr>
                        <th>{t('thExpenseItem')}</th>
                        <th>{t('thCategory')}</th>
                        <th>{t('thPaymentType')}</th>
                        <th>{t('thVendorKhata')}</th>
                        <th>{t('thAmount')}</th>
                        <th className="text-center">{t('thBillReceipt')}</th>
                        <th className="text-center">{t('thStatus')}</th>
                        {/* Security Trace Headers */}
                        <th>{t('thCreatedBy')}</th>
                        <th>{t('thUpdatedBy')}</th>
                        <th className="text-center">{t('thActions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.map((exp) => (
                        <tr key={exp._id}>
                          <td>
                            <div className="fw-bold text-dark">{exp.item}</div>
                            <div className="d-flex align-items-center gap-2 mt-1">
                              <small className="text-muted" style={{ fontSize: '11px' }}>
                                {exp.createdAt ? new Date(exp.createdAt).toLocaleDateString('en-PK') : ''}
                              </small>
                              {exp.receiptImage && (
                                <a href={exp.receiptImage} download={`Parchi-${exp.item}.png`} className="badge bg-light text-dark border text-decoration-none" style={{ fontSize: '10px' }}>
                                  <i className="bi bi-image"></i> {t('viewParchiBadge')}
                                </a>
                              )}
                            </div>
                          </td>
                          <td><Badge bg="secondary" className="p-2">{t(`category_${exp.category}`, { defaultValue: exp.category })}</Badge></td>
                          <td>
                            <Badge bg={exp.paymentType === 'Credit (Udhaar)' ? 'warning' : 'info'} text="dark" className="p-2">
                              {exp.paymentType === 'Credit (Udhaar)' ? t('paymentCredit') : t('paymentCash')}
                            </Badge>
                          </td>
                          <td className="text-capitalize fw-semibold text-secondary">{exp.vendorName || t('generalMarket')}</td>
                          <td className="text-danger fw-bold">{Number(exp.amount).toLocaleString()} /-</td>
                          <td className="text-center">
                            {exp.receiptImage ? (
                              <Button
                                variant="danger"
                                size="sm"
                                className="py-1 px-2 fw-bold text-nowrap"
                                style={{ fontSize: '11px' }}
                                onClick={() => {
                                  setReceiptPreviewUrl(exp.receiptImage);
                                  setShowReceiptModal(true);
                                }}
                              >
                                <i className="bi bi-image-fill me-1"></i> {t('viewReceiptBtn')}
                              </Button>
                            ) : (
                              <span className="text-muted border rounded p-1 bg-light text-nowrap" style={{ fontSize: '11px' }}>
                                {t('kachaBillText')}
                              </span>
                            )}
                          </td>
                          <td className="text-center">
                            <Badge bg={exp.status === 'Pending Approval' ? 'danger' : 'success'} className="p-2">
                              {exp.status === 'Pending Approval' ? t('statusPending') : t('statusApproved')}
                            </Badge>
                          </td>

                          {/* 🔐 Created By Audit Badge */}
                          <td>
                            <span className="badge bg-light text-dark border fw-medium px-2 py-1">
                              👤 {exp.createdBy ? t(`role${exp.createdBy}`) : t('roleUnknown')}
                            </span>
                          </td>

                          {/* 🔐 Updated By Audit Badge */}
                          <td>
                            <span className="badge bg-light text-secondary border fw-normal px-2 py-1">
                              ✍️ {exp.updatedBy ? t(`role${exp.updatedBy}`) : t('txtNoEdits')}
                            </span>
                          </td>

                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-1">
                              {userRole === 'Admin' && exp.status === 'Pending Approval' && (
                                <Button variant="success" size="sm" className="px-2 py-1" title={t('approveEntryTitle')} onClick={() => handleApproveClick(exp._id)}>
                                  <i className="bi bi-check-lg"></i>
                                </Button>
                              )}
                              <Button variant="outline-warning" size="sm" className="px-2 py-1" onClick={() => { setEditingItemId(exp._id); setExpenseData(exp); setShowAddExpenseModal(true); }}>
                                <i className="bi bi-pencil-square"></i>
                              </Button>
                              <Button variant="outline-danger" size="sm" className="px-2 py-1" onClick={() => handleDeleteExpense(exp._id)}>
                                <i className="bi bi-trash-fill"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      <tr className="table-dark fw-bold">
                        <td colSpan="4" className="text-end">{t('totalApprovedCostLabel', 'Total Cost')}</td>
                        <td colSpan="6" className="text-danger text-start text-sm-center ps-3">{totalCost.toLocaleString()} /-</td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </div>
            );
          })()}

          {/* TAB 5: PUNCH LIST (QA/QC CHECK) */}
          {activeTab === 'punchlist' && (() => {
            // 🛡️ Guard Clause: Labour ko data table aur control buttons dono hide karne ke liye
            if (userRole === 'Labour') {
              return (
                <div className="card border-0 shadow-sm p-4 text-center bg-white animate__animated animate__fadeIn">
                  <i className="bi bi-shield-lock text-danger fs-1 mb-2"></i>
                  <h5 className="fw-bold text-dark">{t('accessRestrictedTitle', 'Access Restricted')}</h5>
                  <p className="text-muted small mb-0">
                    {t('punchListLabourRestricted', 'Labour accounts are restricted from viewing quality defect streams and punch list datasets.')}
                  </p>
                </div>
              );
            }

            return (
              <div className="card border-0 shadow-sm p-3 p-sm-4 animate__animated animate__fadeIn">

                {/* Upper Section Header */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
                  <div>
                    <h4 className="fw-bold text-primary mb-1" style={{ fontSize: 'calc(1.1rem + 0.3vw)' }}>
                      {t('punchListHeader', 'Punch List')}
                    </h4>
                    <p className="text-muted small mb-0">
                      {t('punchListSubHeader', 'Quality assurance and field site defect tracking')}
                    </p>
                  </div>
                  <div className="d-flex flex-wrap gap-2 w-100 w-md-auto justify-content-sm-start justify-content-md-end">
                    {/* WhatsApp Tracker Sync Click */}
                    <Button variant="success" size="sm" className="fw-semibold px-3 py-2 py-md-1 flex-grow-1 flex-md-grow-0 text-nowrap" onClick={shareDailyReportOnWhatsApp}>
                      <i className="bi bi-whatsapp me-2"></i>{t('btnShareSummary')}
                    </Button>
                    {(userRole === 'Admin' || userRole === 'SiteManager' || userRole === 'Supervisor') && (
                      <Button variant="primary" size="sm" className="fw-semibold px-3 py-2 py-md-1 flex-grow-1 flex-md-grow-0 text-nowrap" onClick={() => setShowAddPunchModal(true)}>
                        {t('btnLogNewIssue')}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Enhanced Matrix Table with Scroll Wrapper */}
                <div className="table-responsive bg-white border rounded shadow-sm">
                  <Table hover className="align-middle border-light mb-0" style={{ minWidth: '1300px' }}>
                    <thead className="table-primary text-secondary">
                      <tr>
                        <th style={{ width: '25%' }}>{t('thIssueDescription')}</th>
                        <th>{t('thLocation')}</th>
                        <th>{t('thAssignedContractor')}</th>
                        <th>{t('thPriority')}</th>
                        <th>{t('thHoldPaymentLinkage')}</th>
                        <th>{t('thStatusTracking')}</th>
                        {/* Security Trace Headers */}
                        <th>{t('thCreatedBy')}</th>
                        <th>{t('thUpdatedBy')}</th>
                        {userRole !== 'Client' && <th>{t('thAction')}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {punchItems.length === 0 ? (
                        <tr><td colSpan={userRole === 'Client' ? "8" : "9"} className="text-center text-muted py-4">{t('noPunchItems', 'No active quality defects found. All operations verified clear!')}</td></tr>
                      ) : (
                        punchItems.map((item) => (
                          <tr key={item._id} className={item.status === 'Verified' ? 'table-success-muted' : ''}>
                            <td className="fw-bold text-dark" style={{ maxWidth: '300px', whiteSpace: 'normal' }}>
                              {item.issueDescription}
                              <div className="mt-2 d-flex flex-wrap gap-2">
                                <a href={item.beforeImageUrl} target="_blank" rel="noreferrer" className="badge bg-danger text-decoration-none py-1.5 px-2">
                                  📸 {t('beforeProof', 'Before Proof')}
                                </a>
                                {item.afterImageUrl && (
                                  <a href={item.afterImageUrl} target="_blank" rel="noreferrer" className="badge bg-success text-decoration-none py-1.5 px-2">
                                    📸 {t('afterProof', 'After Verified Proof')}
                                  </a>
                                )}
                              </div>
                            </td>
                            <td><Badge bg="light" className="text-dark border px-2 py-1.5">{item.location}</Badge></td>
                            <td><span className="text-secondary fw-semibold">{item.assignedContractor?.name || t('unassignedLabel', 'Unassigned')}</span></td>
                            <td>
                              <Badge bg={item.priority === 'High' ? 'danger' : item.priority === 'Medium' ? 'warning' : 'success'} className={item.priority === 'Medium' ? 'text-dark' : ''}>
                                {item.priority}
                              </Badge>
                            </td>

                            <td>
                              {item.isPaymentBlocked ? (
                                <Badge bg="danger-subtle" className="text-danger border border-danger p-2">⚠️ {t('paymentHold', 'Payment Hold Locked')}</Badge>
                              ) : (
                                <Badge bg="success-subtle" className="text-success border border-success p-2">✅ {t('fundsReleased', 'Funds Released')}</Badge>
                              )}
                            </td>

                            <td>
                              <select
                                className="form-select form-select-sm fw-bold border-secondary-subtle"
                                value={item.status}
                                style={{ width: '145px' }}
                                disabled={item.status === 'Verified' || userRole === 'Client'}
                                onChange={(e) => {
                                  const nextVal = e.target.value;
                                  if (nextVal === 'Resolved') {
                                    const fileInput = document.createElement('input');
                                    fileInput.type = 'file';
                                    fileInput.accept = 'image/*';
                                    fileInput.onchange = (event) => {
                                      handleUpdatePunchStatus(item._id, 'Resolved', event.target.files[0]);
                                    };
                                    fileInput.click();
                                  } else {
                                    handleUpdatePunchStatus(item._id, nextVal);
                                  }
                                }}
                              >
                                <option value="Open">🔴 {t('statusOpen', 'Open')}</option>
                                <option value="In Progress">🟡 {t('statusInProgress', 'In Progress')}</option>
                                <option value="Resolved">🟢 {t('statusResolved', 'Resolved')}</option>
                                {(userRole === 'Admin' || userRole === 'SiteManager') && (
                                  <option value="Verified">🔵 {t('statusVerified', 'Verified')}</option>
                                )}
                              </select>
                            </td>

                            {/* 🔐 Data Accountability Audit Badges */}
                            <td>
                              <span className="badge bg-light text-dark border fw-medium px-2 py-1">
                                👤 {item.createdBy ? t(`role${item.createdBy}`) : t('roleUnknown')}
                              </span>
                            </td>
                            <td>
                              <span className="badge bg-light text-secondary border fw-normal px-2 py-1">
                                ✍️ {item.updatedBy ? t(`role${item.updatedBy}`) : t('txtNoEdits')}
                              </span>
                            </td>

                            {/* Action Column Hidden From Clients */}
                            {userRole !== 'Client' && (
                              <td>
                                <Button variant="outline-danger" size="sm" className="px-2 py-1" onClick={() => handleDeletePunchItem(item._id)}>
                                  <i className="bi bi-trash-fill"></i>
                                </Button>
                              </td>
                            )}

                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            );
          })()}

          {/* ======================================================================== */}
          {/* 🚀 CODE 1: QUICK ACTION FLOATING CONTROLS SYSTEM (RIGHT SIDE)            */}
          {/* ======================================================================== */}
          {userRole !== 'Labour' && (
            <div
              className="position-fixed bottom-0 end-0 m-3 m-sm-4 z-3 d-flex flex-column-reverse align-items-end"
              style={{
                gap: '12px',
                width: 'auto',
                maxWidth: 'calc(100% - 32px)',
                height: 'auto',
                pointerEvents: 'none'
              }}
              onMouseEnter={(e) => {
                const menu = e.currentTarget.querySelector('.mfr-hover-menu');
                const rocket = e.currentTarget.querySelector('.mfr-rocket-btn');
                if (menu) {
                  menu.style.opacity = '1';
                  menu.style.transform = 'translateY(0) scale(1)';
                  menu.style.pointerEvents = 'auto';
                }
                if (rocket) {
                  rocket.style.transform = 'rotate(45deg)';
                  rocket.style.backgroundColor = '#0d6efd';
                }
              }}
              onMouseLeave={(e) => {
                const menu = e.currentTarget.querySelector('.mfr-hover-menu');
                const rocket = e.currentTarget.querySelector('.mfr-rocket-btn');
                if (menu) {
                  menu.style.opacity = '0';
                  menu.style.transform = 'translateY(20px) scale(0.8)';
                  menu.style.pointerEvents = 'none';
                }
                if (rocket) {
                  rocket.style.transform = 'rotate(0deg)';
                  rocket.style.backgroundColor = '#212529';
                }
              }}
              onTouchStart={(e) => {
                const menu = e.currentTarget.querySelector('.mfr-hover-menu');
                const rocket = e.currentTarget.querySelector('.mfr-rocket-btn');
                if (menu && menu.style.opacity !== '1') {
                  menu.style.opacity = '1';
                  menu.style.transform = 'translateY(0) scale(1)';
                  menu.style.pointerEvents = 'auto';
                  if (rocket) {
                    rocket.style.transform = 'rotate(45deg)';
                    rocket.style.backgroundColor = '#0d6efd';
                  }
                } else if (menu) {
                  menu.style.opacity = '0';
                  menu.style.transform = 'translateY(20px) scale(0.8)';
                  menu.style.pointerEvents = 'none';
                  if (rocket) {
                    rocket.style.transform = 'rotate(0deg)';
                    rocket.style.backgroundColor = '#212529';
                  }
                }
              }}
            >
              {/* 🎯 Base Rocket Trigger Button */}
              <div
                className="mfr-rocket-btn shadow-lg text-white d-flex align-items-center justify-content-center"
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: '#212529',
                  fontSize: '20px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  pointerEvents: 'auto'
                }}
              >
                🚀
              </div>

              {/* 🤫 Action Text Menu */}
              <div
                className="mfr-hover-menu d-flex flex-column gap-2 align-items-end"
                style={{
                  opacity: 0,
                  transform: 'translateY(20px) scale(0.8)',
                  pointerEvents: 'none',
                  transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
                  marginBottom: '8px'
                }}
              >
                <button
                  className="btn btn-warning fw-bold text-dark px-3 py-2 text-nowrap rounded-pill shadow-sm"
                  style={{ pointerEvents: 'auto', fontSize: '13px' }}
                  onClick={(e) => { e.stopPropagation(); setShowRfiModal(true); }}
                >
                  ❓ {t('askRfiFloating', 'Ask RFI (Sawaal)')}
                </button>

                {userRole !== 'Client' && (
                  <button
                    className="btn btn-primary fw-bold text-white px-3 py-2 text-nowrap rounded-pill shadow-sm"
                    style={{ pointerEvents: 'auto', fontSize: '13px' }}
                    onClick={(e) => { e.stopPropagation(); setShowAddPunchModal(true); }}
                  >
                    ⚠️ {t('reportDefectFloating', 'Report Defect (Punch)')}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* TAB 6: INVENTORY & MATERIAL MANAGEMENT (0% ➡️ 100%) */}
          {activeTab === 'inventory' && (() => {
            // 🛡️ Guard Clause: Labour restriction block
            if (userRole === 'Labour') {
              return (
                <div className="alert alert-warning border-0 shadow-sm p-4 text-center animate__animated animate__fadeIn" role="alert">
                  <i className="bi bi-shield-lock-fill fs-1 d-block mb-2 text-warning"></i>
                  <strong className="d-block mb-1 fs-5">{t('inventoryRestrictedTitle', 'Inventory Access Denied')}</strong>
                  <span className="small text-muted">
                    {t('inventoryLabourMessage', 'Labour accounts do not have authorized privileges to view real-time material warehouse data logs.')}
                  </span>
                </div>
              );
            }

            // 📦 Local storage parsing with extreme safety fallbacks
            let localOfflineInventory = [];
            try {
              const rawLocal = localStorage.getItem(`offline_inventory_${projectId}`);
              if (rawLocal) {
                localOfflineInventory = JSON.parse(rawLocal);
              }
            } catch (err) {
              console.error("Offline local extraction failed:", err);
            }

            const validOnlineInventory = Array.isArray(inventory) ? inventory : [];

            // 🗺️ Performance optimized Set dictionary maps to eliminate duplicates safely
            const onlineIds = new Set(validOnlineInventory.map(item => item && String(item._id || '')).filter(Boolean));
            const onlineNames = new Set(validOnlineInventory.map(item => item && String(item.itemName || '').trim().toLowerCase()).filter(Boolean));

            // Base state definition array
            const combinedInventory = [...validOnlineInventory];

            // Safely inject local records if they have not yet integrated into the master backend
            if (Array.isArray(localOfflineInventory)) {
              localOfflineInventory.forEach(offlineItem => {
                if (!offlineItem || !offlineItem.itemName) return;

                const currentOfflineId = offlineItem._id ? String(offlineItem._id) : '';
                const currentOfflineName = String(offlineItem.itemName).trim().toLowerCase();

                const existsOnline = (currentOfflineId && onlineIds.has(currentOfflineId)) || onlineNames.has(currentOfflineName);

                if (!existsOnline) {
                  combinedInventory.unshift({
                    ...offlineItem,
                    isOffline: true,
                    _id: offlineItem._id || `offline_fallback_${Math.random()}`
                  });
                }
              });
            }

            const totalTypes = combinedInventory.length;
            const lowStockCount = combinedInventory.filter(item => {
              if (!item) return false;
              const stockValue = item.quantity !== undefined ? item.quantity : (item.quantityChange !== undefined ? item.quantityChange : 0);
              const minLimit = item.minThreshold !== undefined ? item.minThreshold : 10;
              return Number(stockValue) <= Number(minLimit);
            }).length;

            return (
              <div className="animate__animated animate__fadeIn">
                {/* 📊 SUMMARY METRICS CARDS */}
                <div className="row g-3 mb-4">
                  <div className="col-12 col-sm-6 col-md-4">
                    <div className="p-3 bg-white border rounded shadow-sm d-flex align-items-center justify-content-between border-start border-primary border-4 h-100">
                      <div className="text-start me-2">
                        <span className="text-muted fw-bold d-block text-uppercase small" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>{t('lblTotalMaterialTypes')}</span>
                        <h3 className="fw-bold text-primary mb-0 mt-1" style={{ fontSize: 'calc(1.2rem + 0.4vw)' }}>{totalTypes} {t('txtItemsCount')}</h3>
                      </div>
                      <div className="bg-primary-subtle p-2 rounded-3 text-primary flex-shrink-0">
                        <i className="bi bi-box-seam-fill fs-3"></i>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-md-4">
                    <div className="p-3 bg-white border rounded shadow-sm d-flex align-items-center justify-content-between border-start border-danger border-4 h-100">
                      <div className="text-start me-2">
                        <span className="text-muted fw-bold d-block text-uppercase small" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>{t('lblCriticalLowStock')}</span>
                        <h3 className={`fw-bold mb-0 mt-1 ${lowStockCount > 0 ? 'text-danger' : 'text-dark'}`} style={{ fontSize: 'calc(1.2rem + 0.4vw)' }}>
                          {lowStockCount} {t('txtReorderAlerts')}
                        </h3>
                      </div>
                      <div className="bg-danger-subtle p-2 rounded-3 text-danger flex-shrink-0">
                        <i className="bi bi-exclamation-triangle-fill fs-3"></i>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-sm-12 col-md-4">
                    <div className={`p-3 bg-white border rounded shadow-sm d-flex align-items-center justify-content-between border-start ${navigator.onLine ? 'border-success' : 'border-warning'} border-4 h-100`}>
                      <div className="text-start me-2">
                        <span className="text-muted fw-bold d-block text-uppercase small" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>{t('lblMaterialLogsStatus')}</span>
                        <h3 className={`fw-bold mb-0 mt-1 ${navigator.onLine ? 'text-success' : 'text-warning'}`} style={{ fontSize: 'calc(1.2rem + 0.4vw)' }}>
                          {navigator.onLine ? t('txtLiveDatabase') : 'Offline Local Storage'}
                        </h3>
                      </div>
                      <div className={`p-2 rounded-3 ${navigator.onLine ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-warning'} flex-shrink-0`}>
                        <i className={`bi ${navigator.onLine ? 'bi-cloud-check-fill' : 'bi-cloud-slash-fill'} fs-3`}></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 🚀 HEADER CONTROLS SECTION */}
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3 mb-3 p-3 bg-white border rounded-3 shadow-sm">
                  <h4 className="fw-bold text-dark mb-0" style={{ fontSize: 'calc(1.05rem + 0.3vw)' }}>
                    <i className="bi bi-boxes text-primary me-2"></i>{t('inventoryHeaderTitle')}
                  </h4>

                  <button
                    type="button"
                    className="btn btn-primary fw-semibold btn-sm px-3 py-2 shadow-sm rounded-2 text-nowrap"
                    style={{
                      width: 'auto',
                      maxWidth: '220px',
                      minWidth: '150px',
                      whiteSpace: 'nowrap'
                    }}
                    onClick={() => {
                      setInventoryData({
                        type: 'StockIn',
                        itemName: '',
                        quantityChange: '',
                        unit: 'Bags',
                        category: 'Materials',
                        minThreshold: '10'
                      });
                      setShowInventoryModal(true);
                    }}
                  >
                    <i className="bi bi-plus-circle-fill me-1"></i> {t('btnStockInAdjust')}
                  </button>
                </div>

                {/* 📋 DATA TABLE BLOCK */}
                <div className="table-responsive bg-white border rounded-3 shadow-sm">
                  <table className="table table-striped table-hover align-middle mb-0 text-start" style={{ minWidth: '1250px' }}>
                    <thead className="table-info border-bottom text-secondary fw-bold">
                      <tr>
                        <th className="ps-3 py-3" style={{ width: '25%' }}>{t('thMaterialAsset')}</th>
                        <th style={{ width: '12%' }}>{t('thCategory')}</th>
                        <th style={{ width: '15%' }}>{t('thCurrentLevelStock')}</th>
                        <th style={{ width: '15%' }}>{t('thMinSafetyLimit')}</th>
                        <th style={{ width: '10%' }}>{t('thStatus')}</th>
                        <th style={{ width: '11%' }}>{t('thCreatedBy')}</th>
                        <th style={{ width: '11%' }}>{t('thUpdatedBy')}</th>
                        <th className="pe-3 text-center" style={{ width: '11%' }}>{t('thActions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {combinedInventory.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center text-muted py-5 bg-light-subtle">
                            <i className="bi bi-inbox text-secondary fs-1 d-block mb-2"></i>
                            {t('txtNoInventoryLogged')}
                          </td>
                        </tr>
                      ) : (
                        combinedInventory.map((item, idx) => {
                          if (!item) return null;

                          const stockValue = item.quantity !== undefined ? item.quantity : (item.quantityChange !== undefined ? item.quantityChange : 0);
                          const minLimit = item.minThreshold !== undefined ? item.minThreshold : 10;
                          const isLowStock = Number(stockValue) <= Number(minLimit);
                          const unitLabel = item.unit === 'Bags' ? t('unitBags') : item.unit || t('unitBags');

                          // Completely uniform layout configuration matching prior baseline tables
                          const rowClass = "";

                          const parseUserDisplay = (userField, fallbackKey) => {
                            if (!userField) return t(fallbackKey);
                            if (typeof userField === 'object' && userField.name) return userField.name;
                            if (typeof userField === 'string') {
                              const cleaned = userField.replace('role', '');
                              return t(`role${cleaned}`) !== `role${cleaned}` ? t(`role${cleaned}`) : cleaned;
                            }
                            return String(userField);
                          };

                          const displayCreatedBy = parseUserDisplay(item.createdBy, 'roleUnknown');
                          const displayUpdatedBy = parseUserDisplay(item.updatedBy, 'txtNoEdits');

                          return (
                            <tr key={item._id || `inv_row_${idx}`} className={rowClass}>
                              <td className="fw-bold text-dark ps-3 text-wrap">
                                <div>{item.itemName}</div>
                                {item.isOffline && (
                                  <span className="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle py-0.5 px-1.5 rounded mt-1 fw-semibold" style={{ fontSize: '10px' }}>
                                    ⏳ Sync Pending
                                  </span>
                                )}
                              </td>
                              <td>
                                <span className="badge bg-secondary text-white px-2 py-1 text-nowrap">
                                  {item.category === 'Materials' ? t('catMaterials') : item.category || t('catMaterials')}
                                </span>
                              </td>
                              <td className={`fw-bold text-nowrap ${isLowStock ? 'text-danger' : 'text-success'}`}>
                                {stockValue} {unitLabel}
                              </td>
                              <td className="text-muted fw-semibold text-nowrap">
                                {minLimit} {unitLabel}
                              </td>
                              <td>
                                {isLowStock ? (
                                  <span className="badge bg-danger text-white px-2 py-1 text-nowrap">⚠️ {t('badgeLowStock')}</span>
                                ) : (
                                  <span className="badge bg-success text-white px-2 py-1 text-nowrap">✅ {t('badgeHealthy')}</span>
                                )}
                              </td>

                              <td>
                                <span className="badge bg-light text-dark border fw-medium px-2 py-1 text-nowrap">
                                  👤 {displayCreatedBy}
                                </span>
                              </td>

                              <td>
                                <span className="badge bg-light text-secondary border fw-normal px-2 py-1 text-nowrap">
                                  ✍️ {displayUpdatedBy}
                                </span>
                              </td>

                              <td className="pe-3 text-center">
                                <button
                                  className="btn btn-danger btn-sm px-2 py-1 shadow-sm text-white fw-bold text-nowrap"
                                  onClick={() => handleInventoryDelete(item._id)}
                                  title={t('btnDeleteAsset')}
                                >
                                  <i className="bi bi-trash3-fill me-1"></i> {t('btnDelete')}
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* TAB 7: DOCUMENT & BLUEPRINT MANAGEMENT (0% ➡️ 100%) */}
          {activeTab === 'documents' && (() => {
            // 🛡️ Guard Clause: Labour ke liye table matrix aur metrics completely block
            if (userRole === 'Labour') {
              return (
                <div className="card border-0 shadow-sm p-4 text-center bg-white animate__animated animate__fadeIn">
                  <div className="py-4">
                    <i className="bi bi-shield-lock text-danger fs-1 mb-3"></i>
                    <h5 className="fw-bold text-dark">{t('vaultRestrictedTitle', 'Vault Access Restricted')}</h5>
                    <p className="text-muted small mx-auto mb-0" style={{ maxWidth: '480px' }}>
                      {t('vaultLabourMessage', 'Labour profiles are restricted from viewing architectural blueprints, CAD schemas, and secure project documents.')}
                    </p>
                  </div>
                </div>
              );
            }

            // Safe Array mapping to avoid system white-screen crashes
            const safeDocs = Array.isArray(documents) ? documents : [];

            return (
              <div className="animate__animated animate__fadeIn">
                {/* 📊 SUMMARY METRICS CARDS */}
                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <div className="p-3 bg-white border rounded shadow-sm d-flex align-items-center justify-content-between border-start border-info border-4">
                      <div className="text-start me-2">
                        <span className="text-muted fw-bold d-block text-uppercase small" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>{t('lblTotalActiveBlueprints')}</span>
                        <h3 className="fw-bold text-info mb-0 mt-1" style={{ fontSize: 'calc(1.2rem + 0.4vw)' }}>{safeDocs.length} {t('txtLiveFiles')}</h3>
                      </div>
                      <div className="bg-info-subtle p-2 rounded-3 text-info flex-shrink-0">
                        <i className="bi bi-folder-fill fs-3"></i>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 🚀 HEADER CONTROLS SECTION */}
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3 mb-3 p-3 bg-white border rounded-3 shadow-sm">
                  <h4 className="fw-bold text-dark mb-0" style={{ fontSize: 'calc(1.05rem + 0.3vw)' }}>
                    <i className="bi bi-file-earmark-pdf-fill text-info me-2"></i>{t('vaultHeaderTitle')}
                  </h4>
                  <button
                    type="button"
                    className="btn btn-info text-white fw-semibold btn-sm px-3 py-2 shadow-sm rounded-2 text-nowrap"
                    style={{
                      width: 'auto',
                      maxWidth: '220px',
                      minWidth: '150px',
                      whiteSpace: 'nowrap'
                    }}
                    onClick={() => {
                      setDocumentData({ title: '', category: 'Architectural', fileType: 'PDF', fileUrl: '' });
                      setShowDocumentModal(true);
                    }}
                  >
                    <i className="bi bi-cloud-arrow-up-fill me-1"></i> {t('btnUploadBlueprint')}
                  </button>
                </div>

                {/* 📋 DATA TABLE */}
                <div className="table-responsive bg-white border rounded-3 shadow-sm">
                  <table className="table table-hover align-middle mb-0 text-start" style={{ minWidth: '1200px' }}>
                    <thead className="table-info border-bottom text-secondary fw-bold">
                      <tr>
                        <th className="ps-3 py-3" style={{ width: '25%' }}>{t('thDocTitle')}</th>
                        <th style={{ width: '12%' }}>{t('thCategory')}</th>
                        <th style={{ width: '10%' }}>{t('thFormat')}</th>
                        <th style={{ width: '10%' }}>{t('thActiveVersion')}</th>
                        {/* Security Traceability Audit Columns */}
                        <th style={{ width: '13%' }}>{t('thCreatedBy')}</th>
                        <th style={{ width: '13%' }}>{t('thUpdatedBy')}</th>
                        <th className="pe-3 text-center" style={{ width: '17%' }}>{t('thActions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {safeDocs.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center text-muted py-5 bg-light-subtle">
                            <i className="bi bi-file-earmark-lock2 text-secondary fs-1 d-block mb-2"></i>
                            {t('txtNoBlueprintsFound')}
                          </td>
                        </tr>
                      ) : (
                        safeDocs.map((doc) => {
                          // Category Fallback translation logic
                          let categoryLabel = doc.category || "General";
                          if (doc.category === 'Architectural') categoryLabel = t('catArchitectural');
                          else if (!doc.category || doc.category === 'General') categoryLabel = t('catGeneral');

                          return (
                            <tr key={doc._id || Math.random()}>
                              <td className="fw-bold text-dark ps-3 text-wrap">
                                <div className="d-flex align-items-center">
                                  <span className="flex-shrink-0 me-2">
                                    {doc.fileType === 'PDF' && <i className="bi bi-file-earmark-pdf-fill text-danger fs-5"></i>}
                                    {doc.fileType === 'DWG' && <i className="bi bi-file-earmark-ruled-fill text-warning fs-5"></i>}
                                    {doc.fileType === 'Image' && <i className="bi bi-file-earmark-image-fill text-success fs-5"></i>}
                                    {!['PDF', 'DWG', 'Image'].includes(doc.fileType) && <i className="bi bi-file-earmark-text text-secondary fs-5"></i>}
                                  </span>
                                  <span className="text-break">{doc.title}</span>
                                </div>
                              </td>
                              <td><span className="badge bg-light text-dark border px-2 py-1 text-nowrap">{categoryLabel}</span></td>
                              <td>
                                <span className={`badge border px-2 py-1 text-nowrap ${doc.fileType === 'PDF' ? 'bg-danger-subtle text-danger border-danger-subtle' :
                                  doc.fileType === 'DWG' ? 'bg-warning-subtle text-warning border-warning-subtle' :
                                    'bg-success-subtle text-success border-success-subtle'
                                  }`}>
                                  {doc.fileType}
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-primary text-white rounded-pill px-2 py-1 text-nowrap">
                                  v{doc.currentVersion || 1}
                                </span>
                              </td>

                              {/* 🔐 Data Accountability Badges */}
                              <td>
                                <span className="badge bg-light text-dark border fw-medium px-2 py-1 text-nowrap">
                                  👤 {doc.createdBy ? t(`role${doc.createdBy}`) : (doc.uploadedBy || t('roleUnknown'))}
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-light text-secondary border fw-normal px-2 py-1 text-nowrap">
                                  ✍️ {doc.updatedBy ? t(`role${doc.updatedBy}`) : t('txtNoEdits')}
                                </span>
                              </td>

                              <td className="pe-3 text-center">
                                <div className="d-flex gap-2 justify-content-center flex-nowrap">
                                  <a href={doc.fileUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm px-2 fw-semibold rounded-2 text-nowrap">
                                    <i className="bi bi-eye-fill"></i> {t('btnView')}
                                  </a>

                                  <button
                                    type="button"
                                    className="btn btn-outline-warning btn-sm px-2 fw-semibold rounded-2 text-nowrap"
                                    onClick={() => {
                                      setSelectedDocForRevision(doc._id);
                                      setRevisionUrl('');
                                      setShowRevisionModal(true);
                                    }}
                                    title={t('btnTitleReviseSheet')}
                                  >
                                    <i className="bi bi-arrow-up-right-circle"></i> {t('btnRevise')}
                                  </button>

                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm px-2 rounded-2 flex-shrink-0"
                                    onClick={() => handleDocumentDelete(doc._id)}
                                  >
                                    <i className="bi bi-trash3-fill"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* TAB 8: PROCUREMENT MANAGEMENT (0% ➡️ 100%) */}
          {activeTab === 'procurement' && (() => {
            // 🛡️ SECURITY LAYER: Labour profile ke liye tabs, navigation aur financial numbers complete hide
            if (userRole === 'Labour') {
              return (
                <div className="card border-0 shadow-sm p-4 text-center bg-white animate__animated animate__fadeIn">
                  <div className="py-4">
                    <i className="bi bi-shield-slash-fill text-danger fs-1 mb-3"></i>
                    <h5 className="fw-bold text-dark">{t('procureRestrictedTitle', 'Procurement Access Restricted')}</h5>
                    <p className="text-muted small mx-auto mb-0" style={{ maxWidth: '490px' }}>
                      {t('procureLabourMessage', 'Labour accounts are restricted from accessing financial comparison matrices, material indents, or purchasing records.')}
                    </p>
                  </div>
                </div>
              );
            }

            // Safe Array checks to protect application runtime execution
            const safeReqs = Array.isArray(requisitions) ? requisitions : [];
            const safeMatrix = Array.isArray(comparisonMatrix) ? comparisonMatrix : [];
            const safePO = Array.isArray(purchaseOrders) ? purchaseOrders : [];

            return (
              <div className="animate__animated animate__fadeIn">

                {/* 🚀 SUB-TABS NAVIGATION CONTROLS */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-3 mb-4 p-2 bg-white border rounded shadow-sm">
                  <div className="nav nav-pills d-flex flex-wrap gap-2 w-100 w-md-auto">
                    <button
                      className={`btn btn-sm px-3 fw-bold flex-grow-1 flex-md-grow-0 text-nowrap ${activeProcureTab === 'requisitions' ? 'btn-info text-white' : 'btn-light text-secondary'}`}
                      onClick={() => setActiveProcureTab('requisitions')}
                    >
                      <i className="bi bi-cart-plus-fill me-1"></i> {t('subTabSiteIndents')}
                    </button>
                    <button
                      className={`btn btn-sm px-3 fw-bold flex-grow-1 flex-md-grow-0 text-nowrap ${activeProcureTab === 'matrix' ? 'btn-info text-white' : 'btn-light text-secondary'}`}
                      onClick={() => setActiveProcureTab('matrix')}
                      disabled={!selectedReqId}
                    >
                      <i className="bi bi-bezier2 me-1"></i> {t('subTabComparisonMatrix')}
                    </button>
                    <button
                      className={`btn btn-sm px-3 fw-bold flex-grow-1 flex-md-grow-0 text-nowrap ${activeProcureTab === 'po' ? 'btn-info text-white' : 'btn-light text-secondary'}`}
                      onClick={() => setActiveProcureTab('po')}
                    >
                      <i className="bi bi-file-earmark-check-fill me-1"></i> {t('subTabPoAudit')}
                    </button>
                  </div>

                  {activeProcureTab === 'requisitions' && (
                    <button
                      type="button"
                      className="btn btn-info text-white btn-sm fw-semibold rounded shadow-sm text-nowrap"
                      style={{
                        width: '100%',            // Mobile par poori width lega
                        maxWidth: 'max-content',   // Text jitna lamba hai, utni hi width lega
                        paddingLeft: '1.5rem',     // Text cutne se bachane ke liye extra space
                        paddingRight: '1.5rem',
                        minHeight: '38px'          // Button height consistency ke liye
                      }}
                      onClick={() => setShowReqModal(true)}
                    >
                      <i className="bi bi-plus-circle-fill me-2"></i> {t('btnCreateMaterialIndent')}
                    </button>
                  )}
                </div>

                {/* 🛒 PANEL 1: SITE MATERIAL INDENTS */}
                {activeProcureTab === 'requisitions' && (
                  <div className="table-responsive bg-white border rounded shadow-sm">
                    <table className="table table-hover align-middle text-start mb-0" style={{ minWidth: '1100px' }}>
                      <thead className="table-warning text-secondary fw-bold">
                        <tr>
                          <th className="ps-3 py-3" style={{ width: '22%' }}>{t('thRequestedItem')}</th>
                          <th style={{ width: '13%' }}>{t('thQuantityRequired')}</th>
                          <th style={{ width: '15%' }}>{t('thEstimatedBudget')}</th>
                          <th style={{ width: '12%' }}>{t('thStatus')}</th>
                          {/* Security Trackability Headers */}
                          <th style={{ width: '13%' }}>{t('thCreatedBy')}</th>
                          <th style={{ width: '13%' }}>{t('thUpdatedBy')}</th>
                          <th className="text-center pe-3" style={{ width: '12%' }}>{t('thActions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {safeReqs.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center text-muted py-5 bg-light-subtle">
                              <i className="bi bi-basket3 fs-2 text-secondary d-block mb-2"></i>
                              {t('txtNoIndentsLogged')} <strong>{t('btnCreateMaterialIndent')}</strong> {t('txtToRequestSupplies')}
                            </td>
                          </tr>
                        ) : (
                          safeReqs.map((req) => (
                            <tr key={req._id}>
                              <td className="fw-bold text-dark ps-3 text-wrap">
                                <div className="d-flex align-items-center">
                                  <i className="bi bi-box-seam text-info me-2 fs-5 flex-shrink-0"></i>
                                  <span className="text-break">{req.items?.[0]?.materialName || t('txtUnknownMaterial')}</span>
                                </div>
                              </td>
                              <td>
                                <span className="badge bg-light text-dark border px-2 py-1 text-nowrap">
                                  {req.items?.[0]?.quantity || 0} {req.items?.[0]?.unit || ''}
                                </span>
                              </td>
                              <td className="fw-bold text-success text-nowrap">{(req.estimatedBudget || 0).toLocaleString()} PKR</td>
                              <td>
                                <span className={`badge px-2 py-1 text-nowrap ${req.status === 'Pending' ? 'bg-warning-subtle text-warning' : 'bg-success-subtle text-success'}`}>
                                  {req.status === 'Pending' ? t('statusPending') : (req.status || t('statusApproved'))}
                                </span>
                              </td>
                              {/* Security Trace Data Injection */}
                              <td>
                                <span className="badge bg-light text-dark border fw-medium px-2 py-1 text-nowrap">
                                  👤 {req.createdBy ? t(`role${req.createdBy}`) : t('roleAdmin')}
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-light text-secondary border fw-normal px-2 py-1 text-nowrap">
                                  ✍️ {req.updatedBy ? t(`role${req.updatedBy}`) : t('txtNoEdits')}
                                </span>
                              </td>
                              <td className="text-center pe-3">
                                <button className="btn btn-outline-info btn-sm px-3 fw-bold rounded text-nowrap" onClick={() => { fetchMatrix(req._id); setActiveProcureTab('matrix'); }}>
                                  <i className="bi bi-eye-fill me-1"></i> {t('btnRunVendorRfp')}
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* 📊 PANEL 2: AUTOMATED COMPARISON MATRIX (L1 ENGINE) */}
                {activeProcureTab === 'matrix' && (
                  <div className="bg-white border rounded shadow-sm overflow-hidden p-3">
                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3 mb-3">
                      <h5 className="fw-bold text-dark mb-0" style={{ fontSize: 'calc(1.05rem + 0.2vw)' }}><i className="bi bi-calculator-fill text-info me-2"></i>{t('matrixHeaderTitle')}</h5>
                      <button
                        type="button"
                        className="btn btn-warning btn-sm fw-bold shadow-sm text-dark py-2"
                        style={{
                          width: 'auto',          // Content ke hisab se width adjust hogi
                          minWidth: 'fit-content', // Text ka size chhota nahi hone dega
                          padding: '0.5rem 1.25rem' // Standard padding for better look
                        }}
                        onClick={() => setShowQuoteModal(true)}
                      >
                        <i className="bi bi-telephone-plus me-2"></i> {t('btnLogVendorBid')}
                      </button>
                    </div>

                    <div className="table-responsive">
                      <table className="table table-bordered align-middle text-start mb-0" style={{ minWidth: '1100px' }}>
                        <thead className="table-info fw-bold text-secondary">
                          <tr>
                            <th style={{ width: '22%' }}>{t('thVendorEntity')}</th>
                            <th style={{ width: '15%' }}>{t('thTotalQuotedPrice')}</th>
                            <th style={{ width: '12%' }}>{t('thDeliverySpeed')}</th>
                            <th style={{ width: '15%' }}>{t('thL1MatrixTag')}</th>
                            {/* Security Traceability Logs */}
                            <th style={{ width: '12%' }}>{t('thLogBy')}</th>
                            <th style={{ width: '12%' }}>{t('thEditBy')}</th>
                            <th className="text-center" style={{ width: '12%' }}> {t('thCommercialAction')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {safeMatrix.length === 0 ? (
                            <tr>
                              <td colSpan="7" className="text-center text-muted py-4">
                                {t('txtNoQuotationsFed')} <strong>{t('btnLogVendorBid')}</strong>
                              </td>
                            </tr>
                          ) : (
                            safeMatrix.map((matrix, idx) => (
                              <tr key={matrix._id} className={idx === 0 ? 'table-success bg-opacity-25' : ''}>
                                <td className="fw-bold text-dark text-wrap">
                                  <div className="d-flex flex-column">
                                    <span className="text-break"><i className="bi bi-building me-2 text-secondary"></i>{matrix.vendorName}</span>
                                    <small className="text-muted text-xs mt-1 ps-4">{matrix.vendorPhone}</small>
                                  </div>
                                </td>
                                <td className="fw-bold text-primary text-nowrap">{(matrix.totalQuotedAmount || 0).toLocaleString()} PKR</td>
                                <td>
                                  <span className="badge bg-secondary px-2 py-1 text-nowrap">
                                    {matrix.deliveryLeadTimeDays} {t('txtDaysLine')}
                                  </span>
                                </td>
                                <td>
                                  {idx === 0 ? (
                                    <span className="badge bg-success text-white rounded-pill shadow-sm d-inline-block animate__animated animate__pulse animate__infinite text-nowrap">
                                      ★ {t('tagWinnerL1')}
                                    </span>
                                  ) : <span className="text-muted small text-nowrap">{t('tagStandardBid')}</span>}
                                </td>
                                {/* Bid Trace Fields */}
                                <td>
                                  <span className="badge bg-light text-dark border fw-medium px-2 py-1 text-nowrap">
                                    👤 {matrix.loggedBy ? t(`role${matrix.loggedBy}`) : t('roleSupervisor')}
                                  </span>
                                </td>
                                <td>
                                  <span className="badge bg-light text-secondary border fw-normal px-2 py-1 text-nowrap">
                                    ✍️ {matrix.lastEditedBy ? t(`role${matrix.lastEditedBy}`) : t('txtNoEdits')}
                                  </span>
                                </td>
                                <td className="text-center">
                                  <button className="btn btn-sm btn-dark px-3 rounded fw-bold text-nowrap" onClick={() => handleGeneratePO(matrix._id)}>
                                    {t('btnIssuePo')}
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* 📄 PANEL 3: PURCHASE ORDER & 3-WAY AUDIT LEDGER */}
                {activeProcureTab === 'po' && (
                  <div className="bg-white border rounded shadow-sm overflow-hidden">
                    <div className="p-3 border-bottom bg-light">
                      <h5 className="fw-bold text-dark mb-0"><i className="bi bi-shield-check text-success me-2"></i>{t('auditHeaderTitle')}</h5>
                    </div>
                    <div className="p-3 bg-light-subtle border-bottom">
                      <p className="text-muted small mb-1">{t('auditDescLine')}</p>
                      <div className="alert alert-warning p-2 small mb-0">
                        <i className="bi bi-exclamation-triangle-fill me-1"></i> {t('alertAuditMismatch')}
                      </div>
                    </div>

                    <div className="table-responsive">
                      <table className="table table-hover align-middle text-start mb-0" style={{ minWidth: '1150px' }}>
                        <thead className="table-light fw-bold text-secondary">
                          <tr>
                            <th className="ps-3 py-3" style={{ width: '20%' }}>{t('thVendorEntity')}</th>
                            <th style={{ width: '13%' }}>{t('thTotalValue')}</th>
                            <th style={{ width: '22%' }}>{t('thComplianceFlags')}</th>
                            <th style={{ width: '13%' }}>{t('thStatus')}</th>
                            {/* PO Dynamic Security Columns */}
                            <th style={{ width: '11%' }}>{t('thIssuedBy')}</th>
                            <th style={{ width: '11%' }}>{t('thCanceledBy')}</th>
                            <th className="text-center pe-3" style={{ width: '10%' }}>{t('thAction')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {safePO.length === 0 ? (
                            <tr>
                              <td colSpan="7" className="text-center text-muted py-5">
                                <i className="bi bi-folder-x fs-2 d-block mb-2"></i>
                                {t('txtNoPoGenerated')}
                              </td>
                            </tr>
                          ) : (
                            safePO.map((poItem) => (
                              <tr key={poItem._id}>
                                <td className="fw-bold text-dark ps-3 text-wrap">
                                  <div className="d-flex align-items-center">
                                    <i className="bi bi-file-earmark-text text-primary me-2 fs-5 flex-shrink-0"></i>
                                    <span className="text-break">{poItem.vendorName}</span>
                                  </div>
                                </td>
                                <td className="fw-bold text-dark text-nowrap">{(poItem.totalAmount || 0).toLocaleString()} PKR</td>
                                <td>
                                  <div className="d-flex gap-1 flex-wrap">
                                    <span className={`badge border text-nowrap ${poItem.threeWayMatch?.grnQuantityMatched ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                                      {t('lblGrnQuantity')}: {poItem.threeWayMatch?.grnQuantityMatched ? t('flagMatched') : t('flagUnmatched')}
                                    </span>
                                    <span className={`badge border text-nowrap ${poItem.threeWayMatch?.invoiceAmountMatched ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                                      {t('lblInvoiceAmount')}: {poItem.threeWayMatch?.invoiceAmountMatched ? t('flagMatched') : t('flagUnmatched')}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <span className={`badge px-2 py-1 text-nowrap ${poItem.threeWayMatch?.isBillBlocked ? 'bg-danger text-white' : 'bg-success text-white'}`}>
                                    {poItem.threeWayMatch?.isBillBlocked ? `🔒 ${t('statusBillBlocked')}` : `🔓 ${t('statusApprovePay')}`}
                                  </span>
                                </td>
                                {/* Security Integrity Rendering */}
                                <td>
                                  <span className="badge bg-light text-dark border fw-medium px-2 py-1 text-nowrap">
                                    👤 {poItem.issuedBy ? t(`role${poItem.issuedBy}`) : t('roleSiteManager')}
                                  </span>
                                </td>
                                <td>
                                  <span className="badge bg-light text-muted border fw-light px-2 py-1 text-nowrap">
                                    ❌ {poItem.deletedBy ? t(`role${poItem.deletedBy}`) : t('txtActivePo')}
                                  </span>
                                </td>
                                <td className="text-center pe-3">
                                  <button
                                    className="btn btn-sm btn-success fw-bold px-3 shadow-sm text-nowrap"
                                    onClick={() => { setSelectedPo(poItem); setShowMatchModal(true); }}
                                  >
                                    <i className="bi bi-patch-check-fill me-1"></i> {t('btnAuditPoMatch')}
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

              </div>
            );
          })()}


          {/* TAB 9: SAFETY & COMPLIANCE MANAGEMENT (HSE) */}
          {activeTab === 'safety' && (() => {
            // 🛡️ SECURITY LAYER: Labour profile ke liye poora component data aur actions block
            if (userRole === 'Labour') {
              return (
                <div className="card border-0 shadow-sm p-4 text-center bg-white animate__animated animate__fadeIn">
                  <div className="py-4">
                    <i className="bi bi-shield-exclamation text-danger fs-1 mb-3"></i>
                    <h5 className="fw-bold text-dark">{t('safetyRestrictedTitle', 'HSE Portal Restricted')}</h5>
                    <p className="text-muted small mx-auto mb-0" style={{ maxWidth: '460px' }}>
                      {t('safetyLabourMessage', 'Labour accounts are restricted from accessing site safety audits, incident analysis logs, or monetary fine records.')}
                    </p>
                  </div>
                </div>
              );
            }

            return (
              <div className="animate__animated animate__fadeIn">
                {/* 🔄 LAYER 1: LOADING SPINNER CHECK */}
                {safetyLoading ? (
                  <div className="container mt-4 text-center p-5 bg-white border rounded-3 shadow-sm">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">{t('loadingHseDashboard')}</span>
                    </div>
                    <p className="mt-2 text-muted fw-semibold">{t('loadingSafetyRoom')}</p>
                  </div>
                ) : (
                  /* ✅ LAYER 2: ASAL UI JAB DATA LOAD HO JAYE */
                  <>
                    {/* 📊 SUMMARY METRICS CARDS */}
                    <div className="row g-3 mb-4">
                      <div className="col-12 col-md-4">
                        <div className="p-3 bg-white border rounded shadow-sm d-flex align-items-center justify-content-between border-start border-primary border-4">
                          <div className="text-start">
                            <span className="text-muted fw-bold d-block text-uppercase small">{t('cardTbtAttendance')}</span>
                            <h3 className="fw-bold text-primary mb-0">
                              {safetyData?.toolboxTalk?.attendanceCount || 0} {t('unitWorkers')}
                            </h3>
                          </div>
                          <div className="bg-primary-subtle p-2 rounded-3 text-primary flex-shrink-0">
                            <i className="bi bi-people-fill fs-3"></i>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-md-4">
                        <div className="p-3 bg-white border rounded shadow-sm d-flex align-items-center justify-content-between border-start border-danger border-4">
                          <div className="text-start">
                            <span className="text-muted fw-bold d-block text-uppercase small">{t('cardIncidentsReported')}</span>
                            <h3 className="fw-bold text-danger mb-0">
                              {safetyData?.incidentReports?.length || 0} {t('unitCases')}
                            </h3>
                          </div>
                          <div className="bg-danger-subtle p-2 rounded-3 text-danger flex-shrink-0">
                            <i className="bi bi-shield-slash-fill fs-3"></i>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-md-4">
                        <div className="p-3 bg-white border rounded shadow-sm d-flex align-items-center justify-content-between border-start border-warning border-4">
                          <div className="text-start">
                            <span className="text-muted fw-bold d-block text-uppercase small">{t('cardTotalFines')}</span>
                            <h3 className="fw-bold text-warning mb-0">
                              {t('currencyRs')} {(safetyData?.safetyFines?.reduce((acc, curr) => acc + (curr.fineAmount || 0), 0) || 0).toLocaleString()}
                            </h3>
                          </div>
                          <div className="bg-warning-subtle p-2 rounded-3 text-warning flex-shrink-0">
                            <i className="bi bi-cash-coin fs-3"></i>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 🚀 HEADER CONTROLS SECTION */}
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4 p-3 bg-white border rounded-3 shadow-sm">
                      <h4 className="fw-bold text-dark mb-0 text-wrap text-break">
                        <i className="bi bi-shield-check text-primary me-2"></i>{t('hsePortalTitle')}
                      </h4>

                      {/* 🛡️ Strict Role Guards: Client view-only accounts cannot trigger action modals */}
                      {userRole !== 'Client' && (
                        <div className="d-flex flex-wrap gap-2 w-100 w-md-auto">
                          <button onClick={() => setShowInspectionModal(true)} className="btn btn-outline-primary btn-sm fw-semibold rounded-2 flex-grow-1 flex-md-grow-0 text-nowrap">{t('btnLogInspection')}</button>
                          <button onClick={() => setShowPermitModal(true)} className="btn btn-outline-warning btn-sm fw-semibold text-dark rounded-2 flex-grow-1 flex-md-grow-0 text-nowrap">{t('btnApplyPermit')}</button>
                          <button onClick={() => setShowIncidentModal(true)} className="btn btn-outline-danger btn-sm fw-semibold rounded-2 flex-grow-1 flex-md-grow-0 text-nowrap">🚨 {t('btnReportIncident')}</button>
                          <button onClick={() => setShowFineModal(true)} className="btn btn-outline-secondary btn-sm fw-semibold rounded-2 flex-grow-1 flex-md-grow-0 text-nowrap">{t('btnIssueFine')}</button>
                        </div>
                      )}
                    </div>

                    {/* BOOTSTRAP INTERNAL NAV TABS */}
                    <ul className="nav nav-tabs mb-4 bg-white p-2 rounded border shadow-sm d-flex flex-wrap gap-1">
                      <li className="nav-item flex-grow-1 flex-sm-grow-0">
                        <button onClick={() => setSafetyActiveTab('dashboard')} className={`nav-link w-100 fw-medium border-0 text-nowrap ${safetyActiveTab === 'dashboard' ? 'active text-primary fw-bold border-bottom border-primary border-3' : 'text-secondary'}`}>{t('tabDashboard')}</button>
                      </li>
                      <li className="nav-item flex-grow-1 flex-sm-grow-0">
                        <button onClick={() => setSafetyActiveTab('permits')} className={`nav-link w-100 fw-medium border-0 text-nowrap ${safetyActiveTab === 'permits' ? 'active text-primary fw-bold border-bottom border-primary border-3' : 'text-secondary'}`}>{t('tabPermits')}</button>
                      </li>
                      <li className="nav-item flex-grow-1 flex-sm-grow-0">
                        <button onClick={() => setSafetyActiveTab('incidents')} className={`nav-link w-100 fw-medium border-0 text-nowrap ${safetyActiveTab === 'incidents' ? 'active text-primary fw-bold border-bottom border-primary border-3' : 'text-secondary'}`}>{t('tabIncidents')}</button>
                      </li>
                      <li className="nav-item flex-grow-1 flex-sm-grow-0">
                        <button onClick={() => setSafetyActiveTab('fines')} className={`nav-link w-100 fw-medium border-0 text-nowrap ${safetyActiveTab === 'fines' ? 'active text-primary fw-bold border-bottom border-primary border-3' : 'text-secondary'}`}>{t('tabFines')}</button>
                      </li>
                    </ul>

                    {/* DYNAMIC VIEWS INSIDE SAFETY PORTAL */}
                    {safetyActiveTab === 'dashboard' && (
                      <div className="row g-4 text-start">
                        <div className="col-12 col-md-6">
                          <div className="card h-100 shadow-sm border-0 bg-white border border-light">
                            <div className="card-header bg-transparent fw-bold text-secondary border-bottom p-3 text-nowrap">{t('hdrInspectionLogs')}</div>
                            <div className="card-body p-0">
                              <ul className="list-group list-group-flush rounded-0">
                                <li className="list-group-item d-flex justify-content-between align-items-center py-3 gap-2">
                                  <span className="text-wrap">{t('chkHelmetsBoots')}</span>
                                  <span className={`badge px-2 py-1 text-nowrap flex-shrink-0 ${safetyData?.inspectionChecklist?.hasHelmetsAndBoots ? "bg-success text-white" : "bg-danger text-white"}`}>
                                    {safetyData?.inspectionChecklist?.hasHelmetsAndBoots ? t('statusCleared') : t('statusDeficient')}
                                  </span>
                                </li>
                                <li className="list-group-item d-flex justify-content-between align-items-center py-3 gap-2">
                                  <span className="text-wrap">{t('chkScaffolding')}</span>
                                  <span className={`badge px-2 py-1 text-nowrap flex-shrink-0 ${safetyData?.inspectionChecklist?.isScaffoldingSecure ? "bg-success text-white" : "bg-danger text-white"}`}>
                                    {safetyData?.inspectionChecklist?.isScaffoldingSecure ? t('statusSecure') : t('statusHazardous')}
                                  </span>
                                </li>
                                <li className="list-group-item d-flex justify-content-between align-items-center py-3 gap-2">
                                  <span className="text-wrap">{t('chkExtinguisher')}</span>
                                  <span className={`badge px-2 py-1 text-nowrap flex-shrink-0 ${safetyData?.inspectionChecklist?.isFireExtinguisherPresent ? "bg-success text-white" : "bg-danger text-white"}`}>
                                    {safetyData?.inspectionChecklist?.isFireExtinguisherPresent ? t('statusPresent') : t('statusMissing')}
                                  </span>
                                </li>
                                <li className="list-group-item d-flex justify-content-between align-items-center py-3 gap-2">
                                  <span className="text-wrap">{t('chkSignage')}</span>
                                  <span className={`badge px-2 py-1 text-nowrap flex-shrink-0 ${safetyData?.inspectionChecklist?.isSignageVisible ? "bg-success text-white" : "bg-danger text-white"}`}>
                                    {safetyData?.inspectionChecklist?.isSignageVisible ? t('statusVisible') : t('statusPoor')}
                                  </span>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                        <div className="col-12 col-md-6">
                          <div className="card h-100 shadow-sm border-0 bg-white">
                            <div className="card-body d-flex flex-column justify-content-between p-4">
                              <div>
                                <h5 className="fw-bold text-secondary card-title mb-3 text-wrap">{t('hdrTbtSummary')}</h5>
                                <p className="card-text border-bottom pb-2 text-wrap"><strong>{t('lblTopicHandled')}</strong> {safetyData?.toolboxTalk?.topic || t('noBriefingLogged')}</p>
                                <p className="card-text text-wrap"><strong>{t('lblTotalHeadcount')}</strong> {safetyData?.toolboxTalk?.attendanceCount || 0} {t('workersBriefed')}</p>
                              </div>
                              {safetyData?.toolboxTalk?.tbtImageUrl && (
                                <img src={safetyData.toolboxTalk.tbtImageUrl} alt="TBT Proof" className="img-fluid rounded shadow-sm w-100 object-cover mt-2" style={{ maxHeight: '140px' }} />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {safetyActiveTab === 'permits' && (
                      <div className="card shadow-sm border-0 bg-white rounded-3">
                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0 text-start" style={{ minWidth: '1100px' }}>
                            <thead className="table-light text-secondary text-uppercase small fw-bold">
                              <tr>
                                <th className="ps-3 py-3" style={{ width: '20%' }}>{t('thPermitType')}</th>
                                <th style={{ width: '13%' }}>{t('thApplicant')}</th>
                                <th style={{ width: '15%' }}>{t('thValidUntil')}</th>
                                <th style={{ width: '12%' }}>{t('thPreCheckStatus')}</th>
                                <th style={{ width: '10%' }}>{t('thStatus')}</th>
                                {/* Security Trace Columns */}
                                <th style={{ width: '10%' }}>{t('thCreatedBy')}</th>
                                <th style={{ width: '10%' }}>{t('thUpdatedBy')}</th>
                                {userRole !== 'Client' && <th className="pe-3 text-center" style={{ width: '10%' }}>{t('thActionControl')}</th>}
                              </tr>
                            </thead>
                            <tbody className="small">
                              {!safetyData?.workPermits || safetyData.workPermits.length === 0 ? (
                                <tr>
                                  <td colSpan={userRole === 'Client' ? "7" : "8"} className="text-center text-muted py-4">{t('noActivePermits')}</td>
                                </tr>
                              ) : (
                                safetyData.workPermits.map((p) => (
                                  <tr key={p._id}>
                                    <td className="fw-bold text-dark ps-3 text-wrap text-break">{p.permitType}</td>
                                    <td className="text-wrap text-break">{p.requestedBy}</td>
                                    <td className="text-nowrap">{new Date(p.validTill).toLocaleString()}</td>
                                    <td>
                                      <span className={`fw-semibold text-nowrap text-${p.safetyChecklistPassed ? "success" : "danger"}`}>
                                        {p.safetyChecklistPassed ? `¼ ${t('chkPassed')}` : `× ${t('chkFailed')}`}
                                      </span>
                                    </td>
                                    <td>
                                      <span className={`badge px-2 py-1 text-nowrap ${p.status === 'Approved' ? 'bg-success text-white' : p.status === 'Pending Approval' ? 'bg-warning text-dark' : 'bg-secondary text-white'}`}>
                                        {p.status}
                                      </span>
                                    </td>

                                    {/* 🔐 Created By Audit Badge */}
                                    <td>
                                      <span className="badge bg-light text-dark border fw-medium px-2 py-1 text-nowrap">
                                        👤 {p.createdBy ? t(`role${p.createdBy}`) : t('roleSupervisor')}
                                      </span>
                                    </td>

                                    {/* 🔐 Updated By Audit Badge */}
                                    <td>
                                      <span className="badge bg-light text-secondary border fw-normal px-2 py-1 text-nowrap">
                                        ✍️ {p.updatedBy ? t(`role${p.updatedBy}`) : t('txtNoEdits')}
                                      </span>
                                    </td>

                                    {userRole !== 'Client' && (
                                      <td className="pe-3 text-center">
                                        {p.status === 'Pending Approval' && <button onClick={() => handlePermitStatusUpdate(p._id, 'Approved')} className="btn btn-sm btn-success py-1 px-2 fw-semibold rounded-2 text-nowrap">{t('btnApprove')}</button>}
                                        {p.status === 'Approved' && <button onClick={() => handlePermitStatusUpdate(p._id, 'Closed')} className="btn btn-sm btn-dark py-1 px-2 fw-semibold rounded-2 text-nowrap">{t('btnCloseWork')}</button>}
                                      </td>
                                    )}
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {safetyActiveTab === 'incidents' && (
                      <div className="row g-3 text-start">
                        {!safetyData?.incidentReports || safetyData.incidentReports.length === 0 ? (
                          <div className="col-12 text-center text-muted py-4 bg-white border rounded-3 shadow-sm">{t('noIncidentsLogged')}</div>
                        ) : (
                          safetyData.incidentReports.map((i) => (
                            <div className="col-12 col-md-6" key={i._id}>
                              <div className="card h-100 border-start border-danger border-4 p-3 bg-white shadow-sm border-0">
                                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start gap-2">
                                  <div className="flex-grow-1 w-100">
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                                      <span className="badge bg-danger text-white text-nowrap">{i.incidentType}</span>

                                      {/* 🔐 Audit Stamp: Incident Reporter Track */}
                                      <span className="text-muted border rounded px-2 py-0.5 bg-light fw-medium" style={{ fontSize: '10px' }}>
                                        👤 {i.reportedBy ? t(`role${i.reportedBy}`) : t('roleSiteManager')}
                                      </span>
                                    </div>
                                    <h5 className="fw-bold text-dark m-0 text-wrap text-break">{i.title}</h5>
                                    <small className="text-muted d-block my-1 text-wrap text-break"><strong>{t('lblLocationPoint')}</strong> {i.location}</small>
                                    <p className="small text-secondary card-text my-2 text-wrap text-break">{i.description}</p>
                                    <div className="bg-success-subtle p-2 rounded text-success small border border-success border-opacity-25 mt-2 text-wrap text-break">
                                      <strong>{t('lblCapaAction')}</strong> {i.actionTaken}
                                    </div>
                                  </div>
                                  {i.imageUrl && (
                                    <img src={i.imageUrl} alt="Accident Proof" className="rounded border shadow-sm mx-auto mx-sm-0 flex-shrink-0 mt-2 mt-sm-0" style={{ width: '85px', height: '85px', objectFit: 'cover' }} />
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}

                    {safetyActiveTab === 'fines' && (
                      <div className="card shadow-sm border-0 bg-white rounded-3">
                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0 text-start" style={{ minWidth: '1000px' }}>
                            <thead className="table-light text-secondary text-uppercase small fw-bold">
                              <tr>
                                <th className="ps-3 py-3" style={{ width: '20%' }}>{t('thSubcontractorTarget')}</th>
                                <th style={{ width: '25%' }}>{t('thViolationCode')}</th>
                                <th style={{ width: '15%' }}>{t('thFineAmount')}</th>
                                <th style={{ width: '15%' }}>{t('thLedgerTrigger')}</th>
                                {/* Security Columns for Penalties Liability */}
                                <th style={{ width: '12%' }}>{t('thIssuedBy')}</th>
                                <th style={{ width: '13%' }}>{t('thCanceledBy')}</th>
                              </tr>
                            </thead>
                            <tbody className="small">
                              {!safetyData?.safetyFines || safetyData.safetyFines.length === 0 ? (
                                <tr>
                                  <td colSpan="6" className="text-center text-muted py-4">{t('noPenaltiesRecorded')}</td>
                                </tr>
                              ) : (
                                safetyData.safetyFines.map((f) => (
                                  <tr key={f._id}>
                                    <td className="fw-bold text-dark ps-3 text-wrap text-break">{f.subcontractorName}</td>
                                    <td className="text-danger fw-semibold text-wrap text-break">{f.violationReason}</td>
                                    <td className="fw-bold text-dark text-nowrap">{t('currencyRs')} {f.fineAmount.toLocaleString()}</td>
                                    <td>
                                      <span className="badge border px-2 py-1 text-dark text-nowrap" style={{ backgroundColor: '#e0cffc', borderColor: '#c2a6f9' }}>¼ {t('txtAutoDeducted')}</span>
                                    </td>

                                    {/* 🔐 Issued By Penalty Badge */}
                                    <td>
                                      <span className="badge bg-light text-dark border fw-medium px-2 py-1 text-nowrap">
                                        👤 {f.issuedBy ? t(`role${f.issuedBy}`) : t('roleAdmin')}
                                      </span>
                                    </td>

                                    {/* 🔐 Void Track Fine Badge */}
                                    <td>
                                      <span className="badge bg-light text-muted border fw-light px-2 py-1 text-nowrap">
                                        ❌ {f.voidedBy ? t(`role${f.voidedBy}`) : t('txtActiveFine')}
                                      </span>
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

          {/* TAB 10: SAFETY & COMPLIANCE MANAGEMENT (HSE) */}
          {/* Function wrap aur arrow (() => { return ( ko yahan se hata diya hai */}
          {activeTab === 'assets' && userRole !== 'Laboure' && (
            <div className="animate__animated animate__fadeIn">
              {/* 📊 SUMMARY METRICS CARDS GRID LAYER */}
              <div className="row g-3 mb-4">
                <div className="col-12 col-sm-6 col-md-3">
                  <div className="p-3 bg-white border rounded shadow-sm d-flex align-items-center justify-content-between border-start border-primary border-4 h-100">
                    <div className="text-start">
                      <span className="text-muted fw-bold d-block text-uppercase small">{t('cardTotalFleetAssets')}</span>
                      <h3 className="fw-bold text-primary mb-0">{assets.length} {t('unitUnits')}</h3>
                    </div>
                    <div className="bg-primary-subtle p-2 rounded-3 text-primary flex-shrink-0"><i className="bi bi-truck fs-3"></i></div>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <div className="p-3 bg-white border rounded shadow-sm d-flex align-items-center justify-content-between border-start border-success border-4 h-100">
                    <div className="text-start">
                      <span className="text-muted fw-bold d-block text-uppercase small">{t('cardActiveMachinery')}</span>
                      <h3 className="fw-bold text-success mb-0">{assets.filter(a => a.status === 'Active').length} {t('unitLive')}</h3>
                    </div>
                    <div className="bg-success-subtle p-2 rounded-3 text-success flex-shrink-0"><i className="bi bi-play-circle-fill fs-3"></i></div>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <div className="p-3 bg-white border rounded shadow-sm d-flex align-items-center justify-content-between border-start border-danger border-4 h-100">
                    <div className="text-start">
                      <span className="text-muted fw-bold d-block text-uppercase small">{t('cardBreakdownTickets')}</span>
                      <h3 className="fw-bold text-danger mb-0">{assets.filter(a => a.status === 'Breakdown').length} {t('unitUnits')}</h3>
                    </div>
                    <div className="bg-danger-subtle p-2 rounded-3 text-danger flex-shrink-0"><i className="bi bi-exclamation-triangle-fill fs-3"></i></div>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <div className="p-3 bg-white border rounded shadow-sm d-flex align-items-center justify-content-between border-start border-warning border-4 h-100">
                    <div className="text-start">
                      <span className="text-muted fw-bold d-block text-uppercase small">{t('cardGeofenceViolations')}</span>
                      <h3 className="fw-bold text-warning mb-0">{assets.filter(a => a.geofence?.isViolated).length} {t('unitAlerts')}</h3>
                    </div>
                    <div className="bg-warning-subtle p-2 rounded-3 text-warning flex-shrink-0"><i className="bi bi-geo-fill fs-3"></i></div>
                  </div>
                </div>
              </div>

              {/* 🚀 ACTION HEADER CONTROL BAR */}
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4 p-3 bg-white border rounded-3 shadow-sm">
                <h4 className="fw-bold text-dark mb-0 text-wrap text-break">
                  <i className="bi bi-cpu text-primary me-2"></i>{t('assetsRegistryTitle')}
                </h4>
                <div className="d-flex flex-wrap gap-2 w-100 w-md-auto">
                  {(userRole === 'Admin' || userRole === 'Supervisor' || userRole === 'SiteManager') && (
                    <>
                      <Button variant="primary" size="sm" className="fw-semibold rounded-2 flex-grow-1 flex-md-grow-0 text-nowrap" onClick={() => {
                        setAssetForm({ name: '', modelNumber: '', serialNumber: '', category: 'Heavy Machinery', ownership: 'Owned', purchasePrice: '', rentalRatePerHour: '', nextServiceDueHours: 250 });
                        setShowAssetModal(true);
                      }}>{t('btnOnboardMachine')}</Button>

                      <Button variant="info" size="sm" className="text-white fw-semibold rounded-2 flex-grow-1 flex-md-grow-0 text-nowrap" onClick={() => setShowLogShiftModal(true)}>📝 {t('btnLogShiftFuel')}</Button>

                      <Button variant="danger" size="sm" className="fw-semibold rounded-2 flex-grow-1 flex-md-grow-0 text-nowrap" onClick={() => setShowMaintenanceModal(true)}>🔧 {t('btnRaiseRepairTicket')}</Button>
                    </>
                  )}
                </div>
              </div>

              {/* 💻 MAIN MACHINERY FLEET DATA STREAM TABLE */}
              <div className="card shadow-sm border-0 bg-white rounded-3">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0 text-start" style={{ minWidth: '950px' }}>
                    <thead className="table-light text-secondary text-uppercase small fw-bold">
                      <tr>
                        <th className="ps-3 py-3" style={{ width: '25%' }}>{t('thMachineIdentity')}</th>
                        <th style={{ width: '15%' }}>{t('thCategoryTag')}</th>
                        <th style={{ width: '20%' }}>{t('thTotalEngineHours')}</th>
                        <th style={{ width: '20%' }}>{t('thGpsTelemetry')}</th>
                        <th style={{ width: '10%' }}>{t('thStatusBadge')}</th>
                        <th className="pe-3 text-end" style={{ width: '10%' }}>{t('thOperationalControls')}</th>
                      </tr>
                    </thead>
                    <tbody className="small">
                      {assets.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center text-muted py-4">{t('noMachineryRegistry')}</td>
                        </tr>
                      ) : (
                        assets.map((asset) => {
                          const serviceApproaching = asset.totalHoursOperated >= asset.nextServiceDueHours;
                          return (
                            <tr key={asset._id}>
                              <td className="ps-3">
                                <div className="fw-bold text-dark text-wrap text-break">{asset.name}</div>
                                <div className="text-muted text-wrap text-break" style={{ fontSize: '11px' }}>
                                  {t('lblSerialNumber')}: {asset.serialNumber} | {t('lblModelNumber')}: {asset.modelNumber}
                                </div>
                                {/* Audit Trail: Added By Log */}
                                {asset.createdBy && (
                                  <div className="text-primary mt-1" style={{ fontSize: '10px' }}>
                                    <i className="bi bi-person-plus-fill me-1"></i>
                                    {t('auditAddedBy')}: <span className="fw-semibold">{asset.createdBy.role || t('txtUnknown')}</span> ({asset.createdBy.name})
                                  </div>
                                )}
                              </td>
                              <td>
                                <div className="d-flex flex-wrap gap-1 align-items-center">
                                  <Badge bg="secondary" className="p-2 text-nowrap">{asset.category}</Badge>
                                  <Badge bg={asset.ownership === 'Owned' ? 'dark' : 'warning'} text={asset.ownership === 'Rented' ? 'dark' : 'white'} className="p-2 text-nowrap">
                                    {asset.ownership === 'Owned' ? t('badgeOwned') : t('badgeRented')}
                                  </Badge>
                                </div>
                              </td>
                              <td>
                                <div className="fw-bold text-nowrap">{asset.totalHoursOperated} {t('unitHours')}</div>
                                <div className="progress mt-1" style={{ height: '6px', maxWidth: '140px' }}>
                                  <div className={`progress-bar ${serviceApproaching ? 'bg-danger' : 'bg-success'}`} style={{ width: `${Math.min((asset.totalHoursOperated / asset.nextServiceDueHours) * 100, 100)}%` }}></div>
                                </div>
                                <div className="text-muted text-nowrap mt-1" style={{ fontSize: '10px' }}>{t('lblNextMaintenance')}: {asset.nextServiceDueHours} {t('unitHours')}</div>
                              </td>
                              <td>
                                <div className="d-flex flex-column text-wrap">
                                  <span className="fw-semibold text-nowrap"><i className="bi bi-geo-alt-fill text-primary me-1"></i>{t('lblLatitude')}: {asset.gpsLocation?.latitude || 0}</span>
                                  {asset.geofence?.isViolated ? (
                                    <Badge bg="danger" className="mt-1 p-1 text-nowrap align-self-start animate__animated animate__flash infinite">{t('alertBoundaryViolation')}</Badge>
                                  ) : (
                                    <span className="text-success text-nowrap mt-1" style={{ fontSize: '11px' }}><i className="bi bi-shield-fill-check"></i> {t('txtInsideZone')}</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <Badge bg={
                                  asset.status === 'Active' ? 'success' :
                                    asset.status === 'Under Maintenance' ? 'warning' :
                                      asset.status === 'Breakdown' ? 'danger' : 'secondary'
                                } className="p-2 text-capitalize text-nowrap fs-7">
                                  {asset.status === 'Active' ? t('statusActive') :
                                    asset.status === 'Under Maintenance' ? t('statusUnderMaintenance') :
                                      asset.status === 'Breakdown' ? t('statusBreakdown') : asset.status}
                                </Badge>
                              </td>
                              <td className="pe-3 text-end">
                                {userRole !== 'Client' ? (
                                  <Button variant="outline-dark" size="sm" className="py-1 px-2 rounded-2 text-nowrap" onClick={() => {
                                    if (typeof setSelectedAssetTelemetry === 'function') {
                                      setSelectedAssetTelemetry(asset);
                                    }
                                  }}><i className="bi bi-radar"></i> {t('btnPingGps')}</Button>
                                ) : (
                                  <span className="text-muted small text-nowrap"><i className="bi bi-lock-fill"></i> {t('txtViewOnly')}</span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 11: BIM 3D INTEGRATION DIALOGUES  */}
          {activeTab === 'bim' && userRole !== 'Laboure' && (
            <div className="animate__animated animate__fadeIn">
              {/* 📊 CORE TELEMETRY STATISTICS CARDS */}
              <div className="row g-3 mb-4">
                <div className="col-12 col-sm-6 col-md-4">
                  <div className="p-3 bg-white border rounded shadow-sm d-flex align-items-center justify-content-between border-start border-dark border-4 h-100">
                    <div className="text-start min-w-0 flex-grow-1">
                      <span className="text-muted fw-bold d-block text-uppercase small text-truncate">{t('cardActive3dModel')}</span>
                      <h6 className="fw-bold text-dark mb-0 text-truncate" style={{ maxWidth: '100%' }}>
                        {activeBimModel ? activeBimModel.title : t('txtNoActiveModel')}
                      </h6>
                      <small className="text-muted d-block text-truncate">{activeBimModel?.fileSize || '0 MB'} | v{activeBimModel?.version || 0}</small>
                    </div>
                    <div className="bg-dark-subtle p-2 rounded-3 text-dark flex-shrink-0 ms-2"><i className="bi bi-layers fs-3"></i></div>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-md-4">
                  <div className="p-3 bg-white border rounded shadow-sm d-flex align-items-center justify-content-between border-start border-danger border-4 h-100">
                    <div className="text-start min-w-0 flex-grow-1">
                      <span className="text-muted fw-bold d-block text-uppercase small text-truncate">{t('cardOpenInterferences')}</span>
                      <h3 className="fw-bold text-danger mb-0 text-truncate">{bimClashes.filter(c => c.status !== 'Resolved').length} {t('unitActive')}</h3>
                    </div>
                    <div className="bg-danger-subtle p-2 rounded-3 text-danger flex-shrink-0 ms-2"><i className="bi bi-bug-fill fs-3"></i></div>
                  </div>
                </div>
                <div className="col-12 col-sm-12 col-md-4">
                  <div className="p-3 bg-white border rounded shadow-sm d-flex align-items-center justify-content-between border-start border-success border-4 h-100">
                    <div className="text-start min-w-0 flex-grow-1">
                      <span className="text-muted fw-bold d-block text-uppercase small text-truncate">{t('cardResolvedClashes')}</span>
                      <h3 className="fw-bold text-success mb-0 text-truncate">{bimClashes.filter(c => c.status === 'Resolved').length} {t('unitFixed')}</h3>
                    </div>
                    <div className="bg-success-subtle p-2 rounded-3 text-success flex-shrink-0 ms-2"><i className="bi bi-shield-check fs-3"></i></div>
                  </div>
                </div>
              </div>

              {/* 🚀 BIM DYNAMIC CONTROLS CONTROLLER */}
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4 p-3 bg-white border rounded-3 shadow-sm">
                <h4 className="fw-bold text-dark mb-0 text-wrap text-break">
                  <i className="bi bi-box-securify text-dark me-2"></i>{t('bimMeshTitle')}
                </h4>
                <div className="d-flex flex-wrap gap-2 w-100 w-md-auto">
                  {(userRole === 'Admin' || userRole === 'Supervisor' || userRole === 'SiteManager') && (
                    <>
                      <Button variant="dark" size="sm" className="fw-semibold rounded-2 flex-grow-1 flex-md-grow-0 text-nowrap" onClick={() => {
                        setBimModelForm({ title: '', fileUrl: '', fileSize: '' });
                        setShowModelUploadModal(true);
                      }}>{t('btnLoadNewRevision')}</Button>

                      <Button variant="danger" size="sm" className="fw-semibold rounded-2 flex-grow-1 flex-md-grow-0 text-nowrap" disabled={!activeBimModel} onClick={() => {
                        setClashTicketForm({ title: '', description: '', clashType: 'Hard Clash', priority: 'Medium', coordinateX: '', coordinateY: '', coordinateZ: '', assignedTo: '' });
                        setShowClashTicketModal(true);
                      }}>{t('btnPin3dCoordinates')}</Button>
                    </>
                  )}
                </div>
              </div>

              <div className="row g-3">
                {/* 💻 LEFT PANEL: MOCK 3D WIREFRAME MESH VIEW CONTAINER */}
                <div className="col-12 col-lg-7">
                  <div className="card shadow-sm border-0 bg-dark p-3 p-sm-4 rounded-3 text-center d-flex flex-column align-items-center justify-content-center position-relative overflow-hidden" style={{ minHeight: '420px', background: 'linear-gradient(145deg, #1e1e2f, #0f0f1a)' }}>
                    <div className="position-static position-sm-absolute top-0 start-0 m-2 m-sm-3 text-start w-100 px-2 px-sm-0 mb-3 mb-sm-0">
                      <Badge bg="success" className="mb-1 text-wrap">{t('badgeBimEngineReady')}</Badge>
                      <div className="text-white-50 small text-wrap text-break"><i className="bi bi-database-check text-success me-1"></i>{t('lblModelLink')}: {activeBimModel ? activeBimModel.fileUrl : t('txtOffline')}</div>
                    </div>

                    {activeBimModel ? (
                      <div className="w-100 py-3 py-sm-5">
                        <div className="p-4 mx-auto border border-secondary border-dashed rounded-circle animate__animated animate__pulse animate__infinite d-flex align-items-center justify-content-center" style={{ width: '140px', height: '140px', borderStyle: 'dashed' }}>
                          <i className="bi bi-building text-info" style={{ fontSize: '55px' }}></i>
                        </div>
                        <h5 className="text-white mt-4 fw-bold text-wrap px-2">{activeBimModel.title}</h5>
                        <p className="text-white-50 px-3 px-sm-4 small">{t('descInteractiveViewport')}</p>

                        {/* Floating Pin Representation Layout */}
                        <div className="d-flex justify-content-center gap-2 flex-wrap mt-3 px-2">
                          {bimClashes.map((c, index) => (
                            <Badge
                              key={c._id}
                              bg={c.status === 'Resolved' ? 'success' : 'danger'}
                              className="p-2 text-wrap"
                              style={{ cursor: userRole !== 'Client' ? 'pointer' : 'default' }}
                              onClick={() => {
                                if (userRole !== 'Client' && typeof handleSelectClashMarker === 'function') {
                                  handleSelectClashMarker(c);
                                }
                              }}
                            >
                              📍 {t('lblPin')} #{index + 1} (X:{c.coordinates?.x})
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-white-50 py-4">
                        <i className="bi bi-cloud-slash fs-1 text-secondary text-opacity-50"></i>
                        <h5 className="mt-3 text-white fw-semibold">{t('txtNoBlueprintLoaded')}</h5>
                        <p className="small px-3">{t('descNoBlueprintLoaded')}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* 📋 RIGHT PANEL: CLASH TICKETS DETAIL LEDGER */}
                <div className="col-12 col-lg-5">
                  <div className="card shadow-sm border-0 bg-white p-3 rounded-3 d-flex flex-column" style={{ maxHeight: '420px' }}>
                    <h5 className="fw-bold text-dark border-bottom pb-2 mb-3 text-wrap">📍 {t('spatialInterferenceTitle')}</h5>

                    <div className="flex-grow-1 overflow-y-auto pe-1" style={{ overflowX: 'hidden' }}>
                      {bimClashes.length === 0 ? (
                        <div className="text-center text-muted py-5 small">{t('txtNoClashTicketsMapped')}</div>
                      ) : (
                        bimClashes.map((clash) => (
                          <div key={clash._id} className={`p-3 rounded-3 border mb-2 text-start ${clash.status === 'Resolved' ? 'bg-success-subtle border-success' : 'bg-light border-light'}`}>
                            <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                              <h6 className="fw-bold text-dark mb-0 text-wrap text-break">{clash.title}</h6>
                              <Badge bg={clash.priority === 'Critical' || clash.priority === 'High' ? 'danger' : 'warning'} className="text-nowrap">
                                {clash.priority === 'Critical' ? t('priorityCritical') : clash.priority === 'High' ? t('priorityHigh') : clash.priority === 'Medium' ? t('priorityMedium') : clash.priority}
                              </Badge>
                            </div>
                            <p className="text-muted mb-2 text-wrap text-break" style={{ fontSize: '12px' }}>{clash.description}</p>

                            <div className="p-2 bg-white rounded border border-light d-flex flex-wrap justify-content-between gap-1 text-monospace mb-2" style={{ fontSize: '11px', fontFamily: 'monospace' }}>
                              <span className="text-nowrap">🚀 {t('lblAxis')}: <strong>X: {clash.coordinates?.x}</strong></span>
                              <span className="text-nowrap"><strong>Y: {clash.coordinates?.y}</strong></span>
                              <span className="text-nowrap"><strong>Z: {clash.coordinates?.z}</strong></span>
                            </div>

                            {/* Audit Trail: Log Metadata for Created By & Edited/Resolved By */}
                            <div className="mb-2 p-2 bg-white rounded border border-light-subtle row g-1 text-muted" style={{ fontSize: '10px' }}>
                              {clash.createdBy && (
                                <div className="col-12 text-truncate">
                                  <i className="bi bi-person-circle me-1"></i>
                                  {t('auditCreatedBy')}: <span className="fw-bold text-dark">{clash.createdBy.role}</span> ({clash.createdBy.name})
                                </div>
                              )}
                              {clash.updatedBy && (
                                <div className="col-12 text-truncate mt-1">
                                  <i className="bi bi-pencil-square me-1"></i>
                                  {t('auditEditedBy')}: <span className="fw-bold text-dark">{clash.updatedBy.role}</span> ({clash.updatedBy.name})
                                </div>
                              )}
                            </div>

                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2" style={{ fontSize: '12px' }}>
                              <span className="text-secondary text-wrap"><i className="bi bi-person-badge me-1"></i>{t('lblLead')}: <strong className="text-dark">{clash.assignedTo?.name || t('txtUnassigned')}</strong></span>

                              {clash.status !== 'Resolved' ? (
                                (userRole === 'Admin' || userRole === 'Supervisor' || userRole === 'SiteManager') ? (
                                  <Button variant="outline-success" size="sm" className="py-1 px-2 fs-7 rounded text-nowrap ms-auto ms-sm-0" onClick={() => {
                                    if (typeof triggerResolutionFlow === 'function') {
                                      triggerResolutionFlow(clash._id);
                                    }
                                  }}>{t('btnMarkFixed')}</Button>
                                ) : userRole === 'Client' ? (
                                  <span className="badge bg-secondary-subtle text-muted ms-auto ms-sm-0 text-nowrap"><i className="bi bi-eye-fill"></i> {t('txtReadOnly')}</span>
                                ) : null
                              ) : (
                                <div className="ms-auto ms-sm-0 d-flex flex-column align-items-end">
                                  <Badge bg="success" className="text-nowrap"><i className="bi bi-check2-all me-1"></i>{t('statusResolved')}</Badge>
                                  {clash.resolvedBy && (
                                    <small className="text-success mt-1" style={{ fontSize: '9px' }}>
                                      {t('auditBy')}: {clash.resolvedBy.name} ({clash.resolvedBy.role})
                                    </small>
                                  )}
                                </div>
                              )}
                            </div>

                            {clash.resolutionNotes && (
                              <div className="mt-2 p-2 bg-white bg-opacity-75 text-success border-top border-success-subtle rounded small text-wrap text-break" style={{ fontSize: '11px' }}>
                                <strong>{t('lblFixNote')}:</strong> {clash.resolutionNotes}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

        </Card>

        {/* --- ALL OPERATIONAL ACTION DIALOGS / MODALS BANK --- */}
        {/* --- ALL OPERATIONAL ACTION DIALOGS / MODALS BANK --- */}

        {/* ========================================================= */}
        {/* 🟢 POPUP MODAL 1: BILLING MODEL   */}
        {/* ========================================================= */}

        {userRole === 'Admin' && (
          <Modal show={showSubscriptionModal} onHide={() => setShowSubscriptionModal(false)} size="lg" centered>
            <Modal.Header closeButton>
              <Modal.Title className="fw-bold">
                💼 {t('subscriptionModalTitle', 'MFR Build Licensing & Billing Center')}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
  {localStorage.getItem('isPlanActive') === 'true' ? (
    <Alert variant="success" className="text-center">
      ✅ Aapka plan active hai. Koi additional action zaroori nahi.
    </Alert>
  ) : (
    <PaymentGate onApproved={() => {
      localStorage.setItem('isPlanActive', 'true');
      setShowSubscriptionModal(false);
      window.location.reload();
    }} />
  )}
</Modal.Body>
          </Modal>
        )}
        {/* 🔥 METRIC BULK TEAM INVITATION MODAL SYSTEM */}
        {/* Secure Project Team Invitation Dashboard Modal Component Container */}
        {/* ========================================================= */}
        {/* 🟢 POPUP MODAL 1: VIEW INVITED TEAM MEMBERS LIST PORTAL   */}
        {/* ========================================================= */}
        <Modal show={showListModal} onHide={() => setShowListModal(false)} size="lg" centered className="rounded-4">
          <Modal.Header closeButton className="bg-light border-0 px-4 pt-4">
            <Modal.Title className="fw-bold text-dark d-flex justify-content-between align-items-center w-100 pe-3" style={{ fontSize: '1.25rem' }}>
              <span className="d-flex align-items-center gap-2">📋 {t('invitedWorkspaceLedgerTitle', 'Invited Workspace Operational Log Ledger')}</span>

              {/* 🟢 TOP BAR PLUS BUTTON */}
              <Button
                variant="success"
                size="sm"
                className="fw-bold shadow-sm rounded-3 px-3"
                onClick={() => {
                  setShowListModal(false);
                  setInviteRows([{ phone: '', email: '', role: 'Labour', dailyWage: '' }]);
                  setShowInviteModal(true);
                }}
              >
                ➕ {t('createNewNodeEntryBtn', 'Create New Workspace Node Entry')}
              </Button>
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="px-4 pb-4 pt-2">
            <Table responsive striped bordered hover className="align-middle text-center mb-0 mt-2 rounded-3 overflow-hidden shadow-sm border-0">
              <thead className="table-dark">
                <tr>
                  <th className="py-2">{t('tblHeaderWhatsApp', 'WhatsApp Contact')}</th>
                  <th className="py-2">{t('tblHeaderEmail', 'Secure Email Network Node')}</th>
                  <th className="py-2">{t('tblHeaderRole', 'System Operational Role')}</th>
                  <th className="py-2">{t('tblHeaderWage', 'Wage Value Metrics')}</th>
                  <th className="py-2">{t('tblHeaderStatus', 'Status')}</th>
                  <th className="py-2">{t('tblHeaderSend', 'Send')}</th>
                  <th className="py-2">{t('tblHeaderDelete', 'Delete')}</th>
                </tr>
              </thead>
              <tbody>
                {!invitedList || invitedList.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-muted py-4 fw-medium">
                      ❌ {t('noActiveWorkspaceElements', 'No active workspace team crypt elements found linked.')}
                    </td>
                  </tr>
                ) : (
                  invitedList.map((item, idx) => {
                    const createdTime = new Date(item.createdAt).getTime();
                    const isExpired = item.status === 'Pending' && (Date.now() - createdTime > 6 * 60 * 60 * 1000);

                    return (
                      <tr key={idx}>
                        <td className="fw-semibold text-secondary">{item?.phone || 'N/A'}</td>
                        <td>{item?.email || 'N/A'}</td>
                        <td>
                          <span className={`badge ${item?.role === 'Labour' ? 'bg-info' : item?.role === 'Supervisor' ? 'bg-warning text-dark' : 'bg-primary'}`}>
                            {t(item?.role?.toLowerCase() || 'labour', item?.role || 'Labour')}
                          </span>
                        </td>
                        <td className="fw-bold text-dark">
                          {item?.dailyWage ? `${item.dailyWage} PKR` : t('txtSalaryFixed', 'Salary Fixed')}
                        </td>

                        {/* 🟢 Status column */}
                        <td>
                          <span className={`badge ${item.status === 'Active' ? 'bg-success' : isExpired ? 'bg-danger' : 'bg-secondary'}`}>
                            {item.status === 'Active' ? `🟢 ${t('statusJoined', 'Joined')}` : isExpired ? `🔴 ${t('statusExpired', 'Expired')}` : `⚪ ${t('statusPending', 'Pending')}`}
                          </span>
                        </td>

                        {/* 🟢 Send button */}
                        <td>
                          {item.status !== 'Active' && (
                            <Button
                              variant={isExpired ? 'warning' : 'success'}
                              size="sm"
                              className="rounded-3 fw-semibold"
                              onClick={() => {
                                const cleanPhone = (item?.phone || '').replace(/[^0-9]/g, '');
                                const messageText = `*MFR Build Workspace Invitation*\n\nAssigned System Role: ${item?.role}\nSecure Token Link: ${item?.invitationLink}\n\n⚠️ *Constraint Note:* Is security link verification data node parameters strictly carry a *6-Hour expiration* lifetime metric.`;
                                const url = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(messageText)}`;
                                window.open(url, '_blank');
                              }}
                            >
                              {isExpired ? `🔄 ${t('btnResend', 'Resend')}` : `📤 ${t('btnSend', 'Send')}`}
                            </Button>
                          )}
                        </td>

                        {/* 🟢 Delete button */}
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="border-0 rounded-circle"
                            onClick={async () => {
                              if (!window.confirm(`Delete invitation for ${item.email}?`)) return;
                              try {
                                const token = localStorage.getItem('token');
                                await axiosReal.delete(`https://api.mfrbuild.com/api/auth/invite-list/${item._id}`, {
                                  headers: { 'x-auth-token': token }
                                });
                                setInvitedList(prev => prev.filter(inv => inv._id !== item._id));
                              } catch (err) {
                                console.error('Delete failed:', err);
                              }
                            }}
                          >
                            ❌
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </Modal.Body>
        </Modal>
        {/* ========================================================= */}
        {/* 🚀 POPUP MODAL 2: SECURE TEAM INVITATION DATA INPUT FORM   */}
        {/* ========================================================= */}
        {userRole === 'Admin' && (
  <Modal show={showInviteModal} onHide={() => setShowInviteModal(false)} size="lg" centered className="rounded-4">
    <Modal.Header closeButton className="border-0 px-4 pt-4 pb-0">
      <Modal.Title className="fw-bold text-dark d-flex align-items-center" style={{ gap: '10px' }}>
        <span>🚀</span> {t('inviteDashboardTitle', 'Secure Project Team Invitation Dashboard')}
      </Modal.Title>
    </Modal.Header>

    <Modal.Body className="px-4 pb-4 pt-2">
      <p className="text-muted small mb-3">
        {t('inviteDashboardConstraintText', 'Add roles to current project workspace. Generated secure invite links carry a strict 6-Hour Expiration Crypt constraint.')}
      </p>

      {/* LIVE AUTOMATED FORM STATUS FEEDBACK */}
      {formFeedbackMessage && (
        <div className="alert alert-danger border-0 shadow-sm small py-2 px-3 mb-3 d-flex align-items-center gap-2" role="alert">
          <i className="bi bi-exclamation-octagon-fill text-danger"></i>
          <span className="fw-medium">{formFeedbackMessage}</span>
        </div>
      )}

      <Form onSubmit={async (e) => {
        e.preventDefault();
        setInviteLoading(true);
        setFormFeedbackMessage('');

        try {
          const token = localStorage.getItem('token');

          if (!token) {
            setFormFeedbackMessage(t('authSessionExpiredMessage', 'Your session has expired or token is missing! Please logout and login again.'));
            setInviteLoading(false);
            return;
          }

          const response = await axiosReal.post('https://api.mfrbuild.com/api/auth/invite-team',
            {
              projectId: projectId,
              members: inviteRows
            },
            {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            }
          );

          if (response.data && response.data.success) {
            setInviteRows([{ phone: '', email: '', role: 'Labour', dailyWage: '' }]);
            setShowInviteModal(false);
          }
        } catch (err) {
          const serverFeedback = err.response?.data?.message || t('errInitializeMatrix', 'Failure initializing secure database configuration matrix.');
          setFormFeedbackMessage(serverFeedback);
        } finally {
          setInviteLoading(false);
        }
      }}>

        {/* Dynamic Multi-Row Input Container Loop */}
        {inviteRows.map((row, index) => (
          <div key={index} className="row g-3 align-items-md-end mb-3 p-3 bg-light rounded-3 border position-relative mx-0">

            {/* Index Counter Badge Layout */}
            <span className="position-absolute top-0 start-0 badge bg-secondary rounded-0 rounded-bottom-3 small" style={{ width: '30px' }}>
              {index + 1}
            </span>

            {/* 1. WhatsApp Number Entry */}
            <div className="col-12 col-md-3 mt-3 mt-md-2">
              <Form.Label className="text-secondary small fw-semibold mb-1">{t('lblWhatsAppNumber', 'WhatsApp Number')}</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g., 923001234567"
                value={row.phone}
                onChange={(e) => {
                  const updated = [...inviteRows];
                  updated[index].phone = e.target.value;
                  setInviteRows(updated);
                }}
                required
                className="form-control-sm rounded-2 shadow-sm"
              />
            </div>

            {/* 2. Email Identifier Entry */}
            <div className="col-12 col-md-3">
              <Form.Label className="text-secondary small fw-semibold mb-1">{t('lblEmailAddress', 'Email Address')}</Form.Label>
              <Form.Control
                type="email"
                placeholder="name@mfrbuild.com"
                value={row.email}
                onChange={(e) => {
                  const updated = [...inviteRows];
                  updated[index].email = e.target.value;
                  setInviteRows(updated);
                }}
                required
                className="form-control-sm rounded-2 shadow-sm"
              />
            </div>

            {/* 3. Account Type Role Selection Matrix */}
            <div className="col-12 col-md-3">
              <Form.Label className="text-secondary small fw-semibold mb-1">{t('lblAssignSystemRole', 'Assign System Role')}</Form.Label>
              <Form.Select
                value={row.role}
                className="form-select-sm rounded-2 fw-semibold shadow-sm"
                onChange={(e) => {
                  const targetRole = e.target.value;

                  if (ROLE_LIMITS && targetRole !== 'Labour') {
                    const currentRoleCount = inviteRows.filter((r, idx) => r.role === targetRole && idx !== index).length;
                    const allocationCap = ROLE_LIMITS[targetRole] || 1;

                    if (currentRoleCount >= allocationCap) {
                      setFormFeedbackMessage(`${t('errRoleCapExceeded', 'System Constraint Fault: Only')} ${allocationCap} ${t('errRoleCapAllocation', 'register tokens allocated for')} ${targetRole} ${t('errRoleStructure', 'role structural models!')}`);
                      return;
                    }
                  }

                  setFormFeedbackMessage('');
                  const updated = [...inviteRows];
                  updated[index].role = targetRole;
                  if (targetRole !== 'Labour') updated[index].dailyWage = '';
                  setInviteRows(updated);
                }}
              >
                <option value="Labour">{t('roleSelectionLabour', 'Labour Force (Unlimited)')}</option>
                <option value="Supervisor">{t('roleSelectionSupervisor', 'Supervisor (Limit: 1)')}</option>
                <option value="SiteManager">{t('roleSelectionSiteManager', 'Site Manager (Limit: 1)')}</option>
                <option value="Client">{t('roleSelectionClient', 'Client / Owner (Limit: 4)')}</option>
              </Form.Select>
            </div>

            {/* 4. Conditional Wage Controller Input Node */}
            <div className="col-12 col-md-2">
              {row.role === 'Labour' ? (
                <>
                  <Form.Label className="text-secondary small fw-semibold mb-1">
                    {t('lblDailyWagePkr', 'Daily Wage (PKR)')}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g., 1800"
                    value={row.dailyWage || ''}
                    onChange={(e) => {
                      const updated = [...inviteRows];
                      updated[index].dailyWage = e.target.value;
                      setInviteRows(updated);
                    }}
                    required={row.role === 'Labour'}
                    className="form-control-sm rounded-2 border-primary-subtle text-primary fw-bold shadow-sm"
                  />
                </>
              ) : (
                <>
                  <div
                    className="text-start text-md-center text-muted small pb-2 fw-semibold text-uppercase"
                    style={{ fontSize: '0.75rem', marginTop: '1.8rem' }}
                  >
                    ⚙️ {t('txtSalaryFixed', 'Salary Fixed')}
                  </div>

                  <input
                    type="hidden"
                    value=""
                  />
                </>
              )}
            </div>

            {/* 5. Dynamic Row Delete Functional Trigger */}
            <div className="col-12 col-md-1 text-start text-md-end mt-2 mt-md-0">
              <Button
                variant="outline-danger"
                size="sm"
                className="border-0 rounded-circle shadow-sm"
                onClick={() => {
                  if (inviteRows.length === 1) {
                    setFormFeedbackMessage(t('errMinInviteRowRequired', 'At least one target invite row is required.'));
                    return;
                  }
                  const updated = inviteRows.filter((_, i) => i !== index);
                  setInviteRows(updated);
                }}
              >
                ❌
              </Button>
            </div>

          </div>
        ))}

        {/* ADD ROW ACTION CONTROLLER CONTROL */}
        <div className="mb-4">
          <Button
            variant="outline-primary"
            size="sm"
            className="rounded-3 fw-bold px-3 border-dashed w-100 w-md-auto shadow-sm"
            style={{ borderStyle: 'dashed' }}
            onClick={() => {
              setInviteRows([...inviteRows, { phone: '', email: '', role: 'Labour', dailyWage: '' }]);
            }}
          >
            ➕ {t('btnAddTeamRowElement', 'Add Team Row Element (+)')}
          </Button>
        </div>

        {/* Bottom Interface Processing Controls Layout */}
        <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-sm-end gap-2 border-top pt-3">
          <Button variant="light" size="sm" className="fw-semibold px-3 order-2 order-sm-1 shadow-sm" onClick={() => setShowInviteModal(false)}>
            {t('btnCancel', 'Cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            size="sm"
            className="fw-bold px-4 order-1 order-sm-2 mb-2 mb-sm-0 shadow-sm"
            style={{ backgroundColor: '#0d6efd', border: 'none' }}
            disabled={inviteLoading}
          >
            {inviteLoading ? t('txtSendingTokens', 'Sending Tokens...') : `🚀 ${t('btnDispatchCryptLinks', 'Dispatch Crypt Links (WhatsApp Sync)')}`}
          </Button>
        </div>

      </Form>
    </Modal.Body>
  </Modal>
)}
        {/*================== TASK MODAL============================------------- */}
        {/*=================== TASK MODAL====================================---------- */}
        {showAddTaskModal && (
          <Modal show={showAddTaskModal} onHide={() => setShowAddTaskModal(false)} centered className="px-2">
            <Modal.Header closeButton className="bg-dark text-white border-0">
              <Modal.Title className="fw-bold fs-5 text-truncate">
                <i className="bi bi-calendar-plus-fill text-info me-2"></i>
                {editingItemId ? t('modalTitleEdit') : t('modalTitleAdd')}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-start p-3 p-sm-4 bg-white">

              {/* 🔐 RBAC SECURITY BLOCK: Client aur Labour view-only rules status block error text */}
              {(userRole === 'Client' || userRole === 'Labour') ? (
                <div className="text-center py-4 px-2">
                  <i className="bi bi-shield-lock-fill text-danger fs-1 mb-2 d-block"></i>
                  <h6 className="fw-bold text-dark">{t('txtSchedulingRestricted', 'Action Restricted')}</h6>
                  <p className="text-muted small mb-3">
                    {t('txtSchedulingRestrictedDesc', 'Your assigned profile privilege level does not authorize deploying or making revision changes to site tasks operations workflow registries.')}
                  </p>
                  <Button variant="secondary" size="sm" className="w-100" onClick={() => setShowAddTaskModal(false)}>
                    {t('btnClose')}
                  </Button>
                </div>
              ) : (
                <Form onSubmit={handleCreateTaskSubmit}>

                  {/* 🔐 AUDIT LAYER: Active edit mode check indicators tracking badge layout */}
                  {editingItemId && (
                    <div className="alert alert-light border small py-2 px-3 mb-3 text-muted rounded-3" style={{ fontSize: '11px' }}>
                      <div className="text-truncate">
                        👤 <strong>{t('thCreatedBy')}:</strong> {taskData.createdBy ? t(`role${taskData.createdBy}`) : t('roleAdmin')}
                      </div>
                      {taskData.updatedBy && (
                        <div className="text-truncate mt-1">
                          ✍️ <strong>{t('thUpdatedBy')}:</strong> {t(`role${taskData.updatedBy}`)}
                        </div>
                      )}
                    </div>
                  )}

                  {/* 1. Task Title */}
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-secondary">{t('lblTaskTitle')}</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t('phTaskTitle')}
                      value={taskData.title}
                      onChange={(e) => setTaskData({ ...taskData, title: e.target.value })}
                      required
                      className="rounded-2 shadow-sm"
                    />
                  </Form.Group>

                  {/* 2. Description (Technical Scope) */}
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-secondary">{t('lblDescription')}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder={t('phDescription')}
                      value={taskData.description || ''}
                      onChange={(e) => setTaskData({ ...taskData, description: e.target.value })}
                      className="rounded-2 shadow-sm"
                    />
                  </Form.Group>

                  {/* 3. Assigned Personnel Dropdown */}
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-secondary">{t('lblAssignedPersonnel')}</Form.Label>
                    <Form.Select
                      value={taskData.assignedTo}
                      onChange={(e) => setTaskData({ ...taskData, assignedTo: e.target.value })}
                      required
                      className="rounded-2 shadow-sm form-select-sm"
                    >
                      <option value="">{t('optSelectMember')}</option>
                      {allUsers && allUsers.map((u) => (
                        <option key={u._id} value={u._id}>
                          {u.name} ({u.role === 'SiteManager' ? t('roleSiteManager') : u.role === 'Supervisor' ? t('roleSupervisor') : u.role === 'Labour' ? t('roleLabour') : u.role === 'Client' ? t('roleClient') : u.role})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {/* 4. Field Classification Role */}
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-secondary">{t('lblFieldRole')}</Form.Label>
                    <Form.Select
                      value={taskData.roleAtAssignment}
                      onChange={(e) => setTaskData({ ...taskData, roleAtAssignment: e.target.value })}
                      className="rounded-2 shadow-sm form-select-sm"
                    >
                      <option value="Supervisor">{t('roleSupervisor')}</option>
                      <option value="SiteManager">{t('roleSiteManager')}</option>
                      <option value="Labour">{t('roleLabour')}</option>
                      <option value="Client">{t('roleClient')}</option>
                    </Form.Select>
                  </Form.Group>

                  {/* 5. Start Date & Due Date Rows */}
                  <div className="row g-3 mb-3">
                    <div className="col-12 col-sm-6">
                      <Form.Group>
                        <Form.Label className="fw-bold small text-secondary">{t('lblStartDate')}</Form.Label>
                        <Form.Control
                          type="date"
                          value={taskData.startDate || ''}
                          onChange={(e) => setTaskData({ ...taskData, startDate: e.target.value })}
                          required
                          className="rounded-2 shadow-sm form-control-sm"
                        />
                      </Form.Group>
                    </div>
                    <div className="col-12 col-sm-6">
                      <Form.Group>
                        <Form.Label className="fw-bold small text-secondary">{t('lblDueDate')}</Form.Label>
                        <Form.Control
                          type="date"
                          value={taskData.dueDate || ''}
                          onChange={(e) => setTaskData({ ...taskData, dueDate: e.target.value })}
                          required
                          className="rounded-2 shadow-sm form-control-sm"
                        />
                      </Form.Group>
                    </div>
                  </div>

                  {/* 6. Advanced Milestone Toggle Switch */}
                  <div className="form-check form-switch bg-light p-3 border rounded-3 mb-3 d-flex flex-wrap align-items-center justify-content-start gap-2">
                    <input
                      className="form-check-input ms-0 me-2"
                      type="checkbox"
                      id="milestoneSwitch"
                      checked={taskData.isMilestone || false}
                      onChange={(e) => setTaskData({ ...taskData, isMilestone: e.target.checked })}
                      style={{ cursor: 'pointer' }}
                    />
                    <label className="form-check-label fw-bold text-dark mb-0 cursor-pointer" htmlFor="milestoneSwitch" style={{ cursor: 'pointer' }}>
                      🚀 {t('lblMilestoneToggle')}
                    </label>
                    <small className="text-muted d-block w-100 mt-1" style={{ fontSize: '11px' }}>
                      {t('lblMilestoneHelp')}
                    </small>
                  </div>

                  {/* 7. Submission Action Buttons */}
                  <div className="d-flex flex-column flex-sm-row gap-2 justify-content-end border-top pt-3 w-100">
                    <Button variant="secondary" size="sm" className="px-3 flex-grow-1 flex-sm-grow-0 order-2 order-sm-1" onClick={() => setShowAddTaskModal(false)}>
                      {t('btnCancel')}
                    </Button>
                    <Button
                      variant={editingItemId ? "warning" : "info"}
                      type="submit"
                      size="sm"
                      className="text-white px-4 fw-semibold flex-grow-1 flex-sm-grow-0 text-nowrap order-1 order-sm-2"
                    >
                      {editingItemId ? t('btnSaveChanges') : t('btnDeployTask')}
                    </Button>
                  </div>
                </Form>
              )}
            </Modal.Body>
          </Modal>
        )}

        {/* ATTENDANCE MODAL */}
        {/* ==================== INTEGRATED WORKFORCE ATTENDANCE & ADVANCE MODAL ==================== */}
        {showAddAttendanceModal && (
          <Modal show={showAddAttendanceModal} onHide={() => setShowAddAttendanceModal(false)} centered size="lg" className="px-2">
            <Modal.Header closeButton className="bg-success text-white border-0">
              <Modal.Title className="fw-bold text-white text-truncate fs-5">
                {editingItemId ? t('modifyAttendanceTitle') : t('logWorkerAttendanceTitle')}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-3 p-sm-4 bg-white">
              {/* 🛡️ SECURITY LAYER 1: Labour account se financial metrics aur attendance logger completely hide */}
              {userRole === 'Labour' ? (
                <div className="text-center py-4 px-2">
                  <i className="bi bi-shield-lock-fill text-danger fs-1 mb-2 d-block"></i>
                  <h6 className="fw-bold text-dark">{t('txtAttendanceRestricted', 'Access Denied')}</h6>
                  <p className="text-muted small mb-3">
                    {t('txtAttendanceLabourDesc', 'Labour profiles are strictly restricted from initiating attendance sheets or managing daily wage calculations.')}
                  </p>
                  <Button variant="secondary" size="sm" className="w-100" onClick={() => setShowAddAttendanceModal(false)}>
                    {t('btnClose')}
                  </Button>
                </div>
              ) : userRole === 'Client' ? (
                /* 🛡️ SECURITY LAYER 2: Client profile view-only constraints setup */
                <div className="text-center py-4 px-2">
                  <i className="bi bi-eye-fill text-info fs-1 mb-2 d-block"></i>
                  <h6 className="fw-bold text-dark">{t('txtAttendanceReadOnly', 'View Only Mode')}</h6>
                  <p className="text-muted small mb-3">
                    {t('txtAttendanceClientDesc', 'Clients and Owners can review operational rosters via the dashboard table stream stream but cannot modify wage ledgers.')}
                  </p>
                  <Button variant="secondary" size="sm" className="w-100" onClick={() => setShowAddAttendanceModal(false)}>
                    {t('btnClose')}
                  </Button>
                </div>
              ) : (
                <Form onSubmit={handleCreateAttendanceSubmit}>

                  {/* 🔐 DATA TRANS-ACCOUNTABILITY STAMP BADGES */}
                  {editingItemId && (
                    <div className="alert alert-light border small py-2 px-3 mb-3 text-muted rounded-3 d-flex flex-column gap-1" style={{ fontSize: '11px' }}>
                      <div className="text-truncate">
                        👤 <strong>{t('thCreatedBy')}:</strong> {attendanceData.createdBy ? t(`role${attendanceData.createdBy}`) : t('roleSiteManager')}
                      </div>
                      {attendanceData.updatedBy && (
                        <div className="text-truncate">
                          ✍️ <strong>{t('thUpdatedBy')}:</strong> {t(`role${attendanceData.updatedBy}`)}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="row g-3 text-start">

                    {/* 🟢 Worker Selection Dropdown */}
                    <div className="col-12 col-md-6">
                      <Form.Group>
                        <Form.Label className="fw-semibold text-secondary small">{t('selectWorkerLabel')}</Form.Label>
                        <Form.Select
                          name="workerSelectField"
                          value={attendanceData.worker || attendanceData.workerId || ""}
                          onChange={(e) => {
                            const selectedId = e.target.value;
                            const selectedWorker = allUsers && allUsers.find(u => u._id === selectedId);

                            setAttendanceData(prev => ({
                              ...prev,
                              worker: selectedId,
                              workerId: selectedId,
                              workerName: selectedWorker ? selectedWorker.name : ""
                            }));
                          }}
                          required
                          className="form-select-sm rounded-2 shadow-sm"
                        >
                          <option value="">{t('selectRegisteredMemberOpt')}</option>
                          {allUsers && allUsers.map((u) => (
                            <option key={u._id} value={u._id}>
                              {u.name} ({u.role})
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </div>

                    {/* 🟢 Field Role Category Selection */}
                    <div className="col-12 col-md-6">
                      <Form.Group>
                        <Form.Label className="fw-semibold text-secondary small">{t('fieldRoleGroupLabel')}</Form.Label>
                        <Form.Select
                          value={attendanceData.role}
                          onChange={(e) => setAttendanceData({ ...attendanceData, role: e.target.value })}
                          className="form-select-sm rounded-2 shadow-sm"
                        >
                          <option value="Labour">{t('optLabour')}</option>
                          <option value="Supervisor">{t('optSupervisor')}</option>
                          <option value="SiteManager">{t('optSiteManager')}</option>
                        </Form.Select>
                      </Form.Group>
                    </div>

                    {/* 🟢 Attendance Status Tracker */}
                    <div className="col-12 col-md-6">
                      <Form.Group>
                        <Form.Label className="fw-semibold text-secondary small">{t('attendanceStatusTrackerLabel')}</Form.Label>
                        <Form.Select
                          value={attendanceData.status}
                          onChange={(e) => setAttendanceData({ ...attendanceData, status: e.target.value })}
                          className="form-select-sm rounded-2 shadow-sm"
                        >
                          <option value="Present">{t('optPresentFull')}</option>
                          <option value="Half-Day">{t('optHalfDayAadhi')}</option>
                          <option value="Absent">{t('optAbsentZero')}</option>
                          <option value="Leave">{t('optOnLeave')}</option>
                        </Form.Select>
                      </Form.Group>
                    </div>

                    {/* 🛒 ADVANCED FIELD: Wage Structure Model */}
                    <div className="col-12 col-md-6">
                      <Form.Group>
                        <Form.Label className="fw-semibold text-secondary small">{t('wageStructureModelLabel')}</Form.Label>
                        <Form.Select
                          value={attendanceData.paymentType || 'Daily-Wage'}
                          onChange={(e) => setAttendanceData({ ...attendanceData, paymentType: e.target.value })}
                          className="form-select-sm rounded-2 shadow-sm"
                        >
                          <option value="Daily-Wage">{t('optFixedDailyWage')}</option>
                          <option value="Piece-Rate">{t('optPieceRateContract')}</option>
                        </Form.Select>
                      </Form.Group>
                    </div>

                    {/* 🟢 ADVANCED FIELD: Base Daily Dihari or Square Foot Rate */}
                    <div className={`col-12 ${attendanceData.paymentType === 'Piece-Rate' ? 'col-sm-4' : 'col-sm-6'}`}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-secondary small text-truncate d-block">
                          {attendanceData.paymentType === 'Piece-Rate' ? t('ratePerSqFt') : t('baseDailyDihari')}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          placeholder="e.g. 1500"
                          value={attendanceData.dailyRate || ''}
                          onChange={(e) => setAttendanceData({ ...attendanceData, dailyRate: Number(e.target.value) })}
                          min="0"
                          className="form-control-sm rounded-2 shadow-sm"
                        />
                      </Form.Group>
                    </div>

                    {/* 📐 DYNAMIC CONDITIONAL FIELD: Only Displays if Piece-Rate is activated */}
                    {attendanceData.paymentType === 'Piece-Rate' && (
                      <div className="col-12 col-sm-4">
                        <Form.Group className="animate__animated animate__fadeIn">
                          <Form.Label className="fw-semibold text-secondary small text-truncate d-block">{t('outputVolumeLabel')}</Form.Label>
                          <Form.Control
                            type="number"
                            placeholder="e.g. 250"
                            value={attendanceData.workVolume || ''}
                            onChange={(e) => setAttendanceData({ ...attendanceData, workVolume: Number(e.target.value) })}
                            min="1"
                            required
                            className="form-control-sm rounded-2 shadow-sm"
                          />
                        </Form.Group>
                      </div>
                    )}

                    {/* ⚠️ ADVANCED FIELD: Local Market Advance Tracker */}
                    <div className={`col-12 ${attendanceData.paymentType === 'Piece-Rate' ? 'col-sm-4' : 'col-sm-6'}`}>
                      <Form.Group>
                        <Form.Label className="fw-semibold text-warning small fw-bold text-truncate d-block">{t('todaysAdvanceTakenLabel')}</Form.Label>
                        <Form.Control
                          type="number"
                          placeholder="No advance = 0"
                          value={attendanceData.advanceTaken || ''}
                          onChange={(e) => setAttendanceData({ ...attendanceData, advanceTaken: Number(e.target.value) })}
                          min="0"
                          className="form-control-sm rounded-2 border-warning shadow-sm"
                        />
                      </Form.Group>
                      <span className="text-muted d-block mt-1" style={{ fontSize: '10px' }}>
                        *{t('advanceDeductionNote')}
                      </span>
                    </div>

                  </div>

                  {/* Action Submit Buttons Wrapper */}
                  <div className="mt-4 border-top pt-3 d-flex flex-column flex-sm-row gap-2 justify-content-end w-100">
                    <Button variant="secondary" size="sm" className="px-3 rounded-2 fw-semibold flex-grow-1 flex-sm-grow-0 order-2 order-sm-1 shadow-sm" onClick={() => setShowAddAttendanceModal(false)}>
                      {t('btnCancel')}
                    </Button>
                    <Button variant={editingItemId ? "warning" : "success"} size="sm" type="submit" className="px-4 rounded-2 fw-bold shadow-sm flex-grow-1 flex-sm-grow-0 text-nowrap order-1 order-sm-2 text-white">
                      {editingItemId ? `🔄 ${t('btnUpdateStatusLedger')}` : `🚀 ${t('btnSubmitSyncExpenses')}`}
                    </Button>
                  </div>

                </Form>
              )}
            </Modal.Body>
          </Modal>
        )}


        {/* DAILY SITE LOG MODAL */}
        {showAddLogModal && (
          <Modal show={showAddLogModal} onHide={() => setShowAddLogModal(false)} centered size="lg" className="border-0 shadow px-2">
            <Modal.Header closeButton className="bg-white border-bottom-0 pt-4 px-3 px-sm-4">
              <Modal.Title className="fw-bold text-dark d-flex align-items-center gap-2 fs-5 text-truncate">
                <span>{editingItemId ? '📝' : '✨'}</span>
                {editingItemId ? t('editDailyReport') : t('createDailyLog')}
              </Modal.Title>
            </Modal.Header>

            {/* 🛡️ SECURITY LAYER: Client aur Labour force ke liye data inputs aur functional triggers complete hide */}
            {(userRole === 'Client' || userRole === 'Labour') ? (
              <Modal.Body className="p-4 text-center bg-white">
                <div className="py-3">
                  <i className="bi bi-shield-lock-fill text-danger fs-1 mb-2 d-block"></i>
                  <h6 className="fw-bold text-dark">{t('txtDailyLogRestricted', 'Access Restricted')}</h6>
                  <p className="text-muted small mx-auto mb-3" style={{ maxWidth: '420px' }}>
                    {t('txtDailyLogRestrictedDesc', 'Your profile role structure only grants read-only rights to daily site logs. Creating new reports or capturing site progress media is locked.')}
                  </p>
                  <Button variant="secondary" size="sm" className="w-100" onClick={() => setShowAddLogModal(false)}>
                    {t('btnClose')}
                  </Button>
                </div>
              </Modal.Body>
            ) : (
              <>
                {/* Modal Sub-Tabs for Better Data Cleanliness */}
                <div className="px-3 px-sm-4 py-2 bg-white">
                  <ul className="nav nav-pills nav-fill p-1 bg-light rounded-3 small flex-nowrap overflow-x-auto text-nowrap" style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link py-2 rounded-2 fw-semibold transition-all ${logModalTab === 'general' ? 'bg-warning text-dark shadow-sm' : 'text-secondary'}`}
                        onClick={() => setLogModalTab('general')}
                      >
                        1. {t('generalInfo')}
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link py-2 rounded-2 fw-semibold transition-all ${logModalTab === 'material' ? 'bg-warning text-dark shadow-sm' : 'text-secondary'}`}
                        onClick={() => setLogModalTab('material')}
                      >
                        2. {t('materials')}
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link py-2 rounded-2 fw-semibold transition-all ${logModalTab === 'safety' ? 'bg-warning text-dark shadow-sm' : 'text-secondary'}`}
                        onClick={() => setLogModalTab('safety')}
                      >
                        3. {t('safetyHse')}
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        type="button"
                        className={`nav-link py-2 rounded-2 fw-semibold transition-all ${logModalTab === 'photos' ? 'bg-warning text-dark shadow-sm' : 'text-secondary'}`}
                        onClick={() => setLogModalTab('photos')}
                      >
                        4. {t('proofPhotos')}
                      </button>
                    </li>
                  </ul>
                </div>

                <Modal.Body className="p-3 p-sm-4 pt-3 bg-white">
                  <Form onSubmit={handleCreateLogSubmit}>

                    {/* 🔐 INTEGRITY SECURITY AUDIT TRAIL BADGES */}
                    {editingItemId && (
                      <div className="alert alert-light border small py-2 px-3 mb-3 text-muted rounded-3 mx-0 row g-1" style={{ fontSize: '11px' }}>
                        <div className="col-12 text-truncate">
                          👤 <strong>{t('thCreatedBy')}:</strong> {logData.createdBy ? t(`role${logData.createdBy}`) : t('roleSupervisor')}
                        </div>
                        {logData.updatedBy && (
                          <div className="col-12 text-truncate mt-1">
                            ✍️ <strong>{t('thUpdatedBy')}:</strong> {t(`role${logData.updatedBy}`)}
                          </div>
                        )}
                      </div>
                    )}

                    {/* 🔹 TAB 1: GENERAL INFO */}
                    {logModalTab === 'general' && (
                      <div className="animate__animated animate__fadeIn small-duration">
                        <div className="row g-3 mb-4 text-start">
                          <div className="col-12 col-md-4">
                            <Form.Group>
                              <Form.Label className="fw-semibold text-secondary small">{t('weather')}</Form.Label>
                              <Form.Select
                                className="form-select form-select-sm border shadow-sm"
                                value={logData.weather || 'Sunny'}
                                onChange={(e) => setLogData({ ...logData, weather: e.target.value })}
                              >
                                <option value="Sunny">☀️ {t('weatherSunny')}</option>
                                <option value="Rainy">🌧️ {t('weatherRainy')}</option>
                                <option value="Overcast">☁️ {t('weatherOvercast')}</option>
                                <option value="Extreme Heat">🔥 {t('weatherExtremeHeat')}</option>
                              </Form.Select>
                            </Form.Group>
                          </div>

                          <div className="col-12 col-md-4">
                            <Form.Group>
                              <Form.Label className="fw-semibold text-secondary small">{t('weatherDelayHours')}</Form.Label>
                              <Form.Control
                                type="number"
                                min="0"
                                max="24"
                                className="form-control-sm border shadow-sm"
                                value={logData.weatherDelayHours || 0}
                                onChange={(e) => setLogData({ ...logData, weatherDelayHours: parseInt(e.target.value) || 0 })}
                              />
                            </Form.Group>
                          </div>

                          <div className="col-12 col-md-4">
                            <Form.Group>
                              <Form.Label className="fw-semibold text-secondary small">{t('procurementStatus')}</Form.Label>
                              <Form.Select
                                className="form-select form-select-sm border shadow-sm"
                                value={logData.paymentStatus || "Paid"}
                                onChange={(e) => setLogData({ ...logData, paymentStatus: e.target.value })}
                              >
                                <option value="Paid">🟢 {t('statusPaid')}</option>
                                <option value="Unpaid">🔴 {t('statusUnpaid')}</option>
                                <option value="Partial">🟡 {t('statusPartial')}</option>
                              </Form.Select>
                            </Form.Group>
                          </div>
                        </div>

                        {/* 🎙️ SPEECH INTERACTIVE DAILY LOG TEXTAREA */}
                        <Form.Group className="mb-4 text-start">
                          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                            <Form.Label className="fw-semibold text-dark mb-0 small">{t('workSummaryLabel')}</Form.Label>

                            <Button
                              variant={isListening ? "danger" : "outline-dark"}
                              size="sm"
                              className={`py-0.5 px-3 rounded-pill fw-medium animate__animated ${isListening ? 'animate__pulse animate__infinite' : ''}`}
                              onClick={startVoiceToTextEngine}
                              type="button"
                              style={{ fontSize: '11px' }}
                            >
                              {isListening ? (
                                <span><i className="bi bi-mic-fill me-1"></i> {t('listening')}</span>
                              ) : (
                                <span><i className="bi bi-mic me-1"></i> {t('speakLog')}</span>
                              )}
                            </Button>
                          </div>

                          <Form.Control
                            as="textarea"
                            rows={4}
                            className="p-3 rounded-3 dynamic-textarea border shadow-sm"
                            placeholder={t('workSummaryPlaceholder')}
                            value={logData.workSummary || logData.notes || ''}
                            onChange={(e) => setLogData({ ...logData, workSummary: e.target.value })}
                            required
                          />

                          <div className="d-flex justify-content-end mt-2">
                            <Button
                              variant="primary"
                              size="sm"
                              type="button"
                              className="rounded-3 fw-semibold px-3 py-1 shadow-sm text-nowrap"
                              style={{ fontSize: '12px' }}
                              onClick={() => handleAITextPolish(
                                logData.workSummary || logData.notes,
                                "Daily Log Work Summary",
                                (polished) => setLogData(prev => ({ ...prev, workSummary: polished }))
                              )}
                            >
                              <i className="bi bi-stars me-1"></i> ✨ {t('aiPolishBtn')}
                            </Button>
                          </div>
                        </Form.Group>

                        <div className="row g-3 mb-3 text-start">
                          <div className={`col-12 ${logData.roadblocks?.reason !== 'None' ? 'col-sm-6' : 'col-md-12'}`}>
                            <Form.Group>
                              <Form.Label className="fw-semibold text-danger small">{t('roadblocksLabel')}</Form.Label>
                              <Form.Select
                                className="form-select form-select-sm border shadow-sm"
                                value={logData.roadblocks?.reason || 'None'}
                                onChange={(e) => setLogData({ ...logData, roadblocks: { ...logData.roadblocks, reason: e.target.value } })}
                              >
                                <option value="None">{t('roadblockNone')}</option>
                                <option value="Light Issue">{t('roadblockLight')}</option>
                                <option value="Material Shortage">{t('roadblockMaterial')}</option>
                                <option value="Labour Shortage">{t('roadblockLabour')}</option>
                                <option value="Labour Strike">{t('roadblockStrike')}</option>
                              </Form.Select>
                            </Form.Group>
                          </div>

                          {logData.roadblocks?.reason !== 'None' && (
                            <div className="col-12 col-sm-6 animate__animated animate__fadeIn">
                              <Form.Group>
                                <Form.Label className="fw-semibold text-secondary small">{t('roadblockDetails')}</Form.Label>
                                <Form.Control
                                  type="text"
                                  className="form-control-sm border shadow-sm"
                                  placeholder={t('roadblockDetailsPlaceholder')}
                                  value={logData.roadblocks?.description || ''}
                                  onChange={(e) => setLogData({ ...logData, roadblocks: { ...logData.roadblocks, description: e.target.value } })}
                                />
                              </Form.Group>
                            </div>
                          )}
                        </div>

                        <div className="d-flex justify-content-end mt-4 pt-3 border-top w-100">
                          <button type="button" className="btn btn-warning text-dark fw-bold px-4 py-1.5 btn-sm rounded-2 shadow-sm flex-grow-1 flex-sm-grow-0 text-nowrap" onClick={() => setLogModalTab('material')}>
                            {t('nextBtn')} {t('materials')} ➡️
                          </button>
                        </div>
                      </div>
                    )}

                    {/* 🔹 TAB 2: MATERIAL LOGS */}
                    {logModalTab === 'material' && (
                      <div className="animate__animated animate__fadeIn small-duration">
                        <Form.Group className="mb-4 text-start">
                          <Form.Label className="fw-semibold text-dark small">{t('materialsConsumedToday')}</Form.Label>
                          <Form.Control
                            type="text"
                            className="form-control-sm border p-3 shadow-sm"
                            placeholder={t('materialsPlaceholder')}
                            value={logData.materialUsed || ''}
                            onChange={(e) => setLogData({ ...logData, materialUsed: e.target.value })}
                            required
                          />
                        </Form.Group>

                        <div className="alert alert-info border-0 bg-light p-3 rounded-3 d-flex align-items-start gap-2 text-start">
                          <i className="bi bi-info-circle-fill text-primary mt-0.5"></i>
                          <p className="text-muted small mb-0">{t('inventoryLinkedNote')}</p>
                        </div>

                        {/* Responsive Navigation Buttons */}
                        <div className="d-flex flex-column flex-sm-row justify-content-between gap-2 mt-5 pt-3 border-top">
                          <button type="button" className="btn btn-light btn-sm fw-semibold px-4 py-1.5 rounded-2 order-2 order-sm-1 shadow-sm" onClick={() => setLogModalTab('general')}>
                            ⬅️ {t('backBtn')}
                          </button>
                          <button type="button" className="btn btn-warning text-dark btn-sm fw-bold px-4 py-1.5 rounded-2 shadow-sm order-1 order-sm-2" onClick={() => setLogModalTab('safety')}>
                            {t('nextBtn')} {t('safetyHse')} ➡️
                          </button>
                        </div>
                      </div>
                    )}

                    {/* 🔹 TAB 3: SAFETY CHECKLIST */}
                    {logModalTab === 'safety' && (
                      <div className="animate__animated animate__fadeIn small-duration text-start">
                        <h6 className="mb-3 text-warning fw-bold d-flex align-items-center gap-2 small">
                          <span>🛡️</span> {t('safetyRoutineHeading')}
                        </h6>

                        <div className="bg-light p-3 rounded-3 mb-4 border border-light shadow-sm">
                          <Form.Check
                            type="checkbox"
                            id="helmetCheck"
                            className="mb-3 fw-medium custom-checkbox small"
                            label={t('checkHelmets')}
                            checked={logData.safetyChecklist?.helmetsWorn || false}
                            onChange={(e) => setLogData({ ...logData, safetyChecklist: { ...logData.safetyChecklist, helmetsWorn: e.target.checked } })}
                          />
                          <Form.Check
                            type="checkbox"
                            id="shoesCheck"
                            className="mb-0 fw-medium custom-checkbox small"
                            label={t('checkShoes')}
                            checked={logData.safetyChecklist?.safetyShoesWorn || false}
                            onChange={(e) => setLogData({ ...logData, safetyChecklist: { ...logData.safetyChecklist, safetyShoesWorn: e.target.checked } })}
                          />
                        </div>

                        <div className="border border-danger-subtle p-3 rounded-3 mb-4 bg-danger-subtle bg-opacity-10 shadow-sm">
                          <Form.Check
                            type="checkbox"
                            id="incidentCheck"
                            className="mb-0 text-danger fw-bold custom-checkbox small"
                            label={t('checkIncident')}
                            checked={logData.safetyChecklist?.incidentReported || false}
                            onChange={(e) => setLogData({ ...logData, safetyChecklist: { ...logData.safetyChecklist, incidentReported: e.target.checked } })}
                          />

                          {logData.safetyChecklist?.incidentReported && (
                            <Form.Group className="mt-3 animate__animated animate__fadeIn">
                              <Form.Control
                                as="textarea"
                                rows={3}
                                className="border-danger-subtle p-2 small shadow-sm"
                                placeholder={t('incidentDetailsPlaceholder')}
                                value={logData.safetyChecklist?.incidentDetails || ''}
                                onChange={(e) => setLogData({ ...logData, safetyChecklist: { ...logData.safetyChecklist, incidentDetails: e.target.value } })}
                              />
                            </Form.Group>
                          )}
                        </div>

                        <Form.Group className="mb-3 mt-4">
                          <Form.Label className="fw-semibold text-dark small">{t('extraNotesLabel')}</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            className="p-3 border shadow-sm"
                            placeholder={t('extraNotesPlaceholder')}
                            value={logData.notes || ''}
                            onChange={(e) => setLogData({ ...logData, notes: e.target.value })}
                            required
                          />
                        </Form.Group>

                        {/* Responsive Navigation Buttons */}
                        <div className="d-flex flex-column flex-sm-row justify-content-between gap-2 mt-5 pt-3 border-top">
                          <button type="button" className="btn btn-light btn-sm fw-semibold px-4 py-1.5 rounded-2 order-2 order-sm-1 shadow-sm" onClick={() => setLogModalTab('material')}>
                            ⬅️ {t('backBtn')}
                          </button>
                          <button type="button" className="btn btn-warning text-dark btn-sm fw-bold px-4 py-1.5 rounded-2 shadow-sm order-1 order-sm-2" onClick={() => setLogModalTab('photos')}>
                            {t('nextBtn')} {t('proofPhotos')} ➡️
                          </button>
                        </div>
                      </div>
                    )}

                    {/* 🔹 TAB 4: FRAUD-PROOF PHOTOS */}
                    {logModalTab === 'photos' && (
                      <div className="animate__animated animate__fadeIn small-duration text-start">
                        <h6 className="mb-3 fw-bold text-dark d-flex align-items-center gap-2 small">
                          <span>📸</span> {t('realTimePhotoHeading')}
                        </h6>

                        {showCameraView ? (
                          <div className="text-center p-2 p-sm-3 border rounded-4 bg-dark mb-4 position-relative animate__animated animate__fadeIn shadow-sm">
                            <video
                              id="live-webcam-preview"
                              autoPlay
                              playsInline
                              className="w-100 rounded-3 border border-secondary shadow-lg mb-3"
                              style={{ maxHeight: '280px', objectFit: 'cover' }}
                            ></video>
                            <div className="d-flex flex-wrap justify-content-center gap-2">
                              <button
                                type="button"
                                className="btn btn-danger btn-sm fw-bold d-flex align-items-center gap-2 px-3 px-sm-4 py-1.5 shadow text-wrap"
                                onClick={handleCaptureSnapshot}
                              >
                                🎯 {t('capturePhotoBtn')}
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm px-3 py-1.5"
                                onClick={() => {
                                  if (cameraStream) cameraStream.getTracks().forEach(track => track.stop());
                                  setShowCameraView(false);
                                }}
                              >
                                {t('cancelBtn')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-success mb-4 d-flex align-items-center gap-2 px-4 py-2 rounded-2 btn-sm fw-semibold shadow-sm text-white w-100 w-sm-auto justify-content-center"
                            onClick={handleStartCamera}
                            disabled={btnLoading}
                          >
                            <i className="bi bi-camera-fill fs-6"></i>
                            {btnLoading ? t('startingCamera') : t('openCameraBtn')}
                          </button>
                        )}

                        {/* Responsive Grid for Captured Photos */}
                        <div className="row g-3 mb-4">
                          {logData.photos && logData.photos.map((photo, idx) => {
                            const previewUrl = photo.url || photo;
                            if (!previewUrl || previewUrl.includes('...')) return null;

                            return (
                              <div key={idx} className="col-12 col-sm-6">
                                <div className="card h-100 border-0 shadow-sm bg-dark text-white overflow-hidden position-relative rounded-3" style={{ minHeight: '150px' }}>
                                  <img
                                    src={previewUrl}
                                    alt="Upload"
                                    className="w-100 img-fluid"
                                    style={{ height: '110px', objectFit: 'cover' }}
                                  />

                                  <span className={`badge ${photo.isLive ? 'bg-success' : 'bg-primary'} position-absolute top-0 start-0 m-2 shadow`}>
                                    {photo.isLive ? `📷 ${t('badgeLive')}` : `📂 ${t('badgeGallery')}`}
                                  </span>

                                  <div className="card-body p-2 bg-dark" style={{ fontSize: '11px', fontFamily: 'monospace' }}>
                                    {photo.isLive ? (
                                      <div className="text-success-subtle text-break">
                                        📍 Lat: {photo.coordinates?.latitude?.toFixed(4) || '31.4602'}<br />
                                        📍 Lon: {photo.coordinates?.longitude?.toFixed(4) || '74.2034'}
                                      </div>
                                    ) : (
                                      <span className="text-muted text-nowrap">{t('uploadedFromStorage')}</span>
                                    )}
                                  </div>

                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2 rounded-circle p-1 d-flex align-items-center justify-content-center shadow"
                                    style={{ width: '24px', height: '24px', fontSize: '10px' }}
                                    onClick={() => setLogData(prev => ({ ...prev, photos: prev.photos.filter((_, i) => i !== idx) }))}
                                  >
                                    ✕
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {logData.photos && logData.photos.filter(p => p.url && !p.url.includes('...')).length > 0 ? (
                          <div className="mt-3 p-3 p-sm-4 border rounded-3 bg-light animate__animated animate__fadeIn text-start shadow-sm">
                            <label className="form-label fw-bold text-primary small mb-2">📂 {t('optionalGalleryLabel')}</label>
                            <input
                              type="file"
                              className="form-control form-control-sm border bg-white shadow-sm"
                              accept="image/*"
                              onChange={handleChooseFromGallery}
                            />
                            <div className="text-muted small mt-2 fw-medium">{t('totalPhotosAdded')}: {logData.photos.length}</div>
                          </div>
                        ) : (
                          <div className="alert alert-warning small mt-2 fw-semibold border-0 rounded-3 bg-warning-subtle text-warning-emphasis p-3 animate__animated animate__headShake">
                            ⚠️ {t('cameraSecurityRuleAlert')}
                          </div>
                        )}

                        {/* Responsive Navigation & Submit Buttons */}
                        <div className="d-flex flex-column flex-sm-row justify-content-between gap-2 mt-5 pt-3 border-top">
                          <button type="button" className="btn btn-light btn-sm fw-semibold px-4 py-1.5 rounded-2 order-2 order-sm-1 shadow-sm" onClick={() => setLogModalTab('safety')}>
                            ⬅️ {t('backBtn')}
                          </button>
                          <Button variant="warning" type="submit" size="sm" className="text-dark fw-bold px-5 py-1.5 rounded-2 shadow-sm order-1 order-sm-2 text-nowrap" disabled={btnLoading}>
                            {btnLoading ? t('processing') : editingItemId ? `💾 ${t('saveUpdatesBtn')}` : `💾 ${t('submitAndLockBtn')}`}
                          </Button>
                        </div>
                      </div>
                    )}
                  </Form>
                </Modal.Body>
              </>
            )}
          </Modal>
        )}

        {/* ========================================================= */}
        {/* 🧾 BUDGET EXPENSE MODAL (DYNAMIC LOCAL MARKET FORM)       */}
        {/* ========================================================= */}
        <Modal show={showAddExpenseModal} onHide={() => setShowAddExpenseModal(false)} centered className="p-2 p-sm-0">
          <Modal.Header closeButton className="p-3">
            <Modal.Title className="fw-bold text-danger fs-5 fs-sm-4">
              {editingItemId ? t('modalTitleUpdate') : t('modalTitleLog')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-3 p-sm-4">
            {/* 🛑 LABOURE RESTRICTION ALERT - UI design nahi kharab hoga */}
            {userRole === 'Laboure' && (
              <div className="alert alert-warning small py-2 mb-3">
                {t('txtReadOnlyAccess', 'Access Restricted: You are not authorized to log expenses.')}
              </div>
            )}

            <Form onSubmit={async (e) => {
              e.preventDefault();
              if (currentUser?.role === 'Laboure') return;

              const token = localStorage.getItem('token');
              if (!token) return;
              const config = { headers: { 'x-auth-token': token } };

              const expensePayload = {
                item: expenseData.item,
                amount: Number(expenseData.amount),
                category: expenseData.category,
                paymentType: expenseData.paymentType || 'Cash',
                vendorName: expenseData.vendorName || 'General Market',
                receiptImage: expenseData.receiptImage || '',
                project: projectId,
                addedBy: currentUser?.name || 'Admin',
                updatedBy: editingItemId ? currentUser?.name : null
              };

              const localTemporaryId = editingItemId || 'temp_exp_' + Date.now().toString(36);
              let simulatedStatus = expensePayload.amount > 5000 ? 'Pending Approval' : 'Approved';

              const finalLocalData = {
                ...expensePayload,
                _id: localTemporaryId,
                status: simulatedStatus,
                createdAt: new Date().toISOString()
              };

              const saveToDeviceOfflineVault = async () => {
                try {
                  const syncUrl = editingItemId
                    ? `https://api.mfrbuild.com/api/expenses/${editingItemId}`
                    : 'https://api.mfrbuild.com/api/expenses';
                  const syncMethod = editingItemId ? 'PUT' : 'POST';

                  if (typeof offlineDB !== 'undefined' && offlineDB.outboxQueue) {
                    await offlineDB.outboxQueue.add({
                      url: syncUrl,
                      method: syncMethod,
                      data: expensePayload,
                      timestamp: Date.now()
                    });
                  }

                  setExpenses(prevExpenses => {
                    const current = Array.isArray(prevExpenses) ? prevExpenses : [];
                    if (editingItemId) {
                      return current.map(exp => exp._id === editingItemId ? finalLocalData : exp);
                    } else {
                      return [finalLocalData, ...current];
                    }
                  });

                  alert(`⚠️ Offline Saved!\nSaved locally to device storage vault.\nItem: ${expensePayload.item}`);
                } catch (dbErr) {
                  console.error("Local Storage Write Error:", dbErr.message);
                }
              };

              try {
                if (editingItemId) {
                  const res = await axiosReal.put(`https://api.mfrbuild.com/api/expenses/${editingItemId}`, expensePayload, config);
                  const updatedData = res.data?.data || res.data || finalLocalData;
                  setExpenses(expenses.map(exp => exp._id === editingItemId ? updatedData : exp));
                } else {
                  const res = await axiosReal.post('https://api.mfrbuild.com/api/expenses', expensePayload, config);
                  const newData = res.data?.data || res.data || finalLocalData;
                  setExpenses([newData, ...expenses]);
                }
                setShowAddExpenseModal(false);
              } catch (err) {
                console.warn("Network unreachable. Activating local device vault redirect.");
                await saveToDeviceOfflineVault();
                setShowAddExpenseModal(false);
              }
            }}>

              {/* 1. Item Name */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">{t('labelItemAssetName')}</Form.Label>
                <Form.Control type="text" className="form-control-lg fs-6" value={expenseData.item || ''}
                  onChange={(e) => setExpenseData({ ...expenseData, item: e.target.value })} required
                  disabled={userRole === 'Laboure'} />
              </Form.Group>

              {/* 2. Amount */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">{t('labelAmountPkr')}</Form.Label>
                <Form.Control type="number" className="form-control-lg fs-6" value={expenseData.amount || ''}
                  onChange={(e) => setExpenseData({ ...expenseData, amount: e.target.value })} required
                  disabled={userRole === 'Laboure'} />
              </Form.Group>

              {/* 3. Category */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">{t('labelCategoryFocus')}</Form.Label>
                <Form.Select className="form-select-lg fs-6" value={expenseData.category || 'Materials'}
                  onChange={(e) => setExpenseData({ ...expenseData, category: e.target.value })}
                  disabled={userRole === 'Laboure'}>
                  <option value="Materials">{t('optCategoryMaterials')}</option>
                  <option value="Logistics">{t('optCategoryLogistics')}</option>
                  <option value="Labor Wages">{t('optCategoryLabor')}</option>
                  <option value="Equipment">{t('optCategoryEquipment')}</option>
                  <option value="Overheads">{t('optCategoryOverheads')}</option>
                </Form.Select>
              </Form.Group>

              {/* 4. Payment Type */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">{t('labelPaymentMode')}</Form.Label>
                <Form.Select className="form-select-lg fs-6" value={expenseData.paymentType || 'Cash'}
                  onChange={(e) => setExpenseData({ ...expenseData, paymentType: e.target.value })}
                  disabled={userRole === 'Laboure'}>
                  <option value="Cash">{t('optPaymentCash')}</option>
                  <option value="Bank Transfer">{t('optPaymentBank')}</option>
                  <option value="Credit (Udhaar)">{t('optPaymentCredit')}</option>
                  <option value="EasyPaisa/JazzCash">{t('optPaymentMobile')}</option>
                </Form.Select>
              </Form.Group>

              {/* 5. Vendor */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">{t('labelVendorName')}</Form.Label>
                <Form.Control type="text" className="form-control-lg fs-6" value={expenseData.vendorName || ''}
                  placeholder={t('placeholderVendorName')} onChange={(e) => setExpenseData({ ...expenseData, vendorName: e.target.value })}
                  disabled={userRole === 'Laboure'} />
              </Form.Group>

              {/* 6. Receipt (Hidden for Laboure) */}
              {userRole !== 'Laboure' && (
                <Form.Group className="mb-4">
                  <Form.Label className="fw-semibold small">{t('labelUploadParchi')}</Form.Label>
                  <Form.Control type="file" className="fs-6" accept="image/*" onChange={handleParchiFileChange} />
                </Form.Group>
              )}

              {/* 7. Submit Button (Hidden for Laboure) */}
              {userRole !== 'Laboure' && (
                <Button variant={editingItemId ? "warning" : "danger"} type="submit" className="w-100 fw-bold py-2.5 shadow-sm rounded-3">
                  {editingItemId ? t('btnModifyExpense') : t('btnLogFinancialData')}
                </Button>
              )}
            </Form>
          </Modal.Body>
        </Modal>

        {/* Parchi Viewer Modal */}
        <Modal show={showReceiptModal} onHide={() => setShowReceiptModal(false)} centered size="md" className="p-2 p-sm-0">
          <Modal.Header closeButton className="p-3">
            <Modal.Title className="fw-bold text-danger fs-5 fs-sm-4">
              <i className="bi bi-receipt me-2"></i> {t('modalReceiptTitle')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center bg-light p-3 p-sm-4 d-flex justify-content-center align-items-center">
            {receiptPreviewUrl ? (
              <img
                src={receiptPreviewUrl}
                alt="Expense Receipt"
                className="img-fluid rounded shadow border border-2 w-100"
                style={{ maxHeight: '65vh', objectFit: 'contain' }}
              />
            ) : (
              <p className="text-muted mb-0 small">{t('txtReceiptLoadError')}</p>
            )}
          </Modal.Body>
          <Modal.Footer className="bg-white d-flex flex-column flex-sm-row justify-content-end gap-2 p-3">
            <Button variant="secondary" size="sm" onClick={() => setShowReceiptModal(false)}>{t('btnCancel')}</Button>
          </Modal.Footer>
        </Modal>


        {/* ======================================================== */}
        {/* 🎯 MODAL 1: ADD PUNCH ITEM MODAL (WITH BEFORE IMAGE LOGIC) */}
        {/* ======================================================== */}
        {showAddPunchModal && (
          <div className="modal d-block show fade px-2" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered mx-auto" style={{ maxWidth: '500px' }}>
              <div className="modal-content border-0 shadow-lg rounded-3">
                <div className="modal-header bg-primary text-white p-3">
                  <h5 className="modal-title fw-bold fs-5">{t('modalAddPunchTitle')}</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowAddPunchModal(false)}></button>
                </div>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  if (isLaboure) return; // Laboure restriction
                  handleCreatePunchSubmit(e);
                }}>
                  <div className="modal-body p-3 p-sm-4">
                    {isLaboure && <div className="alert alert-warning small">{t('txtReadOnlyAccess')}</div>}

                    <div className="mb-3">
                      <label className="form-label fw-bold small">{t('labelDefectDetail')}</label>
                      <input type="text" className="form-control form-control-lg fs-6"
                        placeholder={t('placeholderDefectDetail')} required value={newPunch.issueDescription}
                        disabled={isLaboure}
                        onChange={(e) => setNewPunch({ ...newPunch, issueDescription: e.target.value })} />

                      {!isLaboure && (
                        <div className="d-flex justify-content-end mt-2">
                          <button type="button" className="btn btn-primary btn-sm rounded-2 fw-semibold"
                            onClick={() => handleAITextPolish(newPunch.issueDescription, "QA/QC", (p) => setNewPunch(prev => ({ ...prev, issueDescription: p })))}>
                            <i className="bi bi-stars"></i> {t('btnAiTechnicalFormat')}
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="row g-3">
                      <div className="col-12 col-sm-6 mb-2">
                        <label className="form-label fw-bold small">{t('labelSpecificLocation')}</label>
                        <input type="text" className="form-control fs-6" placeholder={t('placeholderSpecificLocation')} required
                          disabled={isLaboure}
                          value={newPunch.location} onChange={(e) => setNewPunch({ ...newPunch, location: e.target.value })} />
                      </div>
                      <div className="col-12 col-sm-6 mb-2">
                        <label className="form-label fw-bold small">{t('labelResponsibleThekedar')}</label>
                        <select className="form-select fs-6" required disabled={isLaboure}
                          value={newPunch.assignedContractor} onChange={(e) => setNewPunch({ ...newPunch, assignedContractor: e.target.value })}>
                          <option value="">{t('optSelectContractor')}</option>
                          {contractorsList.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                        </select>
                      </div>
                    </div>

                    {!isLaboure && (
                      <div className="mb-2 mt-2">
                        <label className="form-label fw-bold small">{t('labelUploadEvidence')}</label>
                        <input type="file" className="form-control fs-6" accept="image/*" required onChange={(e) => setPunchBeforeImage(e.target.files[0])} />
                      </div>
                    )}
                  </div>

                  <div className="modal-footer bg-light p-3">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowAddPunchModal(false)}>{t('btnCancel')}</button>
                    {!isLaboure && <button type="submit" className="btn btn-danger fw-semibold">{t('btnLockDefect')}</button>}
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* RFI Modal with same Logic */}
        {showRfiModal && (
          <div className="modal d-block show fade px-2" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered mx-auto" style={{ maxWidth: '500px' }}>
              <div className="modal-content border-0 shadow-lg rounded-3">
                <div className="modal-header bg-warning text-dark p-3">
                  <h5 className="modal-title fw-bold fs-5">❓ {t('modalRfiTitle')}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowRfiModal(false)}></button>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); if (!isLaboure) handleCreateRfiSubmit(e); }}>
                  <div className="modal-body p-3 p-sm-4">
                    {isLaboure && <div className="alert alert-warning small">{t('txtReadOnlyAccess')}</div>}

                    <div className="mb-3">
                      <label className="form-label fw-bold small">{t('labelAssignTechQuery')}</label>
                      <select className="form-select fs-6" required disabled={isLaboure}
                        value={newRfi.assignedTo} onChange={(e) => setNewRfi({ ...newRfi, assignedTo: e.target.value })}>
                        <option value="">{t('optSelectProfessional')}</option>
                        {managementList.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
                      </select>
                    </div>

                    {!isLaboure && (
                      <div className="mb-3 p-3 bg-light rounded text-center">
                        <button type="button" className="btn btn-danger btn-sm" onClick={isRecording ? stopRecording : startRecording}>
                          {isRecording ? "⏹️ " + t('btnStopVoice') : "🎙️ " + t('btnRecordVoice')}
                        </button>
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="form-label fw-bold small">{t('labelTxtContext')}</label>
                      <textarea className="form-control fs-6" rows="2" disabled={isLaboure}
                        value={newRfi.textQuestion} onChange={(e) => setNewRfi({ ...newRfi, textQuestion: e.target.value })} />
                    </div>
                  </div>

                  <div className="modal-footer bg-light p-3">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowRfiModal(false)}>{t('btnCancel')}</button>
                    {!isLaboure && <button type="submit" className="btn btn-warning fw-bold">{t('btnTransmitRfi')}</button>}
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* 🧱 MODAL 1: MATERIAL INVENTORY STOCK IN/OUT */}
        {/* 📥 INVENTORY MODAL */}
        {showInventoryModal && (
          <div className="modal d-block position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 px-2" style={{ zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered mx-auto" style={{ maxWidth: '500px' }}>
              <div className="modal-content shadow-lg border-0 rounded-3">
                <div className="modal-header bg-primary text-white p-3">
                  <h5 className="modal-title fw-bold fs-5">
                    <i className="bi bi-box-seam me-2"></i>{t('modalAdjustStockTitle')}
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowInventoryModal(false)}></button>
                </div>

                <form onSubmit={async (e) => {
                  e.preventDefault();
                  // Security: Laboure cannot submit
                  if (userRole === 'Laboure') return;

                  const inventoryPayload = {
                    ...inventoryData,
                    addedBy: currentUser?.name || 'Admin',
                    updatedBy: editingItemId ? currentUser?.name : null
                  };

                  await handleInventorySubmit(e, inventoryPayload);
                }}>
                  <div className="modal-body text-start p-3 p-sm-4">

                    {/* Security Alert for Laboure */}
                    {userRole === 'Laboure' && (
                      <div className="alert alert-warning small py-2 mb-3">
                        {t('txtReadOnlyAccess', 'Access Restricted: You are not authorized to modify inventory.')}
                      </div>
                    )}

                    {/* Operation Type */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold small">{t('lblOperationType')}</label>
                      <select className="form-select fs-6"
                        disabled={userRole === 'Laboure'}
                        value={inventoryData.type || 'StockIn'}
                        onChange={(e) => setInventoryData({ ...inventoryData, type: e.target.value })}>
                        <option value="StockIn">📥 {t('optStockIn')}</option>
                        <option value="StockOut">📤 {t('optStockOut')}</option>
                      </select>
                    </div>

                    {/* Item Name */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold small">{t('lblMaterialItemName')}</label>
                      <input type="text" className="form-control form-control-lg fs-6"
                        placeholder={t('phMaterialItemName')} required disabled={userRole === 'Laboure'}
                        value={inventoryData.itemName || ''}
                        onChange={(e) => setInventoryData({ ...inventoryData, itemName: e.target.value })} />

                      {userRole !== 'Laboure' && (
                        <div className="d-flex justify-content-end mt-2">
                          <button type="button" className="btn btn-primary btn-sm rounded-2 fw-semibold"
                            onClick={() => handleAITextPolish(inventoryData.itemName, "Inventory", (p) => setInventoryData(prev => ({ ...prev, itemName: p })))}>
                            <i className="bi bi-stars"></i> {t('btnAIStandardizeName')}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Quantity & Unit */}
                    <div className="row g-3 mb-3">
                      <div className="col-12 col-sm-6">
                        <label className="form-label fw-semibold small">{t('lblQuantity')}</label>
                        <input type="number" className="form-control fs-6" min="1" required disabled={userRole === 'Laboure'}
                          value={inventoryData.quantityChange || ''} onChange={(e) => setInventoryData({ ...inventoryData, quantityChange: e.target.value })} />
                      </div>
                      <div className="col-12 col-sm-6">
                        <label className="form-label fw-semibold small">{t('lblUnitType')}</label>
                        <select className="form-select fs-6" value={inventoryData.unit || 'Bags'} disabled={userRole === 'Laboure'}
                          onChange={(e) => setInventoryData({ ...inventoryData, unit: e.target.value })}>
                          <option value="Bags">{t('unitBags')}</option>
                          <option value="Tons">{t('unitTons')}</option>
                          <option value="Pcs">{t('unitPieces')}</option>
                          <option value="Meters">{t('unitMeters')}</option>
                        </select>
                      </div>
                    </div>

                    {/* Category & Threshold */}
                    <div className="row g-3">
                      <div className="col-12 col-sm-6">
                        <label className="form-label fw-semibold small">{t('lblCategory')}</label>
                        <select className="form-select fs-6" value={inventoryData.category || 'Materials'} disabled={userRole === 'Laboure'}
                          onChange={(e) => setInventoryData({ ...inventoryData, category: e.target.value })}>
                          <option value="Materials">{t('catMaterials')}</option>
                          <option value="Structural Steel">{t('catStructuralSteel')}</option>
                          <option value="Finishing Assets">{t('catFinishingAssets')}</option>
                          <option value="Tools & Gear">{t('catToolsGear')}</option>
                        </select>
                      </div>
                      <div className="col-12 col-sm-6">
                        <label className="form-label fw-semibold small">{t('lblSafetyAlertLimit')}</label>
                        <input type="number" className="form-control fs-6" placeholder={t('phMinStockLevel')} disabled={userRole === 'Laboure'}
                          value={inventoryData.minThreshold || ''} onChange={(e) => setInventoryData({ ...inventoryData, minThreshold: e.target.value })} />
                      </div>
                    </div>
                  </div>

                  {/* Footer Action Buttons */}
                  {userRole !== 'Laboure' && (
                    <div className="modal-footer table-light d-flex flex-column flex-sm-row justify-content-end gap-2 p-3">
                      <button type="button" className="btn btn-secondary btn-sm rounded-2 w-100 w-sm-auto" onClick={() => setShowInventoryModal(false)}>
                        {t('btnCancel')}
                      </button>
                      <button type="submit" className="btn btn-primary btn-sm rounded-2 fw-bold px-3 w-100 w-sm-auto">
                        {t('btnSyncAssetEntry')}
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        )}

        {/* 📁 MODAL 2: UPLOAD BLUEPRINT DOCUMENT META */}
        {/* 📁 MODAL 1: LOG SITE BLUEPRINT PLAN */}
        {showDocumentModal && (
          <div className="modal d-block position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 px-2" style={{ zIndex: 1050 }}>
            <div className="modal-dialog modal-dialog-centered mx-auto" style={{ maxWidth: '500px' }}>
              <div className="modal-content shadow-lg border-0 rounded-3">
                <div className="modal-header bg-info text-white p-3">
                  <h5 className="modal-title fw-bold fs-5">
                    <i className="bi bi-file-earmark-pdf me-2"></i>{t('modalLogBlueprintTitle')}
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowDocumentModal(false)}></button>
                </div>
                <form onSubmit={handleDocumentSubmit}>
                  <div className="modal-body text-start p-3 p-sm-4">

                    {/* Document Title Field */}
                    <div className="mb-3">
                      <label className="form-label fw-semibold small">{t('lblBlueprintTitle')}</label>
                      <input
                        type="text"
                        className="form-control form-control-lg fs-6"
                        placeholder={t('phBlueprintTitle')}
                        required
                        value={documentData.title || ''}
                        onChange={(e) => setDocumentData({ ...documentData, title: e.target.value })}
                      />

                      {/* AI Button for Naming */}
                      <div className="d-flex justify-content-end mt-2">
                        <button
                          type="button"
                          className="btn btn-info text-white btn-sm rounded-2 fw-semibold d-flex align-items-center gap-1"
                          style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                          onClick={() => handleAITextPolish(
                            documentData.title,
                            "Site Drawing Blueprint Document Title",
                            (polished) => setDocumentData(prev => ({ ...prev, title: polished }))
                          )}
                        >
                          <i className="bi bi-stars"></i> {t('btnAIFormalizeTitle')}
                        </button>
                      </div>
                    </div>

                    {/* Category & File Extension Selectors */}
                    <div className="row g-3 mb-3">
                      <div className="col-12 col-sm-6">
                        <label className="form-label fw-semibold small">{t('lblCategoryType')}</label>
                        <select className="form-select fs-6" value={documentData.category || 'Architectural'} onChange={(e) => setDocumentData({ ...documentData, category: e.target.value })}>
                          <option value="Architectural">{t('catArchitectural')}</option>
                          <option value="Structural">{t('catStructural')}</option>
                          <option value="MEP Route Plan">{t('catMepRoutePlan')}</option>
                          <option value="Contracts">{t('catContracts')}</option>
                        </select>
                      </div>
                      <div className="col-12 col-sm-6">
                        <label className="form-label fw-semibold small">{t('lblFileExtension')}</label>
                        <select className="form-select fs-6" value={documentData.fileType || 'PDF'} onChange={(e) => setDocumentData({ ...documentData, fileType: e.target.value })}>
                          <option value="PDF">{t('optFormatPdf')}</option>
                          <option value="DWG">{t('optFormatDwg')}</option>
                          <option value="Image">{t('optFormatImage')}</option>
                        </select>
                      </div>
                    </div>

                    {/* Device Storage Upload Field */}
                    <div className="mb-2">
                      <label className="form-label fw-semibold text-dark small">
                        <i className="bi bi-cloud-arrow-up-fill me-1 text-info"></i> {t('lblSelectFileFromDevice')}
                      </label>
                      <input
                        type="file"
                        className="form-control shadow-sm fs-6"
                        accept=".pdf,.png,.jpg,.jpeg,.dwg"
                        onChange={handleLocalFileUpload}
                        required={!documentData.fileUrl}
                      />

                      {uploadingFile && (
                        <div className="text-info small fw-bold mt-2 d-flex align-items-center">
                          <div className="spinner-border spinner-border-sm text-info me-2" role="status"></div>
                          {t('txtUploadingToCloud')}
                        </div>
                      )}

                      {documentData.fileUrl && !uploadingFile && (
                        <div className="text-success small fw-bold mt-2 d-flex align-items-center">
                          <div className="spinner-border spinner-border-sm text-success me-2" role="status"></div>
                          {t('txtUploadSuccess')}
                        </div>
                      )}
                    </div>

                  </div>
                  <div className="modal-footer table-light d-flex flex-column flex-sm-row justify-content-end gap-2 p-3">
                    <button type="button" className="btn btn-secondary btn-sm rounded-2 w-100 w-sm-auto order-2 order-sm-1 py-2 py-sm-1.5" onClick={() => setShowDocumentModal(false)}>
                      {t('btnCancel')}
                    </button>
                    <button
                      type="submit"
                      className="btn btn-info text-white btn-sm rounded-2 fw-bold px-3 w-100 w-sm-auto order-1 order-sm-2 py-2 py-sm-1.5"
                      disabled={uploadingFile || !documentData.fileUrl}
                    >
                      {t('btnRegisterDocument')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
        }

        {/* 🔄 MODAL 2: NEW REVISION VERSION FORM */}
        {showRevisionModal && (
          <div className="modal d-block position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 px-2" style={{ zIndex: 1051 }}>
            <div className="modal-dialog modal-dialog-centered mx-auto" style={{ maxWidth: '500px' }}>
              <div className="modal-content shadow-lg border-0 rounded-3">
                <div className="modal-header bg-warning text-dark p-3">
                  <h5 className="modal-title fw-bold fs-5">
                    <i className="bi bi-arrow-up-right-circle me-2"></i>{t('modalRevisionTitle')}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setShowRevisionModal(false)}></button>
                </div>
                <form onSubmit={handleRevisionSubmit}>
                  <div className="modal-body text-start p-3 p-sm-4">
                    <p className="text-muted small mb-3">
                      {t('txtRevisionDescription')}
                    </p>
                    <div className="mb-2">
                      <label className="form-label fw-semibold small">{t('lblSelectUpdatedDrawing')}</label>
                      <input
                        type="file"
                        className="form-control fs-6"
                        accept=".pdf,.png,.jpg,.jpeg,.dwg"
                        required
                        onChange={async (e) => {
                          setUploadingFile(true);
                          const file = e.target.files[0];
                          if (file) {
                            // Placeholder bypass configuration
                            setRevisionUrl("https://cloudinary-mock-path.pdf");
                          }
                          setUploadingFile(false);
                        }}
                      />
                    </div>
                  </div>
                  <div className="modal-footer table-light d-flex flex-column flex-sm-row justify-content-end gap-2 p-3">
                    <button type="button" className="btn btn-secondary btn-sm w-100 w-sm-auto order-2 order-sm-1 py-2 py-sm-1.5" onClick={() => setShowRevisionModal(false)}>
                      {t('btnClose')}
                    </button>
                    <button type="submit" className="btn btn-warning btn-sm fw-bold shadow-sm w-100 w-sm-auto order-1 order-sm-2 py-2 py-sm-1.5" disabled={uploadingFile || !revisionUrl}>
                      {t('btnPushNewVersion')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
        }

        {/* 🛒 MODAL 1: PROCUREMENT  CREATE SITE MATERIAL INDENT */}
        {showReqModal && (
          <div className="modal d-block position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 px-2" style={{ zIndex: 1060 }}>
            <div className="modal-dialog modal-dialog-centered mx-auto" style={{ maxWidth: '500px' }}>
              <div className="modal-content shadow border-0 rounded-3">
                <div className="modal-header bg-info text-white p-3">
                  <h5 className="modal-title fw-bold fs-5"><i className="bi bi-cart-plus me-2"></i>{t('mdlTitleCreateIndent')}</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowReqModal(false)}></button>
                </div>
                <form onSubmit={handleReqSubmit}>
                  <div className="modal-body text-start p-3 p-sm-4">
                    <div className="mb-3">
                      <label className="form-label fw-semibold small">{t('lblMaterialName')}</label>
                      <input
                        type="text"
                        className="form-control fs-6"
                        placeholder={t('phMaterialName')}
                        required
                        value={reqForm.materialName}
                        onChange={(e) => setReqForm({ ...reqForm, materialName: e.target.value })}
                      />
                    </div>
                    <div className="row g-3 mb-3">
                      <div className="col-12 col-sm-6">
                        <label className="form-label fw-semibold small">{t('lblQuantity')}</label>
                        <input
                          type="number"
                          className="form-control fs-6"
                          required
                          value={reqForm.quantity === 0 ? '' : reqForm.quantity}
                          onChange={(e) => {
                            const val = e.target.value;
                            setReqForm({ ...reqForm, quantity: val === '' ? 0 : Number(val) });
                          }}
                        />
                      </div>
                      <div className="col-12 col-sm-6">
                        <label className="form-label fw-semibold small">{t('lblUnitType')}</label>
                        <select className="form-select fs-6" value={reqForm.unit} onChange={(e) => setReqForm({ ...reqForm, unit: e.target.value })}>
                          <option value="Bags">{t('optBags')}</option>
                          <option value="Tons">{t('optTons')}</option>
                          <option value="Pcs">{t('optPcs')}</option>
                          <option value="Meters">{t('optMeters')}</option>
                        </select>
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="form-label fw-semibold small">{t('lblEstimatedBudget')}</label>
                      <input
                        type="number"
                        className="form-control fs-6"
                        required
                        value={reqForm.estimatedBudget === 0 ? '' : reqForm.estimatedBudget}
                        onChange={(e) => {
                          const val = e.target.value;
                          setReqForm({ ...reqForm, estimatedBudget: val === '' ? 0 : Number(val) });
                        }}
                      />
                    </div>
                  </div>
                  <div className="modal-footer table-light d-flex flex-column flex-sm-row justify-content-end gap-2 p-3">
                    <button type="button" className="btn btn-secondary btn-sm rounded w-100 w-sm-auto order-2 order-sm-1 py-2 py-sm-1.5" onClick={() => setShowReqModal(false)}>{t('btnCancel')}</button>
                    <button type="submit" className="btn btn-info text-white btn-sm rounded fw-bold px-3 w-100 w-sm-auto order-1 order-sm-2 py-2 py-sm-1.5">{t('btnDispatchIndent')}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* 💰 MODAL 2: LOG VENDOR QUOTATION RESPONSE */}
        {showQuoteModal && (
          <div className="modal d-block position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 px-2" style={{ zIndex: 1061 }}>
            <div className="modal-dialog modal-dialog-centered mx-auto" style={{ maxWidth: '500px' }}>
              <div className="modal-content shadow border-0 rounded-3">
                <div className="modal-header bg-warning text-dark p-3">
                  <h5 className="modal-title fw-bold fs-5"><i className="bi bi-telephone-plus me-2"></i>{t('mdlTitleLogQuote')}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowQuoteModal(false)}></button>
                </div>
                <form onSubmit={handleQuoteSubmit}>
                  <div className="modal-body text-start p-3 p-sm-4">
                    <div className="mb-3">
                      <label className="form-label fw-semibold small">{t('lblVendorTitle')}</label>
                      <input
                        type="text"
                        className="form-control fs-6"
                        placeholder={t('phVendorTitle')}
                        required
                        value={quoteForm.vendorName}
                        onChange={(e) => setQuoteForm({ ...quoteForm, vendorName: e.target.value })}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold small">{t('lblVendorPhone')}</label>
                      <input
                        type="text"
                        className="form-control fs-6"
                        placeholder={t('phVendorPhone')}
                        required
                        value={quoteForm.vendorPhone}
                        onChange={(e) => setQuoteForm({ ...quoteForm, vendorPhone: e.target.value })}
                      />
                    </div>
                    <div className="row g-3 mb-2">
                      <div className="col-12 col-sm-6">
                        <label className="form-label fw-semibold small">{t('lblTotalPriceBid')}</label>
                        <input
                          type="number"
                          className="form-control fs-6"
                          required
                          value={quoteForm.ratePerUnit === 0 ? '' : quoteForm.ratePerUnit}
                          onChange={(e) => {
                            const val = e.target.value;
                            setQuoteForm({ ...quoteForm, ratePerUnit: val === '' ? 0 : Number(val) });
                          }}
                        />
                      </div>
                      <div className="col-12 col-sm-6">
                        <label className="form-label fw-semibold small">{t('lblDeliverySpeedDays')}</label>
                        <input
                          type="number"
                          className="form-control fs-6"
                          required
                          value={quoteForm.deliveryLeadTimeDays === 0 ? '' : quoteForm.deliveryLeadTimeDays}
                          onChange={(e) => {
                            const val = e.target.value;
                            setQuoteForm({ ...quoteForm, deliveryLeadTimeDays: val === '' ? 0 : Number(val) });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer table-light d-flex flex-column flex-sm-row justify-content-end gap-2 p-3">
                    <button type="button" className="btn btn-secondary btn-sm w-100 w-sm-auto order-2 order-sm-1 py-2 py-sm-1.5" onClick={() => setShowQuoteModal(false)}>{t('btnClose')}</button>
                    <button type="submit" className="btn btn-warning text-dark btn-sm fw-bold w-100 w-sm-auto order-1 order-sm-2 py-2 py-sm-1.5">{t('btnRegisterRateBid')}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* ⚖️ MODAL 3: LIVE 3-WAY MATCHING AUDIT COMPLIANCE FORM */}
        {showMatchModal && (
          <div className="modal d-block position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 px-2" style={{ zIndex: 1062 }}>
            <div className="modal-dialog modal-dialog-centered mx-auto" style={{ maxWidth: '500px' }}>
              <div className="modal-content shadow border-0 rounded-3">
                <div className="modal-header bg-success text-white p-3">
                  <h5 className="modal-title fw-bold fs-5"><i className="bi bi-shield-shaded me-2"></i>{t('mdlTitleExecuteAudit')}</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setShowMatchModal(false)}></button>
                </div>
                <form onSubmit={handleAuditSubmit}>
                  <div className="modal-body text-start p-3 p-sm-4">
                    <div className="mb-3">
                      <label className="form-label fw-semibold small">{t('lblOrderedQuantity')}</label>
                      <input
                        type="number"
                        className="form-control fs-6"
                        required
                        value={auditForm.orderedQuantity === 0 ? '' : auditForm.orderedQuantity}
                        onChange={(e) => {
                          const val = e.target.value;
                          setAuditForm({ ...auditForm, orderedQuantity: val === '' ? 0 : Number(val) });
                        }}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label fw-semibold small">{t('lblReceivedQuantity')}</label>
                      <input
                        type="number"
                        className="form-control fs-6"
                        required
                        value={auditForm.receivedQuantity === 0 ? '' : auditForm.receivedQuantity}
                        onChange={(e) => {
                          const val = e.target.value;
                          setAuditForm({ ...auditForm, receivedQuantity: val === '' ? 0 : Number(val) });
                        }}
                      />
                    </div>
                    <div className="mb-2">
                      <label className="form-label fw-semibold small">{t('lblInvoiceAmountClaimed')}</label>
                      <input
                        type="number"
                        className="form-control fs-6"
                        required
                        value={auditForm.invoiceAmount === 0 ? '' : auditForm.invoiceAmount}
                        onChange={(e) => {
                          const val = e.target.value;
                          setAuditForm({ ...auditForm, invoiceAmount: val === '' ? 0 : Number(val) });
                        }}
                      />
                    </div>
                  </div>
                  <div className="modal-footer table-light d-flex flex-column flex-sm-row justify-content-end gap-2 p-3">
                    <button type="button" className="btn btn-secondary btn-sm w-100 w-sm-auto order-2 order-sm-1 py-2 py-sm-1.5" onClick={() => setShowMatchModal(false)}>{t('btnClose')}</button>
                    <button type="submit" className="btn btn-success btn-sm fw-bold text-white px-3 w-100 w-sm-auto order-1 order-sm-2 py-2 py-sm-1.5">{t('btnRunCompliance Check')}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* =========================================================================
    5. SAFETY MODULE MODALS COMPONENTS
    ========================================================================= */}

        {/* ========================================================= */}
        {/* 🛡️ MODAL 1: LOG INSPECTION & MORNING TBT BRIEFING        */}
        {/* ========================================================= */}
        <Modal show={showInspectionModal} onHide={() => setShowInspectionModal(false)} centered className="px-2">
          <Modal.Header closeButton className="p-3">
            <Modal.Title className="fw-bold text-primary fs-5">
              <i className="bi bi-clipboard-check-fill me-2"></i>{t('mdlInspectionTitle')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-3 p-sm-4 text-start">
            <Form onSubmit={async (e) => {
              e.preventDefault();
              const token = localStorage.getItem('token');
              const config = { headers: { 'x-auth-token': token } };

              const payload = {
                project: projectId,
                projectId: projectId,
                inspectionChecklist: {
                  hasHelmetsAndBoots: inspectionForm?.checklist?.hasHelmetsAndBoots || false,
                  isScaffoldingSecure: inspectionForm?.checklist?.isScaffoldingSecure || false,
                  isFireExtinguisherPresent: inspectionForm?.checklist?.isFireExtinguisherPresent || false,
                  hasDangerSignage: inspectionForm?.checklist?.hasDangerSignage || false
                },
                toolboxTalk: {
                  topic: inspectionForm?.tbtTopic || "General Site Safety",
                  attendanceCount: Number(inspectionForm?.tbtAttendance) || 0,
                  tbtImageUrl: inspectionForm?.tbtImageUrl || ""
                }
              };

              try {
                const res = await axiosReal.post('https://api.mfrbuild.com/api/safety/inspection', payload, config);
                if (res.data.success) {
                  alert(t('alertInspectionSuccess'));
                  if (typeof fetchSafetyDashboard === 'function') fetchSafetyDashboard();
                  setShowInspectionModal(false);
                }
              } catch (err) {
                console.error("Inspection Save Error:", err);
                alert(t('alertBackendFailure') + (err.response?.data?.message || err.message));
              }
            }}>
              <h6 className="fw-bold text-secondary mb-3 border-bottom pb-1 small">{t('secSafetyChecklist')}</h6>

              <Form.Group className="mb-2 d-flex justify-content-between align-items-center bg-light p-2 rounded">
                <Form.Label className="mb-0 fw-medium small text-wrap me-2">{t('lblHelmetsChecked')}</Form.Label>
                <Form.Check type="switch" id="helmets-check" checked={inspectionForm?.checklist?.hasHelmetsAndBoots || false} onChange={(e) => setInspectionForm({ ...inspectionForm, checklist: { ...inspectionForm.checklist, hasHelmetsAndBoots: e.target.checked } })} />
              </Form.Group>

              <Form.Group className="mb-2 d-flex justify-content-between align-items-center bg-light p-2 rounded">
                <Form.Label className="mb-0 fw-medium small text-wrap me-2">{t('lblScaffoldingSecure')}</Form.Label>
                <Form.Check type="switch" id="scaffold-check" checked={inspectionForm?.checklist?.isScaffoldingSecure || false} onChange={(e) => setInspectionForm({ ...inspectionForm, checklist: { ...inspectionForm.checklist, isScaffoldingSecure: e.target.checked } })} />
              </Form.Group>

              <Form.Group className="mb-2 d-flex justify-content-between align-items-center bg-light p-2 rounded">
                <Form.Label className="mb-0 fw-medium small text-wrap me-2">{t('lblExtinguishersCharged')}</Form.Label>
                <Form.Check type="switch" id="fire-check" checked={inspectionForm?.checklist?.isFireExtinguisherPresent || false} onChange={(e) => setInspectionForm({ ...inspectionForm, checklist: { ...inspectionForm.checklist, isFireExtinguisherPresent: e.target.checked } })} />
              </Form.Group>

              <Form.Group className="mb-4 d-flex justify-content-between align-items-center bg-light p-2 rounded">
                <Form.Label className="mb-0 fw-medium small text-wrap me-2">{t('lblDangerSignageDeployed')}</Form.Label>
                <Form.Check type="switch" id="signage-check" checked={inspectionForm?.checklist?.isSignageVisible || false} onChange={(e) => setInspectionForm({ ...inspectionForm, checklist: { ...inspectionForm.checklist, isSignageVisible: e.target.checked } })} />
              </Form.Group>

              <h6 className="fw-bold text-secondary mb-3 border-bottom pb-1 small">{t('secMorningTbt')}</h6>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">{t('lblTbtTopicCovered')}</Form.Label>
                <Form.Control type="text" className="fs-6" placeholder={t('phTbtTopic')} value={inspectionForm.tbtTopic || ''} onChange={(e) => setInspectionForm({ ...inspectionForm, tbtTopic: e.target.value })} required />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label className="small fw-semibold">{t('lblWorkerAttendance')}</Form.Label>
                <Form.Control type="number" className="fs-6" placeholder={t('phWorkerAttendance')} value={inspectionForm.tbtAttendance || ''} onChange={(e) => setInspectionForm({ ...inspectionForm, tbtAttendance: e.target.value })} required />
              </Form.Group>

              <Button variant="primary" type="submit" className="w-100 fw-bold shadow-sm py-2">{t('btnSaveInspection')}</Button>
            </Form>
          </Modal.Body>
        </Modal>

        {/* ========================================================= */}
        {/* 🛡️ MODAL 2: DIGITAL WORK PERMIT APPLICATION              */}
        {/* ========================================================= */}
        <Modal show={showPermitModal} onHide={() => setShowPermitModal(false)} centered className="px-2">
          <Modal.Header closeButton className="p-3">
            <Modal.Title className="fw-bold text-dark fs-5">
              <i className="bi bi-file-earmark-lock-fill me-2 text-warning"></i>{t('mdlPermitTitle')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-3 p-sm-4 text-start">
            <Form onSubmit={handlePermitSubmit}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">{t('lblPermitClassification')}</Form.Label>
                <Form.Select
                  className="fs-6"
                  value={permitForm.permitType || 'Hot Work / Welding'}
                  onChange={(e) => setPermitForm({ ...permitForm, permitType: e.target.value })}
                >
                  <option value="Hot Work / Welding">{t('optHotWork')}</option>
                  <option value="Height Work > 2 Meters">{t('optHeightWork')}</option>
                  <option value="Confined Space Entry">{t('optConfinedSpace')}</option>
                  <option value="Electrical Shutdown Work">{t('optElectricalShutdown')}</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">{t('lblApplicantName')}</Form.Label>
                <Form.Control
                  type="text"
                  className="fs-6"
                  placeholder={t('phApplicantName')}
                  value={permitForm.requestedBy || ''}
                  onChange={(e) => setPermitForm({ ...permitForm, requestedBy: e.target.value })}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">{t('lblPermitExpiry')}</Form.Label>
                <Form.Control
                  type="datetime-local"
                  className="fs-6"
                  value={permitForm.validTill || ''}
                  onChange={(e) => setPermitForm({ ...permitForm, validTill: e.target.value })}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-4 d-flex justify-content-between align-items-center bg-warning-subtle p-2 rounded border border-warning border-opacity-50">
                <Form.Label className="mb-0 small fw-bold text-dark text-wrap me-2">{t('lblConfirmControls')}</Form.Label>
                <Form.Check
                  type="checkbox"
                  id="permit-safety-lock"
                  checked={permitForm.safetyChecklistPassed || false}
                  onChange={(e) => setPermitForm({ ...permitForm, safetyChecklistPassed: e.target.checked })}
                  required
                />
              </Form.Group>

              <Button variant="warning" type="submit" className="w-100 fw-bold text-dark shadow-sm py-2">
                {t('btnSubmitPermit')}
              </Button>
            </Form>
          </Modal.Body>
        </Modal>

        {/* ========================================================= */}
        {/* 🚨 MODAL 3: EMERGENCY INCIDENT REPORT DISPATCHER          */}
        {/* ========================================================= */}
        <Modal show={showIncidentModal} onHide={() => setShowIncidentModal(false)} centered className="px-2">
          <Modal.Header closeButton className="p-3">
            <Modal.Title className="fw-bold text-danger fs-5">
              <i className="bi bi-exclamation-octagon-fill me-2"></i>🚨 {t('mdlIncidentTitle')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-3 p-sm-4 text-start">
            <Form onSubmit={handleIncidentSubmit}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">{t('lblIncidentTitle')}</Form.Label>
                <Form.Control type="text" className="fs-6" placeholder={t('phIncidentTitle')} value={incidentForm.title || ''} onChange={(e) => setIncidentForm({ ...incidentForm, title: e.target.value })} required />
              </Form.Group>

              <div className="row g-3 mb-3">
                <div className="col-12 col-sm-6">
                  <Form.Group>
                    <Form.Label className="fw-semibold small">{t('lblSeverityType')}</Form.Label>
                    <Form.Select className="fs-6" value={incidentForm.incidentType || 'Near-Miss'} onChange={(e) => setIncidentForm({ ...incidentForm, incidentType: e.target.value })}>
                      <option value="Near-Miss">{t('optNearMiss')}</option>
                      <option value="First-Aid Case">{t('optFirstAid')}</option>
                      <option value="Major Injury">{t('optMajorInjury')}</option>
                      <option value="Asset Damage">{t('optAssetDamage')}</option>
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-12 col-sm-6">
                  <Form.Group>
                    <Form.Label className="fw-semibold small">{t('lblSiteLocation')}</Form.Label>
                    <Form.Control type="text" className="fs-6" placeholder={t('phSiteLocation')} value={incidentForm.location || ''} onChange={(e) => setIncidentForm({ ...incidentForm, location: e.target.value })} required />
                  </Form.Group>
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">{t('lblIncidentDesc')}</Form.Label>
                <Form.Control as="textarea" rows={2} className="fs-6" value={incidentForm.description || ''} onChange={(e) => setIncidentForm({ ...incidentForm, description: e.target.value })} required />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold text-success small">{t('lblCapaTaken')}</Form.Label>
                <Form.Control as="textarea" rows={2} className="fs-6" placeholder={t('phCapaTaken')} value={incidentForm.actionTaken || ''} onChange={(e) => setIncidentForm({ ...incidentForm, actionTaken: e.target.value })} required />
              </Form.Group>

              <Button variant="danger" type="submit" className="w-100 fw-bold shadow-sm py-2">{t('btnBroadcastIncident')}</Button>
            </Form>
          </Modal.Body>
        </Modal>

        {/* ========================================================= */}
        {/* 🛡️ MODAL 4: SUBCONTRACTOR PENALTY MATRIX FORM             */}
        {/* ========================================================= */}
        <Modal show={showFineModal} onHide={() => setShowFineModal(false)} centered className="px-2">
          <Modal.Header closeButton className="p-3">
            <Modal.Title className="fw-bold text-secondary fs-5">
              <i className="bi bi-cash-stack me-2"></i>{t('mdlFineTitle')}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-3 p-sm-4 text-start">
            <Form onSubmit={handleFineSubmit}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">{t('lblTargetSubcontractor')}</Form.Label>
                <Form.Control type="text" className="fs-6" placeholder={t('phTargetSubcontractor')} value={fineForm.subcontractorName || ''} onChange={(e) => setFineForm({ ...fineForm, subcontractorName: e.target.value })} required />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">{t('lblComplianceReason')}</Form.Label>
                <Form.Control type="text" className="fs-6" placeholder={t('phComplianceReason')} value={fineForm.violationReason || ''} onChange={(e) => setFineForm({ ...fineForm, violationReason: e.target.value })} required />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold small">{t('lblFineAmountPkr')}</Form.Label>
                <Form.Control type="number" className="fs-6" placeholder={t('phFineAmountPkr')} value={fineForm.fineAmount || ''} onChange={(e) => setFineForm({ ...fineForm, fineAmount: e.target.value })} required />
              </Form.Group>

              <Button variant="secondary" type="submit" className="w-100 fw-bold shadow-sm py-2">{t('btnCommitFine')}</Button>
            </Form>
          </Modal.Body>
        </Modal>

        {/* =========================================================================
    ⚙️ MACHINERY FLEET MANAGEMENT DIALOGUES (BOOTSTRAP 5 CORE ELEMENTS)
    ========================================================================= */}
        {/* MODAL 1: MACHINERY ASSET DEPLOYMENT */}
        {/* MODAL 1: ONBOARD ASSET */}
        <Modal show={showAssetModal} onHide={() => setShowAssetModal(false)} centered className="px-2">
          <Modal.Header closeButton className="bg-dark text-white p-3">
            <Modal.Title className="fw-bold fs-5">{t('modalOnboardTitle')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-start p-3 p-sm-4">
            <Form onSubmit={handleCreateAssetSubmit}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold small text-secondary">{t('lblAssetMachineryName')}</Form.Label>
                <Form.Control type="text" className="fs-6" placeholder={t('phAssetMachineryName')} value={assetForm.name} onChange={(e) => setAssetForm({ ...assetForm, name: e.target.value })} required />
              </Form.Group>
              <Row className="g-3">
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-secondary">{t('lblModelNumberForm')}</Form.Label>
                    <Form.Control type="text" className="fs-6" placeholder="e.g., 2024-X1" value={assetForm.modelNumber} onChange={(e) => setAssetForm({ ...assetForm, modelNumber: e.target.value })} required />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-secondary">{t('lblSerialRegistrationNumber')}</Form.Label>
                    <Form.Control type="text" className="fs-6" placeholder="e.g., SN-88273" value={assetForm.serialNumber} onChange={(e) => setAssetForm({ ...assetForm, serialNumber: e.target.value })} required />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="g-3">
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-secondary">{t('lblMachineSegmentCategory')}</Form.Label>
                    <Form.Select className="fs-6" value={assetForm.category} onChange={(e) => setAssetForm({ ...assetForm, category: e.target.value })}>
                      <option value="Heavy Machinery">{t('optHeavyMachinery')}</option>
                      <option value="Vehicle">{t('optLogisticsVehicle')}</option>
                      <option value="Mixer">{t('optConcreteMixer')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-secondary">{t('lblOwnershipClass')}</Form.Label>
                    <Form.Select className="fs-6" value={assetForm.ownership} onChange={(e) => setAssetForm({ ...assetForm, ownership: e.target.value })}>
                      <option value="Owned">{t('optOwnedCompany')}</option>
                      <option value="Rented">{t('optRentedThirdParty')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="g-3">
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-secondary">{t('lblCostProfile')}</Form.Label>
                    <Form.Control type="number" className="fs-6" placeholder={t('phValueAmountPkr')} value={assetForm.ownership === 'Owned' ? assetForm.purchasePrice : assetForm.rentalRatePerHour} onChange={(e) => assetForm.ownership === 'Owned' ? setAssetForm({ ...assetForm, purchasePrice: e.target.value }) : setAssetForm({ ...assetForm, rentalRatePerHour: e.target.value })} required />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-secondary">{t('lblServiceIntervalHours')}</Form.Label>
                    <Form.Control type="number" className="fs-6" placeholder="e.g., 250" value={assetForm.nextServiceDueHours} onChange={(e) => setAssetForm({ ...assetForm, nextServiceDueHours: e.target.value })} required />
                  </Form.Group>
                </Col>
              </Row>
              <Button variant="primary" type="submit" className="w-100 py-2 mt-2 fw-semibold shadow-sm">{t('btnSaveAssetParameters')}</Button>
            </Form>
          </Modal.Body>
        </Modal>

        {/* MODAL 2: OPERATIONS USAGE LOG WORK & FUEL ENTRY */}
        <Modal show={showLogShiftModal} onHide={() => setShowLogShiftModal(false)} centered size="lg" className="px-2">
          <Modal.Header closeButton className="bg-info text-white p-3">
            <Modal.Title className="fw-bold text-white fs-5">{t('modalLogShiftTitle')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-start p-3 p-sm-4">
            <Form onSubmit={handleShiftLogSubmit}>
              <Row className="g-3">
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-secondary">{t('lblTargetDeploymentMachine')}</Form.Label>
                    <Form.Select className="fs-6" value={shiftLogForm.assetId} onChange={(e) => setShiftLogForm({ ...shiftLogForm, assetId: e.target.value })} required>
                      <option value="">{t('optChooseMachineTarget')}</option>
                      {assets.map(a => <option key={a._id} value={a._id}>{a.name} ({a.status})</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-secondary">{t('lblAssignedOperatorLead')}</Form.Label>
                    <Form.Select className="fs-6" value={shiftLogForm.operatorId} onChange={(e) => setShiftLogForm({ ...shiftLogForm, operatorId: e.target.value })} required>
                      <option value="">{t('optChooseRegisteredMember')}</option>
                      {allUsers.map(u => <option key={u._id} value={u._id}>{u.name} ({u.role})</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="g-3">
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-secondary">{t('lblNetEngineHoursOperated')}</Form.Label>
                    <Form.Control type="number" className="fs-6" placeholder={t('phHoursCount')} value={shiftLogForm.hoursOperated} onChange={(e) => setShiftLogForm({ ...shiftLogForm, hoursOperated: e.target.value })} required />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-secondary">{t('lblIdleStandbyEngineHours')}</Form.Label>
                    <Form.Control type="number" className="fs-6" placeholder={t('phHoursCount')} value={shiftLogForm.idleHours} onChange={(e) => setShiftLogForm({ ...shiftLogForm, idleHours: e.target.value })} />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="g-3">
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-secondary">{t('lblRefuelFeedAdditions')}</Form.Label>
                    <Form.Control type="number" className="fs-6" placeholder={t('phLitersEntry')} value={shiftLogForm.fuelAddedLiters} onChange={(e) => setShiftLogForm({ ...shiftLogForm, fuelAddedLiters: e.target.value })} />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-secondary">{t('lblTotalFuelCostInvoice')}</Form.Label>
                    <Form.Control type="number" className="fs-6" placeholder={t('phCostAmount')} value={shiftLogForm.fuelCost} onChange={(e) => setShiftLogForm({ ...shiftLogForm, fuelCost: e.target.value })} />
                  </Form.Group>
                </Col>
              </Row>
              <div className="form-check form-switch bg-light p-3 border rounded-3 mb-4 d-flex align-items-start gap-2 flex-wrap">
                <input className="form-check-input m-0 mt-1" type="checkbox" id="checklistSwitch" checked={shiftLogForm.preStartChecklistPassed} onChange={(e) => setShiftLogForm({ ...shiftLogForm, preStartChecklistPassed: e.target.checked })} style={{ cursor: 'pointer' }} />
                <div className="flex-grow-1 ps-1 text-wrap" style={{ maxWidth: 'calc(100% - 40px)' }}>
                  <label className="form-check-label fw-bold text-dark d-block" htmlFor="checklistSwitch">🛡️ {t('lblDigitalPreStartSafety')}</label>
                  <small className="text-muted d-block mt-1">{t('descDigitalPreStartSafety')}</small>
                </div>
              </div>
              <Button variant="info" type="submit" className="w-100 py-2 text-white fw-bold shadow-sm">{t('btnSaveLogToLedger')}</Button>
            </Form>
          </Modal.Body>
        </Modal>

        {/* MODAL 3: BREAKDOWN & MAINTENANCE TICKET ENGINE */}
        <Modal show={showMaintenanceModal} onHide={() => setShowMaintenanceModal(false)} centered className="px-2">
          <Modal.Header closeButton className="bg-danger text-white p-3">
            <Modal.Title className="fw-bold text-white fs-5">{t('modalMaintenanceTitle')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-start p-3 p-sm-4">
            <Form onSubmit={handleMaintenanceSubmit}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold small text-secondary">{t('lblTargetDefectiveMachine')}</Form.Label>
                <Form.Select className="fs-6" value={maintenanceForm.assetId} onChange={(e) => setMaintenanceForm({ ...maintenanceForm, assetId: e.target.value })} required>
                  <option value="">{t('optChooseTargetMachine')}</option>
                  {assets.map(a => <option key={a._id} value={a._id}>{a.name} ({t('lblCurrent')}: {a.status})</option>)}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold small text-secondary">{t('lblMaintenanceTaskCategory')}</Form.Label>
                <Form.Select className="fs-6" value={maintenanceForm.type} onChange={(e) => setMaintenanceForm({ ...maintenanceForm, type: e.target.value })}>
                  <option value="Preventative">{t('optPreventativeService')}</option>
                  <option value="Corrective/Breakdown">{t('optFieldBreakdownResponse')}</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold small text-secondary">{t('lblDiagnosticObservations')}</Form.Label>
                <Form.Control as="textarea" rows={3} className="fs-6" placeholder={t('phDiagnosticObservations')} value={maintenanceForm.description} onChange={(e) => setMaintenanceForm({ ...maintenanceForm, description: e.target.value })} required />
              </Form.Group>
              <Button variant="danger" type="submit" className="w-100 py-2 fw-bold shadow-sm">{t('btnLockAndInitializeLoop')}</Button>
            </Form>
          </Modal.Body>
        </Modal>

        {/* =========================================================================
    🏢 BIM 3D INTEGRATION DIALOGUES (BOOTSTRAP COMPLIANT MODALS BANK)
    ========================================================================= */}

        {/* MODAL 1: LOAD NEW REVISION BIM FILE */}
        {/* MODAL 1: MODEL UPLOAD REVISION */}
        <Modal show={showModelUploadModal} onHide={() => setShowModelUploadModal(false)} centered className="px-2">
          <Modal.Header closeButton className="bg-dark text-white p-3">
            <Modal.Title className="fw-bold text-white fs-5">{t('modalUploadModelTitle')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-start p-3 p-sm-4">
            <Form onSubmit={handleBimModelSubmit}>
              {/* 📝 Model Title */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold small text-secondary">{t('lblModelRevisionTitle')}</Form.Label>
                <Form.Control
                  type="text"
                  className="fs-6"
                  placeholder={t('phModelRevisionTitle')}
                  value={bimModelForm.title || ''}
                  onChange={(e) => setBimModelForm({ ...bimModelForm, title: e.target.value })}
                  required
                />
              </Form.Group>

              {/* 📁 NEW FIXED FILE PICKER */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold small text-success">
                  <i className="bi bi-box-seam me-1"></i>{t('lblSelect3dAssetFile')}
                </Form.Label>
                <Form.Control
                  type="file"
                  className="fs-6"
                  accept=".ifc,.gltf,.glb,.obj"
                  required
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      const selectedFile = e.target.files[0];
                      const sizeInMB = (selectedFile.size / (1024 * 1024)).toFixed(2);
                      const computedSizeStr = `${sizeInMB} MB`;

                      setBimModelForm({
                        ...bimModelForm,
                        modelFile: selectedFile,
                        fileSize: computedSizeStr
                      });
                    }
                  }}
                />
                <div className="form-text text-muted small">{t('descUploadBimFile')}</div>
              </Form.Group>

              {/* 📊 Computed File Size */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold small text-secondary">{t('lblComputedPayloadSize')}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="0.00 MB"
                  value={bimModelForm.fileSize || ''}
                  readOnly
                  className="bg-light text-muted fs-6"
                />
              </Form.Group>

              <Button variant="dark" type="submit" className="w-100 py-2 mt-2 fw-semibold shadow-sm">
                {t('btnLoadModelToViewport')}
              </Button>
            </Form>
          </Modal.Body>
        </Modal>

        {/* MODAL 2: SPATIAL PINPOINT COORDINATES ANNOTATION TICKET */}
        <Modal show={showClashTicketModal} onHide={() => setShowClashTicketModal(false)} centered size="lg" className="px-2">
          <Modal.Header closeButton className="bg-danger text-white p-3">
            <Modal.Title className="fw-bold text-white fs-5">{t('modalPinClashTitle')}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-start p-3 p-sm-4">
            <Form onSubmit={handleClashTicketSubmit}>
              <Row className="g-3">
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-secondary">{t('lblInterferenceElementTitle')}</Form.Label>
                    <Form.Control type="text" className="fs-6" placeholder={t('phInterferenceElementTitle')} value={clashTicketForm.title} onChange={(e) => setClashTicketForm({ ...clashTicketForm, title: e.target.value })} required />
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small text-secondary">{t('lblClashArchSegment')}</Form.Label>
                    <Form.Select className="fs-6" value={clashTicketForm.clashType} onChange={(e) => setClashTicketForm({ ...clashTicketForm, clashType: e.target.value })}>
                      <option value="Hard Clash">{t('optHardClash')}</option>
                      <option value="Soft Clash/Clearance">{t('optSoftClash')}</option>
                      <option value="Workflow Interference">{t('optWorkflowInterference')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold small text-secondary">{t('lblDiagnosticStructuralDesc')}</Form.Label>
                <Form.Control as="textarea" rows={2} className="fs-6" placeholder={t('phDiagnosticStructuralDesc')} value={clashTicketForm.description} onChange={(e) => setClashTicketForm({ ...clashTicketForm, description: e.target.value })} required />
              </Form.Group>

              {/* 📍 3D GEOMETRIC SPATIAL AXIS CONFIGURATOR VECTORS */}
              <div className="p-3 bg-light rounded-3 mb-3 border">
                <h6 className="fw-bold text-dark mb-3"><i className="bi bi-calculator text-danger me-1"></i>{t('titleVectorPointMapping')}</h6>
                <Row className="g-2">
                  <Col xs={12} sm={4}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small fw-semibold text-muted">{t('lblXaxisCoordinate')}</Form.Label>
                      <Form.Control type="number" step="any" className="fs-6" placeholder="e.g., 14.25" value={clashTicketForm.coordinateX} onChange={(e) => setClashTicketForm({ ...clashTicketForm, coordinateX: e.target.value })} required />
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={4}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small fw-semibold text-muted">{t('lblYaxisCoordinate')}</Form.Label>
                      <Form.Control type="number" step="any" className="fs-6" placeholder="e.g., -8.41" value={clashTicketForm.coordinateY} onChange={(e) => setClashTicketForm({ ...clashTicketForm, coordinateY: e.target.value })} required />
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={4}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small fw-semibold text-muted">{t('lblZaxisCoordinate')}</Form.Label>
                      <Form.Control type="number" step="any" className="fs-6" placeholder="e.g., 102.5" value={clashTicketForm.coordinateZ} onChange={(e) => setClashTicketForm({ ...clashTicketForm, coordinateZ: e.target.value })} required />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              <Row className="g-3">
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold small text-secondary">{t('lblRiskMatrixPriority')}</Form.Label>
                    <Form.Select className="fs-6" value={clashTicketForm.priority} onChange={(e) => setClashTicketForm({ ...clashTicketForm, priority: e.target.value })}>
                      <option value="Low">{t('optLowPriority')}</option>
                      <option value="Medium">{t('optMediumPriority')}</option>
                      <option value="High">{t('optHighPriority')}</option>
                      <option value="Critical">🔴 {t('optCriticalHold')}</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold small text-secondary">{t('lblAssignFieldEngineerLead')}</Form.Label>
                    <Form.Select className="fs-6" value={clashTicketForm.assignedTo} onChange={(e) => setClashTicketForm({ ...clashTicketForm, assignedTo: e.target.value })} required>
                      <option value="">{t('optSelectProjectEngineer')}</option>
                      {allUsers.map(u => <option key={u._id} value={u._id}>{u.name} ({u.role})</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Button variant="danger" type="submit" className="w-100 py-2 fw-bold text-white shadow-sm">{t('btnInjectClashPoint')}</Button>
            </Form>
          </Modal.Body>
        </Modal>

        {/* ======================================================================== */}
        {/* 🤖 CODE 2: AI COPILOT CHAT SYSTEM (PERFECT SIZE & AI TEXT INTEGRATION)   */}
        {/* ======================================================================== */}

        {/* 📡 Dynamic CSS Injection for Design 1 Pulse Animation (Compact & Sharp) */}
        <style dangerouslySetInnerHTML={{
          __html: `
  @keyframes mfrLivePulse {
    0% {
      box-shadow: 0 0 0 0 rgba(13, 110, 253, 0.6), 0px 4px 12px rgba(0, 0, 0, 0.25);
    }
    70% {
      box-shadow: 0 0 0 14px rgba(13, 110, 253, 0), 0px 4px 12px rgba(0, 0, 0, 0.25);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(13, 110, 253, 0), 0px 4px 12px rgba(0, 0, 0, 0.25);
    }
  }
  .mfr-ai-pulse-active {
    animation: mfrLivePulse 2s infinite !important;
    background: linear-gradient(135deg, #0d6efd, #00c6ff) !important;
  }
  .mfr-ai-text-glow {
    font-size: 19px;
    font-family: 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-shadow: 0px 1px 3px rgba(0, 0, 0, 0.3);
  }
  @media (max-width: 420px) {
    .mfr-ai-chat-popup {
      width: calc(100% - 30px) !important;
      left: 15px !important;
      bottom: 95px !important;
    }
  }
`}} />

        {/* 1. The Floating Action Button (FAB) - Matched with Rocket Size (56px) */}
        <button
          type="button"
          onClick={() => setShowFloatingAIChat(!showFloatingAIChat)}
          className={`btn text-white shadow-lg d-flex flex-column align-items-center justify-content-center animate__animated animate__fadeIn ${!showFloatingAIChat ? 'mfr-ai-pulse-active' : 'btn-dark'
            }`}
          style={{
            position: 'fixed',
            bottom: '30px',
            left: '30px',              /* 🌟 FIXED: Left Side Position */
            zIndex: 100000,
            width: '56px',             /* 🎯 Matched exactly with Rocket Button */
            height: '56px',            /* 🎯 Matched exactly with Rocket Button */
            borderRadius: '50%',
            border: 'none',
            gap: '1px',                /* Text aur icon ke darmiyan space */
            transition: 'all 0.3s ease-in-out',
            pointerEvents: 'auto'
          }}
        >
          {showFloatingAIChat ? (
            <i className="bi bi-x-lg" style={{ fontSize: '1.4rem' }}></i>
          ) : (
            <>
              <i className="bi bi-stars" style={{ fontSize: '1.1rem', lineHeight: '1' }}></i>
              <span className="mfr-ai-text-glow" style={{ lineHeight: '1' }}>AI</span>
            </>
          )}
        </button>

        {/* 2. The Chat Popup Window */}
        {showFloatingAIChat && (
          <div
            className="card shadow-lg border-0 rounded-3 animate__animated animate__fadeInUp mfr-ai-chat-popup"
            style={{
              position: 'fixed',
              bottom: '105px',
              left: '30px',            /* 🌟 FIXED: Window aligned perfectly on top of button */
              width: '100%',
              maxWidth: '360px',
              height: '480px',
              maxHeight: 'calc(100vh - 140px)',
              zIndex: 99999,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0px 12px 28px rgba(0, 0, 0, 0.25)',
              overflow: 'hidden'
            }}
          >
            <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center" style={{ padding: '12px 16px' }}>
              <h6 className="mb-0 fw-bold"><i className="bi bi-robot me-2"></i>{t('aiCopilotHeaderTitle')}</h6>
              <small className="badge bg-light text-primary fw-bold">{t('aiCopilotEngineTag')}</small>
            </div>

            <div className="card-body bg-light" style={{ overflowY: 'auto', minHeight: '180px' }}>
              {aiResponse ? (
                <div className="p-3 bg-white rounded shadow-sm border-start border-primary border-3 mb-2" style={{ fontSize: '0.9rem', whiteSpace: 'pre-line' }}>
                  <strong>{t('lblMfrAiPrefix')}:</strong> <br />
                  {aiResponse}
                </div>
              ) : (
                <div className="text-muted text-center my-4" style={{ fontSize: '0.85rem' }}>
                  {aiLoading ? (
                    <div>
                      <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                      {t('txtAiIsThinking')}
                    </div>
                  ) : (
                    t('descAiPlaceholderGreeting')
                  )}
                </div>
              )}
            </div>

            <div className="card-footer bg-white border-top-0 p-2">
              <form onSubmit={handleFloatingAISubmit} className="d-flex gap-2">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={t('phTypeYourQuestion')}
                  value={aiQuery}
                  onChange={(e) => setAiQuery(e.target.value)}
                  disabled={aiLoading}
                />
                <button type="submit" className="btn btn-primary btn-sm px-3" disabled={aiLoading || !aiQuery.trim()}>
                  {t('btnAiSend')}
                </button>
              </form>
            </div>
          </div>
        )}
      </Container >
    </>
  );

};

// 🛡️ 1. Protected Route Wrapper (Ise hamesha App se upar hona chahiye)
// eslint-disable-next-line
const ProtectedConsoleRoute = ({ children }) => {
  const isAuthenticated = !!localStorage.getItem('token');
  if (!isAuthenticated) {
    alert("Access Denied! Please login to enter the Project Control Panel.");
    return <Navigate to="/" replace />;
  }
  return children;
};

// 🖥️ 2. Main App Component (Jo poori application ko wrap karega)
const App = () => {
  return (

    <Router>
      {/* 🟢 Yeh line aapki warning khatam karegi kyunki hum AppContent ko use kar rahe hain */}
      <AppContent

      />

    </Router>
  );
};

// Is line ke baad aapki file khatam ho jani chahiye (Export statement agar hai toh)
export default App;